import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { faCheck, faTimes, faSpinner, faArrowLeft, faChevronLeft, faChevronRight, faFilePdf, faXmark, faAngleDown, faMicrophone, faMicrophoneSlash, faMagnifyingGlass, faEllipsisVertical } from '@fortawesome/free-solid-svg-icons';
import { faCalendar as faCalendarRegular } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Check } from 'lucide-react';
import BackButton from './BackToDashboardButton';
import './StockInForm-module.css';
import http from '../services/httpClient';
import UnitMappingService from '../services/unitMappingService';
import { isStockInDateDisabled } from '../utils/stockInDateBounds';
import categoryEventService from '../services/categoryEventService';
import { useTranslate } from '../translations/useTranslate';
import * as semanticFilterService from '../services/semanticFilterService';
import { normalizeVendorPhone, isValidVendorPhone, vendorPhoneError, isTerminalVendorPhoneError } from '../utils/vendorPhone';
import { pushHistoryState, canUndoHistory, canRedoHistory, EMPTY_HISTORY, cloneItemsDeep } from '../utils/editHistory';
import { MAX_ITEMS_PER_SUBMISSION } from '../utils/stockInLimits';
import { quantityBandVerdict } from '../utils/quantityBand';
import { unitAfterMatch } from '../utils/matchedUnit';
import { buildBmItemFields, buildBmTopFields, nameTypedPatch, loadContext, decideTypedRow, matchResultOf, rowView, countNeedTap, rowNeedsTap, headerText, statusFromColour, rerunUnsettled, cleanUnmatchedName, contextVendorId, scanBillMemory, rowPrintedName, confirmationLine, redChoiceFields, rowInputKind, bmRedCardDue, aiNameCheck } from '../billMemory/client';
import VendorButtons from './billMemory/VendorButtons';
import VendorProgressLine from './billMemory/VendorProgressLine';
import IntroLine from './billMemory/IntroLine';
import { reasonText as bmReasonText } from '../billMemory/core';
import RedCardActions from './billMemory/RedCardActions';
// PIECE GUARD (§5, §7) — `pcs` is the one unit that is not self-describing, so the row
// carries what one piece IS, the chips let the person act in packets while the field
// stays in pieces, and the live per-piece price kills most mistakes before submit.
// PIECE_NORMAL_BAND is deliberately NOT imported any more: the repeat-purchase popup used
// to borrow it and now triggers on any movement down to the paisa. The band still governs
// the live typing hint and the gate, both of which live in pieceBand.js / pieceGuard.js.
import {
  pieceBandVerdict, quickAddChipsFor, pieceCaptionFor, isPcsUnit,
} from '../utils/pieceBand';
import PieceProfileModal from './PieceProfileModal';
// P3.4 — ONE interpretation of the profile modal's result, shared by all three doors.
import { applyPieceProfileResult } from '../utils/pieceProfileResult';
// F6 — one definition of "which row is this answer about", exercised directly by
// utils/__tests__/remapConfirmations.test.js.
import { remapConfirmationsToRows, isValidConfirmationToken } from '../utils/pieceConfirmations';
import { PiecePopupFor, PieceBulkReview, PiecePackConfirmPopup } from './PiecePopups';
import CxCheckPopup, { CxRowAid } from './CxCheckPopup';
import { cxGetSettings, cxItemContext, cxCheckLines, cxRecordDecisions, cxFlushDecisionQueue } from '../services/cxGuardClient';
import { cxPrint, cxLinesHash, CX_ANSWERED, cxTokensFor, cxWriteBack, cxFilterWarnings, cxRecordPayload, cxRowAid, cxLastUnit, cxEntered } from '../utils/cxGuardForm';

import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from '../styles/standardStyles';

// API calls now use centralized api client
// Lightweight searchable dropdown used inside the Kitchen Unit popup (ported from item map)
const SearchableSelect = ({
  value,
  onChange,
  options = [],
  placeholder = 'Search...',
  disabled = false,
  style,
  visibleCount = 5,
  onAddClick,
  isOpen,
  onToggle,
  menuAlign = 'left', // 'left' | 'right'
  enableAddPanel = false,
  addOptionLabel = '+ Add Tool',
  addInputPlaceholder = 'Enter Tool Name...',
  onAddValue,
}) => {
  const [open, setOpen] = useState(isOpen || false);
  const [query, setQuery] = useState('');
  const [addMode, setAddMode] = useState(false);
  const [addText, setAddText] = useState('');
  const wrapRef = useRef(null);
  const controlRef = useRef(null);

  useEffect(() => { if (isOpen !== undefined) setOpen(isOpen); }, [isOpen]);
  const handleToggle = (v) => { setOpen(v); onToggle && onToggle(v); };

  useEffect(() => {
    const onDocClick = (e) => { if (!wrapRef.current) return; if (!wrapRef.current.contains(e.target)) handleToggle(false); };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [handleToggle]);

  const selected = options.find(o => o.value === value);
  const withAdd = (() => {
    const base = [...options];
    // Ensure a terminal 'add' option when add panel is enabled.
    if (enableAddPanel && !base.some(o => o.value === 'add')) base.push({ value: 'add', label: addOptionLabel });
    return base;
  })();
  const filtered = withAdd.filter(o => (o.label || o.value || '').toLowerCase().includes((query || '').toLowerCase()));

  return (
    <div ref={wrapRef} style={{ position: 'relative', width: '100%', ...(style || {}) }}>
      <div
        ref={controlRef}
        onClick={() => !disabled && handleToggle(!open)}
        role="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        style={{ width: '100%', height: 48, display: 'flex', alignItems: 'center', justifyContent: 'space-between', border: '1px solid #e5e7eb', borderRadius: 10, background: disabled ? '#f5f5f5' : '#fff', padding: '0 14px', cursor: disabled ? 'not-allowed' : 'pointer' }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 15 }}>{selected?.label ?? selected?.value ?? placeholder}</span>
        <FontAwesomeIcon icon={faAngleDown} style={{ fontSize: 10, color: '#111' }} />
      </div>
      {open && !disabled && (
        <div role="listbox" style={{ position: 'fixed', top: controlRef.current ? controlRef.current.getBoundingClientRect().bottom + 5 : '50%', left: menuAlign === 'left' ? (controlRef.current ? controlRef.current.getBoundingClientRect().left : 'auto') : 'auto', right: menuAlign === 'right' ? (controlRef.current ? window.innerWidth - controlRef.current.getBoundingClientRect().right : 'auto') : 'auto', zIndex: 9999, background: '#fff', borderRadius: 12, boxShadow: '0 6px 16px rgba(0,0,0,0.15)', padding: 8, width: controlRef.current ? controlRef.current.offsetWidth : '100%', maxHeight: '280px', overflowY: 'auto' }}>
          <input autoFocus value={query} onChange={(e) => setQuery(e.target.value)} placeholder={placeholder} style={{ width: '100%', height: 38, padding: '8px 12px', borderRadius: 10, border: '1px solid #e5e7eb', marginBottom: 8, outline: 'none' }} />
          <div style={{ maxHeight: visibleCount * 40, overflowY: 'auto' }}>
            {(filtered.length ? filtered : withAdd).map(opt => (
              <div
                key={opt.value}
                role="option"
                aria-selected={opt.value === value}
                className={opt.value === 'add' ? 'add-row' : undefined}
                onClick={() => {
                  if (opt.value === 'add') {
                    if (enableAddPanel) { setAddMode(true); setAddText(''); return; }
                    onAddClick && onAddClick(); handleToggle(false); setQuery(''); return;
                  }
                  onChange && onChange(opt.value);
                  handleToggle(false); setQuery(''); setAddMode(false);
                }}
                style={{ padding: opt.value === 'add' ? '12px 14px' : '10px 12px', borderRadius: 10, background: opt.value === 'add' ? '#000' : 'transparent', color: opt.value === 'add' ? '#fff' : '#111', cursor: 'pointer', textAlign: opt.value === 'add' ? 'center' : 'left', fontWeight: opt.value === 'add' ? 700 : 400, marginTop: opt.value === 'add' ? 8 : 0, display: 'flex', justifyContent: opt.value === 'add' ? 'center' : 'flex-start' }}
              >
                {opt.value === 'add' ? (opt.label || '+ Add') : (opt.label ?? opt.value)}
              </div>
            ))}
          </div>
          {enableAddPanel && addMode && (
            <div style={{ borderTop: '1px solid #eee', marginTop: 8, paddingTop: 8 }}>
              <input value={addText} onChange={(e) => setAddText(e.target.value)} placeholder={addInputPlaceholder} style={{ width: '100%', height: 38, padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: 10, marginBottom: 8 }} />
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-start' }}>
                <button onClick={() => { const name = (addText || '').trim(); if (!name) return; onAddValue && onAddValue(name); setAddMode(false); handleToggle(false); setQuery(''); }} style={{ padding: '10px 14px', background: '#000', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer' }}>Add</button>
                <button onClick={() => setAddMode(false)} style={{ padding: '10px 14px', background: '#fff', color: '#111', border: '1px solid #e5e7eb', borderRadius: 10, cursor: 'pointer' }}>Cancel</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const genUid = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`;
// Track batch counters per item (prefix-date combination)
const batchCounters = new Map();

// Helper function to generate batch number based on item name
const generateBatchNumber = (itemName) => {
  const today = new Date();
  const month = String(today.getMonth() + 1).padStart(2, '0'); // MM
  const year = String(today.getFullYear()).slice(-2); // YY
  const dateCode = `${month}${year}`; // MMYY

  // Get first 3 letters of item name (uppercase, letters only)
  let prefix = 'XXX'; // Default if no name
  if (itemName && itemName.trim()) {
    const letters = itemName.toUpperCase().replace(/[^A-Z]/g, ''); // Remove non-letters
    prefix = letters.slice(0, 3).padEnd(3, 'X'); // Take first 3, pad with X if needed
  }

  // Create unique key for this item-date combination
  const counterKey = `${prefix}-${dateCode}`;

  // Get or initialize counter for this item
  let counter = batchCounters.get(counterKey) || 0;
  counter += 1;
  batchCounters.set(counterKey, counter);

  // Format sequential number (4 digits)
  const sequentialNumber = String(counter).padStart(4, '0');

  return `${prefix}-${dateCode}-${sequentialNumber}`;
};

/**
 * ONE mapping from a gate blocker to the confirmation modal's data (C2 + C3).
 *
 * There used to be two hand-written copies of this — one on the first-submit path and one
 * on the resubmit path — and they drifted: the resubmit copy hardcoded the PRICE shape, so
 * a quantity blocker raised on a resubmit rendered "Entered ₹0.00 → Expected ₹0.00" with an
 * editable unit-price box. Editing it sent a price correction, which does not satisfy the
 * quantity band, so the row blocked again forever. Two copies is exactly how that happened;
 * there is now one.
 *
 * The quantity flavour must never render rupees: the numbers it carries are quantities, in
 * the item's standard unit, and the gate demands `extreme_quantity_acknowledged` — not a
 * price correction — to let them through.
 */
const gateBlockerToCorrectionData = (blocker) => {
  const itemName = (blocker.message || '').split(':')[0] || 'Item';

  if (blocker.code === 'catastrophic_quantity') {
    const entered = Number(blocker.entered_quantity_standard) || 0;
    const typical = Number(blocker.typical_quantity) || 0;
    const unit = blocker.standard_unit || '';
    const ratio = typical > 0 ? entered / typical : 0;
    return {
      requiresConfirmation: true,
      item_name: itemName,
      // Quantity fields — the ONLY ones the quantity branch of the modal reads.
      isQuantityCheck: true,
      original_quantity: entered,
      corrected_quantity: typical,
      original_unit: unit,
      vcp_quantity: typical,
      quantity_summary: `${entered.toLocaleString('en-IN')} ${unit} entered — you normally buy about ${typical.toLocaleString('en-IN')} ${unit} at a time${ratio >= 2 ? `, about ${Math.round(ratio)}× as much` : ''}.`,
      // Price fields deliberately zeroed: nothing on this path may print a rupee sign.
      original_unit_price: 0,
      corrected_unit_price: 0,
      original_total: 0,
      corrected_total: 0,
      vcp: 0,
      deviation_percentage: null,
      correction_type: 'GATE_CATASTROPHIC_QUANTITY',
      correction_message: blocker.message || 'Extreme quantity detected.',
      confidence: 0,
      isConfirmationOnly: true,
      // The editable-price modal must not open for a quantity problem — the value the user
      // needs to change is the quantity, on the row itself.
      isCriticalPriceError: false,
      isBackendConfirmation: true,
      itemIndex: blocker.index,
      // { index, type: 'extreme_quantity_acknowledged', acknowledgment: '...' } — the exact
      // token backend/services/stockInGate.js demands to let the row through.
      requiredConfirmation: blocker.requires
    };
  }

  return {
    requiresConfirmation: true,
    item_name: itemName,
    original_unit_price: blocker.entered_per_standard_unit || 0,
    corrected_unit_price: blocker.typical || 0,
    original_total: 0,
    corrected_total: 0,
    correction_type: 'GATE_CATASTROPHIC_PRICE',
    correction_message: blocker.message || 'Extreme price detected.',
    confidence: 0,
    vcp: blocker.typical || 0,
    deviation_percentage: (blocker.typical > 0 && blocker.entered_per_standard_unit)
      ? Math.abs(((blocker.entered_per_standard_unit - blocker.typical) / blocker.typical) * 100).toFixed(1)
      : '100+',
    // Without it the pop-up said "higher than expected" for a price 390× LOWER.
    deviation_direction: Number(blocker.entered_per_standard_unit) < Number(blocker.typical) ? 'lower' : 'higher',
    // "15.4× higher" reads; "Deviation: 1438.5%" does not.
    deviation_label: (() => {
      const r = Number(blocker.entered_per_standard_unit) / Number(blocker.typical);
      if (!(r > 0) || !Number.isFinite(r)) return null;
      return r >= 1 ? `${r.toFixed(1)}× higher than usual` : `${(1 / r).toFixed(1)}× lower than usual`;
    })(),
    isConfirmationOnly: true,
    isQuantityCheck: false,
    isCriticalPriceError: true,
    isBackendConfirmation: true,
    itemIndex: blocker.index,
    requiredConfirmation: blocker.requires
  };
};

/**
 * H7 — THE ONE ROUTER from a gate response to the UI that can actually resolve it.
 *
 * There were three hand-maintained copies of "look at gate_blockers and decide what to
 * open": the first submit, the resubmit-after-confirmation, and the piece conversation's
 * own retry. They disagreed, and the resubmit copy had no `piece_` filter at all.
 *
 * What that cost: EVERY piece blocker carries a `requires` field, so
 * `blockers.find(b => b.requires)` picked one up and opened the price modal —
 * `correction_type: 'GATE_CATASTROPHIC_PRICE'`, titled "Extreme price detected" — for a
 * pack-count question. Worse, for Popups A/B/C that `requires` is an ARRAY of possible
 * answers, and the confirm handler pushed it as a single confirmation token. The gate then
 * received an array where it expected an object, `conf.type` was `undefined`, the blocker
 * re-raised unchanged, and the person was stuck in a wrong-titled modal that could never
 * resolve. Reachable on any bill that needed a price acknowledgment and contained any pcs
 * line.
 *
 * This is the third place in this codebase where two hand-maintained copies of blocker
 * handling caused a bug, so they are collapsed into one.
 *
 *   piece_*                → the piece popups (they own their own conversation)
 *   catastrophic_quantity  → the quantity modal, via gateBlockerToCorrectionData
 *   catastrophic_price     → the price modal, via the same mapper
 *   anything with no       → a hard stop; the person edits the row
 *   resolvable affordance
 *
 * @returns {{ piece, deferred, overridable, hard, hasWork }}
 */
const isPieceCode = (code) => String(code || '').startsWith('piece_');

const routeGateBlockers = (blockers) => {
  const all = Array.isArray(blockers) ? blockers : [];
  // NEVER PIN THIS TO []. It was hard-coded empty ("temporarily … treat pcs same as other
  // units"), the frontend twin of the `return true` that was put in pieceGuardDisabled().
  // With it empty, `route.piece.length > 0` is never true, so every piece blocker fell
  // through to the generic "Submission blocked" alert — the person was told to "fix the
  // highlighted values" for a question that has no highlighted value to fix, and the one
  // screen that could resolve it (PieceProfileModal) never opened. The whole feature is
  // the wording; a piece question inside a bulleted error list is a dead end.
  const piece = all.filter(b => isPieceCode(b.code));
  const rest = all.filter(b => !isPieceCode(b.code));

  // A single-token `requires` is an OVERRIDE the acknowledgment modal can collect.
  // An ARRAY `requires` is a multiple-choice question and belongs to a popup, never to
  // the acknowledgment modal — pushing one as a token is exactly the bug above.
  const overridable = rest.find(b => b.requires && !Array.isArray(b.requires)) || null;
  const hard = rest.filter(b => !b.requires || Array.isArray(b.requires));

  return {
    piece,
    // Non-piece blockers on a bill that ALSO has piece ones. The piece conversation runs
    // first (it is a conversation, not a modal), and these are carried alongside it so
    // they are visible rather than silently swallowed until the next round trip.
    deferred: piece.length > 0 ? rest : [],
    overridable: piece.length > 0 ? null : overridable,
    hard: piece.length > 0 ? [] : hard,
    hasWork: all.length > 0,
  };
};

// F6/H7.3 — both live in utils/pieceConfirmations.js now: the remapper needs a test
// that exercises the real code, and the validator belongs beside it.

/**
 * H8 — THE ONE place that answers "this row's category now comes from a match".
 *
 * `autoNonConsumable` drives the purple "Not in inventory → set as Non-Consumable" badge.
 * The red-row effect RAISES it; exactly one place LOWERED it — handleInputChange, when the
 * user picks a category from the dropdown themselves — while five others wrote a category
 * from a match and left it standing: the exact-name match inside handleInputChange's own
 * name branch, the SMMF fuzzy→exact upgrade, applySuggestion's inventory branch, and both
 * autofill branches of the AI validator.
 *
 * What that looked like on screen: a GREEN, matched row reading "Category: Vegetables" with
 * a purple badge underneath saying the item is not in inventory. The card's own colour
 * proves which half is wrong — statusClass tests isNonConsumable BEFORE isExact, so green is
 * only reachable on a consumable category — but the person reading it cannot know that.
 *
 * Nothing downstream reads the flag (the payload sends `category`, and STEP 7.5 filters on
 * matchStatus), so no stock was ever misfiled by this. The cost is trust: a badge that
 * contradicts the field beside it teaches people to stop reading badges, and this badge is
 * the only thing standing between a cleaning supply and being costed as an ingredient.
 *
 * NOT for the "add as new item" path. A typed option really is NOT in inventory, so the
 * badge is true there and must survive — see applySuggestion's isTypedOption branch, which
 * deliberately does not call this.
 *
 * No category offered means nothing was learned, so the row is returned untouched: a match
 * that carries no category must not be able to clear a badge it has no opinion about.
 *
 * Returns a NEW row. Callers must reassign; this never mutates.
 */
const withMatchedCategory = (row, category) => {
  if (!row || !category) return row;
  return { ...row, category, autoNonConsumable: false };
};

const emptyItem = () => {
  return {
    uid: genUid(),
    item_name: '',
    lastStableName: '', // track last confirmed name to prevent unintended overwrites
    original_ocr_name: '', // preserve original OCR extracted text
    original_language: 'en', // language of original OCR text
    translated_name: '', // English translation of original name
    display_name: '', // what user sees (original language)
    db_name: '', // what gets saved to DB (English translation)
    is_translated: false, // flag to indicate if item has translation
    category: '',
    quantity: '',
    unit: '',
    total_price: '', // User enters total price paid
    total_price_with_tax: '',
    unit_price: '', // Auto-calculated: total_price ÷ quantity
    batch_number: generateBatchNumber(''), // Will be updated when item name is set
    expiry_date: '',
    tax_slab: '0',
    // M7 §9.7 / §3.3 — the piece-line tags the gate reads. Present on every row from the
    // start so a scheme or a replacement is a field the person fills in, not a shape the
    // payload has to invent later.
    free_quantity: '',
    is_scheme: false,
    is_free_replacement: false,
    free_reason: '',
    cgst: '',
    sgst: '',
    time: new Date().toTimeString().slice(0, 5),
    // Bill Memory: a new row is a typed row until a scan or autofill says otherwise
    bm_input_kind: 'typed',
    bm_printed_name: '',
    matchStatus: 'pending' // Don't mark as 'none' until user finishes typing
  };
};

const TAX_SLAB_OPTIONS = ['0', '5', '18', '24'].map(rate => ({
  value: rate,
  label: `${rate}%`
}));

const calculateSplitTaxFromSlab = (totalPrice, slabRate) => {
  // User explicitly wants the percentage split (e.g. 18% -> 9 and 9) rather than absolute currency amount
  const rate = parseFloat(slabRate) || 0;
  const halfTax = rate / 2;
  const formatted = halfTax > 0 ? halfTax.toString() : '';
  return { cgst: formatted, sgst: formatted };
};

const calculateTotalWithTax = (totalPrice, slabRate) => {
  const price = parseFloat(totalPrice);
  if (!Number.isFinite(price) || price <= 0) return '';

  const rate = parseFloat(slabRate) || 0;
  return (price + ((price * rate) / 100)).toFixed(2);
};

const calculateBasePriceFromTaxTotal = (totalWithTax, slabRate) => {
  const gross = parseFloat(totalWithTax);
  if (!Number.isFinite(gross) || gross <= 0) return '';

  const rate = parseFloat(slabRate) || 0;
  return (gross / (1 + (rate / 100))).toFixed(2);
};

/**
 * T2 — THE money a line contributes, in one place. Two bugs lived in the expression this
 * replaces, and it was written out by hand in five places:
 *
 *     taxForStockInEnabled && it.tax_slab ? it.total_price_with_tax : it.total_price
 *
 *   1. `it.tax_slab` is a STRING, and '0' is truthy. So with tax switched on EVERY row took
 *      the with-tax branch, including the 0% ones — which is most of them: across the live
 *      database 6,034 items sit at 0% and 131 carry a real slab.
 *
 *   2. `total_price_with_tax` is only ever written by handleInputChange — that is, only on
 *      rows a human has typed into. The OCR and Excel mappers never set it. So on a SCANNED
 *      bill it is undefined, `parseFloat(undefined) || 0` is 0, and the line contributed
 *      NOTHING: the total read ₹0.00 and the payload submitted total_price 0 — stock booked
 *      at no cost. The input beside it rendered correctly the whole time, because that one
 *      alone carried the `?? calculateTotalWithTax(...)` fallback (line ~9453).
 *
 * A slab of 0 is not tax, and a value that was never computed is computed here. Same answer
 * for a typed row, a scanned row and an imported row, which is the point.
 */
const grossLineTotal = (row, taxEnabled) => {
  const net = parseFloat(row?.total_price) || 0;
  const slab = parseFloat(row?.tax_slab) || 0;
  if (!taxEnabled || slab <= 0) return net;
  // A figure the user typed into "Total Price With Tax" wins — they may be copying a
  // rounded number straight off the bill, and the bill is the authority.
  const typed = parseFloat(row?.total_price_with_tax);
  if (Number.isFinite(typed) && typed > 0) return typed;
  return parseFloat(calculateTotalWithTax(net, slab)) || net;
};

/**
 * The price / quantity pop-up's own edit box.
 *
 * Every confirmation pop-up resubmits `pendingSubmitData`, not the form, so a number fixed in
 * the pop-up has to land in BOTH places: the payload row it resubmits, and the visible row —
 * so the form agrees with what was saved, and a failed save leaves the corrected numbers on
 * screen rather than the old ones.
 *
 * The baseline is the payload row itself (the numbers the check actually judged), in the
 * row's own entered unit. Total wins over unit price, the gate's canonical rule.
 */
const fmtEditNum = (n, dp) => (Number.isFinite(n) ? String(Number(n.toFixed(dp))) : '');

const buildCorrectionEdit = (data, pending) => {
  const index = data && Number.isInteger(data.itemIndex) ? data.itemIndex : null;
  const row = index != null && pending && Array.isArray(pending.items) ? pending.items[index] : null;
  const baseQty = row ? parseFloat(row.quantity) : NaN;
  if (!row || !(baseQty > 0)) return { available: false };

  const rowTotal = parseFloat(row.total_price);
  const rowUnit = parseFloat(row.unit_price);
  const basePrice = rowTotal > 0 ? rowTotal / baseQty : rowUnit;
  if (!(basePrice > 0)) return { available: false };
  const baseTotal = rowTotal > 0 ? rowTotal : baseQty * basePrice;

  const initialQty = fmtEditNum(baseQty, 4);
  const initialPrice = fmtEditNum(basePrice, 2);
  const qtyStr = data.editQty !== undefined ? data.editQty : initialQty;
  const priceStr = data.editPrice !== undefined ? data.editPrice : initialPrice;

  const q = parseFloat(qtyStr);
  const typedPrice = parseFloat(priceStr);
  const qtyOk = Number.isFinite(q) && q > 0;
  const priceOk = Number.isFinite(typedPrice) && typedPrice > 0;
  // An untouched price keeps its exact value — the box shows it rounded to the paisa, and
  // re-reading the rounded figure would quietly move a ₹33.3333 line.
  const priceTouched = priceOk && Math.abs(typedPrice - Number(initialPrice)) > 1e-9;
  const p = priceTouched ? typedPrice : basePrice;
  const valid = qtyOk && priceOk;

  return {
    available: true,
    index,
    row,
    unit: row.unit || '',
    baseQty,
    basePrice,
    baseTotal,
    qtyStr,
    priceStr,
    q,
    p,
    valid,
    edited: valid && (Math.abs(q - baseQty) > 1e-9 || priceTouched),
    newTotal: valid ? Number((q * p).toFixed(2)) : NaN
  };
};

/**
 * The resubmitted bill with the pop-up's numbers on its row. That row's confirmation token
 * is DROPPED: the new numbers go back through every check as fresh input, and if they still
 * look wrong the pop-up comes back showing them. Other rows keep their tokens. Only the
 * money keys the row already carried are rewritten, so the row keeps its own basis.
 */
const applyCorrectionEditToPayload = (pending, edit) => ({
  ...pending,
  items: pending.items.map((x, j) => {
    if (j !== edit.index) return x;
    const fixed = { ...x, quantity: edit.q };
    if (x.total_price !== undefined) fixed.total_price = edit.newTotal;
    if (x.unit_price !== undefined) fixed.unit_price = Number(edit.p.toFixed(4));
    return fixed;
  }),
  item_confirmations: (Array.isArray(pending.item_confirmations) ? pending.item_confirmations : [])
    .filter(t => Number(t.index) !== edit.index)
});

/**
 * The same correction on the visible row, in the form's own terms: a NET total with the
 * unit price derived from it, tax recomputed the way handleInputChange does. The payload's
 * money may be net or with-tax depending on the path that built it, so the row's total is
 * scaled by the same ratio rather than overwritten — that is correct under either basis.
 * Quantity moves by the same difference, which keeps any free (scheme) pieces intact.
 */
const applyCorrectionEditToFormRow = (row, edit, taxEnabled) => {
  const movedQty = (parseFloat(row.quantity) || 0) + (edit.q - edit.baseQty);
  const qty = movedQty > 0 ? movedQty : edit.q;
  const scale = edit.baseTotal > 0 ? edit.newTotal / edit.baseTotal : null;
  const oldNet = parseFloat(row.total_price);
  const net = scale != null && oldNet > 0 ? oldNet * scale : edit.newTotal;

  const next = {
    ...row,
    quantity: fmtEditNum(qty, 4),
    total_price: net.toFixed(2),
    unit_price: qty > 0 ? (net / qty).toFixed(4) : ''
  };

  const slab = parseFloat(row.tax_slab) || 0;
  if (taxEnabled && slab > 0) {
    const typedGross = parseFloat(row.total_price_with_tax);
    next.total_price_with_tax = Number.isFinite(typedGross) && typedGross > 0 && scale != null
      ? (typedGross * scale).toFixed(2)
      : calculateTotalWithTax(next.total_price, slab);
    const { cgst, sgst } = calculateSplitTaxFromSlab(next.total_price, slab);
    next.cgst = cgst;
    next.sgst = sgst;
  }
  return next;
};

// Friendly labels for the canonical unit symbols (value stays the symbol; label is display-only).
const UNIT_LABELS = {
  kg: 'kg',
  g: 'g',
  L: 'L',
  ml: 'ml',
  pcs: 'pcs',
  dozen: 'dozen',
  box: 'box',
  packet: 'packet',
  bag: 'bag',
  pack: 'pack',
  carton: 'carton',
  can: 'can',
  bottle: 'bottle'
};
const unitLabelFor = (sym) => UNIT_LABELS[sym] || sym;

// Helper: Get default unit based on category
const getDefaultUnitForCategory = (category) => {
  const categoryLower = (category || '').toLowerCase();

  // Canonical Strict-5 SYMBOLS (must match the unit-dropdown option values).
  const categoryDefaults = {
    'grains': 'kg',
    'grain': 'kg',
    'cereals': 'kg',
    'pulses': 'kg',
    'rice': 'kg',
    'flour': 'kg',
    'vegetables': 'kg',
    'vegetable': 'kg',
    'fruits': 'kg',
    'fruit': 'kg',
    'dairy': 'L',
    'milk': 'L',
    'beverages': 'L',
    'beverage': 'L',
    'drinks': 'L',
    'oils': 'L',
    'oil': 'L',
    'spices': 'g',
    'spice': 'g',
    'meat': 'kg',
    'seafood': 'kg',
    'fish': 'kg'
  };

  // Return '' (unknown) rather than fabricating 'pcs' — an empty unit is surfaced for confirmation.
  return categoryDefaults[categoryLower] || '';
};

// Calendar custom select (Usage-style dropdown) extracted to module scope to preserve state across re-renders
const CalendarSelect = ({ options, value, onChange, width = 140 }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const current = options.find(o => o.value === value) || options[0];
  return (
    <div
      className="calendar-select"
      ref={ref}
      style={{ width }}
      onMouseDown={(e) => {
        // Prevent overlay from seeing this interaction
        e.stopPropagation();
      }}
      onClick={(e) => {
        // Contain clicks within the popup
        e.stopPropagation();
      }}
    >
      <button
        type="button"
        className="calendar-select-trigger"
        onClick={() => setOpen(v => !v)}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <span>{current?.label}</span>
        <span className="calendar-select-caret" style={{ fontSize: '16px', color: '#000', marginLeft: '8px', display: 'inline-block', fontWeight: '600' }}>▾</span>
      </button>
      {open && (
        <div
          className="calendar-select-menu"
          style={{ width }}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="calendar-select-list standard-scrollbar">
            {options.map(opt => (
              <div
                key={opt.value}
                className={`calendar-select-option${opt.value === value ? ' selected' : ''}`}
                onClick={() => { onChange(opt.value); setOpen(false); }}
              >
                <span>{opt.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Usage-style dropdown (matches Usage.css shift dropdown)
// Generic component to render a styled dropdown with label/right text support
const UsageStyleDropdown = ({
  options = [], // array of { value, label, rightText? } or strings
  value,
  onChange,
  placeholder = 'Select',
  style = {},
  className = '',
  contained = false, // when true, menu should stay inside parent card (no absolute overlay)
  onAddClick,
  addLabel = '+ Add',
  forceUp = false
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const menuRef = useRef(null);
  const [flipUp, setFlipUp] = useState(false);
  const [menuMaxHeight, setMenuMaxHeight] = useState(240);

  const normalized = options.map(opt =>
    typeof opt === 'string'
      ? { value: opt, label: opt }
      : { value: opt.value ?? opt.label, label: opt.label ?? String(opt.value ?? ''), rightText: opt.rightText || opt.time }
  );
  if (onAddClick) {
    normalized.push({ value: '__add__', label: addLabel, isAdd: true });
  }

  const current = normalized.find(o => o.value === value) || null;

  useEffect(() => {
    const onDocDown = (e) => {
      if (!ref.current) return;
      if (!ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocDown);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  // When opened, compute if dropdown would overflow item-box and adjust
  useEffect(() => {
    if (!open) {
      setFlipUp(false);
      setMenuMaxHeight(240);
      return;
    }

    // Use timeout to allow DOM to settle before calculating position
    const timer = setTimeout(() => {
      const triggerEl = ref.current;
      const menuEl = menuRef.current;
      if (!triggerEl || !menuEl) return;

      const desired = 200; // reduced desired height to keep menu shorter inside card

      if (forceUp) {
        const triggerRect = triggerEl.getBoundingClientRect();
        const spaceAbove = triggerRect.top - 4;
        setFlipUp(true);
        setMenuMaxHeight(Math.max(120, Math.min(desired, spaceAbove)));
        return;
      }

      // Find nearest item-box ancestor
      const itemBox = triggerEl.closest('.item-box');
      if (!itemBox) {
        const triggerRect = triggerEl.getBoundingClientRect();
        const spaceBelow = window.innerHeight - triggerRect.bottom - 4;
        const spaceAbove = triggerRect.top - 4;
        if (spaceBelow >= 140) {
          setFlipUp(false);
          setMenuMaxHeight(Math.min(desired, spaceBelow));
        } else if (spaceAbove > spaceBelow) {
          setFlipUp(true);
          setMenuMaxHeight(Math.min(desired, spaceAbove));
        } else {
          setFlipUp(false);
          setMenuMaxHeight(Math.max(120, Math.min(desired, spaceBelow)));
        }
        return;
      }

      const boxRect = itemBox.getBoundingClientRect();
      const triggerRect = triggerEl.getBoundingClientRect();
      const viewportBottom = boxRect.bottom; // we only care inside box
      const spaceBelow = viewportBottom - triggerRect.bottom - 4; // 4px gap

      if (spaceBelow >= 140) { // enough space to show downward
        setFlipUp(false);
        setMenuMaxHeight(Math.min(desired, spaceBelow));
      } else {
        // Try flipping upwards inside box
        const spaceAbove = triggerRect.top - boxRect.top - 4;
        if (spaceAbove > spaceBelow) {
          setFlipUp(true);
          setMenuMaxHeight(Math.min(desired, spaceAbove));
        } else {
          setFlipUp(false);
          setMenuMaxHeight(Math.max(120, Math.min(desired, spaceBelow)));
        }
      }
    }, 0);

    return () => clearTimeout(timer);
  }, [open, forceUp]);

  return (
    <div className={`relative ${className} ${contained ? 'dropdown-contained-wrapper' : ''}`} ref={ref} style={{ position: 'relative', ...(style || {}) }}>
      <div className="shift-select" onClick={() => setOpen(o => !o)}>
        <div className="input-text-wrapper">
          <span className="shift-details">
            {current ? (
              <>
                <span className="shift-name">{current.label}</span>
                {current.rightText && <span className="shift-time">{current.rightText}</span>}
              </>
            ) : (
              <span className="shift-name" style={{ color: '#888' }}>{placeholder}</span>
            )}
          </span>
        </div>
        <span className="chevron-down" />
      </div>
      {open && (
        <div
          ref={menuRef}
          className={`shift-dropdown-options ${contained ? 'contained' : ''} ${flipUp ? 'flip-up' : ''}`}
          style={!contained ? { maxHeight: menuMaxHeight + 40 } : undefined}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="select-list" style={{ maxHeight: menuMaxHeight }}>
            {normalized.map(opt => (
              <div
                key={String(opt.value)}
                className={`shift-option${opt.isAdd ? ' add-row' : ''}`}
                style={opt.isAdd ? { justifyContent: 'center' } : undefined}
                onClick={() => { if (opt.isAdd || opt.value === '__add__') { onAddClick && onAddClick(); setOpen(false); } else { onChange(opt.value); setOpen(false); } }}
              >
                <span className="shift-name" style={opt.isAdd ? { fontWeight: 700, color: '#fff', background: '#000', padding: '6px 10px', borderRadius: 8, width: '100%', textAlign: 'center', display: 'block' } : undefined}>
                  {opt.label}
                </span>
                {!opt.isAdd && opt.rightText && <span className="shift-time">{opt.rightText}</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Tax slab: the common GST slabs, plus "Custom" for any other rate (12%, 2.5%, …). A rate
// that is not one of the slabs — typed here, or carried in from the inventory item or a
// scanned bill — opens in the custom box, instead of hiding behind a blank "0%".
const TAX_SLAB_CUSTOM = '__custom__';
const matchTaxSlabPreset = (value) => {
  if (value === '' || value == null) return '0';
  const rate = parseFloat(value);
  if (!Number.isFinite(rate)) return '0';
  const hit = TAX_SLAB_OPTIONS.find(o => parseFloat(o.value) === rate);
  return hit ? hit.value : null;
};

const TaxSlabPicker = ({ value, onChange }) => {
  const preset = matchTaxSlabPreset(value);
  const [customChosen, setCustomChosen] = useState(false);
  const inputRef = useRef(null);
  const showCustom = customChosen || preset == null;

  useEffect(() => {
    if (customChosen && inputRef.current) inputRef.current.focus();
  }, [customChosen]);

  if (!showCustom) {
    return (
      <UsageStyleDropdown
        className="tax-slab-dropdown"
        options={[...TAX_SLAB_OPTIONS, { value: TAX_SLAB_CUSTOM, label: 'Custom' }]}
        value={preset}
        onChange={(val) => {
          if (val === TAX_SLAB_CUSTOM) { setCustomChosen(true); onChange(''); }
          else onChange(val);
        }}
        placeholder="0%"
        contained
      />
    );
  }

  return (
    <div className="tax-slab-custom">
      <input
        ref={inputRef}
        type="text"
        inputMode="decimal"
        placeholder="Rate"
        value={value ?? ''}
        onChange={e => {
          const v = e.target.value.trim();
          // tax_slab is NUMERIC(5,2): at most two decimals, and no rate above 100%.
          if (!/^\d*\.?\d{0,2}$/.test(v)) return;
          onChange(parseFloat(v) > 100 ? '100' : v);
        }}
        className="stockin-tax-custom-input"
        aria-label="Custom tax rate (%)"
      />
      <span className="tax-slab-custom-suffix">%</span>
      <button
        type="button"
        className="tax-slab-custom-back"
        title="Pick from the tax list"
        aria-label="Pick from the tax list"
        onClick={() => { setCustomChosen(false); if (preset == null) onChange('0'); }}
      >
        <span className="chevron-down" />
      </button>
    </div>
  );
};

// Standalone Pre-Validation Modal Component to prevent recreation/blinking on re-renders
//
// A warned row can be corrected right here: quantity and unit price, the same edit box and
// arithmetic as the post-submit price pop-up (buildCorrectionEdit), read from the visible
// row in the form's own net terms. Saving hands the numbers back to the form, which writes
// them exactly as typing would and then re-runs the whole Submit ladder — so the corrected
// row faces every check again, and Proceed is never pressed on the person's behalf.
const PreValidationModal = ({ warnings, errors, onClose, onProceed, rows = [], onSaveEdits }) => {
  const hasErrors = errors && errors.length > 0;
  const hasWarnings = warnings && warnings.length > 0;
  const [rowEdits, setRowEdits] = useState({}); // row index → { editQty?, editPrice? }

  const editFor = (index) => {
    const row = Number.isInteger(index) ? rows[index] : null;
    if (!row || !onSaveEdits) return { available: false };
    return buildCorrectionEdit(
      { itemIndex: 0, ...(rowEdits[index] || {}) },
      { items: [{ quantity: row.quantity, total_price: row.total_price, unit_price: row.unit_price, unit: row.unit }] }
    );
  };
  // One edit box per row, on its first warning — a row warned twice is still one row.
  const firstWarningFor = new Map();
  (warnings || []).forEach((w, i) => {
    if (Number.isInteger(w.index) && !firstWarningFor.has(w.index)) firstWarningFor.set(w.index, i);
  });
  const edited = [...firstWarningFor.keys()].map(index => ({ index, edit: editFor(index) }))
    .filter(x => x.edit.available && (rowEdits[x.index] !== undefined));
  const changed = edited.filter(x => x.edit.edited);
  const anyInvalid = edited.some(x => !x.edit.valid);
  const setEdit = (index, key, value) => setRowEdits(prev => ({ ...prev, [index]: { ...(prev[index] || {}), [key]: value } }));

  return (
    <div className="modal-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000, padding: '20px' }}>
      <div className="modal-content" style={{ width: 'min(94vw, 600px)', maxHeight: '90vh', background: '#fff', borderRadius: 16, padding: 20, boxShadow: '0 20px 60px rgba(0,0,0,0.3)', position: 'relative', fontFamily: 'Roboto, sans-serif', overflowY: 'auto' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 16 }}>
          <div style={{ fontSize: 40, marginBottom: 8 }}>
            {hasErrors ? '❌' : '⚠️'}
          </div>
          <h3 style={{ fontSize: 22, fontWeight: '700', color: '#000', marginBottom: 0 }}>
            {hasErrors ? 'Cannot Submit' : 'Review Before Submitting'}
          </h3>
          {hasErrors && (
            <p style={{ fontSize: 14, color: '#666', marginTop: 6, marginBottom: 0 }}>
              Please fix the following errors before proceeding
            </p>
          )}
        </div>

        {/* Errors Section */}
        {hasErrors && (
          <div style={{ marginBottom: 16 }}>
            <h4 style={{ fontSize: 15, fontWeight: '700', color: '#dc3545', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
              <span>❌</span>
              <span>Errors ({errors.length})</span>
            </h4>
            <div style={{ background: '#fff5f5', border: '2px solid #dc3545', borderRadius: 12, padding: 12 }}>
              {errors.map((error, idx) => (
                <div key={idx} style={{ marginBottom: idx < errors.length - 1 ? 12 : 0, paddingBottom: idx < errors.length - 1 ? 12 : 0, borderBottom: idx < errors.length - 1 ? '1px solid #fecdd3' : 'none' }}>
                  <div style={{ fontWeight: '600', fontSize: 14, color: '#dc3545', marginBottom: 2 }}>
                    {error.item || `Item ${error.index + 1}`}
                  </div>
                  <div style={{ fontSize: 13, color: '#721c24', lineHeight: 1.4 }}>
                    {error.message}
                  </div>
                  {error.details && (
                    <div style={{ marginTop: 6, padding: 10, background: '#fff', borderRadius: 8, fontSize: 12, color: '#666' }}>
                      {error.details.receivedUnit && (
                        <div>Your unit: <strong>{error.details.receivedUnit}</strong> ({error.details.receivedUnitType})</div>
                      )}
                      {error.details.standardUnit && (
                        <div>Required: <strong>{error.details.standardUnit}</strong> ({error.details.standardUnitType})</div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Warnings Section */}
        {hasWarnings && (
          <div style={{ marginBottom: 16 }}>
            <h4 style={{ fontSize: 15, fontWeight: 'bold', color: '#000', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
              <span>Warnings ({warnings.length})</span>
            </h4>
            <div style={{ background: '#fff', border: '1px solid #d1d5db', borderRadius: 12, padding: 12, maxHeight: '280px', overflowY: 'auto' }}>
              {warnings.map((warning, idx) => {
                // Remove item name prefix from message
                const displayMessage = (() => {
                  const message = warning.message || '';
                  const itemName = warning.item || '';
                  if (message.toLowerCase().startsWith(`${itemName.toLowerCase()}:`)) {
                    return message.slice(itemName.length + 1).trim();
                  }
                  return message;
                })();

                return (
                  <div key={idx} style={{ marginBottom: idx < warnings.length - 1 ? 12 : 0, paddingBottom: idx < warnings.length - 1 ? 12 : 0, borderBottom: idx < warnings.length - 1 ? '1px solid #e5e7eb' : 'none' }}>
                    <div style={{ fontWeight: 'bold', fontSize: 14, color: '#000', marginBottom: 2 }}>
                      {warning.item || `Item ${warning.index + 1}`}
                    </div>
                    <div style={{ fontSize: 13, color: '#4b5563', lineHeight: 1.4, marginBottom: 6 }}>
                      {displayMessage}
                    </div>
                    {warning.details && (
                      <div style={{ padding: 10, background: '#f9fafb', borderRadius: 8, fontSize: 12, color: '#666' }}>
                        {warning.details.currentPrice && (
                          <div style={{ marginBottom: 3 }}>
                            <span style={{ color: '#999' }}>Current:</span> <strong>{warning.details.currentPrice}</strong>
                          </div>
                        )}
                        {warning.details.historicalPrice && (
                          <div style={{ marginBottom: 3 }}>
                            <span style={{ color: '#999' }}>Historical:</span> <strong>{warning.details.historicalPrice}</strong>
                          </div>
                        )}
                        {warning.details.deviation && (
                          <div style={{ marginBottom: 3 }}>
                            <span style={{ color: '#999' }}>Deviation:</span> <strong style={{ color: '#dc3545' }}>{warning.details.deviation}</strong>
                          </div>
                        )}
                        {warning.details.suggestion && (
                          <div style={{ marginTop: 6, fontStyle: 'italic', color: '#dc3545', fontWeight: 'bold' }}>
                            Extreme Warning: {warning.details.suggestion}
                          </div>
                        )}
                      </div>
                    )}
                    {firstWarningFor.get(warning.index) === idx && (() => {
                      const ed = editFor(warning.index);
                      if (!ed.available) return null;
                      const money2 = (n) => n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                      return (
                        <div className="prevalidation-edit" data-row-index={warning.index} style={{ marginTop: 10, padding: 12, border: '1px solid #e5e7eb', borderRadius: 10, background: '#fff', textAlign: 'left' }}>
                          <div style={{ fontSize: 13, fontWeight: 700, color: '#111827', marginBottom: 8 }}>
                            Wrong number? Change it here
                          </div>
                          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                            <label style={{ flex: '1 1 120px', fontSize: 12, fontWeight: 600, color: '#374151' }}>
                              Quantity ({ed.unit})
                              <input
                                type="number"
                                step="any"
                                min="0"
                                inputMode="decimal"
                                aria-label={`Quantity for ${warning.item || `item ${warning.index + 1}`}`}
                                className="prevalidation-edit-qty"
                                value={ed.qtyStr}
                                onChange={(e) => setEdit(warning.index, 'editQty', e.target.value)}
                                style={{ display: 'block', width: '100%', marginTop: 6, padding: 10, fontSize: 16, fontWeight: 700, border: '1px solid #d1d5db', borderRadius: 8, boxSizing: 'border-box' }}
                              />
                            </label>
                            <label style={{ flex: '1 1 120px', fontSize: 12, fontWeight: 600, color: '#374151' }}>
                              Unit price (₹ per {ed.unit})
                              <input
                                type="number"
                                step="any"
                                min="0"
                                inputMode="decimal"
                                aria-label={`Unit price for ${warning.item || `item ${warning.index + 1}`}`}
                                className="prevalidation-edit-price"
                                value={ed.priceStr}
                                onChange={(e) => setEdit(warning.index, 'editPrice', e.target.value)}
                                style={{ display: 'block', width: '100%', marginTop: 6, padding: 10, fontSize: 16, fontWeight: 700, border: '1px solid #d1d5db', borderRadius: 8, boxSizing: 'border-box' }}
                              />
                            </label>
                          </div>
                          <div style={{ marginTop: 8, fontSize: 13, color: '#374151' }}>
                            {ed.valid ? (
                              <>
                                Line total: <strong>₹{money2(ed.newTotal)}</strong>
                                {ed.edited && <span style={{ color: '#6b7280' }}> (was ₹{money2(ed.baseTotal)})</span>}
                              </>
                            ) : (
                              <span style={{ color: '#b91c1c' }}>Enter a quantity and a unit price above zero.</span>
                            )}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
          <button
            onClick={onClose}
            style={{ flex: 1, padding: '12px', background: '#f1f3f5', color: '#495057', border: 'none', borderRadius: '12px', fontSize: 15, fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s' }}
            onMouseOver={e => e.target.style.background = '#e9ecef'}
            onMouseOut={e => e.target.style.background = '#f1f3f5'}
          >
            Go Back & Fix
          </button>
          {!hasErrors && (changed.length > 0 || anyInvalid ? (
            // Numbers changed here → they go back to the form and through Submit again.
            <button
              type="button"
              disabled={anyInvalid}
              onClick={() => onSaveEdits(changed.map(({ index, edit }) => ({
                index,
                quantity: fmtEditNum(edit.q, 4),
                total_price: edit.newTotal.toFixed(2)
              })))}
              style={{ flex: 1, padding: '12px', background: anyInvalid ? '#9ca3af' : '#000', color: '#fff', border: 'none', borderRadius: '12px', fontSize: 15, fontWeight: '600', cursor: anyInvalid ? 'not-allowed' : 'pointer', transition: 'all 0.2s' }}
            >
              Save & re-check
            </button>
          ) : (
            <button
              onClick={onProceed}
              style={{ flex: 1, padding: '12px', background: '#000', color: '#fff', border: 'none', borderRadius: '12px', fontSize: 15, fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s' }}
              onMouseOver={e => e.target.style.background = '#333'}
              onMouseOut={e => e.target.style.background = '#000'}
            >
              Proceed Anyway
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

/**
 * '2026-08-30' → '30 Aug 2026'. For messages a human has to act on.
 *
 * Deliberately string-only. `new Date('2026-08-30')` is parsed as UTC midnight, which is
 * the previous evening in every timezone west of Greenwich and prints the 29th — the exact
 * off-by-one this whole date path exists to keep out of the reports.
 */
const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const formatDayForHumans = (ymd) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(ymd || '').slice(0, 10));
  if (!m) return String(ymd || '');
  const monthIdx = Number(m[2]) - 1;
  if (monthIdx < 0 || monthIdx > 11) return String(ymd);
  return `${Number(m[3])} ${MONTH_ABBR[monthIdx]} ${m[1]}`;
};

const StockInForm = ({ ocrData = null, manualHeaderAddTrigger = 0, onBack = null, onSuccess = null, goTo = null, stockInDate = null, onVoiceRecordingChange = null, onCancel = null, bmPickedVendor = null, onBmVendorPicked = null }) => {
  // Translation hook
  const { t, language } = useTranslate();
  const localHost = typeof window !== 'undefined' ? window.location.hostname : '';
  const isLocalCsvDownloadVisible =
    localHost === 'localhost' ||
    localHost === '127.0.0.1' ||
    localHost === '0.0.0.0' ||
    /^10\./.test(localHost) ||
    /^192\.168\./.test(localHost) ||
    /^172\.(1[6-9]|2\d|3[0-1])\./.test(localHost);

  // Vendor info state
  const [vendorName, setVendorName] = useState('');
  const [vendorPhone, setVendorPhone] = useState('');
  const [currentShift, setCurrentShift] = useState('');
  const [productionDate, setProductionDate] = useState(() => stockInDate || new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' }));
  // What happened to the scanned bill's printed date: { tone: 'info'|'warn', text } or null.
  // Cleared the moment the person picks a date themselves.
  const [billDateNote, setBillDateNote] = useState(null);
  const billDateAppliedForRef = useRef(null);
  // The day the LAST saved bill was actually filed under, as the server reported it.
  // Read by the success message so the person sees which day their stock landed on
  // without having to reopen the expense card to find out.
  const savedReceivedDateRef = useRef('');

  /**
   * The tail every success message now carries: the day the bill was filed under.
   *
   * Naming it is the point. Four different doors show a success here, and until now not
   * one of them said which day the stock landed on — so a bill that quietly went to today
   * instead of the day the picker showed was only ever discovered later, in a report, by
   * somebody who could no longer say which bill it was.
   */
  const savedOnLine = () => {
    const d = savedReceivedDateRef.current;
    return d ? `\n\nRecorded for ${formatDayForHumans(d)}.` : '';
  };

  // Item 1 — block Stock-In while a closing-stock count is in progress.
  const [closingStockLocked, setClosingStockLocked] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const bId = UnitMappingService.getBusinessId() || localStorage.getItem('businessId');
        if (!bId) return;
        const res = await http.get(`/closing-stock/lock-status?businessId=${bId}`);
        if (!cancelled && res && res.success && res.locked) setClosingStockLocked(true);
      } catch (e) { /* non-fatal */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // NOTE: unit changes on stock-in rows no longer open the global Unit Change
  // Confirmation modal. The row's unit is just the unit the bill was received
  // in — the backend converts the quantity into the item's standard unit at
  // submit (500 g into a kg item → +0.5 kg stock). Permanently changing an
  // item's unit is done deliberately from the Inventory page.

  const [onboardingDate, setOnboardingDate] = useState(null);

  useEffect(() => {
    const fetchOnboardingDate = async () => {
      try {
        const userStr = localStorage.getItem('user');
        if (userStr) {
          const user = JSON.parse(userStr);
          // Try to get from localStorage first
          let dateStr = user.businessCreatedAt || user.created_at || user.createdAt;
          
          if (!dateStr) {
            // Fetch from backend status API if missing from localStorage user cache
            const statusResponse = await http.get('/auth/status');
            const statusUser = statusResponse?.user;
            if (statusUser) {
              dateStr = statusUser.businessCreatedAt || statusUser.createdAt;
              // Update user in localStorage
              const updatedUser = { 
                ...user, 
                createdAt: statusUser.createdAt, 
                businessCreatedAt: statusUser.businessCreatedAt 
              };
              localStorage.setItem('user', JSON.stringify(updatedUser));
            }
          }

          if (dateStr) {
            const d = new Date(dateStr);
            // An unparseable value must stay UNKNOWN. Stored as an Invalid Date it is
            // still truthy, which used to be enough to arm the clamp below and the
            // calendar's floor with a date that compares false against everything.
            if (!Number.isNaN(d.getTime())) {
              d.setHours(0, 0, 0, 0);
              setOnboardingDate(d);
            }
          }
        }
      } catch (e) {
        console.warn('Failed to fetch onboarding date:', e);
      }
    };

    fetchOnboardingDate();
  }, []);

  useEffect(() => {
    if (stockInDate) {
      setProductionDate(stockInDate);
    }
  }, [stockInDate]);

  useEffect(() => {
    // Only guard against future dates — all past dates are allowed for stock receipt
    if (productionDate) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const parts = String(productionDate).split('T')[0].split('-');
      if (parts.length === 3) {
        const currentProdDate = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
        if (currentProdDate > today) {
          const maxStr = today.toLocaleDateString('en-CA');
          if (productionDate !== maxStr) {
            setProductionDate(maxStr);
          }
        }
      }
    }
  }, [productionDate]);

  const [items, setItems] = useState([emptyItem()]);
  const [totalCost, setTotalCost] = useState(0);
  const [cgst, setCgst] = useState('');
  const [sgst, setSgst] = useState('');
  const [taxForStockInEnabled, setTaxForStockInEnabled] = useState(false);
  const [showOverflowMenu, setShowOverflowMenu] = useState(false);
  // The persisted setting value (so a temporary Excel auto-enable can be reverted after submit)
  const taxSettingRef = useRef(false);
  // True when an uploaded tax file forces tax ON — so the async settings load won't clobber it
  const forceTaxOnRef = useRef(false);
  const [vendorAutofillPromptEnabled, setVendorAutofillPromptEnabled] = useState(false);

  const displayCgst = taxForStockInEnabled
    ? items.reduce((sum, item) => sum + (parseFloat(item.cgst) || 0), 0)
    : (parseFloat(cgst) || 0);

  const displaySgst = taxForStockInEnabled
    ? items.reduce((sum, item) => sum + (parseFloat(item.sgst) || 0), 0)
    : (parseFloat(sgst) || 0);
  // ❌ REMOVED: Client-side batch counter (now server-side)
  // const [batchCounter, setBatchCounter] = useState(1);
  // Separate modal states with unique purposes
  const [showVendorModal, setShowVendorModal] = useState(false);
  const [vendorModalMessage, setVendorModalMessage] = useState('');
  const [showYellowItemsModal, setShowYellowItemsModal] = useState(false);
  const [yellowItemsModalMessage, setYellowItemsModalMessage] = useState('');
  const [showUnitMismatchModal, setShowUnitMismatchModal] = useState(false);
  const [unitMismatchItems, setUnitMismatchItems] = useState([]);
  const [showPreValidationModal, setShowPreValidationModal] = useState(false);
  const [preValidationData, setPreValidationData] = useState({ warnings: [], errors: [] });
  /**
   * T1 — the bill-total mismatch prompt, and the memory of what was answered.
   *
   * The acknowledgement is tied to the DIFFERENCE, not to the session: agreeing that a
   * ₹240 gap is fine says nothing about the ₹1,900 gap you create by fixing a quantity
   * afterwards. Change the numbers and you are asked again, which is the whole point of
   * asking. Cleared on a successful submit, along with the rest of the form.
   */
  const [totalMismatchPrompt, setTotalMismatchPrompt] = useState(null);
  const ackedTotalDifferenceRef = useRef(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  /**
   * Informational toast — reports something and then gets out of the way.
   *
   * Deliberately NOT the SuccessModal: that one's close handler resets the entire form, so
   * using it to say "Added 7 items" deleted the 7 items on OK. Nothing here touches state
   * other than its own.
   */
  const [infoToast, setInfoToast] = useState(null);

  /**
   * "What is normal for this item" — the same vault numbers the submit gate judges by,
   * fetched so the form can say so under the quantity box while the user types instead of
   * rejecting the bill after they finish it. Keyed by item_id; only matched rows have one.
   */
  const [itemBenchmarks, setItemBenchmarks] = useState({});

  /**
   * PIECE GUARD — "what is ONE piece of this item", keyed by item_id.
   *
   * `null` for an item the vault has no profile for, so the form can tell "no profile
   * exists" apart from "the request has not come back yet". Fetched alongside the
   * benchmarks because both are needed BEFORE the person types, not after they submit.
   */
  const [itemPieceProfiles, setItemPieceProfiles] = useState({});

  /**
   * The piece conversation, when the gate has stopped a bill.
   *
   *   blockers        every piece blocker the gate returned
   *   activeBlocker   the one whose popup is on screen (null while the §7 Popup D
   *                   review list is showing instead)
   *   bulkReview      set only when 3+ lines tripped — one list, sorted by value,
   *                   NEVER a queue of popups
   *   confirmations   the per-item answers gathered so far
   *   payload         the submission being negotiated
   *   profileFor      the row whose Step 1 screen is open
   */
  const [pieceGate, setPieceGate] = useState(null);
  /**
   * H8.2 — §8.1's "suggest but never accuse", made visible.
   *
   * When a profile is still PROVISIONAL the backend does not block: it returns its
   * verdicts as `gate_warnings` with an `_unproven` suffix and quarantines the row from
   * the price band. `gate_warnings` had ZERO consumers in this file, so for a provisional
   * profile the person saw nothing at all — no block, and no suggestion either. The
   * suggestion half of the rule simply did not exist on screen.
   *
   * Rendered as a non-blocking summary line after the save. NEVER a modal: a warning that
   * interrupts is a block wearing a different colour, and §8.1 is precisely the rule that
   * says an unproven profile may not stop anybody.
   */
  const [saveWarnings, setSaveWarnings] = useState([]);
  const [saveWarningsOpen, setSaveWarningsOpen] = useState(false);
  /**
   * P5 — new `pcs` items this bill created, each needing its piece definition.
   *
   * A brand-new item has no item_id when the gate runs, so Layer C cannot judge it and its
   * first purchases are quarantined from the price benchmark. Asking now — while the
   * delivery is still on the counter — is what makes the quarantine temporary rather than
   * permanent: saving the profile retroactively releases those rows server-side.
   */
  const [pieceProfileQueue, setPieceProfileQueue] = useState([]);
  const [pieceProfileSaving, setPieceProfileSaving] = useState(false);
  const [pieceProfileError, setPieceProfileError] = useState(null);
  /**
   * THE REPEAT-PURCHASE CHECK — packet items only, once per item per bill.
   *
   * A pack size is the one fact in this system that goes stale without anybody doing
   * anything wrong: the supplier moves an item from a 4-pack to a 6-pack and every row
   * entered afterwards is counted against a pack that no longer exists. The ladder cannot
   * catch that, because the stale fact IS its yardstick. So before the bill is sent, each
   * packet item reads its own definition back — "last time: 4 pieces per packet at ₹45"
   * — with two buttons and no typing.
   *
   * Items declared as arriving in single pieces are never in this queue. There is no pack
   * size on them that could have changed, and asking anyway is the nagging that teaches a
   * team to click past everything.
   *
   * The answers live in a REF, not in state, and it is not a style choice: the last
   * confirmation re-enters handleSubmitClick immediately, and a state write has not landed
   * by then — the queue would be rebuilt from the pre-answer list and the same item would
   * be asked again, forever. `answeredConfirmations` learned this the hard way (H6); the
   * ref is written synchronously so the re-entry sees it.
   *
   * Cleared per bill, not per session: the whole point is that the person sees the number
   * they are about to buy against, and a confirmation carried over from yesterday's
   * delivery is not that.
   */
  const [packConfirmQueue, setPackConfirmQueue] = useState([]);
  const packConfirmedIdsRef = useRef([]);
  // The exact body last sent to the gate — the piece conversation resubmits THIS, with
  // the human's answers attached, so a confirmed value walks through every check again.
  const pieceSubmittedPayloadRef = useRef(null);
  /**
   * S4 — THE ANSWERS ALREADY GIVEN.
   *
   * handleSubmit's payload carried no `item_confirmations` key at all, so the gate always
   * started with `confirmations: []`. Cancelling a piece conversation and submitting again
   * from the form made the person RE-ANSWER EVERYTHING, including the typed reasons §14B
   * insists on — which is precisely how people learn to type "ok" into a reason box.
   *
   * Cleared by resetFormAfterSuccess, because a new bill is a new set of facts and last
   * bill's excuses must never be attached to it.
   */
  const [answeredConfirmations, setAnsweredConfirmations] = useState([]);

  /**
   * ── STOCK-IN SAFETY ENGINE (cx) — THE ENTRY GUARD ────────────────────────────────────
   *
   * INERT UNLESS /api/cx/settings SAYS THIS BUSINESS'S GUARD IS ON. With it off the form
   * makes one settings call on mount and nothing else changes — no request, no state, no
   * token, no aid (StockInForm.cxGuard.render.test.js pins it). Every cx failure is
   * fail-open: the bill carries on exactly as it would have without the guard.
   *
   * With it on, STEP 8.7 of the submit ladder judges the bill's protected lines and opens
   * CxCheckPopup for the ones worth asking about; the answers become the EXISTING tokens in
   * answeredConfirmations (rule 4 — one conversation, never a second safety popup), and
   * after the save record-decisions tells the server what was decided per line.
   *
   * Refs, not state, for the same reason as packConfirmedIdsRef: the ladder re-enters
   * handleSubmitClick, and the re-entry must see the answers immediately.
   */
  const [cxGuardOn, setCxGuardOn] = useState(false);
  const cxGuardRef = useRef({ on: false, popup: false });
  const [cxPopup, setCxPopup] = useState(null);
  // item-context answers: state for the row aids, the same object in a ref for callbacks
  const [cxContext, setCxContext] = useState({ byId: {}, byName: {} });
  const cxContextRef = useRef({ byId: {}, byName: {} });
  const cxCheckedHashRef = useRef('');            // the lines last judged with nothing left to ask
  const cxInFlightRef = useRef(false);            // a double tap must not send two checks
  const cxDecisionsRef = useRef(new Map());       // uid → what record-decisions will be told
  const cxResolvedUidsRef = useRef(new Set());    // rows the popup settled (STEP 9 filter)
  const [cxResume, setCxResume] = useState(0);

  // The numbers the gate will judge for a row — exactly what handleSubmit sends.
  const cxPayloadLine = (it) => {
    const unitNormalized = normalizeUnitForKey(it.unit || '');
    const savedConversion = containerConversions[`${normalizeItemKey(it)}_${unitNormalized}`];
    const packQty = it.supplier_conversion_quantity ?? savedConversion?.factor ?? null;
    const packUnit = it.supplier_conversion_unit ?? savedConversion?.toUnitSymbol ?? null;
    return {
      row_key: it.uid,
      item_id: it.createAsNew ? null : (it.item_id || null),
      item_name: it.is_translated ? (it.db_name || '').trim() : (it.item_name || '').trim(),
      quantity: Number(parseFloat(it.quantity) || 0) + Number(parseFloat(it.free_quantity) || 0),
      unit: UnitMappingService.normalizeUnitSymbol(it.unit),
      total: grossLineTotal(it, taxForStockInEnabled),
      pack: packQty != null && Number(packQty) > 0 && packUnit ? { quantity: Number(packQty), unit: packUnit } : null,
    };
  };

  const cxMergeContext = useCallback((data, asked) => {
    const next = { byId: { ...cxContextRef.current.byId }, byName: { ...cxContextRef.current.byName } };
    const got = new Set();
    ((data && data.items) || []).forEach((c) => {
      next.byId[Number(c.item_id)] = c;
      next.byName[String(c.name || '').trim().toLowerCase()] = c;
      got.add(Number(c.item_id));
    });
    // Asked and not known → null, so it is not asked for again on every keystroke.
    (asked || []).forEach((id) => { if (!got.has(id)) next.byId[id] = null; });
    cxContextRef.current = next;
    setCxContext(next);
  }, []);

  // Settings once per mount. Guard on → warm the item cache with recent purchases (so the
  // unit default is known the first time a row is matched) and send queued decisions.
  useEffect(() => {
    let alive = true;
    cxGetSettings().then((s) => {
      if (!alive || !s || s.guard_enabled !== true) return;
      cxGuardRef.current = { on: true, popup: s.popup_enabled !== false };
      setCxGuardOn(true);
      cxItemContext({ recent: 100 }).then((d) => { if (alive && d && d.enabled) cxMergeContext(d, []); }).catch(() => {});
      cxFlushDecisionQueue().catch(() => {});
    }).catch(() => {});
    return () => { alive = false; };
  }, [cxMergeContext]);

  // The matched rows' context — the benchmarks loader's shape: debounced, additive.
  useEffect(() => {
    if (!cxGuardOn) return undefined;
    const wanted = [...new Set(
      items.map(it => it.item_id).filter(id => Number.isInteger(id) && id > 0)
    )].filter(id => !(id in cxContext.byId)).slice(0, 100);
    if (wanted.length === 0) return undefined;
    const timer = setTimeout(() => {
      cxItemContext({ item_ids: wanted }).then((d) => { if (d && d.enabled) cxMergeContext(d, wanted); }).catch(() => {});
    }, 400);
    return () => clearTimeout(timer);
  }, [items, cxContext, cxGuardOn, cxMergeContext]);

  /**
   * STEP 8.7. True when it opened the popup (the ladder stops there; the popup's Save
   * re-enters it). False in every other case — guard off, nothing to ask, popup switched
   * off for this business (shadow mode: decisions are still recorded), or any failure.
   */
  const cxStep = async (rows) => {
    if (!cxGuardRef.current.on) return false;
    if (cxInFlightRef.current) return true; // the first tap is still checking and carries on itself
    const lines = rows.map(cxPayloadLine);
    const hash = cxLinesHash(lines);
    if (hash === cxCheckedHashRef.current) return false;

    // A row edited since it was decided loses that decision and its tokens, and is asked again.
    const printByUid = new Map(lines.map(l => [l.row_key, cxPrint(l)]));
    cxDecisionsRef.current.forEach((d, uid) => {
      if (printByUid.get(uid) !== d.print) {
        cxDecisionsRef.current.delete(uid);
        cxResolvedUidsRef.current.delete(uid);
      }
    });
    setAnsweredConfirmations(prev => {
      const kept = prev.filter(t => !(t && t.cx && printByUid.get(t.rowId) !== t.cxPrint));
      return kept.length === prev.length ? prev : kept;
    });

    let data;
    cxInFlightRef.current = true;
    try {
      data = await cxCheckLines({ lines, scanned: Boolean(ocrData) });
    } catch (e) {
      return false;
    } finally {
      cxInFlightRef.current = false;
    }
    if (!data || data.enabled !== true || !Array.isArray(data.lines)) return false;

    const rowByUid = new Map(rows.map(r => [r.uid, r]));
    const lineByUid = new Map(lines.map(l => [l.row_key, l]));
    const judged = data.lines.filter(c => c && rowByUid.has(c.row_key));
    const answered = (uid) => CX_ANSWERED.has((cxDecisionsRef.current.get(uid) || {}).decision);
    for (const c of judged) {
      if (!c.item_id || answered(c.row_key)) continue;
      const l = lineByUid.get(c.row_key);
      cxDecisionsRef.current.set(c.row_key, {
        item_id: c.item_id, decision: c.default_decision || null, print: cxPrint(l),
        entered: cxEntered(l, c.family), window_version: c.window_version,
      });
    }
    const ask = judged.filter(c => c.ask && !answered(c.row_key));
    if (ask.length === 0 || data.popup === false || !cxGuardRef.current.popup) {
      cxCheckedHashRef.current = hash;
      return false;
    }
    // Price-only ambers ride along — shown because the popup is open anyway, never blocking (D1).
    const rideAlong = judged.filter(c => !c.ask && c.status === 'amber' && c.window && !answered(c.row_key));
    const toPopup = (c, optional) => {
      const row = rowByUid.get(c.row_key);
      const l = lineByUid.get(c.row_key);
      return {
        uid: c.row_key,
        name: (row.item_name || '').trim() || c.item_name,
        dbName: c.item_name,
        values: { quantity: l.quantity, unit: l.unit, total: Math.round(l.total * 100) / 100 },
        pack: l.pack,
        check: c,
        optional,
      };
    };
    const shown = new Set([...ask, ...rideAlong].map(c => c.row_key));
    setCxPopup({
      key: Date.now(),
      lines: [
        ...ask.filter(c => c.status === 'red').map(c => toPopup(c, false)),
        ...ask.filter(c => c.status !== 'red').map(c => toPopup(c, false)),
        ...rideAlong.map(c => toPopup(c, true)),
      ],
      judged: lineByUid,
      scanUnclear: Boolean(data.summary && data.summary.scan_unclear),
      otherTotal: lines.filter(l => !shown.has(l.row_key)).reduce((s, l) => s + (Number(l.total) || 0), 0),
    });
    return true;
  };

  /**
   * The popup closed. Its values go into the rows either way (closing keeps every edit,
   * edge case 20) — through applyItems, with the price fields re-derived as typing would.
   * Only Save turns decisions into tokens and carries on with the submission.
   */
  const cxPopupDone = (results, save) => {
    const popup = cxPopup;
    setCxPopup(null);
    if (!popup || !Array.isArray(results)) return;
    const byUid = new Map(results.map(r => [r.uid, r]));
    let rows = itemsRef.current;
    if (results.some(r => r.changed)) {
      rows = applyItems(prev => prev.map(row => {
        const r = byUid.get(row.uid);
        if (!r || !r.changed || !popup.judged.get(row.uid)) return row;
        return cxWriteBack(row, r.values, {
          judged: popup.judged.get(row.uid),
          taxSlabActive: taxForStockInEnabled && (parseFloat(row.tax_slab) || 0) > 0,
          baseFromGross: calculateBasePriceFromTaxTotal,
        });
      }));
    }
    if (!save) return;
    const tokens = [];
    rows.forEach((row, index) => {
      const r = byUid.get(row.uid);
      const shownLine = popup.lines.find(l => l.uid === row.uid);
      if (!r || !r.decision || !shownLine) return;
      const line = cxPayloadLine(row);
      const print = cxPrint(line);
      const check = shownLine.check;
      cxDecisionsRef.current.set(row.uid, {
        item_id: check.item_id, decision: r.decision, print, entered: cxEntered(line, check.family),
        window_version: check.window_version, forced_name: r.decision === 'red_forced' ? r.forcedName : undefined,
      });
      cxResolvedUidsRef.current.add(row.uid);
      tokens.push(...cxTokensFor({ decision: r.decision, uid: row.uid, index, print, check, forcedName: r.forcedName }));
    });
    const touched = new Set(results.map(r => r.uid));
    setAnsweredConfirmations(prev => [...prev.filter(t => !(t && t.cx && touched.has(t.rowId))), ...tokens]);
    cxCheckedHashRef.current = cxLinesHash(rows.map(cxPayloadLine));
    setCxResume(1);
  };

  /**
   * Re-entering the ladder after the popup's Save — two renders later, not via setTimeout.
   * The popup can change line totals, and the bill-total audit STEP 2 reads is derived in
   * an effect from the rows: a closure taken before that effect's update lands would judge
   * the OLD total and could skip a real mismatch. Hop 1 lets the audit update batch into
   * the next render; hop 2 runs handleSubmitClick from that render, with everything fresh.
   */
  useEffect(() => {
    if (cxResume === 1) { setCxResume(2); return; }
    if (cxResume === 2) { setCxResume(0); handleSubmitClick(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cxResume]);

  /**
   * M4 §8.4 — WHICH VENDOR THIS BILL IS FROM.
   *
   * The backend resolves profiles vendor-first and computes `borrowedFromItemLevel`; the
   * client sent no vendor on either the GET or the POST, so EVERY profile ever created was
   * item-level and `borrowedFromItemLevel` was computed for nobody. That is not a missing
   * feature, it is a disagreement: the live hint could say "packets of 4" (item level)
   * while the SUBMIT gate — which is handed a vendorId — judged the same row against a
   * vendor row of 6. One bill, two different profiles, no way for the person to tell.
   */

  /**
   * The batched red-row decision.
   *
   * "Red" means two different things — "a genuinely new ingredient" and "a non-consumable
   * we will never cook with" — and only a human can tell them apart. Red rows used to reach
   * the backend unclassified and quietly create new inventory items; asking about them one
   * modal at a time is what made this so irritating. So: ONE list, every red row on it,
   * Non-Consumable pre-selected because that is the default. The honest path is one tap.
   *
   * { rows: [{ index, item_name, choice: 'non_consumable' | 'consumable' }] }
   */
  const [redRowDecision, setRedRowDecision] = useState(null);
  const [successModalMessage, setSuccessModalMessage] = useState('');
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorModalMessage, setErrorModalMessage] = useState('');
  const [showLoadingModal, setShowLoadingModal] = useState(false);
  const [loadingModalMessage, setLoadingModalMessage] = useState('');
  const [shouldScrollToError, setShouldScrollToError] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  // Set when the confirmation box is the LAST step of a piece-guard conversation: Confirm
  // Submit then resubmits this negotiated bill instead of starting a fresh handleSubmit.
  const [pendingPieceSubmit, setPendingPieceSubmit] = useState(null);
  const [vendorAutofillPrompt, setVendorAutofillPrompt] = useState(null);
  const [autofillSelectedIndices, setAutofillSelectedIndices] = useState(new Set());
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Edge Case 1: Idempotency Key — Double Submit Prevention
  // Generated ONCE on mount. Regenerated after each successful submission.
  // Sent with every API call so the backend can deduplicate network-desync duplicates.
  // T3 — true only while the stock-in POST is actually in flight. A ref so two taps in
  // one React batch cannot both read `false`; see handleSubmit.
  const submitInFlightRef = useRef(false);
  const idempotencyKeyRef = useRef(
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`
  );
  const [showNarResolution, setShowNarResolution] = useState(false);
  const [narResolutions, setNarResolutions] = useState([]);
  const [narOriginalFormData, setNarOriginalFormData] = useState(null); // Store form data for "Create New Item"
  const [showItemSelectionModal, setShowItemSelectionModal] = useState(false);
  const [itemSelectionData, setItemSelectionData] = useState(null); // Stores candidates and selection state
  // Item search bar (WhatsApp-style) state
  const [showItemSearch, setShowItemSearch] = useState(false);
  const [itemSearchQuery, setItemSearchQuery] = useState('');
  const itemSearchInputRef = useRef(null);
  // Expiry custom calendar state
  const [showExpiryPicker, setShowExpiryPicker] = useState(false);
  const [expiryPickerIndex, setExpiryPickerIndex] = useState(null);
  const [expiryViewDate, setExpiryViewDate] = useState(new Date());
  // Production Date custom calendar state
  const [showProductionDatePicker, setShowProductionDatePicker] = useState(false);
  const [productionViewDate, setProductionViewDate] = useState(new Date());
  // Undo / revert state
  const [originalItems, setOriginalItems] = useState([]); // Revert All's target: the state BEFORE the last fill
  const [loadedItems, setLoadedItems] = useState([]);     // per-row Reset's target: the state AS loaded (post-OCR/autofill)
  const overflowMenuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (overflowMenuRef.current && !overflowMenuRef.current.contains(event.target)) {
        setShowOverflowMenu(false);
      }
    };
    if (showOverflowMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showOverflowMenu]);

  useEffect(() => {
    if (ocrData) return;
    try {
      const businessId = localStorage.getItem('businessId') || 'unknown';
      const draftStr = localStorage.getItem(`stockInDraft_${businessId}`);
      if (draftStr) {
        const draft = JSON.parse(draftStr);
        if (draft) {
          if (draft.vendorName) setVendorName(draft.vendorName);
          if (draft.vendorPhone) applyVendorPhone(draft.vendorPhone);
          if (draft.items && draft.items.length > 0) {
            // M7.2 — through the ONE door. The hand-rolled version seeded history with the
            // restored items on BOTH sides (`seedWith: draft.items`), so a fresh restore
            // arrived with Undo enabled and pointing at an identical state: pressing it did
            // nothing, visibly. applyItems seeds from what was actually on the form before.
            applyItems(draft.items, { snapshot: true });
          }
          if (draft.cgst) setCgst(draft.cgst);
          if (draft.sgst) setSgst(draft.sgst);
          // Restore the day the goods actually arrived, not the day the draft is reopened.
          if (draft.productionDate) setProductionDate(draft.productionDate);
        }
      }
    } catch (e) {
      console.warn('Failed to load draft:', e);
    }
  }, [ocrData]);

  useEffect(() => {
    const fetchTaxForStockInSetting = async () => {
      const businessId = UnitMappingService.getBusinessId() || localStorage.getItem('businessId') || 'unknown';
      try {
        const res = await http.get(`/settings/${businessId}?keys=sales.tax_for_stock_in_enabled`);
        const data = res.data || [];
        const match = data.find(s => s.key === 'sales.tax_for_stock_in_enabled');
        const enabled = match
          ? match.value === true || String(match.value).toLowerCase() === 'true'
          : false;

        // Don't clobber a tax file that forced tax ON (async load may resolve after that)
        setTaxForStockInEnabled(forceTaxOnRef.current ? true : enabled);
        taxSettingRef.current = enabled;
        localStorage.setItem(`taxForStockInEnabled_${businessId}`, enabled ? 'true' : 'false');
      } catch (error) {
        console.warn('Failed to load tax for stock in setting:', error);
        setTaxForStockInEnabled(forceTaxOnRef.current ? true : false);
        taxSettingRef.current = false;
        localStorage.setItem(`taxForStockInEnabled_${businessId}`, 'false');
      }
    };
    fetchTaxForStockInSetting();
  }, []);

  // Auto-enable per-item tax when the uploaded file had a tax column (temporary, NOT persisted;
  // reverted to the saved setting after the stock-in is submitted).
  useEffect(() => {
    if (ocrData && ocrData.tax_detected) {
      forceTaxOnRef.current = true;
      setTaxForStockInEnabled(true);
    }
  }, [ocrData]);

  // Toggle the "Tax for Stock In" mode directly from the stock-in header.
  // Persists the SAME business setting the Settings page uses, so it stays in sync everywhere.
  const handleToggleTaxForStockIn = async (enabled) => {
    const businessId = UnitMappingService.getBusinessId() || localStorage.getItem('businessId') || 'unknown';
    // Optimistic UI update
    setTaxForStockInEnabled(enabled);
    localStorage.setItem(`taxForStockInEnabled_${businessId}`, enabled ? 'true' : 'false');
    try {
      await http.put(`/settings/${businessId}`, {
        settings: [{
          key: 'sales.tax_for_stock_in_enabled',
          value: enabled,
          data_type: 'boolean',
          module_scope: 'sales',
          description: 'When enabled, manual stock entry displays per-item tax inputs instead of a single header-level tax card.'
        }]
      });
    } catch (error) {
      console.error('Failed to save tax for stock in toggle:', error);
      // Roll back on failure
      setTaxForStockInEnabled(!enabled);
      localStorage.setItem(`taxForStockInEnabled_${businessId}`, !enabled ? 'true' : 'false');
    }
  };

  // Load vendor autofill prompt setting from backend
  useEffect(() => {
    const fetchVendorAutofillSetting = async () => {
      const businessId = UnitMappingService.getBusinessId() || localStorage.getItem('businessId') || 'unknown';
      // Fast-path: check localStorage first to avoid flicker
      const cached = localStorage.getItem(`vendorAutofillPromptEnabled_${businessId}`);
      if (cached !== null) {
        setVendorAutofillPromptEnabled(cached === 'true');
      }
      try {
        const res = await http.get(`/settings/${businessId}?keys=inventory.vendor_autofill_prompt_enabled`);
        const data = res.data || [];
        const match = data.find(s => s.key === 'inventory.vendor_autofill_prompt_enabled');
        const enabled = match
          ? match.value === true || String(match.value).toLowerCase() === 'true'
          : false;
        setVendorAutofillPromptEnabled(enabled);
        localStorage.setItem(`vendorAutofillPromptEnabled_${businessId}`, enabled ? 'true' : 'false');
      } catch (error) {
        console.warn('Failed to load vendor autofill prompt setting:', error);
      }
    };
    fetchVendorAutofillSetting();
  }, []);



  const [itemErrors, setItemErrors] = useState({}); // idx -> array of error strings
  // Track which names the user has locked (selected via suggestion or manual edit after which we freeze)
  const [lockedNames, setLockedNames] = useState(new Set()); // store item.uid values that are locked
  // Kitchen unit popup state (same behavior as item map)
  const [showKitchenUnitModal, setShowKitchenUnitModal] = useState(false);
  const [kitchenUnitForm, setKitchenUnitForm] = useState({ item: '', kitchenTool: 'Tablespoon', quantity: '15', unit: 'ml' });

  // The inline phone warning appears only after the user has left the field once —
  // scolding someone mid-way through typing the third digit teaches them nothing.
  const [phoneTouched, setPhoneTouched] = useState(false);
  // The vendor record's own phone, as autofilled into the field. Empty whenever the value
  // in the box is not (or is no longer) that stored number. See H1 / isLegacyStoredPhone.
  const [vendorRecordPhone, setVendorRecordPhone] = useState('');

  /**
   * Fill the phone field on the user's behalf (OCR, vendor autofill, vendor select, draft
   * restore). A stored or scanned number that fails the rule is still written — it IS the
   * data on record, and hiding it would leave the user editing a field they cannot see is
   * wrong — but the field is marked touched at the same moment so the warning is visible
   * immediately. Never silently accept, never silently discard.
   */
  const applyVendorPhone = useCallback((rawValue, { fromVendorRecord = false } = {}) => {
    const normalized = normalizeVendorPhone(rawValue);
    setVendorPhone(normalized);
    // H1: remember whether this exact value came off a vendor's stored record. A stored
    // number that fails today's rule is a data problem the user did not create, and it must
    // not hold their whole bill hostage — but the moment they edit it, it is theirs.
    setVendorRecordPhone(fromVendorRecord ? normalized : '');
    if (normalized && !isValidVendorPhone(normalized)) {
      setPhoneTouched(true);
    }
  }, []);

  /** The user typed (or pasted) — whatever was stored no longer describes this value. */
  const handleVendorPhoneTyped = useCallback((rawValue, { markTouched = false } = {}) => {
    setVendorPhone(normalizeVendorPhone(rawValue));
    setVendorRecordPhone('');
    if (markTouched) setPhoneTouched(true);
  }, []);

  const phoneValidationError = vendorPhoneError(vendorPhone);
  /**
   * A legacy stored number: invalid, but character-for-character what the vendor record
   * already holds. Warned about softly and allowed through; anything the user typed or
   * edited this session is a hard block, exactly as before.
   */
  const isLegacyStoredPhone = Boolean(phoneValidationError)
    && Boolean(vendorPhone)
    && vendorPhone === vendorRecordPhone;
  /**
   * Warn WHILE TYPING once the number can no longer become valid — too many digits, or a
   * leading digit an Indian mobile never has. Waiting for blur meant someone could type
   * fifteen digits into a silent field and first hear about it at submit.
   *
   * A merely short number still waits for blur: it is the normal state of a number being
   * typed, and flagging it on the third keystroke is noise, not help.
   */
  const phoneErrorIsTerminal = isTerminalVendorPhoneError(vendorPhone);
  const showPhoneError = (phoneTouched || phoneErrorIsTerminal)
    && Boolean(phoneValidationError)
    && !isLegacyStoredPhone;
  const showLegacyPhoneWarning = isLegacyStoredPhone;

  // **NEW: Add Custom Unit Modal State**
  const [showAddUnitModal, setShowAddUnitModal] = useState(false);
  const [newUnitForm, setNewUnitForm] = useState({ unitName: '' });

  // **NEW: Container unit conversion popup (for box, pack, bag, dozen, carton)**
  const [showContainerConversionModal, setShowContainerConversionModal] = useState(false);
  const [containerConversionForm, setContainerConversionForm] = useState({
    itemIndex: null,
    itemName: '',
    containerUnit: 'box',
    quantity: '',
    standardUnit: 'kg'
  });
  const [containerConversions, setContainerConversions] = useState({}); // Store saved conversions: { itemName_unit: {factor, toUnitSymbol, toUnitDisplay} }

  // **NEW: Batch processing for multiple unknown units**
  const [unknownUnitsQueue, setUnknownUnitsQueue] = useState([]); // Array of {itemIndex, itemName, unit, quantity}
  const [currentUnknownUnitIndex, setCurrentUnknownUnitIndex] = useState(0); // Which one we're showing

  // **NEW: Unit Training Modal (SUF - Filter 2 Training)**
  const [showUnitTrainingModal, setShowUnitTrainingModal] = useState(false);
  const [unitTrainingData, setUnitTrainingData] = useState({
    itemIndex: null,
    itemName: '',
    unknownUnit: '',
    quantity: 0,
    standardUnit: 'kg',
    conversionFactor: 1
  });

  // **NEW: Duplicate Detection Modal**
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [duplicateItems, setDuplicateItems] = useState([]);
  const [selectedDuplicates, setSelectedDuplicates] = useState(new Set());
  const [pendingOcrItems, setPendingOcrItems] = useState([]);

  // AI-FIRST: Validation summary from backend
  const [validationSummary, setValidationSummary] = useState(null);
  const [hasOcrRunInSession, setHasOcrRunInSession] = useState(false); // Track if OCR has run in current session
  // const [modalToolOpen, setModalToolOpen] = useState(false);
  // const [modalUnitOpen, setModalUnitOpen] = useState(false);
  // const [modalItemOpen, setModalItemOpen] = useState(false);
  // const [modalItemSearch, setModalItemSearch] = useState('');
  // const [modalToolSearch, setModalToolSearch] = useState('');
  // const [modalUnitSearch, setModalUnitSearch] = useState('');
  const [modalKitchenTools, setModalKitchenTools] = useState(['Tablespoon', 'Teaspoon', 'Cup', 'Bowl']);
  // const [modalShowAddTool, setModalShowAddTool] = useState(false);
  // const [modalNewToolName, setModalNewToolName] = useState('');
  const baseUnitOptions = [
    { value: 'ml', label: 'ml' },
    { value: 'L', label: 'L' },
    { value: 'g', label: 'g' },
    { value: 'kg', label: 'kg' },
  ];

  // **NEW: Inventory items for matching**
  const [inventoryItems, setInventoryItems] = useState([]);
  const [inventoryLoading, setInventoryLoading] = useState(true);

  // **NEW: Dynamic categories from database**
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [categoryTypeMap, setCategoryTypeMap] = useState(new Map()); // category name -> type (consumable/non_consumable)
  const [categoryIdToName, setCategoryIdToName] = useState(new Map()); // Map category_id -> name

  // **Track if OCR has been processed to avoid reprocessing**
  const ocrProcessedRef = useRef(null);

  // **NEW: Semantic Filter System State**
  const [semanticProcessing, setSemanticProcessing] = useState(false);
  const [ocrFormProcessing, setOcrFormProcessing] = useState(false); // Processing overlay: true from OCR arrival until form is ready
  const [ocrQualityBlocked, setOcrQualityBlocked] = useState(false);
  const [qualityReport, setQualityReport] = useState(null);
  const [filterResults, setFilterResults] = useState({}); // idx -> filter result
  const [pendingConfirmations, setPendingConfirmations] = useState(new Set()); // item indices needing confirmation
  const [duplicateWarnings, setDuplicateWarnings] = useState({}); // idx -> duplicate info

  // **NEW: Financial Audit System (Grand Total Audit + Currency + Tax Validation)**

  // **NEW: Vendor Search/Autocomplete**
  const [vendorSuggestions, setVendorSuggestions] = useState([]);
  const [showVendorDropdown, setShowVendorDropdown] = useState(false);
  const [activeVendorField, setActiveVendorField] = useState('name');
  const [vendorSearchLoading, setVendorSearchLoading] = useState(false);
  const vendorInputRef = useRef(null);
  const phoneInputRef = useRef(null);
  const vendorDropdownRef = useRef(null);
  // Prevents the debounced search from re-opening the dropdown after a vendor is selected
  const vendorJustSelectedRef = useRef(false);
  const lastManualHeaderTriggerRef = useRef(0);
  const pendingNewCardFocusRef = useRef(false);
  const [financialAudit, setFinancialAudit] = useState({
    ocrGrandTotal: null,     // Locked OCR extracted grand total
    ocrTaxAmount: null,      // Locked OCR extracted tax
    ocrSubtotal: null,       // Locked OCR extracted subtotal
    userCalculatedSubtotal: 0, // Live sum of all line items
    difference: 0,           // Difference between OCR and calculated
    // T1 — whether `difference` means anything. False on a bill where any row carries a tax
    // slab: the user's total is then partly gross and the OCR's figures are wholly one or
    // the other, so there is nothing honest to compare. Never read `difference` without it.
    comparable: true,
    comparedAgainst: null,   // 'subtotal' | 'grand total' — which figure was used, for the message
    auditEnabled: false,     // Only enable when OCR has financial data
    // New fields for comprehensive financial tracking
    taxAmount: 0,            // Tax/GST amount
    discount: 0,             // Discount amount
    currency: 'INR',         // Detected currency (INR, USD, EUR, etc.)
    originalAmount: null,    // Original amount in foreign currency
    conversionRate: null,    // Currency conversion rate
    taxValidation: null      // Tax structure validation result {isValid, message, components}
  });

  // **NEW: LIFO Revert System (Action Stack for Undo)**
  /**
   * Undo/redo history — ONE state object, never a pair.
   *
   * It was `actionHistory` + `historyIndex`, and pushHistory truncated using the CLOSURE
   * value of the index while incrementing it functionally. Two pushes in the same React
   * batch (which handleInputBlur reliably produces when it reformats a value) clobbered
   * each other. Truncation and the new index are now decided together, inside one
   * functional updater, so they cannot disagree.
   */
  const [history, setHistory] = useState(EMPTY_HISTORY);

  /**
   * The current items, readable synchronously.
   *
   * applyItems has to know what was on the form BEFORE a bulk fill in order to snapshot it
   * for Revert. Reading that from a functional setState updater would mean running side
   * effects inside a reducer (double-invoked under StrictMode); reading it from the `items`
   * closure is stale inside callbacks. This ref is written synchronously by every path that
   * changes items, and re-synced from state as a backstop.
   */
  const itemsRef = useRef(items);
  // Bill Memory: scanned lines the user deleted (a deleted freight line teaches "not stock").
  const bmDeletedLinesRef = useRef([]);
  // Bill Memory switches for this business, as the server reported them. The no-popup screen
  // (red cards, chips on red, no matching popups) runs only when mode and screen are both On.
  const [bmSwitches, setBmSwitches] = useState(null);
  const bmModeOn = !!(bmSwitches && bmSwitches.mode === 'on'); // at least one source is On (Fix 12's lines show only then)
  const bmScreenOn = !!(bmSwitches && bmSwitches.mode === 'on' && bmSwitches.screen === 'on');
  // Fix 12: what the last save taught, one grey line in the save confirmation ('' for none).
  const [bmSaveLine, setBmSaveLine] = useState('');
  const bmScreenOnRef = useRef(false);
  bmScreenOnRef.current = bmScreenOn;

  // S5 — the same clone editHistory uses (structuredClone, JSON fallback). The old local
  // JSON.parse(JSON.stringify()) dropped `undefined` and stringified Dates, so the snapshot
  // and the undo stack could disagree about what a row contained.
  const cloneItems = (list) => cloneItemsDeep(list);

  /**
   * There is deliberately NO `useEffect(() => { itemsRef.current = items })` backstop here.
   *
   * It used to exist, to catch a setItems that skipped the door. There are none left — every
   * write goes through applyItems / commitItemEdit / undo / redo / resetFormAfterSuccess,
   * and each of those writes this ref synchronously, which is pinned by
   * StockInForm.doorInvariant.static.test.js.
   *
   * With nothing left to catch it could only do harm, and it did. `items` in that effect is
   * the RENDERED value, which is never newer than what the door just wrote — so when two
   * effects fire in the same mount flush, the backstop ran between them and handed the
   * second one a stale array. Concretely: the draft-restore effect filled three rows, the
   * backstop then reset the ref to the first render's single empty row, and the clock-stamp
   * effect built from that and wiped the restored draft. A restored draft simply vanished.
   *
   * The ref is seeded from the initial items instead, so it is correct from the first render
   * and is only ever advanced by the door.
   */

  /**
   * Record a state in the undo stack.
   *
   * `seedWith` is the state as it was BEFORE this change. On the very first push the stack
   * is empty, so without a seed entry 0 would be the POST-change state — making the first
   * edit of a session permanently un-undoable and every undo after it off by one. Seeding
   * guarantees index 0 is always somewhere the user can get back to.
   */
  const pushHistory = useCallback((nextItems, { seedWith = null, meta = null } = {}) => {
    // One functional updater computes the truncation AND the new index together, from the
    // same `prev`. See utils/editHistory.js for why that is the whole fix — and for the cap
    // and the coalescing rule `meta` drives (H5).
    setHistory(prev => pushHistoryState(prev, nextItems, seedWith, meta));
  }, []);

  /**
   * THE one door for every programmatic change to `items`.
   *
   * Autofill, OCR load, duplicate resolution and revert all come through here, because each
   * of them has to do the same three things and every one of them used to forget at least
   * one:
   *
   *   1. `snapshot: true` captures Revert's target from the items as they were BEFORE this
   *      change. Every fill path used to set originalItems to the POST-fill state, which
   *      made "Revert All" restore the thing it was reverting — a no-op by construction.
   *   2. Set the items (and keep itemsRef in step).
   *   3. Push onto the undo stack, seeded with the pre-change state, so undo works
   *      immediately after a fill instead of only after the user's first manual edit.
   *
   * @param producer array of items, or (prevItems) => items
   * @param snapshot true for load/fill events — the ones Revert should return from
   */
  const applyItems = useCallback((producer, { snapshot = false, silent = false } = {}) => {
    const before = itemsRef.current;
    const produced = typeof producer === 'function' ? producer(before) : producer;
    const raw = (Array.isArray(produced) && produced.length > 0) ? produced : [emptyItem()];
    // H4: every row carries a stable identity, minted here if a builder forgot. Per-row
    // Reset matches on it, so a row that arrives without one would silently lose its button.
    const next = raw.map(it => (it && it.uid ? it : { ...it, uid: genUid() }));

    if (snapshot) {
      // BEFORE the merge, never after. If a fill lands on top of rows the user typed by
      // hand, those rows are what Revert has to bring back.
      setOriginalItems(cloneItems(before));
      // H4 — the OTHER snapshot, and a different question.
      //
      // `originalItems` answers "what should Revert All return to?" — the state BEFORE the
      // fill. `loadedItems` answers "what did this row look like when it was loaded?" —
      // the state AFTER it. Per-row Reset needs the second one, and was reading the first:
      // after an OCR load onto an empty form, originalItems was [emptyItem()], so row 0's
      // Reset BLANKED the row and rows 1..N had no button at all. Per-row revert of an OCR
      // misread — the only reason the button exists — was impossible.
      setLoadedItems(cloneItems(next));
    }

    itemsRef.current = next;
    setItems(next);

    /**
     * `silent` — through the door, but NOT onto the undo stack.
     *
     * For bookkeeping rewrites that are not the user's edit and that they would never reach
     * for undo to reverse: the clock stamp on load, the lastStableName bookkeeping on blur.
     * The reason those still come through here is itemsRef — a bare setItems leaves the ref
     * a render behind, so the very next write can be built over a stale base.
     */
    if (!silent) pushHistory(next, { seedWith: before });
    return next;
  }, [pushHistory]);

  /**
   * A single-field edit. Same history discipline as applyItems, but no snapshot — typing is
   * not a "fill" and must not move Revert's target.
   */
  const commitItemEdit = useCallback((nextItems, meta = null) => {
    const before = itemsRef.current;
    itemsRef.current = nextItems;
    setItems(nextItems);
    // `meta` ({ rowId, field }) lets editHistory collapse continued typing in one box into a
    // single undo step instead of one per character (H5).
    pushHistory(nextItems, { seedWith: before, meta });
  }, [pushHistory]);

  // **NEW: LIFO Revert (Undo/Redo)**
  const handleUndo = () => {
    if (history.index <= 0) return;
    const nextIndex = history.index - 1;
    const restored = cloneItems(history.stack[nextIndex]);
    itemsRef.current = restored;
    setItems(restored);
    setHistory(prev => ({ ...prev, index: nextIndex }));
  };

  const handleRedo = () => {
    if (history.index >= history.stack.length - 1) return;
    const nextIndex = history.index + 1;
    const restored = cloneItems(history.stack[nextIndex]);
    itemsRef.current = restored;
    setItems(restored);
    setHistory(prev => ({ ...prev, index: nextIndex }));
  };

  const canUndo = canUndoHistory(history);
  const canRedo = canRedoHistory(history);

  // **NEW: Expiry Tracking Requirements (from master control)**
  const [expiryRequirements, setExpiryRequirements] = useState(new Set()); // item_ids that require expiry
  /**
   * The same requirements, by NAME (S3).
   *
   * A manually typed row carries no item_id, so the id set cannot judge it. The old
   * name-fallback compared against inventoryItems[].name — which is the TRANSLATED name —
   * while /expiry-tracking/required returned untranslated ones, so for any non-English
   * business the name path silently matched nothing. The endpoint now returns both, and both
   * are folded in here, lower-cased once.
   */
  const [expiryRequiredNames, setExpiryRequiredNames] = useState(new Set());

  // **NEW: Multi-Page Bill Support**
  const [currentBillImageIndex, setCurrentBillImageIndex] = useState(0);
  const [showBillImage, setShowBillImage] = useState(true); // Control bill image visibility

  // **NEW: Auto-Correction Confirmation System (DSF/TCSF)**
  const [showAutoCorrectionModal, setShowAutoCorrectionModal] = useState(false);
  const [autoCorrectionData, setAutoCorrectionData] = useState(null);
  const [pendingSubmitData, setPendingSubmitData] = useState(null);
  // The pop-up's edit box: what the flagged row was submitted with, and what it now says.
  const correctionEdit = buildCorrectionEdit(autoCorrectionData, pendingSubmitData);
  // DSF offered corrected numbers (token 'correction_applied'): the pop-up may also save the
  // row exactly as entered. Not once the person has typed new numbers into the edit box.
  const canKeepEnteredNumbers = Boolean(
    autoCorrectionData?.isBackendConfirmation
    && autoCorrectionData?.requiredConfirmation?.type === 'correction_applied'
    && pendingSubmitData
    && !correctionEdit.edited
  );

  // **NEW: Currency & Tax Validation**
  const [showCurrencyModal, setShowCurrencyModal] = useState(false);
  const [currencyConversionData, setCurrencyConversionData] = useState(null);
  const [showTaxWarningModal, setShowTaxWarningModal] = useState(false);
  const [taxWarningData, setTaxWarningData] = useState(null);

  // **NEW: Final Commit Block - Track field validation**
  const [fieldValidationErrors, setFieldValidationErrors] = useState({}); // itemIndex -> {field: errorMessage}
  const [commitBlocked, setCommitBlocked] = useState(false);

  // **NEW: VCP Confirmation Tracking**
  const [vcpConfirmed, setVcpConfirmed] = useState(false);
  const [confirmedItems, setConfirmedItems] = useState({}); // Track confirmed items by name: { "salt": { price: true, quantity: true }, ... }
  const skipAllVcpChecksRef = useRef(false); // Global flag to bypass all frontend VCP checks after first confirmation - using ref for immediate access

  // **NEW: Vendor Last-Order Auto-Fill State**
  const [lastOrderBanner, setLastOrderBanner] = useState(null); // { vendorName, orderDate, count } — shown after auto-fill
  const [lastOrderFetching, setLastOrderFetching] = useState(false); // shows spinner on vendor name field
  const [selectedVendor, setSelectedVendor] = useState(null); // Full vendor object when selected from dropdown
  const [bmVendorCertain, setBmVendorCertain] = useState(false); // Bill Memory: picked or tapped, never AI-filled
  // Fix 13 row 7: every certain pick (and every clear) is reported up, so the scanner sends it with the next
  // scan and the form remounted with the scan's rows keeps it (bmPickedVendor, as it was at mount).
  const onBmVendorPickedRef = useRef(onBmVendorPicked);
  onBmVendorPickedRef.current = onBmVendorPicked;
  const bmReportPick = useCallback((v) => { if (onBmVendorPickedRef.current) onBmVendorPickedRef.current(v || null); }, []);
  const bmPickedAtMountRef = useRef(bmPickedVendor);

  /**
   * THE one form reset for every successful save (M7.4).
   *
   * There were five of these, written by hand, and only the SuccessModal's did the whole
   * job. The 1.5-second timer paths reset `items` but left `originalItems`, `itemsRef` and
   * the undo stack pointing at the bill that had just been saved — so the next bill opened
   * with a live "Revert All" button aimed at somebody else's data, and an undo stack that
   * could restore it. One function, called from every success path, or this comes back.
   */
  const resetFormAfterSuccess = useCallback(() => {
    bmDeletedLinesRef.current = [];
    bmAddedBackRef.current = [];
    setBmLeftOut([]);
    setBmVendorCertain(false);
    bmReportPick(null); // the next bill picks its own vendor
    // S4 — a new bill is a new set of facts; last bill's answers must not travel with it.
    setAnsweredConfirmations([]);
    setBillDateNote(null);
    const fresh = [emptyItem()];
    itemsRef.current = fresh;
    setItems(fresh);
    // The snapshots have to die with the form, or Revert survives pointing at rows that no
    // longer exist. Both of them: Revert All's target and per-row Reset's.
    setOriginalItems([]);
    setLoadedItems([]);
    setHistory(EMPTY_HISTORY);
    setTotalCost(0);
    setConfirmedItems({});
    setItemErrors({});
    setFieldValidationErrors({});
    setCommitBlocked(false);
    skipAllVcpChecksRef.current = false;
    // A new bill asks its packet questions again — see packConfirmedIdsRef.
    packConfirmedIdsRef.current = [];
    setPackConfirmQueue([]);
  }, [bmReportPick]);

  /**
   * The category list used when the categories endpoint returns nothing or fails.
   *
   * It MUST populate categoryTypeMap too. Setting only the names left the type map empty,
   * and that one omission disabled two unrelated things at once: the red-row
   * non-consumable default (getDefaultNonConsumableCategory found no non_consumable type
   * and returned '', so the rule exited before doing anything) and validateForm, which
   * rejected EVERY item with "Invalid category" because no name had a known type. A
   * hiccup on one fetch made the whole form unusable with no clue why.
   */
  const applyFallbackCategories = useCallback(() => {
    const fallback = [
      ['Spices', 'consumable'],
      ['Oils', 'consumable'],
      ['Grains', 'consumable'],
      ['Dairy', 'consumable'],
      ['Meat', 'consumable'],
      ['Vegetables', 'consumable'],
      ['Beverages', 'consumable'],
      // Present in the fallback specifically so red rows still have somewhere to land.
      ['NON CONSUMABLE', 'non_consumable'],
      ['Uncategorized', 'consumable']
    ];
    setCategories(fallback.map(([name]) => name));
    setCategoryTypeMap(new Map(fallback));
  }, []);

  const getDefaultNonConsumableCategory = useCallback(() => {
    if (categoryTypeMap && categoryTypeMap.size > 0) {
      const match = Array.from(categoryTypeMap.entries()).find(([, type]) => type === 'non_consumable');
      if (match) {
        return match[0];
      }
    }

    const byName = categories.find(
      cat => String(cat || '').replace(/[_\s-]/g, '').toLowerCase() === 'nonconsumable'
    );

    return byName || '';
  }, [categoryTypeMap, categories]);

  // One rule for a red row the user has answered — the red-row popup and the Bill Memory red card
  // both use it, so a row is classified the same way whichever screen asked.
  const classifyRedRow = useCallback((item, choice) => {
    const nonConsumableCategory = getDefaultNonConsumableCategory();
    if (choice === 'consumable') {
      // A real new ingredient. It keeps whatever consumable category it
      // arrived with; a category this form defaulted falls back to
      // Uncategorized rather than to blank, which would fail validation on
      // the very next step with an error the user cannot connect to a
      // choice they just made.
      const uncategorized = categories.find(c => /^uncategorized$/i.test(String(c || ''))) || '';
      // What it arrived with, if anything; otherwise Uncategorized. Never
      // blank — that would fail validation on the very next step with an
      // error the user cannot connect to the choice they just made.
      const revertedCategory = item.autoNonConsumable
        ? (item.preAutoCategory || uncategorized)
        : item.category;
      return {
        ...item,
        category: revertedCategory,
        autoNonConsumable: false,
        createAsNew: true,
        // C1: without this the auto-default effect re-fires on the very
        // next render and flips the row straight back to non-consumable,
        // silently undoing the choice the user just made. The
        // non-consumable branch below has always set it; this one did not.
        categoryUserSet: true,
        redClassified: true,
        ...redChoiceFields('consumable') // Fix 13 row 4: recorded as a new ingredient, never taught
      };
    }
    return {
      ...item,
      ...redChoiceFields('non_consumable'),
      category: nonConsumableCategory || item.category,
      autoNonConsumable: false,          // confirmed by a human now, not a guess
      categoryUserSet: true,
      redClassified: true,
      // Non-consumables are counted in pieces unless the user says otherwise.
      // Every other checkpoint demands a unit unconditionally, so exempting
      // them here would just move the failure to the gate.
      unit: item.unit || 'pcs'
    };
  }, [categories, getDefaultNonConsumableCategory]);

  // Helpers for calendar
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const getDaysInMonth = (date) => new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  const getFirstDayOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  const openExpiryPicker = (idx) => {
    setExpiryPickerIndex(idx);
    const cur = items[idx]?.expiry_date;
    setExpiryViewDate(cur ? new Date(cur) : new Date());
    setShowExpiryPicker(true);
  };
  const closeExpiryPicker = () => setShowExpiryPicker(false);
  const navigateExpiryMonth = (direction) => {
    const d = new Date(expiryViewDate);
    d.setMonth(d.getMonth() + direction);
    setExpiryViewDate(d);
  };
  const handleExpiryDateClick = (day) => {
    if (expiryPickerIndex == null) return;
    const sel = new Date(expiryViewDate.getFullYear(), expiryViewDate.getMonth(), day);
    const ymd = sel.toLocaleDateString('en-CA');
    // M6: itemsRef.current, not the render closure — a same-tick edit elsewhere would
    // otherwise be built over and lost. Through the door so it lands in history.
    const copy = [...itemsRef.current];
    copy[expiryPickerIndex] = { ...copy[expiryPickerIndex], expiry_date: ymd };
    commitItemEdit(copy, { rowId: copy[expiryPickerIndex]?.uid, field: 'expiry_date' });
    setShowExpiryPicker(false);
  };

  const openProductionPicker = () => {
    // Parse the 'YYYY-MM-DD' string as LOCAL midnight so the calendar opens on the
    // right month in every timezone. new Date('YYYY-MM-DD') treats the string as
    // UTC midnight, which in IST (+5:30) is still the same calendar day, but in
    // any UTC-offset environment it can flip to the previous month at midnight.
    const viewDate = (() => {
      if (!productionDate) return new Date();
      const parts = String(productionDate).split('T')[0].split('-');
      if (parts.length === 3) return new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
      return new Date();
    })();
    setProductionViewDate(viewDate);
    setShowProductionDatePicker(true);
  };
  const closeProductionPicker = () => setShowProductionDatePicker(false);
  const navigateProductionMonth = (direction) => {
    const d = new Date(productionViewDate);
    d.setMonth(d.getMonth() + direction);
    setProductionViewDate(d);
  };
  const handleProductionDateClick = (day) => {
    const sel = new Date(productionViewDate.getFullYear(), productionViewDate.getMonth(), day);
    const ymd = sel.toLocaleDateString('en-CA');
    setProductionDate(ymd);
    setBillDateNote(null); // the person chose the date; the bill-date note no longer applies

    // Re-fetch the last-order template for the newly chosen date. This used to call
    // handleVendorSelect, which rewrites the header — wiping the typed phone and marking
    // the bill as belonging to an "existing" vendor so the mandatory-phone rule stopped
    // applying. Changing a date must never touch the vendor fields.
    if (selectedVendor) {
      fetchVendorLastOrder(selectedVendor, ymd);
    } else if (vendorName.trim()) {
      fetchVendorLastOrder({ name: vendorName.trim() }, ymd);
    }

    setShowProductionDatePicker(false);
  };

  const renderProductionCalendar = () => {
    const daysInMonth = getDaysInMonth(productionViewDate);
    const firstDay = getFirstDayOfMonth(productionViewDate);
    const days = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    for (let i = 0; i < firstDay; i++) days.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
    for (let day = 1; day <= daysInMonth; day++) {
      const cellDate = new Date(productionViewDate.getFullYear(), productionViewDate.getMonth(), day);
      cellDate.setHours(0, 0, 0, 0);

      // THE back-dating bug lived in the rule this now calls. It floored the calendar at
      // `onboardingDate || today`, and the onboarding date is fetched asynchronously — so
      // whenever it was null (before the fetch resolved, when the cached user blob had no
      // creation date, when /auth/status failed) every past day was rendered `disabled`
      // with NO onClick. Tapping the 29th did nothing, the field went on reading today,
      // and the bill was filed under today in every report. See utils/stockInDateBounds.js.
      const isDisabled = isStockInDateDisabled(cellDate, null, today);

      days.push(
        <div
          key={day}
          className={`calendar-day${isDisabled ? ' disabled' : ''}`}
          onClick={!isDisabled ? () => handleProductionDateClick(day) : undefined}
        >
          {day}
        </div>
      );
    }
    return days;
  };

  const renderExpiryCalendar = () => {
    const daysInMonth = getDaysInMonth(expiryViewDate);
    const firstDay = getFirstDayOfMonth(expiryViewDate);
    const days = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    for (let i = 0; i < firstDay; i++) days.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
    for (let day = 1; day <= daysInMonth; day++) {
      const cellDate = new Date(expiryViewDate.getFullYear(), expiryViewDate.getMonth(), day);
      cellDate.setHours(0, 0, 0, 0);
      const isPast = cellDate < today; // no past expiry
      days.push(
        <div
          key={day}
          className={`calendar-day${isPast ? ' disabled' : ''}`}
          onClick={!isPast ? () => handleExpiryDateClick(day) : undefined}
        >
          {day}
        </div>
      );
    }
    return days;
  };


  // Helper function to abbreviate unit names for display
  const abbreviateUnit = (unit) => {
    const abbrevMap = {
      'kilogram': 'kg',
      'gram': 'g',
      'milligram': 'mg',
      'liter': 'L',
      'milliliter': 'mL',
      'piece': 'pc',
      'dozen': 'doz'
    };
    return abbrevMap[unit?.toLowerCase()] || unit || 'unit';
  };

  // Hardcoded fallback categories (used only if API fails)
  const fallbackCategories = ['Meat', 'Seafood', 'Vegetables', 'Dairy', 'Spices', 'Grains', 'Beverages', 'Oils'];
  // Strict-5 ONLY: the unit dropdown offers exactly these five canonical units.
  // Container/supplier units (box, bag, dozen…) are NOT selectable here — they are captured from a
  // scanned bill and converted to one of these base units via the unit-training / "+ Add" flow.
  const baseUnits = ['kg', 'g', 'L', 'ml', 'pcs'];
  const [units, setUnits] = useState(baseUnits);
  const shifts = [t('stockIn.morning'), 'Afternoon', t('stockIn.evening'), 'Night'];

  useEffect(() => {
    const now = new Date();
    const hour = now.getHours();
    let detectedShift = 'Night';
    if (hour >= 6 && hour < 12) detectedShift = 'Morning';
    else if (hour >= 12 && hour < 17) detectedShift = 'Afternoon';
    else if (hour >= 17 && hour < 22) detectedShift = 'Evening';
    setCurrentShift(detectedShift);
    // Only ever a default, never an override. This effect is declared AFTER the
    // draft-restore effect and re-runs whenever `ocrData` changes, so stamping today
    // unconditionally wiped a restored draft's purchase date — and any date already
    // chosen — the moment a scan landed. The state initialiser already defaults to today.
    setProductionDate(prev => prev || stockInDate || now.toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' }));

    // ✅ Batch numbers will be generated server-side - no need to clear
    // Through the door so itemsRef cannot be left a render behind; `silent` because a clock
    // stamp is bookkeeping, not something anybody reaches for Undo to reverse.
    applyItems(prev => prev.map(it => ({ ...it, time: now.toTimeString().slice(0, 5) })), { silent: true });

    // Prefill vendor info from OCR (accept both top-level and nested fields)
    if (ocrData) {
      // Show processing overlay immediately when OCR data arrives
      if (ocrData.items && ocrData.items.length > 0) {
        setOcrFormProcessing(true);
      }
      setShowBillImage(true); // Show image when new OCR data arrives


      const vn = (ocrData.vendor_name || (ocrData.vendor && (ocrData.vendor.name || ocrData.vendor.vendor_name)) || '').trim();
      const vp = (ocrData.vendor_phone || (ocrData.vendor && (ocrData.vendor.phone || ocrData.vendor.vendor_phone)) || '').trim();


      const keptPick = bmPickedAtMountRef.current;
      if (keptPick && keptPick.vendor_id) {
        // Fix 13 row 7: the vendor picked before the scan stays the bill's vendor, certain (design P3)
        vendorJustSelectedRef.current = true;
        setVendorName(keptPick.name || vn);
        setSelectedVendor(keptPick);
        setBmVendorCertain(true);
      } else {
        if (vn) setVendorName(vn);
        setBmVendorCertain(false); // a vendor name the AI filled in is never certain (design §9)
      }
      if (vp) applyVendorPhone(vp);

      // Auto-fill CGST/SGST from OCR extraction
      if (ocrData.cgst) {
        setCgst(String(parseFloat(ocrData.cgst).toFixed(2)));
      }
      if (ocrData.sgst) {
        setSgst(String(parseFloat(ocrData.sgst).toFixed(2)));
      }

      // AI-FIRST: Extract validation summary if present
      if (ocrData.validation) {
        setValidationSummary(ocrData.validation);
      }

      // If vendor name still missing after OCR, nudge user to fill in
      if (!vn) {
        // Delay slightly to avoid being overridden by the OCR success toast
        setTimeout(() => {
          showModalMessage('Vendor not detected from receipt. Please enter the Vendor Name and Phone Number.', 'error');
        }, 400);
      }
    }
  }, [ocrData]);

  // The bill's printed date becomes the purchase date when it is safe to: the server
  // (utils/billDate) has read it as today or up to 60 days back. Too old, in the future or
  // unreadable keeps the picker as it is and says why. Onboarding is left alone — it files
  // opening stock under yesterday on purpose (stockInDate). Once per scan, so a date the
  // person then picks is never undone by a re-render.
  useEffect(() => {
    if (!ocrData || stockInDate) return;
    if (billDateAppliedForRef.current === ocrData) return;
    billDateAppliedForRef.current = ocrData;

    const iso = ocrData.invoice_date_iso || null;
    const status = ocrData.invoice_date_status || 'missing';
    const today = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
    const allDates = Array.isArray(ocrData.bill_dates) ? ocrData.bill_dates.filter(Boolean) : [];

    if (status === 'ok' && iso) {
      setProductionDate(iso);
      if (allDates.length > 1) {
        setBillDateNote({
          tone: 'warn',
          text: `These bills have different dates (${allDates.map(formatDayForHumans).join(', ')}). ` +
            `Everything is filed under ${formatDayForHumans(iso)} — scan them one at a time to file each on its own date.`,
        });
      } else if (iso !== today) {
        setBillDateNote({
          tone: 'info',
          text: `Date taken from the bill: ${formatDayForHumans(iso)}. Change it if the goods arrived on another day.`,
        });
      } else {
        setBillDateNote(null);
      }
    } else if (status === 'too_old' && iso) {
      setBillDateNote({
        tone: 'warn',
        text: `Bill is dated ${formatDayForHumans(iso)} — ${ocrData.invoice_date_days_ago} days ago. ` +
          `Kept today's date; change it if this bill really is that old.`,
      });
    } else if (status === 'future' && iso) {
      setBillDateNote({
        tone: 'warn',
        text: `Bill is dated ${formatDayForHumans(iso)}, which is in the future. Kept today's date — check the date on the bill.`,
      });
    } else if (status === 'unreadable') {
      setBillDateNote({
        tone: 'warn',
        text: `Couldn't read the date on the bill${ocrData.invoice_date ? ` ("${ocrData.invoice_date}")` : ''}. Kept today's date.`,
      });
    } else {
      setBillDateNote(null);
    }
  }, [ocrData, stockInDate]);

  useEffect(() => {
    // **UPDATED: Calculate total = sum of total_price for all items**
    // Users enter total price, system auto-calculates unit price
    const total = items.reduce((sum, it) => sum + grossLineTotal(it, taxForStockInEnabled), 0);
    setTotalCost(total);

    // ── T1 — THE AUDIT ONLY COMPARES WHAT IS COMPARABLE ────────────────────────────
    //
    // `total` above switches basis PER ROW: gross on a row that carries a tax slab, net on
    // one that does not. So on a bill where some items are taxed and some are not — which
    // the data says is the NORMAL case — the user's total is partly gross and partly net,
    // and it matches neither of the two figures the OCR gives us: `ocrSubtotal` is entirely
    // net, `ocrGrandTotal` entirely gross.
    //
    // The old line picked `ocrSubtotal || ocrGrandTotal`, which silently swaps a pre-tax
    // figure for a post-tax one whenever the bill printed no subtotal — and `||` treats a
    // subtotal of 0 as absent too. The difference it produced was the tax amount, the button
    // read `difference !== 0`, and every taxed bill became unsubmittable with no message.
    //
    // There is no correct comparison available until the bases are normalised at the parser
    // boundary (that is the real fix). Rather than report a number that is known to be
    // wrong, the check STANDS DOWN whenever any row carries a slab, and says so. That loses
    // a safety net on taxed bills; it is a deliberate, temporary retreat, and it is strictly
    // better than a wrong answer that blocks the bill. Every line still goes through the
    // backend gate (Layers A/B/C) either way — this check was only ever advisory.
    if (financialAudit.auditEnabled) {
      const anyTaxedRow = taxForStockInEnabled
        && items.some(it => (parseFloat(it.tax_slab) || 0) > 0);

      setFinancialAudit(prev => ({
        ...prev,
        userCalculatedSubtotal: total,
        // Net against net. Prefer the printed subtotal; fall back to the grand total only
        // when no subtotal was printed, which on an untaxed bill is the same number.
        difference: anyTaxedRow
          ? 0
          : Math.abs(total - (prev.ocrSubtotal || prev.ocrGrandTotal || 0)),
        comparable: !anyTaxedRow,
        comparedAgainst: anyTaxedRow
          ? null
          : (prev.ocrSubtotal ? 'subtotal' : 'grand total')
      }));
    }
  }, [items, taxForStockInEnabled, financialAudit.auditEnabled, financialAudit.ocrSubtotal, financialAudit.ocrGrandTotal]);

  // Auto-focus new card's Item Name when Enter is pressed on Total Price
  useEffect(() => {
    if (pendingNewCardFocusRef.current) {
      pendingNewCardFocusRef.current = false;
      requestAnimationFrame(() => {
        const allCards = document.querySelectorAll('.item-box');
        if (allCards.length > 0) {
          const lastCard = allCards[allCards.length - 1];
          const nameInput = lastCard.querySelector('.stockin-item-name-input');
          if (nameInput) {
            nameInput.focus();
            nameInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }
      });
    }
  }, [items.length]);

  // **NEW: Fetch inventory items on mount**
  useEffect(() => {
    const fetchInventory = async () => {
      try {
        setInventoryLoading(true);
        const businessId = UnitMappingService.getBusinessId();
        if (!businessId) {
          console.warn('⚠️ No businessId found, skipping inventory fetch');
          setInventoryItems([]);
          setInventoryLoading(false);
          return;
        }

        const response = await http.get(`/inventory/items/${businessId}?language=${language}`);

        // API returns { success: true, data: [...], count: X }
        const inventoryData = response.data?.data || response.data || [];

        if (Array.isArray(inventoryData)) {
          setInventoryItems(inventoryData);

          // **DEBUG: Expose to window for console testing**
          window.__DEBUG_INVENTORY__ = inventoryData;
          window.__DEBUG_INVENTORY_COUNT__ = inventoryData.length;
        } else {
          console.warn('⚠️ Inventory data is not an array:', inventoryData);
          setInventoryItems([]);
        }
      } catch (error) {
        console.error('❌ Failed to fetch inventory items:', error);
        // Continue with empty inventory list
        setInventoryItems([]);
      } finally {
        setInventoryLoading(false);
      }
    };

    fetchInventory();
  }, [language]); // Added language dependency

  // Bill Memory: the ladder's context for typed rows, loaded with the form and again when the
  // certain vendor changes — the one picked from the dropdown or the vendor buttons, else the one
  // the scan was certain of (the vendor the server uses at Save). null = could not load → typed
  // rows use today's matching.
  const bmCtxRef = useRef(null);
  const bmTakeItemRef = useRef(null); // the form's exact-match door (takeExactItem), for the vendor-tap re-run
  const bmScan = useMemo(() => scanBillMemory(ocrData && ocrData.bill_memory), [ocrData]); // one record per uploaded file → what the form reads
  const bmPickedVendorId = bmVendorCertain ? (selectedVendor?.vendor_id || null) : null;
  const bmContextVendorId = contextVendorId({
    vendorCertain: bmVendorCertain,
    selectedVendorId: selectedVendor?.vendor_id,
    scanVendor: bmScan ? bmScan.vendor : null,
  });
  useEffect(() => {
    let cancelled = false;
    loadContext(bmContextVendorId).then((bm) => {
      if (cancelled) return;
      bmCtxRef.current = bm;
      if (bm && bm.switches && bm.switches.mode && bm.switches.mode !== 'off') {
        setBmSwitches({ mode: bm.switches.mode, screen: bm.switches.screen });
      }
      // A vendor was picked or tapped: rows the user has not settled are asked again (checks 1–3
      // now apply). The scan's own certain vendor needs no re-run: the scan already used it.
      if (bm && bm.ctx && bmPickedVendorId && bmScreenOnRef.current) {
        // Fix 13 (Task 11) row 1: a row now naming another item takes its category, tax and unit; changed rows show their new chips
        const chips = new Map();
        applyItems(prev => rerunUnsettled(prev, bm, { enrich: bmTakeItemRef.current, chips }), { silent: true });
        if (chips.size) {
          setSuggestionsMap(prev => { const next = { ...prev }; chips.forEach((v, i) => { next[i] = v; }); return next; });
          setActiveSuggestionIndexMap(prev => {
            const next = { ...prev };
            chips.forEach((_, i) => { const r = itemsRef.current[i]; next[i] = r && r.bm_colour === 'yellow' && r.bm_preselect != null ? r.bm_preselect : -1; });
            return next;
          });
        }
      }
    });
    return () => { cancelled = true; };
  }, [bmContextVendorId, bmPickedVendorId, applyItems]);

  // Bill Memory screen: scroll to a row and give it today's brief highlight — never a popup.
  const bmScrollToRow = useCallback((index) => {
    if (index == null || index < 0) return;
    const card = document.querySelector(`.item-box[data-item-index="${index}"]`);
    if (!card) return;
    if (card.scrollIntoView) card.scrollIntoView({ behavior: 'smooth', block: 'center' });
    card.style.transition = 'box-shadow 0.3s ease';
    card.style.boxShadow = '0 0 0 4px rgba(239, 68, 68, 0.5)';
    setTimeout(() => { card.style.boxShadow = ''; }, 2000);
  }, []);
  const bmCounts = useMemo(() => countNeedTap(items), [items]);
  // Lines usually not stock, left out of the list by the scan (design §14); "Add back" returns one.
  const [bmLeftOut, setBmLeftOut] = useState([]);
  const bmAddedBackRef = useRef([]);

  // **NEW: Fetch categories from database**
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setCategoriesLoading(true);
        const businessId = UnitMappingService.getBusinessId();
        if (!businessId) {
          console.warn('⚠️ No businessId found, using fallback categories');
          setCategories(['Spices', 'Oils', 'Grains', 'Dairy', 'Meat', 'Vegetables', 'Beverages']);
          setCategoriesLoading(false);
          return;
        }

        const response = await http.get(`/inventory-categories/${businessId}`);

        // API returns { success: true, data: [{ category_id, name, category_type, is_active, created_at }] }
        const categoriesData = response.data?.data || response.data || [];

        if (Array.isArray(categoriesData) && categoriesData.length > 0) {
          // Extract category names and create ID->Name map
          const categoryNames = categoriesData.map(cat => cat.name);
          const idMap = new Map();
          const typeMap = new Map();
          categoriesData.forEach(cat => {
            idMap.set(cat.category_id, cat.name);
            typeMap.set(cat.name, cat.category_type || 'consumable'); // Default to consumable if not set
          });

          setCategories(categoryNames);
          setCategoryIdToName(idMap);
          setCategoryTypeMap(typeMap);
        } else {
          console.warn('⚠️ No categories found in database, using fallback categories');
          applyFallbackCategories();
        }
      } catch (error) {
        console.error('❌ Failed to fetch categories:', error);
        // Use fallback categories on error
        applyFallbackCategories();
      } finally {
        setCategoriesLoading(false);
      }
    };

    fetchCategories();
    // applyFallbackCategories is stable (useCallback with no deps); listed so the fallback
    // can never silently capture a stale closure.
  }, [applyFallbackCategories]);

  // Strict-5 ONLY: the unit dropdown must offer exactly kg, g, L, ml, pcs.
  // (Kitchen/container units are intentionally NOT merged into the dropdown; they are handled via
  //  the unit-training / "+ Add" conversion flow, which converts them to one of these base units.)

  // **HOISTED: Convert unit symbols to full names for dropdown**
  // Defined here early because it is used in the useEffects below.
  const convertUnitSymbolToName = useCallback((symbol) => {
    if (!symbol) return '';
    // Canonical Title-case British full names for the 5 units (backend normalizes case-insensitively).
    const mapping = {
      'kg': 'Kilogram',
      'g': 'Gram',
      'ml': 'Millilitre',
      'L': 'Litre',      // Only uppercase L maps to litre
      'ltr': 'Litre',
      'pc': 'Pcs',
      'pcs': 'Pcs',
      'doz': 'Dozen',
      'box': 'Box',
      'pkt': 'Packet',
      'packet': 'Packet',
      'tbsp': 'Tablespoon',
      'tsp': 'Teaspoon',
      'cup': 'Cup'
      // REMOVED: 'l': 'liter' - lowercase l is too ambiguous
    };
    const normalized = String(symbol).trim();

    // Special handling for lowercase 'l' - warn and default to uppercase L
    if (normalized === 'l') {
      console.warn(`⚠️ Ambiguous unit 'l' detected - converting to 'L' (liter)`);
      return 'liter';
    }

    return mapping[normalized.toLowerCase()] || symbol;
  }, []);

  // **NEW: Fetch saved supplier/container conversions on mount**
  useEffect(() => {
    const fetchContainerConversions = async () => {
      try {
        const businessId = UnitMappingService.getBusinessId();
        if (!businessId) return;

        console.log('🔄 [FETCH CONVERSIONS] Fetching all unit conversions from backend...');
        const conversions = await UnitMappingService.getSupplierConversions(businessId);

        // Build conversion map: itemName_unit -> {factor, toUnit}
        const conversionMap = {};
        if (Array.isArray(conversions)) {
          console.log(`📋 [FETCH CONVERSIONS] Received ${conversions.length} conversion(s)`);
          conversions.forEach(conv => {
            const itemName = extractConversionItemName(conv);

            if (itemName) {
              const normalizedItemName = normalizeItemKey({ item_name: itemName });
              const containerType = String(conv.from_unit_symbol || conv.containerType || '').toLowerCase().trim();
              const normalizedUnit = normalizeUnitForKey(containerType);
              const toUnitSymbol = normalizeUnitSymbol(conv.to_unit_symbol || conv.unit || '');
              const toUnit = convertUnitSymbolToName(toUnitSymbol);
              const factor = parseFloat(conv.conversion_factor || conv.quantity || 1);

              const key = `${normalizedItemName}_${normalizedUnit}`;
              conversionMap[key] = { factor, toUnitSymbol, toUnitDisplay: toUnit };

              console.log(`   ✅ Mapped: "${key}" → ${factor} ${toUnitSymbol} (${toUnit})`);
            } else {
              console.warn(`   ⚠️ Could not extract item name from:`, conv.description);
            }
          });
          console.log(`✅ [FETCH CONVERSIONS] Built conversion map with ${Object.keys(conversionMap).length} entries`);
        }

        setContainerConversions(conversionMap);
      } catch (error) {
        console.error('❌ [Fetch Container Conversions] Error:', error);
      }
    };

    fetchContainerConversions();
  }, []); // Only fetch once on mount

  // ✅ Listen for real-time category changes from Settings
  useEffect(() => {

    const unsubscribe = categoryEventService.subscribe((event) => {

      if (event.type === 'added') {
        // Add new category to the list
        setCategories(prev => {
          if (prev.includes(event.category)) {
            return prev; // Already exists
          }
          const updated = [...prev, event.category].sort();
          return updated;
        });
      } else if (event.type === 'deleted') {
        // Remove deleted category from the list
        setCategories(prev => {
          const updated = prev.filter(cat => cat !== event.category);
          return updated;
        });
      }
    });

    // Cleanup: unsubscribe when component unmounts
    return () => {
      unsubscribe();
    };
  }, []); // Empty dependency array - only runs once on mount

  /**
   * A red row — one that matched nothing in inventory — defaults to the non-consumable
   * category.
   *
   * It used to fire only when the row arrived with an EMPTY or "Uncategorized" category, so
   * a red row carrying a category from OCR, from last-order autofill, or from a match it
   * later lost kept that consumable category and sailed through. The condition is now the
   * match status alone: red means "not in your inventory", whatever label came with it.
   *
   * Two things keep this honest rather than sneaky:
   *   - `autoNonConsumable` marks the row so the card can SAY it was defaulted;
   *   - `categoryUserSet` (written by handleInputChange) makes the rule stand down for good
   *     on any row the user has touched, so it can never fight the person using it.
   *
   * The empty-form default remains Uncategorized. This applies to red rows only.
   */
  /**
   * Rows this rule has already defaulted, by uid (M7.3).
   *
   * The rule proposes once and then leaves the row alone. Undo restores a red row whose
   * category is not the non-consumable one — precisely this effect's firing condition — so
   * without the memory the rule and the person would fight over the same row forever. A ref,
   * not state: it must not itself cause a render, and it is a session fact, not form data.
   */
  const autoDefaultedRowsRef = useRef(new Set());

  useEffect(() => {
    if (bmScreenOn) return; // Bill Memory screen: a red row waits for one of the three red-card buttons (D4)
    const nonConsumableCategory = getDefaultNonConsumableCategory();
    if (!nonConsumableCategory) {
      return;
    }

    const shouldDefault = (item) => {
      const status = item.matchStatus || item.match_type || 'pending';
      const itemNameLength = (item.item_name || '').trim().length;
      if (status !== 'none' || itemNameLength <= 2) return false;
      if (item.categoryUserSet) return false;
      // Second guard (C1). A row the red-row modal has answered is settled, whichever way
      // it was answered — the effect must never get a second opinion on it.
      if (item.redClassified) return false;
      // Third guard (M7.3). This row has already been defaulted once this session, so the
      // rule stands down for good — including after an undo, which restores the row to the
      // very state that makes this rule fire. Without it the two would fight.
      if (item.uid && autoDefaultedRowsRef.current.has(item.uid)) return false;
      // Already there — do not churn state on every render.
      return String(item.category || '').trim() !== nonConsumableCategory;
    };

    if (!items.some(shouldDefault)) {
      return;
    }

    /**
     * M7.3 — through the door, and deliberately NOT onto the undo stack.
     *
     * Through the door because a bare setItems left itemsRef a render behind, so the very
     * next write could be built over a pre-default snapshot. That part is the actual bug and
     * it is fixed unconditionally.
     *
     * Off the stack because this rewrite does not happen when the user acts — it happens
     * when the ASYNC matcher finally reports 'none', which can be several edits later. I
     * tried recording it and the itemflow suite showed exactly what that costs: type a name,
     * type a quantity, and the default lands on TOP of the quantity edit, so one Undo
     * returns the row to a state the user never typed and their quantity survives. Same for
     * "type a name, add a row, delete a row": the default arrives after the delete and eats
     * the undo. An automatic background reclassification must not be the top of somebody
     * else's undo stack.
     *
     * The second reason is the one that made me look: the rule fires on exactly the state an
     * undo restores (a red row whose category is not the non-consumable one IS the firing
     * condition), so a recorded default is an undo that can never complete — press, re-apply,
     * no movement. `autoDefaultedRowsRef` closes that door too, and is kept: one default per
     * row per session, so the rule can never argue with the person using it.
     */
    const targetUids = new Set(items.filter(shouldDefault).map(it => it.uid).filter(Boolean));

    applyItems(prevItems => prevItems.map(item => (
      shouldDefault(item)
        ? {
            ...item,
            category: nonConsumableCategory,
            autoNonConsumable: true,
            // Remember what the row arrived with. The rule now overwrites a category the
            // bill supplied, so "New ingredient" has to be able to hand it back — losing
            // the OCR's own guess would make the honest choice the destructive one.
            preAutoCategory: item.preAutoCategory !== undefined ? item.preAutoCategory : (item.category || '')
          }
        : item
    )), { silent: true });

    // Recorded only AFTER the write. Marking first would make shouldDefault() false inside
    // the producer above and the rule would default nothing at all.
    targetUids.forEach(uid => autoDefaultedRowsRef.current.add(uid));
  }, [items, getDefaultNonConsumableCategory, applyItems, bmScreenOn]);

  /**
   * Load benchmarks for the matched rows. Debounced and additive: an item already fetched
   * is never fetched again, so typing does not produce a request per keystroke, and a
   * failure leaves the hint absent rather than showing an error the user cannot act on.
   */
  useEffect(() => {
    const wanted = [...new Set(
      items.map(it => it.item_id).filter(id => Number.isInteger(id) && id > 0)
    )].filter(id => !(id in itemBenchmarks));

    if (wanted.length === 0) return undefined;

    const timer = setTimeout(async () => {
      try {
        const businessId = UnitMappingService.getBusinessId();
        if (!businessId) return;
        const res = await http.get(`/stock-in/v2/benchmarks?businessId=${businessId}&itemIds=${wanted.join(',')}`);
        if (!res || !res.success) return;
        setItemBenchmarks(prev => {
          const next = { ...prev };
          // Record a null for items the vault had nothing for, so they are not re-requested
          // on every keystroke for the rest of the session.
          wanted.forEach(id => { next[id] = (res.benchmarks && res.benchmarks[id]) || null; });
          return next;
        });
      } catch (e) {
        console.debug('Benchmark hint unavailable:', e.message);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [items, itemBenchmarks]);

  /**
   * M4 — the vendor this bill belongs to, as an id, or null.
   *
   * Declared ABOVE the profile loader on purpose: it is read in that effect's dependency
   * array, which is evaluated during render, so a declaration below it would be a TDZ
   * ReferenceError on the first paint rather than a subtle bug.
   */
  const currentVendorId = selectedVendor?.vendor_id || null;

  /**
   * PIECE GUARD — load "what is one piece" for the matched `pcs` rows.
   *
   * Same shape as the benchmark loader above: debounced, additive, and a failure leaves
   * the hint absent rather than showing an error the user cannot act on. Only `pcs` rows
   * are asked about; kg/g/L/ml are self-describing and have nothing to look up.
   */
  useEffect(() => {
    const wanted = [...new Set(
      items
        .filter(it => isPcsUnit(it.unit))
        .map(it => it.item_id)
        .filter(id => Number.isInteger(id) && id > 0)
    )].filter(id => !(id in itemPieceProfiles));

    if (wanted.length === 0) return undefined;

    const timer = setTimeout(async () => {
      try {
        const businessId = UnitMappingService.getBusinessId();
        if (!businessId) return;
        // M4 — vendorId on the READ as well as the write, or the hint and the gate are
        // reading two different profiles for the same row.
        const res = await http.get(
          `/stock-in/v2/piece-profiles?businessId=${businessId}&itemIds=${wanted.join(',')}`
          + (currentVendorId ? `&vendorId=${currentVendorId}` : '')
        );
        if (!res || !res.success) return;
        setItemPieceProfiles(prev => {
          const next = { ...prev };
          // Record an explicit null for items with no profile, so the row is not
          // re-requested on every keystroke for the rest of the session.
          wanted.forEach(id => { next[id] = (res.profiles && res.profiles[id]) || null; });
          return next;
        });
      } catch (e) {
        console.debug('Piece profile hint unavailable:', e.message);
      }
    }, 400);

    return () => clearTimeout(timer);
    // currentVendorId is in the deps on purpose: changing the vendor changes which profile
    // applies, and a stale item-level answer would then be shown against a vendor rule.
  }, [items, itemPieceProfiles, currentVendorId]);

  /**
   * The live per-piece verdict for one row (§5.2), or null.
   *
   * This is where most mistakes die — silently, with no popup at all. A person who sees
   * "₹27.50 per piece — usually ₹6.88" while they are still typing fixes it themselves.
   */
  const pieceHintFor = useCallback((row) => {
    if (!isPcsUnit(row?.unit)) return null;
    const profile = row?.item_id ? itemPieceProfiles[row.item_id] : null;
    const bench = row?.item_id ? itemBenchmarks[row.item_id] : null;
    return pieceBandVerdict(row, profile, bench);
  }, [itemPieceProfiles, itemBenchmarks]);

  /** The permanent row caption and the quick-add chips for a `pcs` row. */
  const pieceProfileFor = useCallback(
    (row) => (isPcsUnit(row?.unit) && row?.item_id ? itemPieceProfiles[row.item_id] || null : null),
    [itemPieceProfiles]
  );

  /**
   * THE REPEAT-PURCHASE CHECK — which packet items on this bill still need confirming.
   *
   * Profiles are re-read from the server rather than taken from `itemPieceProfiles`. That
   * cache is filled by a 400 ms debounced effect and records an explicit null for items
   * with no profile so it never re-asks, which is right for a live row caption and wrong
   * here: a profile created minutes ago in another tab, or by the gate on the previous
   * bill, would read as "no profile" and the check would silently not happen. This runs
   * once per submit, so the extra round trip costs nothing anybody can feel.
   *
   * ONE ENTRY PER ITEM, not per row — the pack size is a fact about the item, so a bill
   * with four Kit Kat lines asks once.
   *
   * It never throws and it never blocks: a confirmation whose data could not be loaded is
   * a confirmation that cannot be asked, and a bill must not become unsaveable because a
   * price lookup timed out.
   */
  const buildPackConfirmQueue = useCallback(async (rows) => {
    try {
      const businessId = UnitMappingService.getBusinessId();
      if (!businessId) return [];

      /**
       * THE ROW'S OWN id FIRST, THE INVENTORY NAME SECOND.
       *
       * `item_id` is written by the server matcher, and plenty of real rows reach submit
       * without it — a name typed by hand, a bill whose match came back fuzzy, a row the
       * red-row screen let through. Keying this check on `item_id` alone therefore meant
       * the question silently did not get asked for exactly the items whose identity the
       * matcher was least sure about, which is not a defensible place to go quiet: the
       * profile is attached to the ITEM, and the item is the same item whether or not this
       * row managed to carry its number.
       *
       * `createAsNew` is excluded on purpose — the person has said this is a NEW item, so
       * whatever an existing name matches, it is not this row's definition.
       */
      const nameKey = (s) => String(s || '').trim().toLowerCase();
      const byName = new Map();
      (inventoryItems || []).forEach((inv) => {
        const id = Number(inv?.item_id);
        if (!Number.isInteger(id) || id <= 0) return;
        [inv.item_name, inv.name].forEach((n) => {
          const k = nameKey(n);
          if (k && !byName.has(k)) byName.set(k, id);
        });
      });
      const idOf = (it) => {
        const own = Number(it?.item_id);
        if (Number.isInteger(own) && own > 0) return own;
        if (it?.createAsNew) return null;
        return byName.get(nameKey(it?.item_name)) || byName.get(nameKey(it?.display_name)) || null;
      };

      const candidates = (rows || [])
        .map((it, index) => ({ it, index, id: idOf(it) }))
        .filter(({ it, id }) =>
          isPcsUnit(it.unit)
          && Number.isInteger(id) && id > 0
          && !packConfirmedIdsRef.current.includes(id));

      /**
       * SAYS WHY IT IS SILENT.
       *
       * This check not firing looks identical, from the outside, to this check being
       * broken — and the three reasons it legitimately stays quiet (no `pcs` row, no
       * identity for the row, no packet profile for the item) are invisible on screen.
       * One line in the console turns "it is not asking" into a fact somebody can read.
       */
      console.log('📦 [PackConfirm] pcs rows:', (rows || []).filter(r => isPcsUnit(r.unit)).length,
        '| identified:', candidates.map(c => `${c.it.item_name}#${c.id}`));

      if (candidates.length === 0) return [];

      const ids = [...new Set(candidates.map(({ id }) => id))].slice(0, 200);
      // M4 — vendorId on this read too, or the confirmation reads one profile and the
      // gate judges the row against another.
      const res = await http.get(
        `/stock-in/v2/piece-profiles?businessId=${businessId}&itemIds=${ids.join(',')}`
        + (currentVendorId ? `&vendorId=${currentVendorId}` : '')
      );
      const profiles = (res && res.success && res.profiles) || {};

      const seen = new Set();
      const entries = [];
      for (const { it, index, id } of candidates) {
        if (seen.has(id)) continue;
        const profile = profiles[id];
        const pack = Number(profile?.piecesPerPack);
        // Singles are never asked — there is no pack size on them that could have moved.
        // `arrivalMode` is checked as well as the number, because those two are written
        // together and a disagreement between them is not something to guess about.
        if (!profile || !Number.isInteger(pack) || pack < 2) {
          console.log(`📦 [PackConfirm] ${it.item_name}#${id}: no packet profile —`,
            profile ? `${profile.arrivalMode} of ${profile.piecesPerPack}` : 'none saved yet');
          continue;
        }
        if (profile.arrivalMode && profile.arrivalMode !== 'packet') continue;
        seen.add(id);
        entries.push({
          item_id: id,
          index,
          item_name: it.display_name || it.item_name || 'this item',
          pieces_per_pack: pack,
          quantity: Number(it.quantity),
          total_price: Number(it.total_price),
          last_price_per_piece: null,
        });
      }
      if (entries.length === 0) return [];

      // The price half of the read-back: what was last actually paid for one piece. For a
      // `pcs` item the stock unit IS the piece, so this endpoint's unit_cost is already
      // per piece and must not be converted again.
      await Promise.all(entries.map(async (entry) => {
        try {
          const r = await http.get(`/stock-in/v2/inventory/items/${entry.item_id}/latest-batch?businessId=${businessId}&purpose=stockin_filter`);
          const cost = Number(r?.batch?.unit_cost);
          if (Number.isFinite(cost) && cost > 0) entry.last_price_per_piece = cost;
        } catch (e) {
          // No known price → nothing to compare, and the filter below drops the entry.
        }
      }));

      /**
       * ── THE PRICE IS THE TRIGGER, NOT THE PACK ────────────────────────────────
       *
       * Asking on every repeat purchase was the wrong rule and it showed the moment it ran:
       * 3 pieces for ₹150 against a remembered ₹50 a piece is the SAME price, so the
       * question had no content — "is it still the same packet?" when every number says it
       * is. A confirmation nobody can fail is not a check, it is a keystroke, and it is
       * exactly how a team learns to click past the box that will one day matter.
       *
       * A stale pack size is only ever DISCOVERED through the money: if the supplier moved
       * from a 4-pack to a 6-pack, the per-piece rate moves with it. So the per-piece rate
       * this bill works out to is compared against the one on record, and the question is
       * asked whenever the two disagree AT ALL.
       *
       * ── WHY THE ±25% BAND IS GONE ────────────────────────────────────────────
       * This used to stay silent unless the rate moved by more than PIECE_NORMAL_BAND, on
       * the reasoning that the interruption budget is finite. But the band was borrowed
       * from the money check, which is answering a different question. The money check asks
       * "is this price abnormal?", and ±25% is a fair answer to that. This popup asks "is
       * the packet size still what we recorded?" — and a pack size that has gone stale does
       * not announce itself with a big number. A 4-pack quietly becoming a 5-pack moves the
       * per-piece rate by 25%, which landed exactly ON the old threshold and was rounded
       * into silence; a supplier trimming a pack while holding the packet price moves it
       * less than that and was never going to be seen at all.
       *
       * So the trigger is now any movement down to the paisa: ₹10.00 → ₹11.00 asks, and
       * only a rate that is the SAME to the paisa stays quiet. Rounding is what makes that
       * safe to write — an exact float comparison would fire on 49.999999999 against 50.
       *
       * The cost of this is real and worth naming: owners who buy at genuinely moving
       * prices will see this box often. It stays cheap on purpose — two buttons, no typing,
       * once per item per bill, and the second button now does the work for them.
       *
       * No price on either side means nothing to compare: silent. A row with no quantity or
       * no total cannot be priced at all, and the mandatory-field check upstream has
       * already refused it.
       */
      return entries.filter((entry) => {
        const last = Number(entry.last_price_per_piece);
        const qty = Number(entry.quantity);
        const total = Number(entry.total_price);
        if (!(last > 0) || !(qty > 0) || !(total > 0)) return false;
        const now = total / qty;
        entry.now_price_per_piece = now;
        // To the paisa. Money is counted in paise, so two rates that round to the same
        // paisa ARE the same price; anything else is a movement, however small.
        if (Math.round(now * 100) === Math.round(last * 100)) {
          console.log(`📦 [PackConfirm] ${entry.item_name}#${entry.item_id}: ₹${now.toFixed(2)}/piece against ₹${last.toFixed(2)} — same price, nothing to ask`);
          return false;
        }
        return true;
      });
    } catch (e) {
      console.warn('📦 [PackConfirm] skipped — could not load profiles:', e.message);
      return [];
    }
  }, [currentVendorId, inventoryItems]);

  /**
   * The hint for one row, or null. Converts the entered quantity into the item's standard
   * unit first — 2 kg against a per-gram benchmark would otherwise read as 500× too small.
   */
  const quantityHintFor = useCallback((row) => {
    const bench = row?.item_id ? itemBenchmarks[row.item_id] : null;
    if (!bench) return null;

    const qty = parseFloat(row.quantity);
    if (!Number.isFinite(qty) || qty <= 0) return null;

    const enteredBase = UnitMappingService.factorToBase(row.unit);
    const standardBase = UnitMappingService.factorToBase(bench.standardUnit);
    if (!(enteredBase > 0) || !(standardBase > 0)) return null;
    // Refuse across unit families rather than comparing grams to millilitres.
    if (UnitMappingService.baseUnitOf(row.unit) !== UnitMappingService.baseUnitOf(bench.standardUnit)) return null;

    const qtyInStandardUnit = (qty * enteredBase) / standardBase;
    return quantityBandVerdict(qtyInStandardUnit, bench);
  }, [itemBenchmarks]);

  /**
   * The expiry requirements — the list that makes an expiry date mandatory (M3).
   *
   * This used to be a mount-only effect that gave up silently on `if (!businessId) return`.
   * businessId is resolved asynchronously, so on any session where it arrived a moment late
   * the Set stayed empty for the WHOLE session and expiry enforcement was simply off — with
   * nothing on screen or in the console saying so. That is the exact failure the expiry fix
   * existed to kill, reached through a different door.
   *
   * So: keep looking until the id exists, retry a failed fetch once, and if it still cannot
   * be loaded, SAY what the consequence is. Enforcement being unavailable is a fact the
   * person reading the console needs, not a swallowed exception.
   */
  useEffect(() => {
    let cancelled = false;
    const timers = [];
    const RESOLVE_POLL_MS = 400;
    const MAX_RESOLVE_ATTEMPTS = 25;   // ~10s, then stop asking

    const load = async (attempt = 0, resolveAttempt = 0) => {
      if (cancelled) return;
      const businessId = UnitMappingService.getBusinessId() || localStorage.getItem('businessId');
      if (!businessId) {
        if (resolveAttempt >= MAX_RESOLVE_ATTEMPTS) {
          console.warn('⚠️ Expiry enforcement disabled — no business id resolved, could not load requirements');
          return;
        }
        timers.push(setTimeout(() => load(attempt, resolveAttempt + 1), RESOLVE_POLL_MS));
        return;
      }

      try {
        const response = await http.get(`/expiry-tracking/required?businessId=${businessId}&language=${language || 'en'}`);
        if (cancelled) return;
        if (response && response.success && Array.isArray(response.items)) {
          // Both keys: the id for matched rows, and the name for manually typed ones, which
          // carry no item_id at all. See rowRequiresExpiry — one resolver, two doors.
          setExpiryRequirements(new Set(response.items.map(item => item.item_id)));
          setExpiryRequiredNames(new Set(
            response.items.flatMap(item => [item.item_name, item.translated_name])
              .filter(Boolean)
              .map(n => String(n).trim().toLowerCase())
          ));
          return;
        }
        throw new Error('unexpected response shape');
      } catch (error) {
        if (cancelled) return;
        if (attempt === 0) {
          // One delayed re-attempt. A cold start behind a slow API is the common case and
          // it costs nothing to ask twice.
          timers.push(setTimeout(() => load(1, resolveAttempt), 1500));
          return;
        }
        console.warn(
          '⚠️ Expiry enforcement disabled — could not load requirements from /expiry-tracking/required:',
          error.message
        );
      }
    };

    load();
    return () => { cancelled = true; timers.forEach(clearTimeout); };
    // Re-fetch on a language change: the name half of the requirement list is translated.
  }, [language]);

  // **NEW: Vendor phone autofill - fuzzy match vendor name**
  useEffect(() => {
    if (!vendorName || vendorName.trim().length < 3) return;

    const timer = setTimeout(async () => {
      try {
        const businessId = UnitMappingService.getBusinessId();
        const response = await http.post('/vendors/fuzzy-match', {
          vendorName: vendorName.trim(),
          businessId
        });

        if (response.success && response.vendor && response.vendor.vendor_phone) {
          // Only autofill if phone is currently empty
          if (!vendorPhone || vendorPhone.trim() === '') {
            applyVendorPhone(response.vendor.vendor_phone, { fromVendorRecord: true });
          }
        }
      } catch (error) {
        // Silently fail - this is just a convenience feature
        console.debug('Vendor autofill check:', error.message);
      }
    }, 800); // Debounce 800ms

    return () => clearTimeout(timer);
  }, [vendorName, vendorPhone]);

  // **NEW: Vendor search/autocomplete handler**
  const searchVendors = useCallback(async (searchQuery) => {
    if (!searchQuery || searchQuery.trim().length < 1) {
      setVendorSuggestions([]);
      setShowVendorDropdown(false);
      return;
    }

    try {
      setVendorSearchLoading(true);
      const businessId = UnitMappingService.getBusinessId();
      if (!businessId) {
        console.warn('⚠️  No businessId available for vendor search');
        return;
      }


      // Build query string manually
      const queryParams = new URLSearchParams({
        businessId: businessId,
        search: searchQuery.trim(),
        active: 'true'
      });

      const response = await http.get(`/vendors?${queryParams.toString()}`);


      if (response.success && Array.isArray(response.vendors)) {
        setVendorSuggestions(response.vendors);
        setShowVendorDropdown(response.vendors.length > 0);
      } else {
        console.warn('⚠️  Invalid vendor search response:', response);
        setVendorSuggestions([]);
        setShowVendorDropdown(false);
      }
    } catch (error) {
      console.error('❌ Failed to search vendors:', error);
      console.error('   Error details:', error.response || error.message);
      setVendorSuggestions([]);
      setShowVendorDropdown(false);
    } finally {
      setVendorSearchLoading(false);
    }
  }, []);

  // **NEW: Debounced vendor search**
  useEffect(() => {
    // Skip this run if a vendor was just selected — prevents re-opening the dropdown
    if (vendorJustSelectedRef.current) {
      vendorJustSelectedRef.current = false;
      return;
    }
    const timer = setTimeout(() => {
      if (activeVendorField === 'name') {
        searchVendors(vendorName);
      } else if (activeVendorField === 'phone') {
        searchVendors(vendorPhone);
      }
    }, 300); // Debounce 300ms

    return () => clearTimeout(timer);
  }, [vendorName, vendorPhone, activeVendorField, searchVendors]);

  // **NEW: Close vendor dropdown when clicking outside**
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        vendorDropdownRef.current &&
        !vendorDropdownRef.current.contains(event.target) &&
        vendorInputRef.current &&
        !vendorInputRef.current.contains(event.target) &&
        (!phoneInputRef.current || !phoneInputRef.current.contains(event.target))
      ) {
        setShowVendorDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  /**
   * Fetch this vendor's last order and offer it as an item template.
   *
   * SPLIT OUT OF handleVendorSelect, and the split is the fix for a data-loss bug.
   * Changing the purchase date re-fetches this template, and it did so by calling
   * handleVendorSelect — which is the "the user picked a vendor from the dropdown"
   * handler. When no vendor was selected it was handed a SYNTHETIC `{ name }` object,
   * and that object did two silent, damaging things:
   *
   *   1. `applyVendorPhone(vendor.contact_phone || '')` — `contact_phone` is undefined on
   *      a synthetic object, so the phone the user had just typed was WIPED, and marked
   *      as "came from the vendor record" on the way out.
   *   2. `setSelectedVendor(vendor)` — the form now believed this was an existing vendor,
   *      which switches OFF the "Phone Number is mandatory for new vendors" rule.
   *
   * So: type the vendor, type the phone, change the purchase date — and the bill submits
   * with no phone at all, past a check that exists precisely to stop that. Vendor `dmart`
   * (created 2026-08-30 23:42:00, same second as stock-in #2664) has contact_phone NULL
   * for exactly this reason.
   *
   * This function touches NO header field. The date handler calls this; only a real
   * selection from the dropdown calls handleVendorSelect.
   */
  const fetchVendorLastOrder = useCallback(async (vendor, customDate = null) => {
    if (!vendor || !vendor.name) return;
    const businessId = UnitMappingService.getBusinessId();
    if (!businessId) return;

    try {
      setLastOrderFetching(true);

      const params = new URLSearchParams({ business_id: businessId, vendorName: vendor.name });
      if (vendor.vendor_id) params.set('vendorId', vendor.vendor_id);

      const targetDate = customDate || productionDate;
      if (targetDate) params.set('date', targetDate);

      const resp = await http.get(`/vendors/last-order-items?${params.toString()}`);

      if (!resp.success || !Array.isArray(resp.items) || resp.items.length === 0) {
        // No history — leave blank items as-is (but vendor name/phone already filled)
        return;
      }

      // 3. Map fetched items into the form's item shape
      const prefilled = resp.items
        .map(row => {
          const name = String(row.item_name || '').trim();
          if (!name) return null;
          const unitName = UnitMappingService.normalizeUnitSymbol(row.unit_symbol || '') || (row.unit_symbol || '');
          const qty = row.quantity !== null && row.quantity !== undefined && Number.isFinite(parseFloat(row.quantity))
            ? String(parseFloat(row.quantity))
            : '';
          const totalPriceVal = row.total_price !== null && row.total_price !== undefined && Number.isFinite(parseFloat(row.total_price))
            ? String(parseFloat(Number(row.total_price).toFixed(2)))
            : '';
          const unitPriceVal = row.unit_price !== null && row.unit_price !== undefined && Number.isFinite(parseFloat(row.unit_price))
            ? String(parseFloat(Number(row.unit_price).toFixed(4)))
            : '';

          return {
            ...emptyItem(),
            item_id: row.item_id || null,
            item_name: name,
            bm_input_kind: 'autofill',
            lastStableName: name,
            display_name: name,
            db_name: name,
            category: row.category || '',
            quantity: qty,
            unit: unitName || 'gram',
            total_price: totalPriceVal,
            unit_price: unitPriceVal,
            matchStatus: row.item_id ? 'exact' : 'pending',
            userConfirmed: Boolean(row.item_id),
          };
        })
        .filter(Boolean);

      if (prefilled.length === 0) return;

      // 4. Prompt for confirmation instead of merging directly
      const dateLabel = resp.order_date
        ? new Date(resp.order_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
        : 'a previous order';

      // Only show the autofill popup if the setting is enabled in Settings > Inventory Management
      if (!vendorAutofillPromptEnabled) return;

      setVendorAutofillPrompt({
        vendorName: vendor.name,
        prefilledItems: prefilled,
        dateLabel: dateLabel,
        source: resp.source || 'history'
      });
      setAutofillSelectedIndices(new Set(prefilled.map((_, i) => i)));

    } catch (err) {
      console.warn('[Vendor Autofill] Failed to fetch last order:', err.message);
      // Silently fail — do not disrupt the user
    } finally {
      setLastOrderFetching(false);
    }
  }, [convertUnitSymbolToName, productionDate, vendorAutofillPromptEnabled]);

  /**
   * The user picked a vendor from the dropdown. THIS is the only thing allowed to
   * overwrite the header fields, because it is the only moment the user actually asked
   * for a different vendor's details.
   */
  const handleVendorSelect = useCallback(async (vendor, customDate = null, { bmCertain = false } = {}) => {
    if (!vendor || !vendor.name) return;
    // Bill Memory: only a vendor picked from the dropdown or tapped from the vendor buttons is certain (design §9).
    setBmVendorCertain(!!(bmCertain && vendor.vendor_id));
    bmReportPick(bmCertain && vendor.vendor_id ? vendor : null);
    // Set the flag BEFORE updating vendorName so the debounced search effect ignores the change
    vendorJustSelectedRef.current = true;
    setVendorName(vendor.name);
    // Only a row that actually came out of the vendor table may rewrite the phone. A
    // synthetic `{ name }` has no contact_phone, and blanking the field from one is how a
    // typed number used to disappear.
    if (Object.prototype.hasOwnProperty.call(vendor, 'contact_phone')) {
      applyVendorPhone(vendor.contact_phone || '', { fromVendorRecord: true });
    }
    setShowVendorDropdown(false);
    setLastOrderBanner(null);
    // Store the full vendor object so vendor_id is available without fuzzy search at
    // submit time. Only a REAL vendor row belongs here — see isNewVendor at submit.
    setSelectedVendor(vendor);

    await fetchVendorLastOrder(vendor, customDate);
  }, [applyVendorPhone, fetchVendorLastOrder, bmReportPick]);

  const prefillVendorItemsForManualEntry = async () => {
    const nameToSearch = String(vendorName || '').trim();
    if (!nameToSearch) {
      setErrorModalMessage('Select a vendor first, then tap + to auto-fill items.');
      setShowErrorModal(true);
      if (vendorInputRef.current) vendorInputRef.current.focus();
      return;
    }

    try {
      const businessId = UnitMappingService.getBusinessId();
      const queryParams = new URLSearchParams({
        businessId,
        search: nameToSearch,
        active: 'true'
      });

      const vendorSearchResponse = await http.get(`/vendors?${queryParams.toString()}`);
      const vendors = Array.isArray(vendorSearchResponse?.vendors) ? vendorSearchResponse.vendors : [];

      const selectedVendor =
        vendors.find(v => String(v?.name || '').toLowerCase() === nameToSearch.toLowerCase()) ||
        vendors[0];

      if (!selectedVendor?.vendor_id) {
        setErrorModalMessage(`No vendor record found for "${nameToSearch}".`);
        setShowErrorModal(true);
        return;
      }

      const prefillResponse = await http.get(`/vendors/${selectedVendor.vendor_id}/manual-entry-items`);
      const sourceItems = Array.isArray(prefillResponse?.items) ? prefillResponse.items : [];

      if (!sourceItems.length) {
        setErrorModalMessage(`No previous vendor items found for "${selectedVendor.name}".`);
        setShowErrorModal(true);
        return;
      }

      const mappedItems = sourceItems
        .map((row) => {
          const itemName = String(row?.item_name || '').trim();
          if (!itemName) return null;

          const unitFromVendor = UnitMappingService.normalizeUnitSymbol(row?.unit_symbol || '');
          const quantityNumber = row?.suggested_quantity !== null && row?.suggested_quantity !== undefined
            ? Number(row.suggested_quantity)
            : null;

          const nextItem = {
            ...emptyItem(),
            item_id: row?.item_id || null,
            item_name: itemName,
            bm_input_kind: 'autofill',
            lastStableName: itemName,
            quantity: Number.isFinite(quantityNumber) ? String(quantityNumber) : '',
            unit: unitFromVendor || 'gram',
            category: row?.category || '',
            matchStatus: row?.item_id ? 'exact' : 'pending',
            userConfirmed: Boolean(row?.item_id)
          };

          return nextItem;
        })
        .filter(Boolean);

      if (!mappedItems.length) {
        setErrorModalMessage(`No usable vendor items found for "${selectedVendor.name}".`);
        setShowErrorModal(true);
        return;
      }

      setVendorName(selectedVendor.name || nameToSearch);
      if (!vendorPhone && selectedVendor.contact_phone) {
        applyVendorPhone(selectedVendor.contact_phone, { fromVendorRecord: true });
      }

      applyItems((prevItems) => {
        const nonEmptyItems = prevItems.filter((item) => item.item_name && item.item_name.trim());
        return [...nonEmptyItems, ...mappedItems];
      }, { snapshot: true });

      // An informational toast, NOT the post-submit SuccessModal. That modal's close
      // handler is the global form reset, so reporting "Added 7 items" through it wiped
      // the 7 items the moment the user clicked OK.
      setInfoToast(`Added ${mappedItems.length} items from ${selectedVendor.name}.`);
    } catch (error) {
      console.error('❌ Failed to prefill vendor items:', error);
      setErrorModalMessage('Failed to auto-fill vendor items. Please try again.');
      setShowErrorModal(true);
    }
  };

  useEffect(() => {
    if (!manualHeaderAddTrigger) return;
    if (manualHeaderAddTrigger === lastManualHeaderTriggerRef.current) return;

    lastManualHeaderTriggerRef.current = manualHeaderAddTrigger;
    prefillVendorItemsForManualEntry();
  }, [manualHeaderAddTrigger]);

  // NOTE: Re-matching on inventory load removed due to initialization order issues
  // Instead, matching happens automatically when user types in the item_name field
  // via handleInputChange() which calls matchAgainstInventory()

  // **NEW: Match against inventory items - MUST be defined before useEffect**
  const matchAgainstInventory = useCallback((rawName, maxSuggestions = 5, vendorId = null) => {
    // Expand common abbreviations
    const expandAbbreviations = (str) => {
      if (!str) return '';
      const abbrevMap = {
        'pow': 'powder',
        'pwd': 'powder',
        'samb': 'sambar',
        'veg': 'vegetable',
        'chk': 'chicken',
        'gm': 'gram',
        'gr': 'gram',
        'kg': 'kilogram',
        'pkt': 'packet',
        'bot': 'bottle',
        'btl': 'bottle'
      };

      let expanded = str.toLowerCase();
      for (const [abbrev, full] of Object.entries(abbrevMap)) {
        const regex = new RegExp(`\\b${abbrev}\\b`, 'gi');
        expanded = expanded.replace(regex, full);
      }
      return expanded;
    };

    const norm = (str) => {
      if (!str) return '';
      return String(str).toLowerCase().replace(/[^a-z0-9]/g, '').trim();
    };

    const levenshtein = (a, b) => {
      if (!a.length) return b.length;
      if (!b.length) return a.length;
      const matrix = Array.from({ length: b.length + 1 }, (_, i) => [i]);
      matrix[0] = Array.from({ length: a.length + 1 }, (_, j) => j);
      for (let i = 1; i <= b.length; i++) {
        for (let j = 1; j <= a.length; j++) {
          const cost = b[i - 1] === a[j - 1] ? 0 : 1;
          matrix[i][j] = Math.min(
            matrix[i - 1][j] + 1,
            matrix[i][j - 1] + 1,
            matrix[i - 1][j - 1] + cost
          );
        }
      }
      return matrix[b.length][a.length];
    };

    const n = norm(rawName);
    if (!n) {
      return { status: 'pending', inventoryItem: null, suggestions: [] };
    }


    if (inventoryItems.length === 0) {
      console.warn('⚠️ Inventory is empty! No items to match against.');
      console.warn('   Check window.__DEBUG_INVENTORY_COUNT__ in console');
      return { status: 'none', inventoryItem: null, suggestions: [] };
    }

    // 1️⃣ Exact match against inventory (case-insensitive)
    const exactMatch = inventoryItems.find(it => norm(it.name) === n);
    if (exactMatch) {
      return { status: 'exact', inventoryItem: exactMatch, suggestions: [] };
    }

    // 2️⃣ Fuzzy suggestions (similar spelling - YELLOW indicator)
    const scored = inventoryItems.map(it => {
      const itemNorm = norm(it.name);
      const dist = levenshtein(n, itemNorm);
      const shorter = Math.min(n.length, itemNorm.length);
      const longer = Math.max(n.length, itemNorm.length);
      const threshold = Math.max(1, Math.floor(shorter * 0.4));

      // **Method 1: Substring matching (for single-word items like "rajinioil")**
      const itemLower = it.name.toLowerCase();
      const rawLower = rawName.toLowerCase();
      const substringMatch = itemLower.includes(rawLower) || rawLower.includes(itemLower);

      // **Method 2: Normalized substring (remove spaces/special chars)**
      const normalizedContains = n.includes(itemNorm) || itemNorm.includes(n);

      // **Method 3: Expanded abbreviations matching**
      const rawExpanded = expandAbbreviations(rawName);
      const itemExpanded = expandAbbreviations(it.name);
      const expandedMatch = rawExpanded.includes(itemExpanded.toLowerCase()) ||
        itemExpanded.toLowerCase().includes(rawExpanded);

      // **Method 4: Word-based matching (for multi-word items)**
      const nWords = rawExpanded.split(/[\s\-\/]+/).filter(w => w.length > 2);
      const itemWords = itemExpanded.split(/[\s\-\/]+/).filter(w => w.length > 2);
      const wordOverlap = nWords.filter(nw =>
        itemWords.some(iw => iw.includes(nw) || nw.includes(iw) || levenshtein(nw, iw) <= 2)
      ).length;
      const wordScore = wordOverlap / Math.max(nWords.length, itemWords.length, 1);

      // **Method 5: Character similarity for typos**
      const similarityRatio = 1 - (dist / longer);

      // Consider close if ANY method matches well
      const isClose =
        dist <= threshold ||
        substringMatch ||
        normalizedContains ||
        expandedMatch ||
        (wordScore >= 0.5 && wordOverlap >= 2) ||
        similarityRatio >= 0.6;

      return {
        ...it,
        distance: dist,
        wordScore: wordScore,
        similarityRatio: similarityRatio,
        isClose: isClose
      };
    });
    const close = scored.filter(s => s.isClose).sort((a, b) => {
      // Prioritize similarity ratio (best overall match)
      if (Math.abs(a.similarityRatio - b.similarityRatio) > 0.15) {
        return b.similarityRatio - a.similarityRatio;
      }
      // Then word score
      if (Math.abs(a.wordScore - b.wordScore) > 0.2) {
        return b.wordScore - a.wordScore;
      }
      // Finally edit distance
      return a.distance - b.distance;
    });
    if (close.length > 0) {
      const suggestions = close.slice(0, maxSuggestions).map(s => ({
        name: s.name,
        category: s.category_name || s.category,
        category_name: s.category_name || s.category,
        unit: s.unit_symbol || s.unit,
        unit_symbol: s.unit_symbol || s.unit,
        tax_slab: s.tax_slab
      }));
      return { status: 'fuzzy', inventoryItem: close[0], suggestions };
    }

    // 3️⃣ Not in inventory
    return { status: 'none', inventoryItem: null, suggestions: [] };
  }, [inventoryItems]);

  // ❌ REMOVED: Client-side batch number generation
  // Batch numbers are now generated server-side with format: YYYYMMDD-XXXXX
  // This ensures globally unique, sequential batch numbers across all sessions

  // **NEW: Duplicate Detection System - MUST BE DEFINED BEFORE OCR useEffect**
  const detectAndHandleDuplicates = useCallback((newItems) => {

    // Use a function to access the latest items state
    // Reads the live items instead of hiding this whole decision tree inside a setState
    // updater: it fires modals and flags, none of which belong in a reducer (StrictMode
    // double-invokes those). `return` below means "leave the form alone".
    (() => {
      const currentItems = itemsRef.current;

      // **CASE 1: Check for duplicates WITHIN the new scan (same bill scanned twice)**
      const itemNameCounts = {};
      const duplicatesWithinScan = [];

      newItems.forEach((item, idx) => {
        const itemName = item.item_name.trim().toLowerCase();
        // Only count non-empty item names
        if (itemName.length > 0) {
          if (!itemNameCounts[itemName]) {
            itemNameCounts[itemName] = [];
          }
          itemNameCounts[itemName].push({ ...item, originalIndex: idx });
        }
      });

      // Find items that appear more than once in the new scan
      Object.keys(itemNameCounts).forEach(itemName => {
        if (itemNameCounts[itemName].length > 1) {
          for (let i = 1; i < itemNameCounts[itemName].length; i++) {
            duplicatesWithinScan.push({
              ...itemNameCounts[itemName][i],
              isDuplicate: true,
              duplicateType: 'within-scan'
            });
          }
        }
      });

      // **CASE 2: Check for duplicates against existing items in form**
      // Only check if OCR has already run in this session (i.e., this is a second/third scan)
      // Don't check against items from previous sessions
      const shouldCheckAgainstExisting = hasOcrRunInSession &&
        currentItems.length > 0 &&
        currentItems.some(it => it.item_name && it.item_name.trim().length > 0);

      const existingItemNames = shouldCheckAgainstExisting
        ? currentItems
          .filter(it => it.item_name && it.item_name.trim().length > 0)
          .map(it => it.item_name.trim().toLowerCase())
        : [];
      const duplicatesAgainstExisting = [];

      if (shouldCheckAgainstExisting && existingItemNames.length > 0) {

        newItems.forEach((newItem, idx) => {
          const itemName = newItem.item_name.trim().toLowerCase();
          // Only check non-empty item names
          if (itemName.length > 0 && existingItemNames.includes(itemName)) {
            duplicatesAgainstExisting.push({
              ...newItem,
              originalIndex: idx,
              isDuplicate: true,
              duplicateType: 'against-existing'
            });
          }
        });
      }

      // Combine both types of duplicates (avoid double-counting)
      const allDuplicateIndices = new Set([
        ...duplicatesWithinScan.map(d => d.originalIndex),
        ...duplicatesAgainstExisting.map(d => d.originalIndex)
      ]);

      // Map duplicates with their ORIGINAL indices from newItems array
      const allDuplicates = Array.from(allDuplicateIndices).map(originalIdx => ({
        ...newItems[originalIdx],
        originalIndex: originalIdx // Preserve the index in pendingOcrItems
      }));


      if (allDuplicates.length > 0) {

        // Store pending items and show modal (DON'T modify items state yet)
        setPendingOcrItems(newItems);
        setDuplicateItems(allDuplicates);
        setSelectedDuplicates(new Set(allDuplicates.map((_, idx) => idx))); // Select all by default
        setShowDuplicateModal(true);
        setSemanticProcessing(false);

        // **NEW: Check for container units in new items after duplicate modal is handled**
        setTimeout(() => {
          checkForContainerUnits(newItems);
        }, 500);

        // Force a re-render with immediate state check
        setTimeout(() => {
          console.log('duplicateItems count:', allDuplicates.length);
        }, 100);


        // Return currentItems unchanged - wait for user to decide what to keep
        return;
      } else {

        // No unknown units - add items to form immediately
        if (currentItems.length === 0 || (currentItems.length === 1 && !currentItems[0].item_name)) {
          applyItems(newItems, { snapshot: true });
          setSemanticProcessing(false);
          setHasOcrRunInSession(true);
          return;
        } else {
          const nonEmptyItems = currentItems.filter(item => item.item_name && item.item_name.trim());
          applyItems([...nonEmptyItems, ...newItems], { snapshot: true });
          setSemanticProcessing(false);
          setHasOcrRunInSession(true);
          return;
        }

        /* DISABLED - UNIT CONVERSION CHECK - Will be enabled after stockin is fully working
        // Collect ALL unknown units
        const unknownUnitsList = [];
        
        for (let i = 0; i < newItems.length; i++) {
          const item = newItems[i];
          const unitRaw = String(item.unit || '').toLowerCase().trim();
          
          if (!unitRaw || unitRaw.length === 0) continue;
          
          const isKnownUnit = knownUnits.includes(unitRaw);
          
          if (!isKnownUnit) {
            const itemName = String(item.item_name || '').trim();
            const normalizedUnit = normalizeUnitForKey(unitRaw);
            const conversionKey = `${normalizeItemKey(item)}_${normalizedUnit}`;
            const hasConversion = containerConversions[conversionKey];
            
            if (!hasConversion) {
              unknownUnitsList.push({
                itemIndex: i,
                itemName: itemName,
                unit: unitRaw,
                quantity: String(item.quantity || '1')
              });
            } else {
              // Apply saved conversion
              const { factor, toUnit } = hasConversion;
              const originalQty = parseFloat(item.quantity) || 1;
              const convertedQty = originalQty * factor;
              
              newItems[i] = {
                ...item,
                quantity: convertedQty.toString(),
                unit: toUnit,
                original_quantity: originalQty,
                original_unit: unitRaw,
                conversion_applied: true
              };
            }
          }
        }
        
        // If unknown units found, show conversion modal
        if (unknownUnitsList.length > 0) {
          setUnknownUnitsQueue(unknownUnitsList);
          setCurrentUnknownUnitIndex(0);
          
          // Store items to add after conversion
          if (currentItems.length === 0 || (currentItems.length === 1 && !currentItems[0].item_name)) {
            setPendingOcrItems(newItems); // Will replace empty form
          } else {
            const nonEmptyItems = currentItems.filter(item => item.item_name && item.item_name.trim());
            setPendingOcrItems([...nonEmptyItems, ...newItems]); // Will merge with existing
          }
          
          // Show modal for first unknown unit
          const first = unknownUnitsList[0];
          setContainerConversionForm({
            itemIndex: first.itemIndex,
            itemName: first.itemName,
            containerUnit: first.unit,
            quantity: '1',
            standardUnit: 'kg'
          });
          setShowContainerConversionModal(true);
          setSemanticProcessing(false);
          return; // Don't add items yet - wait for conversion
        }
        END OF DISABLED CODE */

        // No duplicates and no unknown units - add items to form

        // No unknown units - add items to form immediately
        if (currentItems.length === 0 || (currentItems.length === 1 && !currentItems[0].item_name)) {
          applyItems(newItems, { snapshot: true });
          setSemanticProcessing(false);
          setHasOcrRunInSession(true);
          return;
        } else {
          const nonEmptyItems = currentItems.filter(item => item.item_name && item.item_name.trim());
          applyItems([...nonEmptyItems, ...newItems], { snapshot: true });
          setSemanticProcessing(false);
          setHasOcrRunInSession(true);
          return;
        }
      }
    })();
  }, [applyItems]);

  useEffect(() => {
    if (!ocrData || !Array.isArray(ocrData.items) || ocrData.items.length === 0) return;

    // **IMPORTANT: Wait for inventory to load before processing OCR**
    if (inventoryLoading) {
      return;
    }

    // Check if we've already processed this exact ocrData WITH inventory
    const ocrId = ocrData.timestamp || JSON.stringify(ocrData.items.map(i => i.item_name));
    if (ocrProcessedRef.current === ocrId && inventoryItems.length > 0) {
      return;
    }

    if (inventoryItems.length === 0) {
      console.warn('⚠️ OCR processing: Inventory is empty, items will not match!');
      // Continue processing but items won't match
    } else {
      ocrProcessedRef.current = ocrId; // Mark as processed
    }

    const normalize = (s) => String(s || '').trim().toLowerCase();
    const singularize = (s) => {
      const t = normalize(s);
      if (t.endsWith('s') && t.length > 1) return t.slice(0, -1);
      return t;
    };
    const symbolToName = (sym) => {
      const m = {
        kg: 'kilogram',
        g: 'gram',
        ml: 'milliliter',
        l: 'liter',
        pc: 'piece',
        pcs: 'piece',
        doz: 'dozen',
        box: 'box',
        pkt: 'packet'
      };
      return m[normalize(sym)] || sym;
    };

    (async () => {
      setSemanticProcessing(true);

      // ONE BRAIN (final-connections Lock 1): the two legacy pre-checks that
      // lived here (Failsafe-5 OCR quality gate, Failsafe-6 financial
      // validation) were built for the old Vision pipeline and keyed on
      // fields (confidence_scores / full_text) the single-call parser never
      // emits — they silently self-skipped on every scan. The server now does
      // both jobs for real (image_quality report + reconcileBill math), so
      // the UI just seeds its audit display from server truth:
      if (ocrData.grand_total || ocrData.summary?.grandTotal) {
        setFinancialAudit(prev => ({
          ...prev,
          auditEnabled: true,
          ocrGrandTotal: Number(ocrData.grand_total || ocrData.summary?.grandTotal) || null,
          ocrSubtotal: Number(ocrData.summary?.subtotal) || null,
          taxAmount: Number(ocrData.summary?.totalTax) || 0
        }));
      }

      // Load supplier conversions and build a fast lookup
      let conversions = [];
      try {
        const businessId = UnitMappingService.getBusinessId();
        conversions = await UnitMappingService.getSupplierConversions(businessId);
      } catch (e) {
        console.warn('Could not load supplier conversions, proceeding without auto-convert:', e.message);
      }

      const convMap = new Map();
      const convByItem = new Map(); // item -> unique conversion (if exactly one)
      // Key: `${itemName}|${containerSymbol}` in lowercase
      conversions.forEach((c) => {
        const item = normalize(extractConversionItemName(c));
        const container = normalize(c.containerType || c.from_unit_symbol);
        if (!item || !container) return;
        convMap.set(`${item}|${container}`, {
          factor: parseFloat(c.quantity || c.conversion_factor) || 0,
          toSymbol: normalize(c.unit || c.to_unit_symbol)
        });

        // Track per-item conversions to allow fallback when OCR unit doesn't match container term
        const existing = convByItem.get(item) || [];
        existing.push({
          container,
          factor: parseFloat(c.quantity || c.conversion_factor) || 0,
          toSymbol: normalize(c.unit || c.to_unit_symbol)
        });
        convByItem.set(item, existing);
      });

      // **Helper: Clean obviously wrong item names from OCR**
      const cleanItemName = (name) => {
        if (!name) return '';
        const cleaned = String(name).trim();

        // Remove bullet points and leading symbols
        const withoutBullets = cleaned.replace(/^[•●○◦▪▫■□▸▹►▻◄◅⦿⦾⊙⊚★☆✓✔✗✘×]+\s*/g, '');

        // Check if it's just a price (number with optional currency symbols and decimals)
        const isPurePrice = /^[₹$€£¥₩]?\s*[\d,]+\.?\d*$/.test(withoutBullets.trim());
        if (isPurePrice) {
          console.warn('⚠️ Skipping OCR item - appears to be pure price:', cleaned);
          return ''; // Return empty - will be filtered out
        }

        // Check if it's too short (likely OCR garbage)
        if (withoutBullets.length < 2) {
          console.warn('⚠️ Skipping OCR item - too short:', cleaned);
          return '';
        }

        return withoutBullets;
      };

      // **STEP 3: Process each item through semantic filters**
      const businessId = UnitMappingService.getBusinessId();
      const processedWithFilters = [];
      const filterResultsMap = {};

      for (let idx = 0; idx < ocrData.items.length; idx++) {
        const it = ocrData.items[idx];

        // **DEBUG: Log COMPLETE raw item from API before any processing**


        // Clean the item name
        const cleanedName = cleanItemName(it.item_name);
        if (!cleanedName) {
          console.warn('⚠️ Skipping invalid OCR item:', it.item_name);
          continue; // Skip this item entirely
        }

        // Use cleaned name
        const cleanedItem = { ...it, item_name: cleanedName };


        try {
          // Process through semantic filters
          // Backend expects: item_name, quantity, unit_price, total_price
          // OR the mapped versions: ocr_item_name, ocr_quantity, ocr_unit_cost, ocr_total_cost

          // **CRITICAL: Trust backend price calculation - DO NOT recalculate**
          // Backend priceCalculator has already processed the prices correctly
          // If we recalculate here, we'll get wrong values!

          const extractedTotalPrice = parseFloat(cleanedItem.total_price || cleanedItem.total_cost) || 0;
          const extractedUnitPrice = parseFloat(cleanedItem.unit_price || cleanedItem.rate) || 0;
          const extractedQuantity = parseFloat(cleanedItem.quantity) || 0;

          // ❌ REMOVED BUGGY FALLBACK: Don't calculate total from unit_price
          // The backend has already done the correct calculation
          const finalTotalPrice = extractedTotalPrice;

          // ONE BRAIN (final-connections Lock 1): matching and prices arrive
          // ALREADY RESOLVED from the server (InventoryNameMatcher with its
          // vetoes + priceCalculator + gate). The old code here made one HTTP
          // round-trip PER ITEM through the legacy filterOrchestrator — full
          // seconds of latency on a 36-line bill — and its veto-less matcher
          // could OVERWRITE the server's carefully vetoed name. The UI now
          // renders server truth; zero decision calls.
          {
            const fd = {}; // legacy filter payload — server enrichment rides on the item itself
            const filterResults = {};
            filterResultsMap[idx] = { processed: cleanedItem, filterResults };

            // Server-resolved identity (the matcher set item_id / matchStatus / chips)
            const matchedItem = cleanedItem.item_id
              ? { item_id: cleanedItem.item_id, name: cleanedItem.item_name, category_name: cleanedItem.category || null, tax_slab: cleanedItem.matched_tax_slab }
              : null;
            const smmfSuggestions = cleanedItem.smmf_suggestions || it.smmf_suggestions || [];
            const shouldUseMatch = !!matchedItem;

            let displayName = cleanedItem.item_name;

            // **FRONTEND TEXT CLEANING**: If no good match, clean the OCR text ourselves
            if (!shouldUseMatch && displayName) {
              // Remove common patterns: brand names, quantity+unit, box/pkt counts
              displayName = cleanUnmatchedName(displayName);
            }

            // **FRONTEND UNIT EXTRACTION**: Extract unit from item name if backend didn't provide one
            // CRITICAL: OCR/backend unit is ALWAYS the final unit — never override with DB default unit
            // Priority: OCR backend unit > raw OCR unit > semantic filter unit (DB fallback)
            let extractedUnit = cleanedItem.unit || it.unit || fd.unit || fd.unit_symbol || '';
            let cleanedItemName = displayName || '';

            if (!extractedUnit && cleanedItemName) {
              // Extract common unit patterns from item name: -KG, -LTR, -GM, /KG, etc.
              const unitPattern = /[-\/\s](KG|KILOGRAM|LITRE|LITER|LTR|L|GRAM|GM|G|ML|MILLILITER|PC|PCS|PIECE|BOX|BAG|PKT|PACKET)S?$/i;
              const match = cleanedItemName.match(unitPattern);

              if (match) {
                extractedUnit = match[1].toUpperCase();
                // Remove unit from item name
                cleanedItemName = cleanedItemName.replace(unitPattern, '').trim();
              }
            }

            // **CRITICAL FIX: Apply category-based unit correction for Grains**
            const itemCategory = (cleanedItem.category || it.category || '').toLowerCase();
            const itemNameLower = cleanedItemName.toLowerCase();
            const unitLower = (extractedUnit || '').toLowerCase();

            const requiresWeightUnit = itemCategory.includes('grain') ||
              itemCategory.includes('rice') ||
              itemCategory.includes('flour') ||
              itemCategory.includes('cereal') ||
              itemCategory.includes('pulse') ||
              itemCategory.includes('dal') ||
              itemNameLower.includes('atta') ||
              itemNameLower.includes('maida') ||
              itemNameLower.includes('rawa') ||
              itemNameLower.includes('rava') ||
              itemNameLower.includes('sooji') ||
              itemCategory.includes('spice');

            if (requiresWeightUnit && (!unitLower || unitLower === 'piece' || unitLower === 'pc' || unitLower === 'pcs')) {
              extractedUnit = 'kg';
            }

            // **MATCH STATUS UPGRADE LOGIC**:
            // - Backend 'exact' (95%+) → GREEN
            // - Backend 'fuzzy' (85-94%) → YELLOW
            // - Has suggestions with text similarity → YELLOW
            // - Has suggestions but NO text similarity → RED (unrelated suggestions)
            let upgradedMatchStatus = cleanedItem.matchStatus || fd.match_status || 'pending';

            // Check if input text has meaningful overlap with suggestions
            if ((upgradedMatchStatus === 'pending' || upgradedMatchStatus === 'none') && smmfSuggestions.length > 0) {
              const inputWords = cleanedItemName.toLowerCase().split(/\s+/).filter(w => w.length >= 3);

              // Check if any suggestion contains a word from input or vice versa
              const hasSimilarity = smmfSuggestions.some(sugg => {
                const suggWords = sugg.name.toLowerCase().split(/\s+/);
                return inputWords.some(inputWord =>
                  suggWords.some(suggWord =>
                    inputWord.includes(suggWord) || suggWord.includes(inputWord)
                  )
                );
              });

              if (hasSimilarity) {
                upgradedMatchStatus = 'fuzzy';
              }
            }

            // If fuzzy, check if cleaned name matches any suggestion exactly → upgrade to exact (GREEN)
            if (upgradedMatchStatus === 'fuzzy' && smmfSuggestions.length > 0) {
              const normalizeForMatch = (str) => str.toLowerCase().replace(/[^a-z0-9]/g, '');
              const cleanedNormalized = normalizeForMatch(cleanedItemName);

              const exactMatch = smmfSuggestions.find(s =>
                normalizeForMatch(s.name) === cleanedNormalized
              );

              if (exactMatch) {
                upgradedMatchStatus = 'exact';
              }
            }
            // Bill Memory On: the ladder's colour stands — no upgrade here (a twin's exact name stays yellow).
            if (cleanedItem.bm_colour && statusFromColour(cleanedItem.bm_colour)) {
              upgradedMatchStatus = statusFromColour(cleanedItem.bm_colour);
            }

            // **TRUST BACKEND PRICES - Use values calculated by backend priceCalculator**
            // Backend has already correctly mapped OCR fields and calculated prices
            // Frontend should ONLY display these values, not recalculate them

            const finalQuantity = parseFloat(
              fd.quantity ?? it.quantity ?? cleanedItem.quantity ?? 0
            );

            // Get prices from backend's calculation (these are already correct)
            const backendTotalPrice = parseFloat(cleanedItem.total_price || fd.total_price || finalTotalPrice || 0);
            const backendUnitPrice = parseFloat(cleanedItem.unit_price || fd.unit_price || 0);

            // Use backend values directly - they're already correct!
            const finalItemTotalPrice = backendTotalPrice;
            const finalItemUnitPrice = backendUnitPrice > 0 ? backendUnitPrice.toFixed(4) : '';

            // **AUTO-FILL CATEGORY AND UNIT FOR EXACT MATCHES (GREEN)**
            let finalCategory = matchedItem?.category_name || it.category || '';
            let finalUnit = extractedUnit || '';

            const matchedCategoryId =
              cleanedItem.matched_category_id ??
              it.matched_category_id ??
              fd.matched_category_id ??
              null;

            if (!finalCategory && matchedCategoryId && categoryIdToName.size > 0) {
              const categoryName = categoryIdToName.get(Number(matchedCategoryId));
              if (categoryName) {
                finalCategory = categoryName;
              }
            }


            // **CANONICAL: normalize to a Strict-5 SYMBOL (kg,g,L,ml,pcs) — matches the dropdown option values.**
            // Never convert to a full name here; that produced values ("Kilogram") that didn't match the
            // dropdown options ("kg"/"kilogram") and rendered blank after OCR.
            if (finalUnit) {
              finalUnit = UnitMappingService.normalizeUnitSymbol(finalUnit);
            }

            // Check if backend already resolved the unit via embedded unit extraction from OCR
            const isUnitAutoResolved = cleanedItem.unit_auto_resolved || it.unit_auto_resolved || false;

            if (isUnitAutoResolved) {
              // Backend already extracted the correct unit from raw OCR text (e.g., "1ltr" → "L")
              // ALWAYS trust the backend OCR unit — explicitly restore it in case anything overrode it
              const ocrUnit = cleanedItem.unit || it.unit || extractedUnit;
              finalUnit = UnitMappingService.normalizeUnitSymbol(ocrUnit);

              // Still use inventory category if available
              if (upgradedMatchStatus === 'exact' && matchedItem && matchedItem.category_name) {
                finalCategory = matchedItem.category_name;
              }
            } else if (upgradedMatchStatus === 'exact' && matchedItem) {
              // For exact matches, use inventory category but KEEP OCR unit
              // User requirement: OCR unit is always final, never override with DB default unit
              if (matchedItem.category_name) {
                finalCategory = matchedItem.category_name;
              }

            }

            if (!finalCategory) {
              finalCategory = 'Uncategorized';
            }

            // **NEW: If no unit detected, use category-based default**
            if (!finalUnit && finalCategory) {
              finalUnit = getDefaultUnitForCategory(finalCategory);
            }

            // **NEW: Auto-fill tax_slab from matched inventory item**
            let finalTaxSlab = cleanedItem.tax_slab; // Priority 1: OCR extracted tax slab
            if (!finalTaxSlab && matchedItem && matchedItem.tax_slab !== undefined && matchedItem.tax_slab !== null) {
              finalTaxSlab = String(parseFloat(matchedItem.tax_slab) || 0); // Priority 2: Inventory tax slab
            }

            const { cgst: ocrCgst, sgst: ocrSgst } = calculateSplitTaxFromSlab(finalItemTotalPrice || 0, finalTaxSlab || '0');

            const processed = {
              ...emptyItem(),
              ...cleanedItem,
              item_name: cleanedItemName || '',
              category: finalCategory,
              unit: finalUnit,
              tax_slab: finalTaxSlab || '0',
              cgst: cleanedItem.cgst || ocrCgst,
              sgst: cleanedItem.sgst || ocrSgst,
              quantity: finalQuantity || '',
              total_price: finalItemTotalPrice || '', // Backend-calculated line total
              unit_price: finalItemUnitPrice, // Backend-calculated per-unit price
              // Keep batch_number from emptyItem() - don't overwrite
              time: new Date().toTimeString().slice(0, 5),
              matchStatus: upgradedMatchStatus,
              filterStatus: 'server_resolved', // matching + validation happened server-side
              correction_applied: fd.correction_applied,
              correction_details: fd.correction_details,
              smmf_confidence: cleanedItem.smmf_confidence, // server matcher confidence (when provided)
              smmf_suggestions: smmfSuggestions, // suggestion chips from the server matcher
              smmf_match_type: cleanedItem.matched_via, // exact / token_set / vendor_memory, etc.
              original_ocr_name: cleanedItem.item_name, // Store original for reference
              // DSF (Decimal Sanity Filter) correction data
              dsf_correction_message: fd.dsf_correction_message,
              dsf_correction_type: fd.dsf_correction_type,
              original_quantity: fd.original_quantity,
              // Embedded unit auto-resolution flag from backend
              unit_auto_resolved: isUnitAutoResolved,
              // Explicit "unknown unit" state — surfaced in the UI and blocks submit until resolved.
              unit_needs_review: !finalUnit
            };


            // **SUF (Step 3): Check for unknown unit that needs training**
            if (filterResults.suf && filterResults.suf.status === 'unknown' && filterResults.suf.needsTraining) {

              // Trigger Unit Training Modal
              setUnitTrainingData({
                itemIndex: idx,
                itemName: displayName || cleanedItem.item_name,
                unknownUnit: filterResults.suf.unknownUnit,
                quantity: filterResults.suf.quantity || cleanedItem.quantity || 0,
                standardUnit: 'kg', // Default
                conversionFactor: 1
              });
              setShowUnitTrainingModal(true);

              // Mark this item as needing unit training
              processed.needsUnitTraining = true;
              processed.unknownUnit = filterResults.suf.unknownUnit;
            }

            processedWithFilters.push(processed);
          }
        } catch (error) {
          console.warn('⚠️ Filter processing error for item:', cleanedItem.item_name, error.message);
          // Fallback to basic matching
          const { status, inventoryItem } = matchAgainstInventory(cleanedItem.item_name || '');
          processedWithFilters.push({
            ...emptyItem(),
            ...cleanedItem,
            // Keep batch_number from emptyItem()
            time: new Date().toTimeString().slice(0, 5),
            matchStatus: status
          });
        }
      }

      setFilterResults(filterResultsMap);
      bmDeletedLinesRef.current = []; // a new scan starts a new list of deleted lines
      bmAddedBackRef.current = [];
      setBmSwitches(ocrData && ocrData.bill_memory ? { mode: ocrData.bill_memory.mode, screen: ocrData.bill_memory.screen } : null);
      setBmLeftOut((scanBillMemory(ocrData && ocrData.bill_memory) || { left_out: [] }).left_out);
      const prepared = processedWithFilters.map((it, idx) => {
        const processed = { ...it };

        // **NEW: Handle multilingual OCR data**
        // Store both original (Telugu/Hindi) and translated (English) names
        processed.original_ocr_name = it.original_name || it.item_name || '';
        processed.bm_input_kind = rowInputKind(it); // Fix 13 row 6: the kind the scanner set (excel / text), else scanned
        processed.original_language = it.original_language || 'en';
        processed.translated_name = it.translatedName || '';

        // Show original text in frontend, store translation for DB
        if (it.original_language && it.original_language !== 'en') {
          // Show original language text to user (yellow color)
          processed.item_name = it.original_name || it.item_name || '';
          processed.display_name = it.original_name || it.item_name || '';
          processed.db_name = it.translatedName || it.item_name || ''; // For DB storage
          processed.is_translated = true;
        }
        else {
          processed.item_name = it.item_name || '';
          processed.display_name = it.item_name || '';
          processed.db_name = it.item_name || '';
          processed.is_translated = false;
        }

        // Try auto-conversion using supplier conversions when OCR detected a container unit
        const itemKey = normalize(it.item_name);
        const ocrUnit = singularize(it.unit);
        const key = `${itemKey}|${ocrUnit}`;
        let conv = convMap.get(key) || convMap.get(`${itemKey}|${normalize(it.unit)}`);
        // Fallback: if no container match but exactly one conversion exists for this item, use it
        if (!conv) {
          const list = convByItem.get(itemKey) || [];
          if (list.length === 1) conv = list[0];
        }
        // Fuzzy fallback: try includes matching on item names across conversions
        if (!conv && conversions && conversions.length) {
          const candidates = conversions.map(c => {
            const nm = normalize(extractConversionItemName(c));
            return {
              nm,
              container: normalize(c.containerType || c.from_unit_symbol),
              factor: parseFloat(c.quantity || c.conversion_factor) || 0,
              toSymbol: normalize(c.unit || c.to_unit_symbol)
            };
          }).filter(x => x.nm);

          const byName = candidates.filter(x => x.nm.includes(itemKey) || itemKey.includes(x.nm));
          // Prefer container match among byName
          let chosen = byName.find(x => x.container === ocrUnit) || byName[0];
          if (chosen) {
            conv = { factor: chosen.factor, toSymbol: chosen.toSymbol };
          }
        }

        // DISABLED: Don't auto-apply supplier conversions during OCR
        // This was causing incorrect quantities (e.g., 25g becoming 375g)
        // User should manually select container units if needed
        /*
        if (conv && processed.quantity) {
          const factor = conv.factor > 0 ? conv.factor : 0;
          if (factor > 0) {
            processed.quantity = parseFloat(processed.quantity) * factor;
            processed.unit = symbolToName(conv.toSymbol);
          }
        }
        */

        // Normalize quantity display (strip trailing .00 by rounding when whole)
        if (processed.quantity !== undefined && processed.quantity !== null && processed.quantity !== '') {
          const qNum = parseFloat(processed.quantity) || 0;
          processed.quantity = Math.abs(qNum - Math.round(qNum)) < 1e-9 ? Math.round(qNum) : qNum;
        }

        return processed;
      });

      // **Use backend SMMF match status if available, otherwise run frontend matching**
      const withMatchStatus = prepared.map((item, idx) => {
        if (item.item_name && item.item_name.length > 2) {
          const batchNumber = generateBatchNumber(item.item_name);

          // Check if backend already provided match_status from SMMF
          if (item.matchStatus && item.matchStatus !== 'pending') {
            // DON'T set suggestions here - let auto-init effect handle it after items are finalized
            // (indices may change due to duplicate detection)

            return { ...item, batch_number: batchNumber };
          }

          // Fallback to frontend matching if backend didn't provide status
          const { status, suggestions } = matchAgainstInventory(item.item_name, 5);
          return { ...item, matchStatus: status, batch_number: batchNumber };
        }
        return item;
      });

      // Call duplicate detection (don't await inside useEffect, but the function will handle state updates)
      detectAndHandleDuplicates(withMatchStatus);
    })();
  }, [ocrData, inventoryItems, inventoryLoading, matchAgainstInventory, detectAndHandleDuplicates]); // Added detectAndHandleDuplicates to dependencies

  // **Auto-dismiss OCR processing overlay when all filters complete**
  useEffect(() => {
    if (ocrFormProcessing && !semanticProcessing && !inventoryLoading) {
      // Small delay to ensure items state has updated from detectAndHandleDuplicates
      const timer = setTimeout(() => {
        setOcrFormProcessing(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [ocrFormProcessing, semanticProcessing, inventoryLoading]);

  // **Handle duplicate modal actions**
  const handleDuplicateModalRemove = () => {

    // Build a set of indices to remove from pendingOcrItems
    const indicesToRemove = new Set();

    duplicateItems.forEach((dup, dupIdx) => {
      if (selectedDuplicates.has(dupIdx)) {
        const originalIdx = dup.originalIndex;
        if (originalIdx !== undefined) {
          indicesToRemove.add(originalIdx);
        } else {
          console.warn(`    ⚠️ No originalIndex for duplicate "${dup.item_name}"`);
        }
      }
    });

    // Keep items that are NOT in the remove indices
    const itemsToAdd = pendingOcrItems.filter((item, idx) => {
      const shouldKeep = !indicesToRemove.has(idx);
      return shouldKeep;
    });

    // Helper function to check if a unit is a standard/known unit
    const isStandardUnit = (unitStr) => {
      const unit = String(unitStr || '').toLowerCase().trim();
      if (!unit) return false;

      // Standard weight units
      if (/^(kg|kgs|kilogram|kilograms|g|gm|gms|gram|grams|mg|mgs|milligram|milligrams|lb|lbs|pound|pounds|oz|ounce|ounces)$/i.test(unit)) return true;

      // Standard volume units
      if (/^(l|lt|ltr|ltrs|liter|litre|liters|litres|ml|milliliter|millilitre|milliliters|millilitres|gal|gallon|gallons|qt|quart|quarts|pt|pint|pints|cup|cups|fl|fluid)$/i.test(unit)) return true;

      // Standard count units
      if (/^(piece|pieces|pcs|pc|dozen|doz|unit|units|nos|no|number|numbers)$/i.test(unit)) return true;

      return false;
    };

    // Check for unknown units in items to add
    const knownUnits = [
      // Weight units
      'kg', 'kilogram', 'kilograms', 'kgs',
      'g', 'gram', 'grams', 'gm', 'gms',
      'mg', 'milligram', 'milligrams', 'mgs',
      'lb', 'lbs', 'pound', 'pounds',
      'oz', 'ounce', 'ounces',
      // Volume units
      'l', 'liter', 'litre', 'liters', 'litres',
      'ml', 'milliliter', 'millilitre', 'milliliters', 'millilitres',
      'gal', 'gallon', 'gallons',
      'qt', 'quart', 'quarts',
      'pt', 'pint', 'pints',
      'cup', 'cups',
      'tbsp', 'tablespoon', 'tablespoons',
      'tsp', 'teaspoon', 'teaspoons',
      // Count units
      'piece', 'pieces', 'pcs', 'pc',
      'dozen', 'doz',
      'unit', 'units'
    ];

    const unknownUnitsList = [];
    for (let i = 0; i < itemsToAdd.length; i++) {
      const item = itemsToAdd[i];
      const unitRaw = String(item.unit || '').toLowerCase().trim();
      if (!unitRaw) continue;

      // Skip items where backend already auto-resolved the unit
      if (item.unit_auto_resolved) {
        continue;
      }


      // Use robust standard unit check instead of array includes
      const isKnownUnit = isStandardUnit(unitRaw);


      if (!isKnownUnit) {
        const itemName = String(item.item_name || '').trim();
        const normalizedUnit = normalizeUnitForKey(unitRaw);
        const conversionKey = `${normalizeItemKey(item)}_${normalizedUnit}`;
        const savedConversion = containerConversions[conversionKey];
        if (savedConversion) {
          console.log(`✅ [Container Unit] Saved conversion found for ${itemName} (${unitRaw})`);
        } else {
          unknownUnitsList.push({
            itemIndex: i,
            itemName: itemName,
            unit: unitRaw,
            quantity: String(item.quantity || '1')
          });
        }
      }
    }

    if (unknownUnitsList.length > 0) {
      setUnknownUnitsQueue(unknownUnitsList);
      setCurrentUnknownUnitIndex(0);
      const nonEmptyForQueue = items.filter(item => item.item_name && item.item_name.trim());
      setPendingOcrItems([...nonEmptyForQueue, ...itemsToAdd]);

      const first = unknownUnitsList[0];
      setContainerConversionForm({
        itemIndex: first.itemIndex,
        itemName: first.itemName,
        containerUnit: first.unit,
        quantity: '1',
        standardUnit: 'kg'
      });
      setShowContainerConversionModal(true);
      setShowDuplicateModal(false);
      setDuplicateItems([]);
      setSelectedDuplicates(new Set());
      return; // Don't add items yet
    }

    // No unknown units - add items (filter out empty placeholder cards)
    const nonEmptyExisting = items.filter(item => item.item_name && item.item_name.trim());
    applyItems([...nonEmptyExisting, ...itemsToAdd], { snapshot: true });
    setShowDuplicateModal(false);
    setPendingOcrItems([]);
    setDuplicateItems([]);
    setSelectedDuplicates(new Set());
    setSemanticProcessing(false);
  };

  const handleDuplicateModalKeep = () => {
    // Keep all items including duplicates

    // Helper function to check if a unit is a standard/known unit
    const isStandardUnit = (unitStr) => {
      const unit = String(unitStr || '').toLowerCase().trim();
      if (!unit) return false;

      // Standard weight units
      if (/^(kg|kgs|kilogram|kilograms|g|gm|gms|gram|grams|mg|mgs|milligram|milligrams|lb|lbs|pound|pounds|oz|ounce|ounces)$/i.test(unit)) return true;

      // Standard volume units
      if (/^(l|lt|ltr|ltrs|liter|litre|liters|litres|ml|milliliter|millilitre|milliliters|millilitres|gal|gallon|gallons|qt|quart|quarts|pt|pint|pints|cup|cups|fl|fluid)$/i.test(unit)) return true;

      // Standard count units
      if (/^(piece|pieces|pcs|pc|dozen|doz|unit|units|nos|no|number|numbers)$/i.test(unit)) return true;

      return false;
    };

    // Check for unknown units before adding
    const knownUnits = [
      // Weight units
      'kg', 'kilogram', 'kilograms', 'kgs',
      'g', 'gram', 'grams', 'gm', 'gms',
      'mg', 'milligram', 'milligrams', 'mgs',
      'lb', 'lbs', 'pound', 'pounds',
      'oz', 'ounce', 'ounces',
      // Volume units
      'l', 'liter', 'litre', 'liters', 'litres',
      'ml', 'milliliter', 'millilitre', 'milliliters', 'millilitres',
      'gal', 'gallon', 'gallons',
      'qt', 'quart', 'quarts',
      'pt', 'pint', 'pints',
      'cup', 'cups',
      'tbsp', 'tablespoon', 'tablespoons',
      'tsp', 'teaspoon', 'teaspoons',
      // Count units
      'piece', 'pieces', 'pcs', 'pc',
      'dozen', 'doz',
      'unit', 'units'
    ];

    const unknownUnitsList = [];
    for (let i = 0; i < pendingOcrItems.length; i++) {
      const item = pendingOcrItems[i];
      const unitRaw = String(item.unit || '').toLowerCase().trim();
      if (!unitRaw) continue;

      // Skip items where backend already auto-resolved the unit
      if (item.unit_auto_resolved) {
        continue;
      }


      // Use robust standard unit check instead of array includes
      const isKnownUnit = isStandardUnit(unitRaw);


      if (!isKnownUnit) {
        const itemName = String(item.item_name || '').trim();
        const normalizedUnit = normalizeUnitForKey(unitRaw);
        const conversionKey = `${normalizeItemKey(item)}_${normalizedUnit}`;
        const savedConversion = containerConversions[conversionKey];
        if (savedConversion) {
          console.log(`✅ [Container Unit] Saved conversion found for ${itemName} (${unitRaw})`);
        } else {
          unknownUnitsList.push({
            itemIndex: i,
            itemName: itemName,
            unit: unitRaw,
            quantity: String(item.quantity || '1')
          });
        }
      }
    }

    if (unknownUnitsList.length > 0) {
      setUnknownUnitsQueue(unknownUnitsList);
      setCurrentUnknownUnitIndex(0);
      const nonEmptyForQueue = items.filter(item => item.item_name && item.item_name.trim());
      setPendingOcrItems([...nonEmptyForQueue, ...pendingOcrItems]);

      const first = unknownUnitsList[0];
      setContainerConversionForm({
        itemIndex: first.itemIndex,
        itemName: first.itemName,
        containerUnit: first.unit,
        quantity: '1',
        standardUnit: 'kg'
      });
      setShowContainerConversionModal(true);
      setShowDuplicateModal(false);
      setDuplicateItems([]);
      setSelectedDuplicates(new Set());
      return; // Don't add items yet
    }

    // No unknown units - add items (filter out empty placeholder cards)
    const nonEmptyExisting = items.filter(item => item.item_name && item.item_name.trim());
    applyItems([...nonEmptyExisting, ...pendingOcrItems], { snapshot: true });
    setShowDuplicateModal(false);
    setPendingOcrItems([]);
    setDuplicateItems([]);
    setSelectedDuplicates(new Set());
    setSemanticProcessing(false);
    setHasOcrRunInSession(true);
  };

  const handleDuplicateModalRemoveSelected = () => {


    // Build a map of pendingOcrItems indices to remove
    // We need to track the ORIGINAL index in pendingOcrItems, not just the name
    const indicesToRemove = new Set();

    // For each selected duplicate, find its original index in pendingOcrItems
    duplicateItems.forEach((dup, dupIdx) => {
      if (selectedDuplicates.has(dupIdx)) {
        // Find this duplicate in pendingOcrItems
        const originalIdx = dup.originalIndex;
        if (originalIdx !== undefined) {
          indicesToRemove.add(originalIdx);
        } else {
          console.warn(`  ⚠️ Duplicate item "${dup.item_name}" has no originalIndex!`);
        }
      }
    });

    // Keep items from pendingOcrItems that are NOT in the remove indices
    const itemsToAdd = pendingOcrItems.filter((item, idx) => {
      const shouldKeep = !indicesToRemove.has(idx);
      return shouldKeep;
    });

    const nonEmptyExisting = items.filter(item => item.item_name && item.item_name.trim());
    applyItems([...nonEmptyExisting, ...itemsToAdd], { snapshot: true });
    setShowDuplicateModal(false);
    setPendingOcrItems([]);
    setDuplicateItems([]);
    setSelectedDuplicates(new Set());
    setSemanticProcessing(false);
    setHasOcrRunInSession(true); // Mark that OCR has run in this session
  };

  const toggleDuplicateSelection = (index) => {
    setSelectedDuplicates(prev => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const toggleSelectAllDuplicates = () => {
    if (selectedDuplicates.size === duplicateItems.length) {
      // Deselect all
      setSelectedDuplicates(new Set());
    } else {
      // Select all
      setSelectedDuplicates(new Set(duplicateItems.map((_, idx) => idx)));
    }
  };

  // **NEW: Helper to detect if unit is a container unit**
  const isContainerUnit = useCallback((unit) => {
    if (!unit) return false;
    const normalized = String(unit).toLowerCase().trim();
    const containerUnits = ['box', 'boxes', 'packet', 'pack', 'bag', 'dozen', 'carton', 'can', 'bottle', 'jar', 'tin', 'tins', 'crate', 'bundle', 'case', 'cases', 'cose', 'coses', 'pkt', 'pkts', 'bx', 'ctn', 'btl'];
    return containerUnits.includes(normalized);
  }, []);

  // **Helper: Normalize unit to singular form for consistent key building**
  const normalizeUnitForKey = useCallback((unit) => {
    if (!unit) return '';
    const u = String(unit).toLowerCase().trim();

    // Convert plural to singular for consistent keying
    const pluralToSingular = {
      'bags': 'bag', 'boxes': 'box', 'packets': 'packet', 'packs': 'pack',
      'bottles': 'bottle', 'cans': 'can', 'tins': 'tin', 'jars': 'jar',
      'cartons': 'carton', 'cases': 'case', 'crates': 'crate', 'bundles': 'bundle',
      'dozens': 'dozen', 'pkts': 'pkt', 'pieces': 'piece', 'pcs': 'piece',
      'units': 'unit', 'kgs': 'kg', 'gms': 'g', 'liters': 'liter', 'litres': 'liter',
      'milliliters': 'milliliter', 'millilitres': 'milliliter', 'kilograms': 'kilogram',
      'grams': 'gram', 'ltrs': 'ltr'
    };

    return pluralToSingular[u] || u;
  }, []);

  // **Helper: Normalize unit symbols to standard display**
  const normalizeUnitSymbol = useCallback((symbol) => {
    return UnitMappingService.normalizeUnitSymbol(symbol);
  }, []);

  // Classify unit into family: 'weight', 'volume', 'count', or 'unknown'
  const getUnitFamily = useCallback((unitStr) => {
    if (!unitStr) return 'unknown';
    const u = String(unitStr).toLowerCase().trim();
    const weightUnits = ['g', 'gram', 'gm', 'gms', 'kg', 'kilogram', 'kgs', 'mg', 'milligram', 'lb', 'pound', 'oz', 'ounce', 'tbsp', 'tablespoon', 'tsp', 'teaspoon'];
    const volumeUnits = ['ml', 'milliliter', 'millilitre', 'l', 'liter', 'litre', 'ltr', 'lt', 'cl', 'centiliter', 'fl', 'cup'];
    const countUnits = ['pc', 'pcs', 'piece', 'pieces', 'nos', 'no', 'unit', 'units', 'doz', 'dozen', 'box', 'boxes', 'pkt', 'packet', 'packets', 'bag', 'bags', 'can', 'cans', 'tin', 'tins', 'bottle', 'bottles', 'jar', 'jars', 'case', 'cases', 'carton', 'bundle', 'crate'];
    if (weightUnits.includes(u)) return 'weight';
    if (volumeUnits.includes(u)) return 'volume';
    if (countUnits.includes(u)) return 'count';
    return 'unknown';
  }, []);

  // **NEW: Get filtered units based on item's standard unit type**
  const getFilteredUnitsForItem = useCallback((itemName) => {
    if (!itemName) return units; // No item selected, show all units

    // Find the inventory item
    const matchedItem = inventoryItems.find(inv => {
      const invName = (inv.name || inv.item_name || '').toLowerCase().trim();
      return invName === itemName.toLowerCase().trim();
    });

    if (!matchedItem) return units; // Item not in inventory, show all units

    // Get the standard unit's family
    const standardUnit = matchedItem.unit_symbol || matchedItem.unit || matchedItem.standard_unit_symbol || matchedItem.standard_unit;
    const standardUnitFamily = getUnitFamily(standardUnit);

    if (standardUnitFamily === 'unknown') return units; // Unknown standard unit, show all

    // Filter units to only show compatible ones
    const filtered = units.filter(unit => {
      const unitFamily = getUnitFamily(unit);
      return unitFamily === standardUnitFamily || unitFamily === 'unknown';
    });

    console.log(`🔧 [UNIT FILTER] Item "${itemName}" has standard unit "${standardUnit}" (${standardUnitFamily})`);
    console.log(`   Available units: ${filtered.length}/${units.length} compatible units`);

    return filtered.length > 0 ? filtered : units; // Fallback to all if empty
  }, [units, inventoryItems, getUnitFamily]);

  // convertUnitSymbolToName is defined earlier in this component (before the useEffects that use it)

  const normalizeItemKey = useCallback((item) => {
    const raw = (item?.item_name || item?.display_name || item?.db_name || item?.original_ocr_name || '').toString();
    return raw.toLowerCase().replace(/\s+/g, ' ').trim();
  }, []);

  const extractConversionItemName = useCallback((conversion) => {
    const directName = (conversion?.item_name || conversion?.item || conversion?.name || '').toString().trim();
    if (directName) return directName;

    const description = String(conversion?.description || '').trim();
    if (!description) return '';

    const patterns = [
      /(?:Supplier|Kitchen)\s+conversion:\s*(.+?)\s*-\s*\d+(?:\.\d+)?\s+/i,
      /(?:Supplier|Kitchen)\s+conversion:\s*(.+?)\s*-/i,
      /^(.+?):\s*\d+(?:\.\d+)?\s+/i
    ];

    for (const pattern of patterns) {
      const match = description.match(pattern);
      if (match && match[1]) {
        return match[1].trim();
      }
    }

    return '';
  }, []);

  // const normalizeName = (val) => (val || '').toLowerCase();

  // Auto-dismiss lives here, not in InfoToast: that component is declared inside this one,
  // so it is a new component type on every render and any timer it owned would reset.
  useEffect(() => {
    if (!infoToast) return undefined;
    const timer = setTimeout(() => setInfoToast(null), 4000);
    return () => clearTimeout(timer);
  }, [infoToast]);


  // **NEW: Check for container units in items and show conversion modal**
  const checkForContainerUnits = useCallback((itemsToCheck) => {

    // Find first item with container unit that doesn't have a saved conversion
    for (let i = 0; i < itemsToCheck.length; i++) {
      const item = itemsToCheck[i];
      const unit = String(item.unit || '').toLowerCase().trim();

      // **SKIP items where backend already auto-resolved the unit from embedded OCR text**
      // e.g., "Amul Fresh Cream 1ltr" + "60 Pcs" → 60 L (already handled by backend)
      if (item.unit_auto_resolved) {
        continue;
      }

      if (isContainerUnit(unit)) {
        const itemName = String(item.item_name || '').trim();
        const normalizedUnit = normalizeUnitForKey(unit);
        const conversionKey = `${normalizeItemKey(item)}_${normalizedUnit}`;

        // Check if we already have a saved conversion for this item+unit combo
        if (!containerConversions[conversionKey]) {

          // Show conversion modal for this item
          setContainerConversionForm({
            itemIndex: i,
            itemName: itemName,
            containerUnit: unit,
            quantity: String(item.quantity || '1'),
            standardUnit: 'kg'
          });
          setShowContainerConversionModal(true);
          return; // Show modal for first unresolved container unit
        }
      }
    }

  }, [isContainerUnit, containerConversions]);

  /**
   * H6 — the derived price fields, recomputed exactly as typing into the row would.
   *
   * The canonical triple is quantity / unit_price / total_price and TOTAL WINS: the total
   * on the bill is what was actually paid, so a corrected COUNT changes the price per
   * unit, never the total. handleInputChange has always done this; the piece paths did
   * not, which is how a corrected row could go on displaying the pre-correction per-piece
   * price to the very person being asked to confirm it.
   *
   * One implementation, used by both. Returns a NEW row object; it mutates nothing.
   */
  /**
   * One door for "this row is now this inventory item" (an exact match): its category (the not-in-inventory
   * badge comes down), its tax split, its base unit and — unless a container unit or a supplier conversion
   * was set — its unit. The typed-row match and Bill Memory's vendor-tap re-run (Fix 13) both use it.
   * Returns a NEW row.
   */
  const takeExactItem = (row, inventoryItem) => {
    // **ALWAYS update category, unit, and tax_slab from inventory on exact match**
    // This ensures inventory data takes precedence
    // H8 — and the "not in inventory" badge comes down with it: the row demonstrably
    // IS in inventory now. This is the likeliest way the stale badge was reached —
    // a name that matched nothing while the inventory list was still loading gets
    // defaulted to non-consumable, then re-matches the moment the list arrives.
    const out = { ...withMatchedCategory(row, inventoryItem.category) };

    if (inventoryItem.tax_slab !== undefined && inventoryItem.tax_slab !== null) {
      const newTaxSlab = parseFloat(inventoryItem.tax_slab) || 0;
      out.tax_slab = String(newTaxSlab);

      if (taxForStockInEnabled) {
        const baseTotal = parseFloat(out.total_price) || 0;
        const { cgst: nextCgst, sgst: nextSgst } = calculateSplitTaxFromSlab(baseTotal, newTaxSlab);
        out.cgst = nextCgst;
        out.sgst = nextSgst;
        if (out.total_price) {
          out.total_price_with_tax = calculateTotalWithTax(baseTotal, newTaxSlab);
        }
      }
    }

    // Store the base unit from inventory for later conversion checks
    const unitSymbol = inventoryItem.unit_symbol || inventoryItem.unit;
    if (unitSymbol) {
      out.inventoryBaseUnit = normalizeUnitSymbol(unitSymbol);
      out.inventoryBaseUnitDisplay = convertUnitSymbolToName(unitSymbol);
    }

    // Take the unit from inventory unless the user picked a container unit or set a
    // supplier conversion; a same-family bill unit stays (see unitAfterMatch).
    const hasContainerUnit = isContainerUnit(out.unit);
    const hasSupplierConversion = !!out.supplier_conversion_quantity;
    if (unitSymbol && !hasContainerUnit && !hasSupplierConversion) {
      out.unit = unitAfterMatch(out.unit || cxLastUnit(cxContextRef.current, inventoryItem), unitSymbol);
    }
    return out;
  };
  bmTakeItemRef.current = takeExactItem;

  const recomputeDerivedPrice = (row) => {
    if (!row) return row;
    const quantity = parseFloat(row.quantity);
    const totalPrice = parseFloat(row.total_price);
    const next = { ...row };
    next.unit_price = quantity > 0 && totalPrice > 0 ? (totalPrice / quantity).toFixed(4) : '';
    if (row.tax_slab != null && row.total_price !== '' && row.total_price != null) {
      // Keep the tax-inclusive mirror in step, the same way handleInputChange does.
      try {
        next.total_price_with_tax = calculateTotalWithTax(totalPrice, row.tax_slab);
      } catch (e) { /* the mirror is a convenience; never let it break a correction */ }
    }
    return next;
  };

  const handleInputChange = (index, field, value, { boundary = false } = {}) => {
    // M6 — the write base is itemsRef.current, NOT the render closure.
    //
    // applyItems/commitItemEdit were converted to the ref; this function, which PRODUCES the
    // arrays they commit, was not. handleInputBlur reformats a value immediately after a
    // keystroke — two writes in one React batch — and the second was built over a pre-
    // keystroke snapshot, clobbering the character the user had just typed.
    const copy = [...itemsRef.current];
    const uid = copy[index]?.uid;
    // H5 — what this edit touched, so consecutive keystrokes in the same box collapse into
    // ONE undo step. `boundary` marks a blur-reformat so it never absorbs the next edit.
    const editMeta = { rowId: uid, field, boundary };

    copy[index] = { ...copy[index], [field]: value };

    // The user has an opinion about this row's category — the red-row default must never
    // overwrite it, now or on any later render. Choosing a consumable category also clears
    // the "set as Non-Consumable" badge, because it is no longer true.
    if (field === 'category') {
      copy[index].categoryUserSet = true;
      copy[index].autoNonConsumable = false;
    }

    // Auto-populate tax_slab when item is selected
    if (field === 'item_name' && value) {
      const matchedInv = inventoryItems.find(inv =>
        (inv.item_name || inv.name || '').toLowerCase() === value.toLowerCase()
      );
      if (matchedInv && matchedInv.tax_slab !== undefined) {
        const newTaxSlab = parseFloat(matchedInv.tax_slab) || 0;
        copy[index].tax_slab = String(newTaxSlab);

        if (taxForStockInEnabled) {
          const baseTotal = parseFloat(copy[index].total_price) || 0;
          const { cgst: nextCgst, sgst: nextSgst } = calculateSplitTaxFromSlab(baseTotal, newTaxSlab);
          copy[index].cgst = nextCgst;
          copy[index].sgst = nextSgst;
          if (copy[index].total_price) {
            copy[index].total_price_with_tax = calculateTotalWithTax(baseTotal, newTaxSlab);
          }
        }
      }
    }

    if (taxForStockInEnabled && field === 'total_price_with_tax') {
      const baseTotal = calculateBasePriceFromTaxTotal(value, copy[index].tax_slab);
      copy[index].total_price = baseTotal;

      const quantity = parseFloat(copy[index].quantity);
      const totalPrice = parseFloat(baseTotal);
      copy[index].unit_price = quantity > 0 && totalPrice > 0
        ? (totalPrice / quantity).toFixed(4)
        : '';

      const { cgst: nextCgst, sgst: nextSgst } = calculateSplitTaxFromSlab(baseTotal, copy[index].tax_slab);
      copy[index].cgst = nextCgst;
      copy[index].sgst = nextSgst;
    }

    // **NEW: Auto-calculate unit_price when quantity or total_price changes**
    // H6 — the arithmetic itself lives in recomputeDerivedPrice() so the piece-correction
    // paths can run the IDENTICAL calculation. They used to change the quantity and leave
    // unit_price alone, so after a 1→4 correction the row still displayed "₹27.50/pc" and
    // the confirm modal and mandatory-field checks all operated on that stale number.
    if (field === 'quantity' || field === 'total_price') {
      const quantity = parseFloat(field === 'quantity' ? value : copy[index].quantity);
      const totalPrice = parseFloat(field === 'total_price' ? value : copy[index].total_price);

      if (quantity > 0 && totalPrice > 0) {
        // Calculate unit price = total price ÷ quantity
        const unitPrice = totalPrice / quantity;
        copy[index].unit_price = unitPrice.toFixed(4);
      } else {
        copy[index].unit_price = '';
      }
    }

    if (field === 'tax_slab' || (taxForStockInEnabled && field === 'total_price')) {
      const baseTotal = field === 'total_price' ? value : copy[index].total_price;
      const { cgst: nextCgst, sgst: nextSgst } = calculateSplitTaxFromSlab(
        baseTotal,
        field === 'tax_slab' ? value : copy[index].tax_slab
      );
      copy[index].cgst = nextCgst;
      copy[index].sgst = nextSgst;
      copy[index].total_price_with_tax = calculateTotalWithTax(
        baseTotal,
        field === 'tax_slab' ? value : copy[index].tax_slab
      );
    }

    if (field === 'item_name') {
      const raw = value || '';
      // Bill Memory: remember exactly what was typed (a scanned row keeps its printed name on the server)
      Object.assign(copy[index], nameTypedPatch(copy[index], raw));

      // **Update batch number based on new item name**
      if (raw.length >= 3) {
        const newBatchNumber = generateBatchNumber(raw);
        copy[index].batch_number = newBatchNumber;
      }

      // ✅ If the user had confirmed this item via a chip but is now typing a
      // different name, drop the confirmation so the color reflects the new
      // (unmatched) name instead of staying green. Operate on `copy` directly
      // because the setItems in handleNameChange runs on a stale snapshot.
      const prevName = itemsRef.current[index]?.item_name || '';
      if (copy[index].userConfirmed && raw.trim().toLowerCase() !== prevName.trim().toLowerCase()) {
        copy[index].userConfirmed = false;
        copy[index].createAsNew = false;
      }

      // A changed name invalidates any resolved inventory id — a stale item_id
      // must never be submitted under a different name.
      if (raw.trim().toLowerCase() !== prevName.trim().toLowerCase()) {
        copy[index].item_id = null;
        copy[index].is_existing_item = false;
      }

      // ✅ Skip automatic matching if user has confirmed this item by clicking a chip
      if (copy[index].userConfirmed) {
        // Keep existing matchStatus = 'exact', don't override
        // User has explicitly selected this item, so keep it green
        copy[index].matchStatus = 'exact';
      } else {
        // **UPDATED: Use inventory-based matching**
        let matchResult;
        // Bill Memory screen: the ladder answers a typed row (checks 2–6); today's matching is the fallback.
        const bmAnswer = bmScreenOnRef.current ? decideTypedRow({ ...copy[index], item_name: raw }, bmCtxRef.current) : null;
        if (bmAnswer) {
          matchResult = matchResultOf(bmAnswer, bmCtxRef.current);
          Object.assign(copy[index], {
            bm_colour: bmAnswer.colour, bm_reason: bmAnswer.reason, bm_notes: bmAnswer.notes,
            bm_preselect: bmAnswer.preselect, bm_source_check: bmAnswer.check_no,
            item_id: matchResult.itemId, is_existing_item: !!matchResult.itemId,
          });
        } else {
          try {
            matchResult = matchAgainstInventory(raw);
          } catch (error) {
            console.warn('❌ Match error for:', raw, error);
            matchResult = { status: 'none', inventoryItem: null, suggestions: [] };
          }
        }

        const { status, inventoryItem } = matchResult;
        copy[index].matchStatus = status;

        // REMOVED: No longer checking lockedNames - always allow enrichment
        copy[index].lastStableName = raw;
        if (status === 'exact' && inventoryItem) {
          copy[index] = takeExactItem(copy[index], inventoryItem);
        }
      } // End of userConfirmed check

      // Generate batch number for fuzzy (yellow) or no match (red) items
      // ✅ Batch numbers are now auto-generated by backend - no need to generate here
      // Backend will create unique sequential batch numbers for all items

      // Don't auto-generate batch number on every keystroke for exact matches - only when finalizing
    }

    // No paint here. Every path below either returns through commitItemEdit or falls to the
    // one at the tail, and that is what sets the state — a setItems here was a second render
    // per keystroke for a value the very next line overwrites, and the only direct write to
    // `items` left outside the door.

    // **Enhanced: Check for unit conversions when unit field changes**
    if (field === 'unit') {
      const selectedUnit = String(value || '').toLowerCase().trim();
      const itemName = copy[index].item_name || '';

      /**
       * F7 — LEAVING pcs DROPS THE FREE / SCHEME TAGS.
       *
       * `is_free_replacement`, `free_reason` and `free_quantity` are plain row fields and
       * the tagging UI only renders on pcs rows — but nothing removed them when the unit
       * changed. Tag a pcs row free, switch it to kg, and the row kept a tag it could no
       * longer display, exempting a ₹0 line from the client's price check on a unit where
       * free lines are not a supported concept at all.
       *
       * Cleared here rather than only validated against, because a hidden field that still
       * means something is how the next person is surprised. The gate refuses the same
       * combination independently — this makes it visible rather than merely refused.
       */
      if (!isPcsUnit(selectedUnit)) {
        const row = copy[index];
        if (row.is_free_replacement || row.is_scheme || row.free_reason || row.free_quantity) {
          copy[index] = {
            ...row,
            is_free_replacement: false,
            is_scheme: false,
            free_reason: '',
            free_quantity: '',
          };
        }
      }

      // Skip if no item name yet. M7: this return used to leave the unit change out of
      // history entirely — the third of three siblings, and the one that was missed. Undo
      // jumped straight past it.
      if (!itemName.trim()) {
        commitItemEdit(copy, editMeta);
        return;
      }

      // Get the base unit from inventory (if item was matched)
      const baseUnit = copy[index].inventoryBaseUnit;
      const normalizedBaseUnit = baseUnit ? String(baseUnit).toLowerCase().trim() : '';
      const normalizedSelectedUnit = normalizeUnitForKey(selectedUnit);

      // **Case 1: Selected unit matches the base unit from inventory**
      // No conversion needed - clear any existing conversion
      if (normalizedBaseUnit && normalizedSelectedUnit === normalizedBaseUnit) {
        console.log(`✅ [UNIT CHANGE] Selected unit "${selectedUnit}" matches base unit - no conversion needed`);
        copy[index].supplier_conversion_quantity = null;
        copy[index].supplier_conversion_unit = null;
        copy[index].supplier_conversion_unit_display = null;
        // Through the door: this return used to skip the pushHistory at the tail of the
        // function, so changing a unit was silently unrecordable and undo jumped past it.
        commitItemEdit(copy, editMeta);
        return;
      }

      // **Case 2: Check if the unit is a standard unit**
      // Standard units (kg, g, L, ml, piece, etc.) should NEVER require conversion
      const selectedFamily = getUnitFamily(selectedUnit);
      const baseFamily = normalizedBaseUnit ? getUnitFamily(normalizedBaseUnit) : null;
      const isStandardWeight = ['kg', 'kilogram', 'kilograms', 'kgs', 'g', 'gram', 'grams', 'gm', 'gms', 'mg', 'milligram', 'milligrams'].includes(normalizedSelectedUnit);
      const isStandardVolume = ['l', 'liter', 'litre', 'liters', 'litres', 'ltr', 'ltrs', 'ml', 'milliliter', 'millilitre', 'milliliters', 'millilitres'].includes(normalizedSelectedUnit);
      const isStandardCount = ['piece', 'pieces', 'pc', 'pcs', 'unit', 'units', 'dozen', 'doz', 'nos', 'no'].includes(normalizedSelectedUnit);
      const isStandardUnit = isStandardWeight || isStandardVolume || isStandardCount;

      // If it's a standard unit, NEVER ask for conversion
      if (isStandardUnit) {
        console.log(`✅ [UNIT CHANGE] "${selectedUnit}" is a standard unit - no conversion needed`);
        copy[index].supplier_conversion_quantity = null;
        copy[index].supplier_conversion_unit = null;
        copy[index].supplier_conversion_unit_display = null;
        // Through the door: this return used to skip the pushHistory at the tail of the
        // function, so changing a unit was silently unrecordable and undo jumped past it.
        commitItemEdit(copy, editMeta);
        return;
      }

      // **Case 3: Selected unit is different and not auto-convertible - check if conversion exists**
      const normalizedKey = normalizeItemKey(copy[index]);
      const conversionKey = `${normalizedKey}_${normalizedSelectedUnit}`;
      const savedConversion = containerConversions[conversionKey];

      if (savedConversion) {
        // **Auto-apply saved conversion without prompting**
        console.log(`✅ [UNIT CHANGE] Found saved conversion for "${conversionKey}" - auto-applying: 1 ${selectedUnit} = ${savedConversion.factor} ${savedConversion.toUnitSymbol}`);
        console.log(`   📊 Conversion details:`, {
          factor: savedConversion.factor,
          toUnitSymbol: savedConversion.toUnitSymbol,
          toUnitDisplay: savedConversion.toUnitDisplay,
          originalQuantity: copy[index].quantity,
          originalUnit: copy[index].unit
        });
        copy[index] = {
          ...copy[index],
          supplier_conversion_quantity: savedConversion.factor,
          supplier_conversion_unit: savedConversion.toUnitSymbol,
          supplier_conversion_unit_display: savedConversion.toUnitDisplay
        };
        console.log(`   ✅ Updated item:`, {
          supplier_conversion_quantity: copy[index].supplier_conversion_quantity,
          supplier_conversion_unit: copy[index].supplier_conversion_unit,
          supplier_conversion_unit_display: copy[index].supplier_conversion_unit_display
        });
        // Falls through to the commitItemEdit at the tail, which sets the state.
      } else {
        // **No saved conversion - prompt user to define it**
        console.log(`⚠️ [UNIT CHANGE] No conversion found for "${conversionKey}" - prompting user`);
        console.log(`   Available conversions:`, Object.keys(containerConversions).filter(k => k.startsWith(normalizedKey)));

        // Determine the target unit (prefer base unit from inventory, fallback to kg/L/pcs)
        let targetUnit = 'kg';
        if (normalizedBaseUnit) {
          targetUnit = normalizedBaseUnit;
        } else {
          // Fallback: infer from unit family
          const unitFamily = getUnitFamily(selectedUnit);
          if (unitFamily === 'volume') targetUnit = 'L';
          else if (unitFamily === 'count') targetUnit = 'pcs';
        }

        setTimeout(() => {
          setContainerConversionForm({
            itemIndex: index,
            itemName: itemName,
            containerUnit: selectedUnit,
            quantity: copy[index].quantity || '1',
            standardUnit: targetUnit
          });
          setShowContainerConversionModal(true);
        }, 300);
      }
    }

    // **OLD CODE REMOVED - replaced by enhanced logic above**

    // **NEW: Record this change in history for undo**
    commitItemEdit(copy, editMeta);
  };

  const handleInputBlur = (index, field, value) => {
    if (!value) return;
    const parsed = parseFloat(value);
    if (!isNaN(parsed)) {
      const precision = field === 'quantity' ? 4 : 2;
      const formatted = parseFloat(parsed.toFixed(precision)).toString();
      if (formatted !== value) {
        // H5: marked as a history BOUNDARY. A blur-reformat is the end of one edit, so it
        // must neither be swallowed by the typing before it nor swallow the edit after it.
        handleInputChange(index, field, formatted, { boundary: true });
      }
    }
  };

  // Suggestion fetching (debounced) for auto-correct
  const suggestionTimeout = useRef(null);
  // ⚡ AI validation debounce timer (prevents excessive API calls on typing)
  const aiValidationTimeout = useRef({});
  const [suggestionsMap, setSuggestionsMap] = useState({}); // index -> suggestions array
  const [activeSuggestionIndexMap, setActiveSuggestionIndexMap] = useState({}); // index -> focused visible suggestion index

  const getVisibleNameSuggestions = useCallback((suggestions = []) => {
    return (suggestions || []).filter(s => !s.isTypedOption);
  }, []);

  const focusNameSuggestion = useCallback((itemIndex, suggestionIndex) => {
    requestAnimationFrame(() => {
      const chip = document.querySelector(
        `.item-box[data-item-index="${itemIndex}"] .suggestion-chip[data-suggestion-index="${suggestionIndex}"]`
      );
      if (chip) chip.focus();
    });
  }, []);

  const focusItemNameInput = useCallback((itemIndex) => {
    requestAnimationFrame(() => {
      const input = document.querySelector(
        `.item-box[data-item-index="${itemIndex}"] .stockin-item-name-input`
      );
      if (input) input.focus();
    });
  }, []);

  const moveToQuantityInput = useCallback((target) => {
    const card = target?.closest?.('.item-box');
    if (card) {
      const q = card.querySelector('.stockin-quantity-input');
      if (q) q.focus();
    }
  }, []);

  const handleItemNameKeyDown = useCallback((idx, e) => {
    const visibleSuggestions = getVisibleNameSuggestions(suggestionsMap[idx] || []);

    if ((e.key === 'ArrowDown' || e.key === 'ArrowRight') && visibleSuggestions.length > 0) {
      e.preventDefault();
      setActiveSuggestionIndexMap(prev => ({ ...prev, [idx]: 0 }));
      focusNameSuggestion(idx, 0);
      return;
    }

    if (e.key === 'Enter') {
      e.preventDefault();
      const activeIndex = activeSuggestionIndexMap[idx];
      if (Number.isInteger(activeIndex) && visibleSuggestions[activeIndex]) {
        // applySuggestion already handles focusing quantity via requestAnimationFrame
        applySuggestion(idx, visibleSuggestions[activeIndex]);
      } else {
        // No active suggestion – check if there are suggestions visible; pick the first one.
        // Bill Memory screen: nothing clearly leads, so Enter waits for a tap (no coin-flip).
        const bmWaits = bmScreenOnRef.current && itemsRef.current[idx] && itemsRef.current[idx].bm_colour;
        if (visibleSuggestions.length > 0 && !bmWaits) {
          applySuggestion(idx, visibleSuggestions[0]);
        } else if (visibleSuggestions.length === 0) {
          moveToQuantityInput(e.target);
        }
      }
      return;
    }

    if (e.key === 'Escape') {
      setActiveSuggestionIndexMap(prev => ({ ...prev, [idx]: -1 }));
    }
  }, [activeSuggestionIndexMap, focusNameSuggestion, getVisibleNameSuggestions, moveToQuantityInput, suggestionsMap]);

  const handleSuggestionChipKeyDown = useCallback((idx, suggestionIndex, suggestion, e) => {
    const visibleSuggestions = getVisibleNameSuggestions(suggestionsMap[idx] || []);
    const lastIndex = visibleSuggestions.length - 1;

    if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
      e.preventDefault();
      const nextIndex = suggestionIndex >= lastIndex ? 0 : suggestionIndex + 1;
      setActiveSuggestionIndexMap(prev => ({ ...prev, [idx]: nextIndex }));
      focusNameSuggestion(idx, nextIndex);
      return;
    }

    if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
      e.preventDefault();
      if (suggestionIndex <= 0) {
        setActiveSuggestionIndexMap(prev => ({ ...prev, [idx]: -1 }));
        focusItemNameInput(idx);
      } else {
        const prevIndex = suggestionIndex - 1;
        setActiveSuggestionIndexMap(prev => ({ ...prev, [idx]: prevIndex }));
        focusNameSuggestion(idx, prevIndex);
      }
      return;
    }

    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      applySuggestion(idx, suggestion);
      return;
    }

    if (e.key === 'Escape') {
      e.preventDefault();
      setActiveSuggestionIndexMap(prev => ({ ...prev, [idx]: -1 }));
      focusItemNameInput(idx);
    }
  }, [focusItemNameInput, focusNameSuggestion, getVisibleNameSuggestions, suggestionsMap]);

  const fetchSuggestions = useCallback((index, name) => {
    // **UPDATED: Use inventory-based matching for suggestions + always include typed text**
    const trimmedName = (name || '').trim();
    const lowered = trimmedName.toLowerCase();

    if (!lowered || lowered.length === 0) {
      setSuggestionsMap(prev => ({ ...prev, [index]: [] }));
      return;
    }

    // Get inventory suggestions - matchAgainstInventory already does fuzzy matching, don't filter again
    const { suggestions } = matchAgainstInventory(lowered, 10);
    const filteredSuggestions = suggestions || []; // Use all suggestions from fuzzy matcher

    // Always add the typed text as the first option. This used to reach the row through
    // `setItems(cur => { ...side effects...; return cur; })` — a state update that changed
    // nothing, ran another setState inside a reducer, and is invoked TWICE under StrictMode.
    // itemsRef.current is the same value without any of that.
    const currentRow = itemsRef.current[index];
    const typedSuggestion = {
      name: trimmedName,
      category: currentRow?.category || '',
      unit: currentRow?.unit || '',
      isTypedOption: true
    };
    setSuggestionsMap(prev => ({ ...prev, [index]: [typedSuggestion, ...filteredSuggestions] }));
  }, [matchAgainstInventory]); // Removed items and inventoryItems dependencies

  // **NEW: Initialize suggestions for fuzzy/none match items from OCR**
  useEffect(() => {
    if (!items || items.length === 0) return;
    if (inventoryLoading) return;

    // Initialize suggestions for items that need user selection (fuzzy or none status)
    items.forEach((item, idx) => {
      // ✅ SKIP user-confirmed items entirely - they've been locked by user
      if (item.userConfirmed) {
        return;
      }

      // Check if suggestions already exist for this item (from backend SMMF or previous fetch)
      const existingSuggestions = suggestionsMap[idx];
      const alreadyHasSuggestions = existingSuggestions && existingSuggestions.length > 0;


      // Bill Memory screen: the ladder's own chips, in its order, the leader pre-selected only when
      // it clearly leads. A green row shows none until "Change"; nothing is upgraded or fetched here.
      if (item.bm_colour && bmScreenOnRef.current) {
        if (item.bm_colour !== 'green' && !alreadyHasSuggestions && item.smmf_suggestions && item.smmf_suggestions.length > 0) {
          const typedOption = { name: item.item_name, category: item.category || '', unit: item.unit || '', isTypedOption: true };
          setSuggestionsMap(prev => ({ ...prev, [idx]: [typedOption, ...item.smmf_suggestions] }));
          if (item.bm_colour === 'yellow' && item.bm_preselect != null) {
            setActiveSuggestionIndexMap(prev => ({ ...prev, [idx]: item.bm_preselect }));
          }
        }
        return;
      }

      // Use backend SMMF suggestions if available, otherwise fetch from frontend
      if (item.smmf_suggestions && item.smmf_suggestions.length > 0 && !alreadyHasSuggestions && !item.userConfirmed) {

        // Only upgrade to exact if confidence is VERY high (>=95%) AND normalized names match
        const normalizeForMatch = (str) => str.toLowerCase().replace(/[^a-z0-9]/g, '');
        const itemNameNormalized = normalizeForMatch(item.item_name);
        const hasVeryHighConfidence = item.smmf_confidence >= 0.95;

        const exactMatchSuggestion = item.smmf_suggestions.find(s =>
          normalizeForMatch(s.name) === itemNameNormalized
        );

        if (exactMatchSuggestion && item.matchStatus === 'fuzzy' && hasVeryHighConfidence) {
          // Through the door, and copying the ROW rather than mutating it: `[...prev]` is a
          // shallow copy, so assigning into updated[idx] rewrote the object the previous
          // state — and anything else still holding that row — was pointing at.
          applyItems(prev => {
            const updated = [...prev];
            if (updated[idx]) {
              // H8 — through withMatchedCategory, which also lowers the "not in inventory"
              // badge. A suggestion with no category leaves the row's own category alone,
              // exactly as the old `|| updated[idx].category` did.
              updated[idx] = withMatchedCategory({
                ...updated[idx],
                matchStatus: 'exact',
                unit: unitAfterMatch(updated[idx].unit || cxLastUnit(cxContextRef.current, exactMatchSuggestion), exactMatchSuggestion.unit)
              }, exactMatchSuggestion.category);
            }
            return updated;
          });
          // Don't show suggestions for exact matches
          return;
        }

        const typedSuggestion = {
          name: item.item_name,
          category: item.category || '',
          unit: item.unit || '',
          isTypedOption: true
        };
        setSuggestionsMap(prev => ({
          ...prev,
          [idx]: [typedSuggestion, ...item.smmf_suggestions]
        }));
        return;
      }

      const needsSuggestions =
        (item.matchStatus === 'fuzzy' || item.matchStatus === 'none' || item.matchStatus === 'pending') &&
        item.item_name &&
        item.item_name.trim().length > 0 &&
        !item.userConfirmed && // Don't override user-confirmed items
        !alreadyHasSuggestions; // Don't re-fetch if already have suggestions

      if (needsSuggestions) {
        fetchSuggestions(idx, item.item_name);
      }
    });
  }, [items, inventoryLoading, fetchSuggestions, suggestionsMap]); // Trigger when items array changes

  // **REAL-TIME DUPLICATE NAME DETECTION**
  // Instantly flags items with identical names as the user types
  useEffect(() => {
    const nameMap = new Map(); // lowercase name -> [indices]
    items.forEach((it, idx) => {
      const name = (it.item_name || '').trim().toLowerCase();
      if (name.length > 0) {
        if (!nameMap.has(name)) nameMap.set(name, []);
        nameMap.get(name).push(idx);
      }
    });

    // Collect indices that are duplicates
    const dupIndices = new Set();
    nameMap.forEach((indices) => {
      if (indices.length > 1) {
        indices.forEach(i => dupIndices.add(i));
      }
    });

    // Update itemErrors: add/remove 'Duplicate name' without touching other errors
    setItemErrors(prev => {
      const next = { ...prev };
      let changed = false;

      items.forEach((_, idx) => {
        const existing = next[idx] || [];
        const hasDupErr = existing.some(e => /^Duplicate name$/i.test(e));
        const shouldHaveDupErr = dupIndices.has(idx);

        if (shouldHaveDupErr && !hasDupErr) {
          next[idx] = [...existing, 'Duplicate name'];
          changed = true;
        } else if (!shouldHaveDupErr && hasDupErr) {
          const filtered = existing.filter(e => !/^Duplicate name$/i.test(e));
          if (filtered.length > 0) {
            next[idx] = filtered;
          } else {
            delete next[idx];
          }
          changed = true;
        }
      });

      return changed ? next : prev;
    });
  }, [items]);

  const handleNameChange = (idx, val) => {
    setActiveSuggestionIndexMap(prev => ({ ...prev, [idx]: -1 }));

    // handleInputChange already does the matching and sets matchStatus.
    // It also clears userConfirmed when the edited name differs from the
    // previously confirmed one, so the green state doesn't stick.
    handleInputChange(idx, 'item_name', val);

    // ✅ DYNAMIC FILTERING: Update suggestions based on typed text
    const trimmedVal = (val || '').trim();


    if (!trimmedVal || trimmedVal.length === 0) {
      // Clear suggestions if input is empty
      setSuggestionsMap(prev => ({ ...prev, [idx]: [] }));
    } else {
      // ALWAYS show typed text as first suggestion, even for short text
      // Bill Memory screen: the chips are the ladder's (ranked); today's matching is the fallback.
      const bmAnswer = bmScreenOnRef.current
        ? decideTypedRow({ ...(itemsRef.current[idx] || {}), item_name: trimmedVal, bm_printed_name: trimmedVal }, bmCtxRef.current)
        : null;
      const matchResult = bmAnswer ? matchResultOf(bmAnswer, bmCtxRef.current) : matchAgainstInventory(val, 10); // Increased to 10 for more options

      const { suggestions } = matchResult;

      // Use all suggestions from matchAgainstInventory (it already does fuzzy matching)
      const filteredSuggestions = suggestions || [];


      // The typed text is always the FIRST suggestion. Read from itemsRef, not through a
      // no-op setItems: that pattern ran setSuggestionsMap inside a reducer, which React
      // double-invokes under StrictMode.
      const currentRow = itemsRef.current[idx];
      const typedSuggestion = {
        name: trimmedVal,
        category: currentRow?.category || '',
        unit: currentRow?.unit || '',
        isTypedOption: true // Mark as user-typed option
      };
      setSuggestionsMap(prev => ({ ...prev, [idx]: [typedSuggestion, ...filteredSuggestions] }));
      if (bmAnswer && bmAnswer.preselect != null) {
        setActiveSuggestionIndexMap(prev => ({ ...prev, [idx]: bmAnswer.preselect }));
      }
    }

    // Clear timeout and fetch new suggestions (debounced)
    // ✅ Don't fetch if user has confirmed this item
    if (suggestionTimeout.current) clearTimeout(suggestionTimeout.current);

    // Confirmed rows are left alone. Read the flag; do not schedule a timeout from inside a
    // state updater, which React runs twice under StrictMode and would double-fire.
    // With the Bill Memory screen answering, its ranked chips stand; no server suggestions replace them.
    const bmAnswering = !!(bmScreenOnRef.current && bmCtxRef.current && bmCtxRef.current.ctx);
    if (!itemsRef.current[idx]?.userConfirmed && !bmAnswering) {
      suggestionTimeout.current = setTimeout(() => fetchSuggestions(idx, val), 180);
    }
  };

  const applySuggestion = (idx, suggestion) => {
    if (!suggestion) return;
    if (suggestion.isTypedOption && bmScreenOnRef.current) {
      applyItems(prev => prev.map((row, i) => (i === idx ? { ...row, bmShowRedCard: true } : row)));
      return;
    }

    // Clear any pending suggestion timeouts
    if (suggestionTimeout.current) clearTimeout(suggestionTimeout.current);

    const name = suggestion.name || '';
    const isTypedOption = suggestion.isTypedOption === true;

    // Clear any errors for this item when applying a suggestion
    setItemErrors(prev => {
      const next = { ...prev };
      delete next[idx];
      return next;
    });

    // Through the door: picking a suggestion is one of the biggest single changes a row can
    // undergo (name, id, category, unit, tax, match status) and it was invisible to both
    // itemsRef and undo. The row is COPIED rather than mutated — `[...prev]` is shallow, so
    // every assignment below used to rewrite the object the previous state still held.
    applyItems(prev => {
      const next = [...prev];
      if (next[idx]) {
        next[idx] = { ...next[idx] };
        next[idx].item_name = name;
        next[idx].lastStableName = name;

        // **Generate batch number based on selected item name**
        next[idx].batch_number = generateBatchNumber(name);
        if (suggestion.bmMarks) Object.assign(next[idx], suggestion.bmMarks);

        if (isTypedOption) {
          // ✅ User selected their typed text = NEW ITEM → Mark as EXACT (GREEN)
          // This confirms the item name is correct and will be added to inventory
          next[idx].matchStatus = 'exact';
          next[idx].userConfirmed = true; // Lock this status - don't auto-change
          next[idx].createAsNew = true; // Flag to skip NAR and create as new item
          next[idx].item_id = null; // new item — no inventory id
        } else {
          // ✅ User selected inventory suggestion = EXACT MATCH (GREEN status)
          // Category always comes from inventory; the unit follows unitAfterMatch (a
          // same-family bill unit like kg on a gram item stays — the backend converts).
          // Carry the suggestion's inventory id so submit takes the deterministic
          // fast path (backend verifies ownership, skips NAR).
          next[idx].item_id = suggestion.item_id || null;
          // H8 — the category comes from inventory, so the "not in inventory" badge must
          // come down with it. The isTypedOption branch above deliberately does NOT do
          // this: a typed option really is not in inventory, and there the badge is true.
          next[idx] = withMatchedCategory(next[idx], suggestion.category_name || suggestion.category);
          next[idx].unit = unitAfterMatch(next[idx].unit || cxLastUnit(cxContextRef.current, suggestion), suggestion.unit_symbol || suggestion.unit);

          if (suggestion.tax_slab !== undefined && suggestion.tax_slab !== null) {
            const newTaxSlab = parseFloat(suggestion.tax_slab) || 0;
            next[idx].tax_slab = String(newTaxSlab);

            if (taxForStockInEnabled) {
              const baseTotal = parseFloat(next[idx].total_price) || 0;
              const { cgst: nextCgst, sgst: nextSgst } = calculateSplitTaxFromSlab(baseTotal, newTaxSlab);
              next[idx].cgst = nextCgst;
              next[idx].sgst = nextSgst;
              if (next[idx].total_price) {
                next[idx].total_price_with_tax = calculateTotalWithTax(baseTotal, newTaxSlab);
              }
            }
          }

          // Mark as exact match since user selected it
          next[idx].matchStatus = 'exact';
          next[idx].userConfirmed = true; // Lock this status - don't auto-change

        }

        // ✅ Batch numbers will be auto-generated by backend
        // No need to generate client-side
      }
      return next;
    });

    // REMOVED: No longer locking items - they remain editable

    // After applying suggestion remove suggestions list for that row
    setSuggestionsMap(prev => ({ ...prev, [idx]: [] }));
    setActiveSuggestionIndexMap(prev => ({ ...prev, [idx]: -1 }));

    // Auto-focus quantity input after item is selected
    requestAnimationFrame(() => {
      const card = document.querySelector(`.item-box[data-item-index="${idx}"]`);
      if (card) {
        const qtyInput = card.querySelector('.stockin-quantity-input');
        if (qtyInput) qtyInput.focus();
      }
    });
  };

  // Bill Memory screen: "Change" on a green row reopens the chips; the new choice is learned.
  const bmReopenChips = (idx) => {
    const row = itemsRef.current[idx];
    if (!row) return;
    const marks = { bm_action: 'changed', bm_user_changed: true, bm_settled: true, bmShowRedCard: false };
    const printed = rowPrintedName(row);
    const { suggestions = [] } = matchAgainstInventory(printed, 5) || {};
    const current = row.item_id ? [{ name: row.item_name, category: row.category || '', unit: row.unit || '', item_id: row.item_id }] : [];
    const others = suggestions.filter(s => !row.item_id || Number(s.item_id) !== Number(row.item_id));
    const chips = [...current, ...others].map(s => ({ ...s, bmMarks: marks }));
    setSuggestionsMap(prev => ({ ...prev, [idx]: [{ name: printed, category: row.category || '', unit: row.unit || '', isTypedOption: true }, ...chips] }));
    setActiveSuggestionIndexMap(prev => ({ ...prev, [idx]: -1 }));
  };

  // Bill Memory: a tap on a vendor button fills the vendor, makes it certain, teaches the bill's
  // GSTIN/phone to that vendor (never junk or ambiguous), and the context reload re-asks open rows.
  const bmPickVendor = (v) => {
    if (!v || !v.vendor_id) return;
    handleVendorSelect({ vendor_id: v.vendor_id, name: v.name }, null, { bmCertain: true });
    // One call for every bill with no certain vendor: the server teaches only what those bills agree on.
    const scanRefs = bmScan ? bmScan.unsureScanRefs : [];
    if (scanRefs.length) http.post('/bill-memory/vendor-clue', { vendor_id: v.vendor_id, scan_refs: scanRefs }).catch(() => {});
  };

  // Bill Memory: a 409 bm_rows_need_tap — each stale row takes the server's answer, unsettled, and
  // the first is scrolled to. A green answer fills the item; yellow and red wait for a tap.
  const bmApplyServerAnswers = (rows) => {
    const byIndex = new Map(rows.map(r => [r.index, r.decision || {}]));
    applyItems(prev => prev.map((row, i) => {
      const d = byIndex.get(i);
      if (!d) return row;
      const green = d.colour === 'green' && d.item_id != null;
      const chip = green ? (d.chips || []).find(c => c.item_id === d.item_id) : null;
      return {
        ...row,
        bm_colour: d.colour, bm_reason: d.reason || null, bm_notes: d.notes || [], bm_preselect: d.preselect, bm_source_check: d.check_no,
        smmf_suggestions: (d.chips || []).map(c => ({ name: c.name, item_id: c.item_id, bmWhy: c.why })),
        matchStatus: statusFromColour(d.colour) || 'none',
        item_id: green ? d.item_id : null,
        is_existing_item: green,
        item_name: green && chip ? chip.name : row.item_name,
        userConfirmed: false, bm_settled: false, redClassified: false,
      };
    }));
    setSuggestionsMap(prev => {
      const next = { ...prev };
      rows.forEach(r => { delete next[r.index]; });
      return next;
    });
    const first = Math.min(...rows.map(r => r.index));
    requestAnimationFrame(() => bmScrollToRow(first));
  };

  // Bill Memory screen: "Add back" returns a left-out line as an ordinary row (it then teaches "is stock").
  const bmAddBack = (l) => {
    const it = l.item || {};
    const row = {
      ...emptyItem(),
      item_name: l.name, original_ocr_name: l.name, quantity: it.quantity ?? '', unit: it.unit || '',
      unit_price: it.unit_price ?? '', total_price: it.total_price ?? '',
      bm_scan_ref: it.bm_scan_ref, bm_line_no: it.bm_line_no, bm_input_kind: 'scanned', bm_action: 'added_back',
      matchStatus: 'none',
    };
    applyItems(prev => [...prev, row]);
    setBmLeftOut(prev => prev.filter(x => x !== l)); // line numbers repeat across the bills of one stock-in
    if (it.bm_scan_ref) bmAddedBackRef.current = [...bmAddedBackRef.current, { bm_scan_ref: it.bm_scan_ref, bm_line_no: it.bm_line_no }];
  };

  const addItem = () => commitItemEdit([...items, emptyItem()]);

  // ========================================
  // VOICE INPUT HANDLERS
  // ========================================



  const removeItem = (i) => {
    if (items.length > 1) {
      const removed = items[i];
      if (removed && removed.bm_scan_ref && removed.bm_line_no) {
        bmDeletedLinesRef.current = [...bmDeletedLinesRef.current, { bm_scan_ref: removed.bm_scan_ref, bm_line_no: removed.bm_line_no, printed_name: removed.original_ocr_name || removed.item_name || '' }];
      }
      // Through the door: deleting a row used to leave no history entry at all, so undo
      // jumped over the deletion and the row was gone for good.
      commitItemEdit(items.filter((_, idx) => idx !== i));
      // originalItems is Revert All's target — the pre-fill state — and is NOT positionally
      // tied to the live rows any more (per-row Reset matches on uid, see loadedRowFor), so
      // it must NOT be trimmed here: doing so would shorten the state Revert All restores.

      const shiftObject = (obj) => {
        if (!obj) return obj;
        const newObj = {};
        for (const [key, value] of Object.entries(obj)) {
          const k = parseInt(key, 10);
          if (Math.abs(k) === k) { // is valid index
            if (k < i) newObj[k] = value;
            else if (k > i) newObj[k - 1] = value;
          }
        }
        return newObj;
      };

      setItemErrors(prev => {
        const shifted = shiftObject(prev);
        // Strip stale duplicate errors — the duplicate condition may no longer hold
        const cleaned = {};
        for (const [key, errArr] of Object.entries(shifted)) {
          if (!Array.isArray(errArr)) continue;
          const filtered = errArr.filter(e => !/duplicate name/i.test(e) && !/duplicate batch/i.test(e));
          if (filtered.length > 0) cleaned[key] = filtered;
        }
        return cleaned;
      });
      setFieldValidationErrors(prev => shiftObject(prev));
      setSuggestionsMap(prev => shiftObject(prev));
      setActiveSuggestionIndexMap(prev => shiftObject(prev));
      setDuplicateWarnings(prev => shiftObject(prev));
      setFilterResults(prev => shiftObject(prev));

      setPendingConfirmations(prev => {
        const newSet = new Set();
        prev.forEach(k => {
          if (k < i) newSet.add(k);
          else if (k > i) newSet.add(k - 1);
        });
        return newSet;
      });
    } else {
      // When only one card exists, clear it and add a new empty card
      commitItemEdit([emptyItem()]);
      setOriginalItems([]);
      // Clear any errors or warnings for the removed item
      setItemErrors({});
      setFieldValidationErrors({});
      setSuggestionsMap({});
      setActiveSuggestionIndexMap({});
      setDuplicateWarnings({});
      setFilterResults({});
      setPendingConfirmations(new Set());
    }
  };

  const handleDuplicateNameClick = (currentIdx, itemName) => {
    if (!itemName) return;
    const cleanName = itemName.trim().toLowerCase();
    const otherIdx = items.findIndex((it, i) => i !== currentIdx && (it.item_name || '').trim().toLowerCase() === cleanName);
    if (otherIdx !== -1) {
      const element = document.querySelector(`[data-item-index="${otherIdx}"]`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        
        // Temporarily highlight the other card
        const originalTransition = element.style.transition;
        const originalBg = element.style.backgroundColor;
        const originalBorder = element.style.border;
        const originalBoxShadow = element.style.boxShadow;
        
        element.style.transition = 'all 0.3s ease';
        element.style.backgroundColor = '#fffbeb'; // warm amber background
        element.style.border = '2px solid #f59e0b'; // amber border
        element.style.boxShadow = '0 0 15px rgba(245, 158, 11, 0.4)';
        
        setTimeout(() => {
          element.style.backgroundColor = originalBg;
          element.style.border = originalBorder;
          element.style.boxShadow = originalBoxShadow;
          setTimeout(() => {
            element.style.transition = originalTransition;
          }, 300);
        }, 1200);

        // Focus the input inside
        const input = element.querySelector('input[name="item_name"]');
        if (input) {
          setTimeout(() => input.focus(), 300);
        }
      }
    }
  };

  /**
   * Reset ONE row back to the snapshot — genuinely useful after an OCR bill, where the
   * user usually wants to undo their edits to a single misread line rather than the whole
   * form. It was dead code with an eslint-disable on it; it is now wired to the row
   * overflow menu.
   */
  /**
   * The loaded state of ONE row, matched by its stable uid (H4).
   *
   * Not by array index: rows get added, removed and reordered, and an index-matched restore
   * quietly writes one row's loaded values over a different row. Rows created after the load
   * have no counterpart here and get no Reset button, which is the correct answer — there is
   * nothing to reset them to.
   */
  const loadedRowFor = useCallback(
    (row) => (row && row.uid ? loadedItems.find(o => o && o.uid === row.uid) || null : null),
    [loadedItems]
  );

  const revertItem = (idx) => {
    const current = itemsRef.current[idx];
    const o = loadedRowFor(current);
    if (!o) return;
    // Through the door, so the reset itself lands in history and is undoable.
    applyItems(prev => {
      const copy = [...prev];
      // restore original language text for display, but keep translation for DB
      const nameToUse = o.original_ocr_name || o.item_name;
      copy[idx] = {
        ...o,
        item_name: nameToUse,
        lastStableName: nameToUse,
        display_name: nameToUse,
        db_name: o.translated_name || nameToUse
      };
      return copy;
    });
    setItemErrors(prev => ({ ...prev, [idx]: undefined }));
  };

  // Revert all items to the snapshot taken before the last fill.
  const revertAll = () => {
    if (originalItems.length === 0) return;
    // Through the door WITHOUT snapshot: reverting is not a fill, so it must not move the
    // target it is reverting to. It is pushed onto the undo stack, so an accidental revert
    // is itself undoable.
    applyItems(originalItems.map(o => ({ ...o })));
    setItemErrors({});
  };

  const scrollToFirstError = () => {
    // Find first item with errors (excluding 'global' key)
    const errorIndices = Object.keys(itemErrors).filter(key => key !== 'global').map(k => parseInt(k));
    if (errorIndices.length === 0) return;

    const firstErrorIdx = Math.min(...errorIndices);

    // Try multiple selectors to find the error card
    const errorCard = document.querySelector(`[data-item-index="${firstErrorIdx}"]`) ||
      document.querySelector(`.item-box.has-errors`);

    if (errorCard) {
      errorCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Add a brief highlight effect
      errorCard.style.transition = 'box-shadow 0.3s ease';
      errorCard.style.boxShadow = '0 0 0 4px rgba(239, 68, 68, 0.5)';
      setTimeout(() => {
        errorCard.style.boxShadow = '';
      }, 2000);
    } else {
      console.warn('❌ Could not find error card to scroll to');
    }
  };

  const showModalMessage = (message, type = 'success', scrollToError = false) => {
    setShouldScrollToError(scrollToError);

    // Determine modal type and show appropriate modal
    const isVendorModal = message && message.includes('Vendor not detected');
    const isYellowItemsModal = message && message.includes('YELLOW status');

    if (isVendorModal) {
      setVendorModalMessage(message);
      setShowVendorModal(true);
    } else if (isYellowItemsModal) {
      setYellowItemsModalMessage(message);
      setShowYellowItemsModal(true);
    } else if (type === 'success') {
      setSuccessModalMessage(message);
      setShowSuccessModal(true);
    } else if (type === 'error') {
      setErrorModalMessage(message);
      setShowErrorModal(true);
    } else if (type === 'loading') {
      setLoadingModalMessage(message);
      setShowLoadingModal(true);
    } else {
      // Default to success modal for any other types
      setSuccessModalMessage(message);
      setShowSuccessModal(true);
    }
  };

  // **NEW: Debounced AI Validation Trigger (prevents excessive API calls)**
  const triggerAIValidation = (index) => {
    const it = items[index];
    if (!it) return;

    const name = (it.item_name || '').trim();

    // Clear any existing timeout for this specific item
    if (aiValidationTimeout.current[index]) {
      clearTimeout(aiValidationTimeout.current[index]);
    }

    // Only trigger validation for inputs with 3+ characters
    if (name && name.length >= 3) {
      // Wait 800ms after user stops typing before validating
      aiValidationTimeout.current[index] = setTimeout(() => {
        handleAIValidate(index);
      }, 800);
    }
  };

  // **NEW: Comprehensive AI Validator with Restaurant Context**
  const handleAIValidate = async (index) => {
    const it = items[index];
    if (!it) return;

    // ✅ Skip AI validation if user has confirmed this item by selecting a chip
    if (it.userConfirmed) {
      return;
    }

    const name = (it.item_name || '').trim();

    // ⚡ Skip validation for short inputs (must be at least 3 characters)
    if (!name || name.length < 3) {
      return;
    }

    try {
      const businessId = UnitMappingService.getBusinessId();

      // The comprehensive validator — at its real path, and only with the no-popup screen On (Fix 13):
      // that is the only place its answer is used, so it costs nothing for every other business.
      const res = await aiNameCheck({ screenOn: bmScreenOnRef.current, businessId, index, row: it, inventory: inventoryItems });
      if (!res) return;

      if (res && res.success && res.data && Array.isArray(res.data.results) && res.data.results[0]) {
        const r = res.data.results[0];
        // M6: this array is a WRITE base built after an await — the render closure it used
        // to copy is guaranteed stale by then.
        const copy = [...itemsRef.current];

        // Bill Memory screen: the ladder owns the row's colour and chips. The validator only
        // adds its one grey line when it says this is not a kitchen item — no popup (design §14).
        if (bmScreenOnRef.current && copy[index] && copy[index].bm_colour) {
          if (r.status === 'rejected') {
            const notes = (copy[index].bm_notes || []).filter(n => n.code !== 'not_kitchen_item');
            copy[index] = { ...copy[index], bm_ai_rejected: true, bm_notes: [...notes, { code: 'not_kitchen_item', params: {} }] };
            commitItemEdit(copy);
          }
          return;
        }

        // Handle validation result
        if (r.status === 'exact') {
          // GREEN: Exact match with inventory
          copy[index].matchStatus = 'exact';
          copy[index].match_type = 'exact';

          // Auto-fill from matched inventory item
          if (r.matched_inventory_item) {
            // H8 — matched against inventory, so the "not in inventory" badge comes down.
            copy[index] = withMatchedCategory(copy[index], r.matched_inventory_item.category);
            // Only fill the unit when the row has NONE — never override a unit that came from
            // the bill/upload (e.g. an Excel row of "6 kg" must stay kg, not flip to the
            // inventory's stored base unit "g").
            const hasUnit = copy[index].unit && String(copy[index].unit).trim();
            if (r.matched_inventory_item.unit && !hasUnit) {
              copy[index].unit = UnitMappingService.normalizeUnitSymbol(r.matched_inventory_item.unit);
            }
          }


        } else if (r.status === 'fuzzy') {
          // YELLOW: Similar match or valid restaurant item
          copy[index].matchStatus = 'fuzzy';
          copy[index].match_type = 'fuzzy';

          // Store suggestions from inventory
          if (r.suggestions && r.suggestions.length > 0) {
            setSuggestionsMap(prev => ({ ...prev, [index]: r.suggestions }));
          }

          // Auto-fill category if provided
          if (r.suggested_category && !copy[index].category) {
            if (categories.includes(r.suggested_category)) {
              // H8 — through the same door as every other match-derived category. The
              // `!copy[index].category` guard means a defaulted row never reaches here
              // (the default always leaves a category behind), so this clears nothing
              // today; it goes through the door anyway so the rule has no exceptions to
              // remember.
              copy[index] = withMatchedCategory(copy[index], r.suggested_category);
            }
          }

        } else if (r.status === 'rejected') {
          // RED: Not allowed - not restaurant-related
          copy[index].matchStatus = 'none';
          copy[index].match_type = 'rejected';

          // Show error message
          showModalMessage(
            `⛔ "${name}" is not allowed.\n\nReason: ${r.reason || 'Not a valid restaurant/business item'}\n\nOnly food ingredients, beverages, and non-consumables (cleaning supplies, packaging) are allowed.`,
            'error'
          );

        }

        // Through the door: this is a bulk rewrite of a row's match status, category and
        // unit, and it used to be invisible to both itemsRef and undo.
        commitItemEdit(copy);
      }
    } catch (e) {
      console.warn('⚠️ AI validation failed:', e.message);
      // Keep current status on error
    }
  };

  // Lock name on blur if user manually entered text (prevents later AI overwrite)
  const lockIfNeeded = (idx) => {
    const item = items[idx];
    if (!item || !item.item_name) return;

    // REMOVED: Auto-locking behavior - items remain editable
    // Only generate batch number if needed.
    // Through the door so itemsRef stays in step, `silent` because lastStableName is
    // internal bookkeeping and nobody reaches for Undo to reverse it. Copies the row rather
    // than mutating it in place, which is what the old version did to the live state.
    applyItems(prev => prev.map((it, j) => (
      j === idx && it && it.item_name ? { ...it, lastStableName: it.item_name } : it
    )), { silent: true });

    // REMOVED: No longer adding to lockedNames Set
    // Items remain editable at all times
  };

  const validateForm = () => {
    const newErrors = {};
    if (!items || items.length === 0) newErrors.global = [...(newErrors.global || []), 'At least one item is required'];
    const seenBatch = new Set();
    const today = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
    const nameMap = new Map(); // lower->indexes
    items.forEach((it, idx) => {
      const errs = [];
      const n = (it.item_name || '').trim();
      if (!n) errs.push('Name required');
      if (n.length > 255) errs.push('Name too long');
      const lower = n.toLowerCase();
      if (!nameMap.has(lower)) nameMap.set(lower, []);
      nameMap.get(lower).push(idx);

      // **NEW: Validate category type**
      const categoryType = categoryTypeMap.get(it.category);

      if (!it.category) {
        errs.push('Category required');
      } else {
        if (!categoryType) {
          errs.push('Invalid category - please select from dropdown');
        } else if (!['consumable', 'non_consumable'].includes(categoryType)) {
          errs.push('Category must be either consumable or non-consumable');
        }
      }

      const qty = parseFloat(it.quantity);
      if (!it.quantity || isNaN(qty) || qty <= 0) errs.push('Quantity invalid');

      // Unit is required for EVERYTHING. This used to exempt non-consumables, but the
      // three other checkpoints — validateMandatoryFields, submit STEP 8.5, and the gate's
      // STRICT5 rule — all demanded a unit unconditionally. So an exempted row passed here
      // and was rejected moments later by a check the user had no way to connect to this
      // one. Non-consumables default to `pcs` when classified, which satisfies every
      // checkpoint without a special case spreading through four of them.
      if (!it.unit) {
        errs.push('Unit required');
      }

      const up = parseFloat(it.unit_price);
      if (it.unit_price === '' || isNaN(up) || up < 0) errs.push('Unit price invalid');
      const bn = (it.batch_number || '').trim();
      if (!bn) errs.push('Batch required');
      if (bn.length > 100) errs.push('Batch too long');
      if (seenBatch.has(bn)) errs.push('Duplicate batch');
      seenBatch.add(bn);

      // Expiry enforcement — same resolver as validateMandatoryFields, so the two cannot
      // disagree about whether a row needs a date.
      if (rowRequiresExpiry(it) && !it.expiry_date) {
        errs.push('Expiry required (enforced)');
      }
      if (it.expiry_date && it.expiry_date < today) errs.push('Expiry in past');
      if (errs.length) newErrors[idx] = errs;
    });
    // Duplicate names case-insensitive
    nameMap.forEach((arr) => { if (arr.length > 1) arr.forEach(i => { newErrors[i] = [...(newErrors[i] || []), 'Duplicate name']; }); });
    setItemErrors(newErrors);
    if (Object.keys(newErrors).length) {
      const globalErr = newErrors.global ? newErrors.global.join(', ') : 'Fix highlighted errors';
      throw new Error(globalErr);
    }
  };

  const makeApiCall = async (endpoint, data) => {
    try {
      const result = await http.post(`/stock-in/v2${endpoint}`, data, { timeoutMs: 30000 });
      if (!result.success) throw new Error(result.error || 'Request failed');
      return result;
    } catch (err) {
      if (err.name === 'AbortError') throw new Error('Request timed out');
      throw err;
    }
  };

  /**
   * THE ONE SUCCESS PATH (P2 + P4).
   *
   * There were TWO, hand-maintained, and the piece one was a stripped copy of the other.
   * It omitted, in a single function: the idempotency-key regeneration, the draft removal,
   * the onSuccess() parent notification, the refreshNotifications event, the tax-toggle
   * revert, vendor learning, expiry auto-enable and haptics.
   *
   * Two of those omissions were live data loss:
   *
   *   P2  `idempotencyKeyRef` lives in a ref that SURVIVES the form reset, and only
   *       handleSubmit regenerated it. Every bill that went through a piece popup left the
   *       key unchanged, so the NEXT bill entered without remounting the component was
   *       replayed by the backend — `{ success: true, idempotent: true }` — and the person
   *       was shown a success message carrying the PREVIOUS bill's stock_in_id. The second
   *       bill was silently discarded, in the most-used path of the module.
   *
   *   P4  the draft was never removed, so a bill that had already been saved could be
   *       restored later from its own stale draft; and the piece path's 1.5s timer cleared
   *       only items/totalCost/confirmedItems while the real reset lived in the success
   *       modal's OK button. If the person did not press OK, `originalItems` survived —
   *       and "Revert All" would resurrect the bill that had just been saved and offer it
   *       for re-submission.
   *
   * Duplication was the bug, so the fix is that there is now one of these and both callers
   * use it. Anything a successful save must do belongs HERE, and nowhere else.
   */
  const finalizeSuccessfulSubmit = async (response) => {
    // ── 1. THE KEY. Unconditionally, first, before anything can throw. ─────────
    // Everything below is best-effort; this is not. A failure in vendor learning must
    // never be able to leave the next bill sharing this one's idempotency key.
    idempotencyKeyRef.current = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`;

    // ── 2. A REPLAY IS NOT A SAVE, and must never again read like one. ─────────
    // If the backend ever does replay a key, the person is told plainly, with the id of
    // the record that already exists, instead of being shown a success for a bill that
    // was not written. Returning false tells the caller not to celebrate.
    const replayed = response?.idempotent === true;
    // Fix 12 — what this bill taught Bill Memory; the server sends it only while a source is On.
    setBmSaveLine(replayed ? '' : confirmationLine(response?.bill_memory));
    if (replayed) {
      showModalMessage(
        `⚠️ This bill was already recorded — nothing new was saved.\n\n` +
        `It is stored as stock-in ID ${response?.data?.stock_in_id ?? '(unknown)'}. ` +
        `If you meant to enter a DIFFERENT bill, please check that record first, then enter it again.`,
        'error'
      );
    }

    // ── 2b. THE DAY THE BILL LANDED ON — checked, not assumed. ────────────────
    // A bill filed under the wrong day is the most expensive silent fault this form has:
    // it moves the purchase into a different expense card, a different daily contribution
    // and, if the month boundary is crossed, a different EOM report — and nothing on
    // screen ever said which day was actually written. So the server's answer is compared
    // against what the picker asked for, and the day is NAMED in the success message.
    //
    // The comparison is string-to-string on 'YYYY-MM-DD'. received_date is a calendar day
    // and the API now sends it as a plain date string for exactly this reason; never parse
    // it with `new Date()` — that reintroduces the UTC shift the string exists to avoid.
    const askedForDate = String(productionDate || '').slice(0, 10);
    const appliedDate = String(response?.data?.received_date || '').slice(0, 10);
    savedReceivedDateRef.current = appliedDate || askedForDate || '';
    if (!replayed && askedForDate && appliedDate && appliedDate !== askedForDate) {
      showModalMessage(
        `⚠️ This bill was saved under ${formatDayForHumans(appliedDate)}, not ` +
        `${formatDayForHumans(askedForDate)} as the date field showed.\n\n` +
        `Record ID ${response?.data?.stock_in_id ?? '(unknown)'}. Revert it and enter it ` +
        `again, or ask an admin to correct the date on that record.`,
        'error'
      );
    }

    // ── 3. The tax toggle goes back to the business setting. ──────────────────
    // An Excel upload can temporarily force tax on for one bill. Leaving it on meant the
    // NEXT bill silently inherited a tax setting nobody chose.
    forceTaxOnRef.current = false;
    setTaxForStockInEnabled(taxSettingRef.current);

    // ── 4. H8.2 — the server's suggestions become visible, without interrupting. ──
    setSaveWarnings(Array.isArray(response?.gate_warnings) ? response.gate_warnings : []);
    setSaveWarningsOpen(false);

    // ── 5. P5 — new pcs items, asked about now rather than never. ──────────────
    setPieceProfileQueue(Array.isArray(response?.piece_profiles_needed) ? response.piece_profiles_needed : []);

    // ── 5b. Stock-In Safety Engine — what was decided per line, fire-and-forget. ──
    // Only while the guard is on, never on a replay (nothing new was saved). The server
    // re-judges every line from what was actually SAVED, so this can only add facts.
    if (!replayed && cxGuardRef.current.on) {
      try {
        const cxPayload = cxRecordPayload({
          stockInId: response?.data?.stock_in_id,
          rows: itemsRef.current,
          decisions: cxDecisionsRef.current,
          printOf: (row) => cxPrint(cxPayloadLine(row)),
          lineItems: response?.data?.line_items,
        });
        if (cxPayload) cxRecordDecisions(cxPayload);
      } catch (e) { /* advisory only — never in the way of a saved bill */ }
      cxDecisionsRef.current = new Map();
      cxResolvedUidsRef.current = new Set();
      cxCheckedHashRef.current = '';
    }

    if (!replayed && 'vibrate' in navigator) navigator.vibrate(200);

    // ── 6. Vendor learning (OCR rows only). ───────────────────────────────────
    try {
      if (ocrData && Array.isArray(ocrData.items)) {
        const businessId = UnitMappingService.getBusinessId();
        let vendorId = selectedVendor?.vendor_id || null;
        if (!vendorId) {
          try {
            const vendorMatch = await http.post('/vendors/fuzzy-match', {
              vendorName: vendorName.trim(),
              businessId
            });
            if (vendorMatch.success && vendorMatch.vendor) vendorId = vendorMatch.vendor.vendor_id;
          } catch (e) {
            console.warn('Could not find vendor for learning:', e.message);
          }
        }
        if (vendorId) {
          for (const item of itemsRef.current) {
            if (item.original_ocr_name && item.matchStatus === 'exact') {
              const matchedItem = resolveInventoryItemForRow(item);
              if (matchedItem) {
                try {
                  await http.post('/vendor-learning/record', {
                    vendorId,
                    businessId,
                    ocrExtractedName: item.original_ocr_name,
                    confirmedItemId: matchedItem.item_id,
                    confirmedItemName: matchedItem.name
                  });
                } catch (e) {
                  console.warn('Failed to record vendor learning:', e.message);
                }
              }
            }
          }
        }
      }
    } catch (e) {
      console.warn('Vendor learning step failed (non-fatal):', e.message);
    }

    // ── 7. Expiry auto-enable. ────────────────────────────────────────────────
    try {
      const businessId = UnitMappingService.getBusinessId();
      for (const item of itemsRef.current) {
        if (item.expiry_date && item.item_name) {
          const matchedItem = resolveInventoryItemForRow(item);
          if (matchedItem) {
            await http.post('/expiry-tracking/auto-enable', {
              itemId: matchedItem.item_id,
              businessId
            });
          }
        }
      }
    } catch (e) {
      console.warn('Failed to auto-enable expiry tracking:', e.message);
    }

    // ── 8. The parent, the draft, the notifications. ──────────────────────────
    if (onSuccess) onSuccess();

    // THE DRAFT DIES WITH THE BILL. Leaving it meant a saved bill could be restored from
    // its own stale draft later and submitted a second time.
    try {
      const businessId = localStorage.getItem('businessId') || 'unknown';
      localStorage.removeItem(`stockInDraft_${businessId}`);
    } catch (e) {
      console.warn('Could not clear the saved draft:', e.message);
    }

    setTimeout(() => {
      if (typeof window !== 'undefined' && window.dispatchEvent) {
        window.dispatchEvent(new CustomEvent('refreshNotifications'));
      }
    }, 1000);

    // ── 9. S9 — the profile/benchmark caches for the items on this bill are stale now. ──
    // A piece profile created during this save, or a status that has just been promoted by
    // this very purchase, would otherwise stay invisible until the component remounted.
    invalidatePieceCachesFor(itemsRef.current);

    return !replayed;
  };

  /**
   * S9 — drop the cached profile/benchmark entries for these rows.
   *
   * `itemPieceProfiles` records an explicit `null` for an item with no profile and never
   * re-fetches, so a profile created elsewhere (the onboarding door, the edit door, another
   * tab) or a promotion from provisional to trusted never appeared for the rest of the
   * session. Deleting the key — rather than writing null — is what lets the existing
   * "fetch the ids I do not have" effect pick them up again.
   */
  const invalidatePieceCachesFor = (rows) => {
    const ids = [...new Set((rows || []).map(r => r && r.item_id).filter(Boolean))];
    if (!ids.length) return;
    setItemPieceProfiles(prev => {
      const next = { ...prev };
      ids.forEach(id => { delete next[id]; });
      return next;
    });
    setItemBenchmarks(prev => {
      const next = { ...prev };
      ids.forEach(id => { delete next[id]; });
      return next;
    });
  };

  /* ══════════════════════════════════════════════════════════════════════════
   * PIECE GUARD — the conversation that follows a blocked bill (spec §7).
   *
   * THE SYSTEM NEVER AUTO-CORRECTS. Every answer below either resubmits the numbers
   * unchanged with a reason attached, or applies a correction A HUMAN JUST TAPPED and
   * then sends it back through the SAME gate as fresh input. Nothing here trusts its
   * own arithmetic enough to write stock on it.
   * ══════════════════════════════════════════════════════════════════════════ */

  /** Resubmit the negotiated bill. Any answer can still be rejected by the gate. */
  const submitPieceAnswers = async (gateState) => {
    // T3 — same guard as handleSubmit: this posts stock too.
    if (submitInFlightRef.current) {
      console.warn('[StockIn] piece resubmit already in flight — ignoring the repeat tap');
      return;
    }
    submitInFlightRef.current = true;
    setIsSubmitting(true);
    // H7.3 — never send a token that is not a token. An array pushed here reaches the gate
    // as `conf.type === undefined`, matches nothing, and the blocker re-raises for ever.
    const malformed = (gateState.confirmations || []).filter(t => !isValidConfirmationToken(t));
    if (malformed.length > 0) {
      console.error('[PieceGuard] refusing to send malformed confirmation tokens:', malformed);
    }
    const body = {
      ...gateState.payload,
      item_confirmations: (gateState.confirmations || []).filter(isValidConfirmationToken),
    };
    pieceSubmittedPayloadRef.current = body;
    try {
      const response = await makeApiCall('', body);
      setPieceGate(null);

      // P2/P4 — THE SAME success path handleSubmit uses. This block used to be a stripped
      // copy that forgot the idempotency key (losing the next bill) and the draft removal
      // (letting a saved bill be resurrected).
      const saved = await finalizeSuccessfulSubmit(response);
      if (saved) {
        const stockInId = response?.data?.stock_in_id;
        showModalMessage(
          (response?.message || (stockInId ? `Stock recorded successfully!\n\nID: ${stockInId}` : 'Stock recorded successfully!')) + savedOnLine(),
          'success'
        );
      }
      // ONE reset, in one place. The 1.5s timer used to clear items/totalCost/confirmedItems
      // only, while the real reset lived in SuccessModal.handleClose — so if the person did
      // not press OK, originalItems survived and "Revert All" resurrected the saved bill.
      //
      // F3 — AND IT ONLY RUNS WHEN SOMETHING WAS SAVED. This was unconditional, so on a
      // replayed key `finalizeSuccessfulSubmit` returned false, correctly told the person
      // "nothing new was saved — please enter it again", and then 1.5 seconds later wiped
      // items, originalItems, loadedItems and the undo history. They were told to re-enter
      // a bill that had just been erased in front of them, with no way back.
      if (saved) setTimeout(() => resetFormAfterSuccess(), 1500);
    } catch (err) {
      // A second round of piece questions is legitimate — a corrected count can itself
      // trip the count check. Re-open the conversation rather than dropping the bill.
      const route = routeGateBlockers(err.data?.gate_blockers);
      if (route.piece.length > 0) {
        const bulk = err.data.piece_bulk_review || null;
        setPieceGate({
          blockers: route.piece,
          bulkReview: bulk,
          activeBlocker: bulk ? null : route.piece[0],
          confirmations: body.item_confirmations.filter(isValidConfirmationToken),
          resolvedIndexes: [],
          payload: body,
          deferredBlockers: route.deferred,
        });
      } else if (route.overridable) {
        // H7 — a price or quantity blocker raised on the piece resubmit now opens ITS OWN
        // modal instead of falling into the generic error text. Same router, same mapper.
        setPieceGate(null);
        setAutoCorrectionData(gateBlockerToCorrectionData(route.overridable));
        setPendingSubmitData(body);
        setShowAutoCorrectionModal(true);
      } else {
        setPieceGate(null);
        showModalMessage(
          `🚫 ${err.data?.error || err.message || 'Submission failed'}${
            err.data?.gate_blockers?.length
              ? `\n\n${err.data.gate_blockers.map(b => `• ${b.message}`).join('\n')}`
              : ''
          }`,
          'error'
        );
      }
    } finally {
      // T3 — release it here too, or one piece resubmit would silently block every later
      // submit for the life of the page: the guard would keep returning early and the person
      // would be back to a button that does nothing, by a new route.
      submitInFlightRef.current = false;
      setIsSubmitting(false);
    }
  };

  /**
   * Every piece question is answered — show the final "Confirm Stock Entry" box again,
   * now carrying the corrected rows, before anything is posted. The negotiated state is
   * held as a plain value (never a ref), and the box's Confirm Submit hands exactly that
   * value to submitPieceAnswers. Cancel keeps the answers (answeredConfirmations), so a
   * later submit does not ask again.
   */
  const requestPieceFinalConfirm = (gateState) => {
    if (!gateState) return;
    setPendingPieceSubmit(gateState);
    setShowConfirmation(true);
  };

  /** Closing the confirmation box during a piece conversation ends that conversation too. */
  const closeStockConfirmation = () => {
    setShowConfirmation(false);
    if (pendingPieceSubmit) {
      setPendingPieceSubmit(null);
      setPieceGate(null);
      setPieceProfileError(null);
    }
  };

  /**
   * One popup answered. Records the token, applies a tapped correction to both the
   * payload and the visible row, and then either moves to the next question or submits.
   */
  const resolvePieceAnswer = (token, baseState) => {
    /**
     * ── S5 — COMPUTED OUT HERE, NOT INSIDE A STATE UPDATER ─────────────────────
     *
     * This used to do all of its work inside `setPieceGate(prev => …)` and schedule the
     * resubmit from in there. Two things are wrong with that, and the second is worse:
     *
     *   1. React double-invokes updaters under StrictMode (index.js wraps the app), so the
     *      side effect fired TWICE and POSTed the bill twice. Only the idempotency key hid
     *      it — and that key was itself broken on this exact path (P2), so the two bugs
     *      were covering for each other.
     *
     *   2. Moving the effect to a ref set *inside* the updater does not fix it, it makes it
     *      NON-DETERMINISTIC. A `useState` updater is not guaranteed to run at the call
     *      site: React invokes it during the following render, and only sometimes eagerly
     *      at dispatch. So the ref was frequently still null on the next line and the
     *      resubmit simply never happened — the conversation would answer its last question
     *      and then sit there. Caught by rendering the form and clicking the button.
     *
     * So the next state is derived from `pieceGate` — the value this click's closure was
     * rendered with, which is exactly the right one — and the updater below is a plain
     * assignment with no logic and no effects in it at all.
     *
     * ── AND THE CALLER MAY HAND IN ITS OWN BASE ────────────────────────────────
     *
     * `savePieceProfile` used to queue the corrected payload with a FUNCTIONAL
     * `setPieceGate(prev => …)` and then call straight into here, which read the render
     * closure's `pieceGate` — the value from BEFORE that queued update — and finished with
     * a plain assignment. Last write won, so the correction reached the visible row and
     * never reached the submitted payload: the screen said 4 pcs and the database was sent
     * 1. Silently, because a freshly created profile is provisional and the ladder only
     * warns. That is P3's exact failure re-entering through a different door.
     *
     * So there is now exactly one writer of `pieceGate` on this path. A caller that has
     * already derived a newer state passes it as `baseState` instead of queueing it.
     */
    const prev = baseState || pieceGate;
    if (!prev) return;

    const index = Number(token.index != null ? token.index : prev.activeBlocker?.index);
    if (!Number.isInteger(index)) return;

    /**
     * A tapped correction can arrive on either token: `piece_pack_corrected` from Popup A,
     * or `piece_profile_defined` when the Step 1 screen's read-back says "the N I entered
     * was packets". Both mean the same thing about the row, so both are honoured here —
     * keying only on the popup's token was half of how the profile correction went missing.
     */
    const correctedQty = Number(token.quantity);
    const hasCorrection = (
      (token.type === 'piece_pack_corrected' || token.type === 'piece_profile_defined')
      && Number.isFinite(correctedQty) && correctedQty > 0
    );

    // H6 — a correction the human tapped goes through the ONE items door, and the derived
    // per-piece price moves with it. The total is what was paid and never changes.
    if (hasCorrection) {
      applyItems(cur => cur.map((it, j) => (
        j === index
          ? recomputeDerivedPrice({ ...it, quantity: String(token.quantity) })
          : it
      )));
    }

    // H5 — several tokens may legitimately belong to one line (free AND loose, scheme AND
    // price-changed), so the same TYPE at an index replaces and a different type is added
    // alongside. This used to drop every earlier answer for the line.
    const confirmations = [
      ...prev.confirmations.filter(t => !(Number(t.index) === index && t.type === token.type)),
      { ...token, index },
    ];

    // The SAME condition that moved the visible row moves the payload. These two used to
    // be able to disagree — that disagreement is exactly what shipped a screen saying 4
    // and a payload carrying 1 — so they are one flag now.
    let payload = prev.payload;
    if (hasCorrection) {
      payload = {
        ...prev.payload,
        items: prev.payload.items.map((it, j) =>
          j === index ? { ...it, quantity: correctedQty } : it
        ),
      };
    }

    /**
     * M5.3 — ONE ANSWER COVERS EVERY ROW OF THE SAME ITEM.
     *
     * A bill often lists an item twice (two batches, two expiry dates), and each row raised
     * its own blocker. Answering them one at a time meant meeting the same profile screen
     * twice for the same item — and each profile save wrote a NEW SUPERSEDING VERSION,
     * moving effective_from and resetting the clean-purchase count to zero, so answering
     * carefully made the item LESS trusted than ignoring it.
     *
     * A `piece_profile_defined` answer is a fact about the ITEM, so it applies to every row
     * of that item. A quantity correction is a fact about ONE ROW and never propagates: two
     * lines of the same item can legitimately have different counts.
     */
    const rowsOf = prev.payload?.items || [];
    const identity = (i) => {
      const it = rowsOf[i] || {};
      return it.item_id != null ? `id:${it.item_id}` : `name:${String(it.item_name || i).toLowerCase()}`;
    };
    const siblings = (token.type === 'piece_profile_defined'
      ? rowsOf.map((_, i) => i).filter(i => i !== index && identity(i) === identity(index))
      : []
    ).filter(i => prev.blockers.some(b => Number(b.index) === i));

    const resolvedIndexes = [...new Set([...prev.resolvedIndexes, index, ...siblings])];

    // The gate keys confirmations by INDEX, so a per-item fact has to be restated for every
    // index it covers.
    const allConfirmations = [
      ...confirmations,
      ...siblings
        .filter(i => !confirmations.some(t => Number(t.index) === i && t.type === token.type))
        // The DEFINITION carries to the item's other rows; the COUNT does not. Two lines of
        // one item can legitimately hold different counts, and a correction is a fact about
        // one row only — copying `quantity` across would invent stock on the sibling.
        .map(i => { const { quantity, ...itemFact } = token; return { ...itemFact, index: i }; }),
    ];

    const remaining = prev.blockers.find(b => !resolvedIndexes.includes(Number(b.index)));
    const next = {
      ...prev,
      confirmations: allConfirmations,
      payload,
      resolvedIndexes,
      // Popup D: go back to the list. The list decides when everything is done —
      // deliberately not this handler, so there is no path that submits early.
      activeBlocker: prev.bulkReview ? null : (remaining || null),
    };

    setPieceGate(next);
    // S4 — kept outside the conversation too, so cancelling it does not throw the answers
    // away and make the person re-type every §14B reason.
    /**
     * F6 — TOKENS CARRY THE ROW'S IDENTITY, NOT JUST ITS POSITION.
     *
     * The gate keys confirmations purely by `index`, and this list is cleared only by
     * resetFormAfterSuccess — not by removeItem, addItem, Revert All, a draft load or an
     * Excel/OCR bulk fill. So: type a §14B reason for row 3, go back to the form, delete
     * row 1, submit — and the override lands on a DIFFERENT item, with that person's typed
     * reason recorded in ItemPieceProfileEvents against the wrong line.
     *
     * `uid` is the stable row identity (applyItems guarantees every row has one), so it
     * travels with the answer and the index is re-derived from it at submit time.
     */
    const rowsNow = itemsRef.current || [];
    setAnsweredConfirmations(
      allConfirmations
        .filter(isValidConfirmationToken)
        .map(t => {
          const row = rowsNow[Number(t.index)];
          return row && row.uid ? { ...t, rowId: row.uid } : t;
        })
    );

    // Everything answered — final confirmation, then resubmit. Exactly once, and unconditionally.
    if (!prev.bulkReview && !remaining) requestPieceFinalConfirm(next);
  };

  /** §4 — the Step 1 screen's save, then straight back into the same conversation. */
  const savePieceProfile = async (data) => {
    setPieceProfileSaving(true);
    setPieceProfileError(null);
    try {
      const businessId = UnitMappingService.getBusinessId();
      const res = await http.post(`/stock-in/v2/piece-profiles?businessId=${businessId}`, {
        ...data,
        /**
         * M4 §8.4 — your regular distributor may pack in 4s while the kirana down the road
         * packs in 6s. A profile saved without a vendor is a business-wide rule, which is
         * the right default; a profile saved WITH one is that vendor's own memory.
         *
         * THE DEFAULT IS `null`, AND THAT MATTERS. This used to fall back to
         * `currentVendorId`, so the FIRST definition of an item was born vendor-level — and
         * the vault resolves with `AND (vendor_id IS NULL OR vendor_id = $3)`, so that row
         * is invisible to every other vendor. Measured against a real database:
         *
         *   born vendor-level  → same vendor: found. ANY other vendor: NOTHING FOUND.
         *   born item-level    → every vendor finds it, and a vendor row still overrides.
         *
         * Two things broke. §4 promises "you will never be asked again for this item", and
         * the person met the unskippable Step 1 screen again for the same Kit Kat the first
         * time it arrived from anyone else. And `borrowedFromItemLevel` could never become
         * true — there was no item-level profile left to lend — so the §8.4 confirm line
         * below, the whole point of M4, was unreachable code.
         *
         * The one caller that genuinely wants a vendor-level row (that confirm line) passes
         * `vendor_id` explicitly, and still does.
         */
        vendor_id: data.vendor_id !== undefined ? data.vendor_id : null,
        businessId,
      });
      if (!res?.success) throw new Error(res?.error || 'Could not save the piece definition');

      // The row now has a profile, so the caption, chips and live hint appear at once.
      setItemPieceProfiles(prev => ({ ...prev, [data.item_id]: res.profile }));

      // S9 — the cache entry for this item is stale the instant the profile exists.
      invalidatePieceCachesFor([{ item_id: data.item_id }]);

      // P3 — the count is applied ONLY when the person said the number they typed was
      // packets. It used to be multiplied by the pack size unconditionally, with no way
      // to say otherwise: somebody who had correctly entered 12 pcs of a 4-pack item was
      // told the row would be saved as 48 and had no honest exit. One shared function
      // decides this now, in all three doors, so they cannot drift apart again.
      const idx = pieceGate?.activeBlocker?.index;
      if (Number.isInteger(idx)) {
        const row = (itemsRef.current || [])[idx] || {};
        const applied = applyPieceProfileResult(data, row);

        /**
         * ONE WRITER. This used to apply the correction here — a functional `setPieceGate`
         * for the payload, `applyItems` for the row — and then call `resolvePieceAnswer`,
         * which read the stale render closure and overwrote the queued state with a plain
         * assignment. The row showed 4 pcs; the payload still said 1; nothing complained,
         * because a brand-new profile is provisional and the ladder only warns.
         *
         * So the correction is now stated once, as data on the token, and
         * `resolvePieceAnswer` — the single owner of both the row and the payload — is the
         * only thing that acts on it.
         */
        resolvePieceAnswer({
          index: idx,
          type: 'piece_profile_defined',
          ...(applied.corrected ? { quantity: applied.quantity } : {}),
          acknowledgment: `defined: 1 pc = ${data.piece_description}`,
        });
      }
    } catch (err) {
      setPieceProfileError(err.data?.error || err.message || 'Could not save the piece definition');
    } finally {
      setPieceProfileSaving(false);
    }
  };

  const toCsvCell = (value) => {
    if (value === null || value === undefined) return '""';
    const text = typeof value === 'object' ? JSON.stringify(value) : String(value);
    return `"${text.replace(/"/g, '""')}"`;
  };


  const handleSaveDraft = () => {
    try {
      const draftData = {
        vendorName,
        vendorPhone,
        items: items.filter(it => (it.item_name || '').trim() !== '' || (it.quantity || '').toString().trim() !== ''),
        cgst,
        sgst,
        // The purchase date is part of the bill, not of the session that typed it. Without
        // it, a bill parked on the 29th and finished on the 30th silently changed which day
        // it counted against.
        productionDate
      };
      const businessId = localStorage.getItem('businessId') || 'unknown';
      localStorage.setItem(`stockInDraft_${businessId}`, JSON.stringify(draftData));
      showModalMessage('Draft saved successfully.', 'success');
    } catch (e) {
      showModalMessage(`Failed to save draft: ${e.message}`, 'error');
    }
  };

  /**
   * Does this form row require an expiry date?
   *
   * The two validators used to answer this differently: validateForm matched the row to
   * inventory BY NAME, while validateMandatoryFields keyed off `item.item_id` — which is
   * null for every manually typed row and for every OCR row that has not been matched yet.
   * So the check that runs BEFORE the confirm modal (the one the user actually sees) was
   * blind to exactly the rows most likely to need it. One resolver now, asked by both.
   *
   * item_id first when present (an explicit match is authoritative), name second.
   */
  /**
   * THE item-lookup for a form row (S3).
   *
   * Extracted because it existed twice with different fields — rowRequiresExpiry matched on
   * `inv.item_name || inv.name`, the auto-enable matcher on `inv.name` alone — so the two
   * could and did disagree about which inventory item a row was. One resolver now.
   */
  const resolveInventoryItemForRow = useCallback((row) => {
    if (!row) return null;
    if (row.item_id) {
      const byId = inventoryItems.find(inv => inv.item_id === row.item_id);
      if (byId) return byId;
    }
    const rowName = String(row.item_name || '').trim().toLowerCase();
    if (!rowName) return null;
    return inventoryItems.find(inv => {
      const invName = inv.item_name || inv.name || '';
      return String(invName).trim().toLowerCase() === rowName;
    }) || null;
  }, [inventoryItems]);

  const rowRequiresExpiry = useCallback((row) => {
    if (expiryRequirements.size === 0 && expiryRequiredNames.size === 0) return false;

    if (row?.item_id && expiryRequirements.has(row.item_id)) return true;

    const rowName = String(row?.item_name || '').trim().toLowerCase();
    if (!rowName) return false;

    // S3: match the row's own text against the requirement list's names — stored AND
    // translated. Comparing only against the translated inventory list meant a non-English
    // business never matched, because the requirement list spoke a different language.
    if (expiryRequiredNames.has(rowName)) return true;

    const matched = resolveInventoryItemForRow(row);
    return Boolean(matched && expiryRequirements.has(matched.item_id));
  }, [expiryRequirements, expiryRequiredNames, resolveInventoryItemForRow]);

  // **NEW: Comprehensive Field Validation for Final Commit Block**
  const validateMandatoryFields = () => {
    const validationErrors = {};
    let hasErrors = false;

    // Same reason as handleSubmitClick (H6/M6): this runs as the FIRST step of the submit
    // ladder, including on a re-entry whose closure predates the writes it must judge.
    itemsRef.current.forEach((item, idx) => {
      const errors = {};

      // Item name
      if (!item.item_name || !item.item_name.trim()) {
        errors.item_name = 'Item name required';
        hasErrors = true;
      }

      // Quantity (positive)
      const qty = parseFloat(item.quantity);
      if (!item.quantity || isNaN(qty) || qty <= 0) {
        errors.quantity = 'Quantity cannot be zero';
        hasErrors = true;
      }

      // Unit
      if (!item.unit || !item.unit.trim()) {
        errors.unit = 'Unit required';
        hasErrors = true;
      }

      // Unit price (positive) — UNLESS the line is a declared free/replacement one.
      //
      // M7 — this check rejected every zero-price line before it could reach the gate, so
      // PieceFreeLinePopup was dead code in this door and §3.3's "free samples and
      // replacements are real" was unreachable from the form. The exemption is narrow on
      // purpose: it applies ONLY when the row is tagged AND carries a typed reason, which
      // is the same pair the gate itself demands. An untagged zero-price line is still a
      // mistake, and still refused here.
      const price = parseFloat(item.unit_price);
      //
      // F7 — AND ONLY ON A pcs ROW. `is_free_replacement` / `free_reason` are plain row
      // fields; the tagging UI only renders on pcs rows, but nothing removed the tags when
      // the unit changed. So: tag a pcs row free, switch it to kg, and a ₹0 line passed
      // this check. The server did not catch it either (Layer A reads 0 as "a price is
      // present", Layer B returns early, Layer C only runs on pcs), and the ₹0 batch then
      // dragged the item's typical cost down.
      //
      // handleInputChange also clears the three tags when a row leaves pcs, so this is the
      // second of three layers rather than the only one.
      const declaredFree = isPcsUnit(item.unit)
        && item.is_free_replacement === true
        && String(item.free_reason || '').trim().length >= 3;
      if (!declaredFree && (!item.unit_price || isNaN(price) || price <= 0)) {
        errors.unit_price = 'Price cannot be zero';
        hasErrors = true;
      }

      // Category
      if (!item.category || !item.category.trim()) {
        errors.category = 'Category required';
        hasErrors = true;
      }

      // Expiry date (for items requiring it). Resolved by the shared rule so a manually
      // typed row — which carries no item_id — is held to the same requirement as a
      // matched one. This is the check that fires BEFORE the confirm modal and names the
      // offending item, which is why it has to be the accurate one.
      if (rowRequiresExpiry(item) && !item.expiry_date) {
        errors.expiry_date = 'Expiry date mandatory for perishables';
        hasErrors = true;
      }

      if (Object.keys(errors).length > 0) {
        validationErrors[idx] = errors;
      }
    });

    setFieldValidationErrors(validationErrors);
    setCommitBlocked(hasErrors);
    return { isValid: !hasErrors, errors: validationErrors };
  };

  /**
   * T1 — the bill-total check. ADVISORY, never a hard stop.
   *
   * This rule used to exist twice with two different thresholds: here, forgivingly, at ₹10,
   * and again on the Submit button as `difference !== 0` — exact, to the paisa. The button
   * won, because it disabled itself before this code could ever run, so the tolerant version
   * with the explanatory message was unreachable and the person got a grey button and
   * silence. One rule, one place, and it is this one.
   *
   * It returns a WARNING rather than an error because the OCR's reading of a photograph does
   * not outrank the person who has just checked every line against the paper in front of
   * them. Bills legitimately differ from the sum of their items — transport, packing, old
   * dues, round-off — and a hard block on an advisory number is what produced the original
   * fault. The real integrity checks live in the backend gate and run regardless.
   */
  const validateTotalBlock = () => {
    if (!financialAudit.auditEnabled) return { valid: true };
    // T1 — a comparison that is not meaningful must not be reported as if it were.
    if (financialAudit.comparable === false) return { valid: true };

    const difference = Math.abs(financialAudit.difference);
    if (difference <= 10) return { valid: true };
    // Already answered, for THIS difference. A different gap is a different question.
    if (ackedTotalDifferenceRef.current !== null
        && Math.abs(ackedTotalDifferenceRef.current - difference) < 0.01) {
      return { valid: true };
    }

    const against = financialAudit.comparedAgainst || 'total';
    const billFigure = financialAudit.comparedAgainst === 'subtotal'
      ? financialAudit.ocrSubtotal
      : financialAudit.ocrGrandTotal;
    return {
      valid: false,
      message:
        `The bill's ${against} is ₹${Number(billFigure || 0).toFixed(2)}, but the items on this form ` +
        `add up to ₹${totalCost.toFixed(2)} — a difference of ₹${difference.toFixed(2)}. ` +
        `That is normal when the bill includes transport, packing or old dues, which are not stock. ` +
        `Check the lines if you were not expecting it; otherwise carry on.`
    };
  };

  // **NEW: Check for Unmapped Units**
  const checkUnmappedUnits = () => {
    console.log('🔍 [CHECK UNMAPPED] Checking for unmapped units...');
    console.log(`   📦 Total items: ${items.length}`);
    console.log(`   🗂️  Available conversions: ${Object.keys(containerConversions).length}`);
    Object.keys(containerConversions).forEach(key => {
      console.log(`      - ${key}:`, containerConversions[key]);
    });

    // Helper function to check if a unit is a standard/known unit
    const isStandardUnit = (unitStr) => {
      const unit = String(unitStr || '').toLowerCase().trim();
      if (!unit) return false;

      // Standard weight units
      if (/^(kg|kgs|kilogram|kilograms|g|gm|gms|gram|grams|mg|mgs|milligram|milligrams|lb|lbs|pound|pounds|oz|ounce|ounces)$/i.test(unit)) return true;

      // Standard volume units
      if (/^(l|lt|ltr|ltrs|liter|litre|liters|litres|ml|milliliter|millilitre|milliliters|millilitres|gal|gallon|gallons|qt|quart|quarts|pt|pint|pints|cup|cups|fl|fluid)$/i.test(unit)) return true;

      // Standard count units
      if (/^(piece|pieces|pcs|pc|dozen|doz|unit|units|nos|no|number|numbers)$/i.test(unit)) return true;

      return false;
    };

    const unmapped = [];
    let updated = false;
    // M6: write base from the ref, so a conversion applied here cannot be built over a
    // pre-keystroke snapshot of the form.
    const copy = itemsRef.current.map(it => ({ ...it }));

    copy.forEach((item, idx) => {
      if (item.unit) {
        const unitNormalized = normalizeUnitForKey(item.unit);
        const itemName = item.item_name || '';

        console.log(`\n   🔎 [Item ${idx}] ${itemName} - ${item.quantity} ${unitNormalized}`);

        // Check if it's a standard unit
        if (isStandardUnit(unitNormalized)) {
          console.log(`      ✅ Standard unit - no conversion needed`);
        } else {
          const convKey = `${normalizeItemKey(item)}_${unitNormalized}`;
          console.log(`      🔑 Lookup key: "${convKey}"`);

          const savedConversion = containerConversions[convKey];
          if (!savedConversion) {
            console.log(`      ❌ NO conversion found - will prompt user`);
            unmapped.push({ itemIndex: idx, ...item });
          } else {
            console.log(`      ✅ Conversion found: ${savedConversion.factor} ${savedConversion.toUnitSymbol}`);
            copy[idx] = {
              ...item,
              supplier_conversion_quantity: savedConversion.factor,
              supplier_conversion_unit: savedConversion.toUnitSymbol,
              supplier_conversion_unit_display: savedConversion.toUnitDisplay
            };
            updated = true;
          }
        }
      }
    });

    if (updated) {
      console.log(`\n✅ [CHECK UNMAPPED] Applied ${copy.filter(it => it.supplier_conversion_quantity).length} saved conversion(s)`);
      // Through the door: a bulk application of saved conversions is exactly the kind of
      // programmatic change Undo has to be able to see.
      commitItemEdit(copy);
    }

    console.log(`\n📊 [CHECK UNMAPPED] Result: ${unmapped.length} unmapped unit(s)`);
    if (unmapped.length > 0) {
      unmapped.forEach(item => {
        console.log(`   ⚠️ ${item.item_name} (${item.unit}) - needs conversion`);
      });
    }

    return unmapped;
  };

  const handleSubmitClick = async () => {
    /**
     * H6 — every step below reads THIS array, not the render closure.
     *
     * The red-row modal re-enters this function immediately after writing its answers, and
     * the closure it re-entered through predates those writes: STEP 7.5 re-detected the very
     * rows the user had just classified and reopened the modal, so only the SECOND press of
     * Continue ever worked. Shadowing the closure here hardens every other step of the
     * ladder against the same class of bug at the same time — itemsRef is written
     * synchronously by applyItems/commitItemEdit, so it is always the newest truth.
     */
    const items = itemsRef.current;

    // Item 1 — re-check the closing-stock lock at save time; a count may have
    // started after this screen was opened. If locked, block and show the overlay.
    try {
      const bId = UnitMappingService.getBusinessId() || localStorage.getItem('businessId');
      if (bId) {
        const lockRes = await http.get(`/closing-stock/lock-status?businessId=${bId}`);
        if (lockRes && lockRes.success && lockRes.locked) {
          setClosingStockLocked(true);
          return;
        }
      }
    } catch (e) { /* non-fatal — fall through to normal save */ }

    // **STEP 1: Validate All Mandatory Fields First (before vendor check)**
    const validation = validateMandatoryFields();
    if (!validation.isValid) {
      const errors = validation.errors;
      const firstErrorIdx = Object.keys(errors)[0];
      const item = items[firstErrorIdx];
      const itemName = item?.item_name || `Item #${parseInt(firstErrorIdx) + 1}`;
      const firstErrorField = Object.keys(errors[firstErrorIdx])[0];
      const errorMessage = errors[firstErrorIdx][firstErrorField];

      let modalMessage = '';
      if (firstErrorField === 'quantity') {
        modalMessage = `Quantity in ${itemName} cannot be zero`;
      } else if (firstErrorField === 'unit_price') {
        modalMessage = `Price in ${itemName} cannot be zero`;
      } else if (firstErrorField === 'item_name') {
        modalMessage = 'Item name is required';
      } else if (firstErrorField === 'unit') {
        modalMessage = `Unit in ${itemName} is required`;
      } else if (firstErrorField === 'category') {
        modalMessage = `Category in ${itemName} is required`;
      } else if (firstErrorField === 'expiry_date') {
        modalMessage = `Expiry date is mandatory for ${itemName}`;
      } else {
        modalMessage = errorMessage;
      }

      showModalMessage(modalMessage, 'error');

      // Scroll to first error
      const errorElement = document.querySelector(`[data-item-index="${firstErrorIdx}"]`);
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    // **STEP 2: Check Total Block**
    // T1 — a WARNING with a Proceed. It used to be showModalMessage(..., 'error') and an
    // unconditional return: a dead end on an advisory number.
    //
    // NOT routed through PreValidationModal, though it is the right shape: that modal's
    // onProceed calls setShowConfirmation(true), which opens the final confirm dialog
    // DIRECTLY and would skip steps 4 through 9 — unmapped units, pending confirmations,
    // unit families, fuzzy rows, red-row classification, the vendor check and the piece
    // guard. Acknowledging a total mismatch must not buy a pass on any of those.
    //
    // So Continue re-enters handleSubmitClick from the top, the way the red-row modal does,
    // and this check waves the bill through on the second pass because the difference it is
    // looking at has already been acknowledged.
    const totalValidation = validateTotalBlock();
    if (!totalValidation.valid) {
      setTotalMismatchPrompt({
        message: totalValidation.message,
        difference: Math.abs(financialAudit.difference)
      });
      return;
    }


    // **EC3: Payload Size Limit — Hard cap at 100 items**
    // Prevents V8 OOM panics and JSON payload size limit errors on the backend.
    // This is a hard ceiling with a clear, actionable error message.
    if (items.length > MAX_ITEMS_PER_SUBMISSION) {
      showModalMessage(
        `⚠️ Too many items (${items.length} items).\n\n` +
        `The maximum allowed per submission is ${MAX_ITEMS_PER_SUBMISSION} items.\n` +
        `Please split this bill into multiple submissions.`,
        'error'
      );
      return;
    }

    // **STEP 4: Check Unmapped Units**
    const unmappedUnits = checkUnmappedUnits();
    if (unmappedUnits.length > 0) {
      setUnknownUnitsQueue(unmappedUnits);
      setCurrentUnknownUnitIndex(0);

      // Set the first item's data in the container conversion form
      const firstItem = unmappedUnits[0];
      setContainerConversionForm({
        itemIndex: firstItem.itemIndex,
        itemName: firstItem.item_name || 'Unknown Item',
        containerUnit: firstItem.unit || '',
        quantity: firstItem.quantity || '1',
        standardUnit: 'kg'
      });

      setShowContainerConversionModal(true);
      // Removed annoying warning modal - container modal is already showing
      return;
    }

    // **STEP 5: Check for Pending Confirmations**
    if (pendingConfirmations.size > 0) {
      showModalMessage(
        `⚠️ ${pendingConfirmations.size} item(s) need confirmation.\n\nPlease review and confirm all auto-corrections (yellow boxes).`,
        'error'
      );
      return;
    }

    // **STEP 6: Check for unit family mismatches (weight vs volume)**
    const mismatchedItems = [];
    items.forEach((item, idx) => {
      const itemName = (item.item_name || '').trim();
      if (!itemName || itemName.length <= 2) return;

      // Find matching inventory item to get DB default unit
      const matchedInv = inventoryItems.find(inv => {
        const invName = (inv.item_name || inv.name || '').toLowerCase();
        return invName === itemName.toLowerCase();
      });

      if (!matchedInv) return; // No DB match, skip check

      const dbUnit = matchedInv.unit_symbol || matchedInv.unit || '';
      const ocrUnit = item.unit || '';
      if (!dbUnit || !ocrUnit) return;

      const dbFamily = getUnitFamily(dbUnit);
      const ocrFamily = getUnitFamily(ocrUnit);

      // Only flag cross-family mismatches: weight↔volume
      // count units and unknown units are flexible, don't flag them
      if (dbFamily !== 'unknown' && ocrFamily !== 'unknown' &&
        dbFamily !== 'count' && ocrFamily !== 'count' &&
        dbFamily !== ocrFamily) {
        mismatchedItems.push({
          itemName,
          dbUnit,
          dbFamily,
          ocrUnit,
          ocrFamily,
          index: idx
        });
      }
    });

    if (mismatchedItems.length > 0) {
      setUnitMismatchItems(mismatchedItems);
      setShowUnitMismatchModal(true);
      return;
    }

    // **STEP 7: Block Fuzzy (Yellow) Items**
    const yellowItems = items.filter((item) => {
      const matchStatus = item.matchStatus || item.match_type || 'pending';
      return matchStatus === 'fuzzy' && (item.item_name || '').trim().length > 2;
    });

    // Bill Memory screen: no popup — the first row that needs a tap is scrolled to and highlighted.
    // (Red rows are STEP 7.5's.)
    if (bmScreenOnRef.current) {
      const firstToTap = items.findIndex(it => (it.item_name || '').trim().length > 2 && it.matchStatus !== 'none' && rowNeedsTap(it));
      if (firstToTap >= 0) {
        bmScrollToRow(firstToTap);
        return;
      }
    } else if (yellowItems.length > 0) {
      const itemNames = yellowItems.map(it => `• ${it.item_name}`).join('\n');
      showModalMessage(
        `⚠️ Please select correct items to enter\n\n` +
        `${yellowItems.length} item(s) in YELLOW status (uncertain match):\n\n${itemNames}`,
        'error'
      );
      return;
    }

    // **STEP 7.5: Classify RED (unmatched) items — ONE decision moment, not one per item.**
    // A red row is not in inventory. Submitting it as-is creates a brand-new inventory item
    // silently, which is how cleaning supplies end up costed as ingredients. Rows the user
    // has already classified (by choosing a category themselves) are not asked about again.
    const redItems = items
      .map((item, index) => ({ item, index }))
      .filter(({ item }) => {
        const matchStatus = item.matchStatus || item.match_type || 'pending';
        return matchStatus === 'none'
          && (item.item_name || '').trim().length > 2
          && !item.redClassified;
      });

    if (redItems.length > 0 && bmScreenOnRef.current) {
      bmScrollToRow(redItems[0].index);
      return;
    }
    if (redItems.length > 0) {
      setRedRowDecision({
        rows: redItems.map(({ item, index }) => ({
          index,
          item_name: item.item_name,
          // H6 — NOT pre-selected. Seeding every row with 'non_consumable' and leaving
          // Continue enabled meant one click silently classified every unmatched row as a
          // non-consumable without the user reading a single one of them, which is exactly
          // what this screen exists to prevent. Non-consumable is still hinted as the
          // likely answer in the UI; it is not chosen for them.
          choice: null
        }))
      });
      return;
    }

    // **STEP 8: Vendor Check - Show modal if missing (MOVED AFTER OTHER VALIDATIONS)**
    if (!vendorName.trim()) {
      showModalMessage('Vendor not detected. Please enter the Vendor Name and Phone Number.', 'error');
      return;
    }

    // Make Phone Number mandatory for new vendors
    // A vendor counts as EXISTING only when it carries a real vendor_id — i.e. it came out
    // of the vendor table. Name-matching alone let a synthetic `{ name }` object stand in
    // for a real vendor and switch this rule off; requiring the id closes that off at the
    // gate as well as at the source.
    const isNewVendor = !selectedVendor
      || !selectedVendor.vendor_id
      || String(selectedVendor.name || '').toLowerCase() !== vendorName.trim().toLowerCase();
    if (isNewVendor && !vendorPhone.trim()) {
      showModalMessage('Phone Number is mandatory for new vendors. Please enter the Phone Number.', 'error');
      return;
    }

    // A present-but-wrong number is worse than a blank one: it looks answered, so nobody
    // ever fills it in, and the business finds out when it needs to call about a delivery.
    // Checked for EVERY vendor, not just new ones — an existing vendor's stored number can
    // be edited here too, and OCR autofill writes straight into this field.
    // A legacy number carried in from the vendor's own record is warned about, not blocked
    // (H1) — the user did not type it, there is no migration for it, and blocking would
    // make every bill for that vendor unrecordable.
    const submitPhoneError = isLegacyStoredPhone ? null : vendorPhoneError(vendorPhone);
    if (submitPhoneError) {
      setPhoneTouched(true);
      showModalMessage(submitPhoneError, 'error');
      if (phoneInputRef.current) {
        phoneInputRef.current.focus();
        phoneInputRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    // **STEP 8.5: Block submit if any item has an unresolved unit.**
    // A missing unit means OCR/normalization couldn't determine it — never save with a guessed unit.
    const itemsMissingUnit = items.filter(it => !it.unit || !String(it.unit).trim());
    if (itemsMissingUnit.length > 0) {
      const names = itemsMissingUnit.map(it => it.item_name || it.original_ocr_name || 'Unnamed item').join(', ');
      showModalMessage(`Please select a unit for: ${names}. Units could not be detected from the bill.`, 'error');
      setIsSubmitting(false);
      return;
    }

    // **STEP 8.6: The repeat-purchase check — packet items read their own definition back.**
    //
    // Last on the ladder before the bill leaves, because it is the only step that shows the
    // person a number from the PAST: asking it earlier, while rows are still being fixed,
    // would confirm a quantity they then change. Single-piece items are not in the queue at
    // all (see buildPackConfirmQueue) — "1 coconut" is not a question.
    //
    // Each answer is recorded against the item id, so re-entering here after the last
    // confirmation walks straight through rather than asking again.
    {
      const needConfirm = await buildPackConfirmQueue(items);
      if (needConfirm.length > 0) {
        setPackConfirmQueue(needConfirm);
        return;
      }
    }

    // **STEP 8.7: The Stock-In Safety Engine's entry guard (inert unless enabled — see cxStep).**
    // Last before pre-validate, so it judges the rows every earlier step has finished
    // fixing; a question it asks here is one DSF and the gate then do not ask again.
    if (await cxStep(items)) return;

    // **STEP 9: Pre-Validation Call (Check for warnings before submission)**
    try {
      const response = await http.post('/stock-in/v2/pre-validate', {
        items: items.map(item => ({
          item_name: item.item_name,
          quantity: item.quantity,
          unit: item.unit,
          total_price: item.total_price || item.unit_price,
          category: item.category
        }))
      });

      if (response.success && response.data) {
        // cx — a price warning about a row the safety popup already settled is not asked twice.
        response.data.warnings = cxFilterWarnings(response.data.warnings, items, cxResolvedUidsRef.current);
        const { warnings, errors, canProceed } = response.data;

        // If there are errors, block submission
        if (errors && errors.length > 0) {
          setPreValidationData({ warnings: [], errors });
          setShowPreValidationModal(true);
          return;
        }

        // If there are warnings, show them for user confirmation
        if (warnings && warnings.length > 0) {
          setPreValidationData({ warnings, errors: [] });
          setShowPreValidationModal(true);
          return;
        }

        // No warnings or errors, proceed
        setShowConfirmation(true);
      } else {
        // Pre-validation failed, but don't block - show confirmation anyway
        console.warn('Pre-validation failed:', response);
        setShowConfirmation(true);
      }
    } catch (error) {
      console.error('Pre-validation error:', error);
      // Don't block submission on pre-validation error - show confirmation anyway
      setShowConfirmation(true);
    }
  };

  const handleSubmit = async () => {
    /**
     * T3 — THE double-submit guard, now that the button no longer is one.
     *
     * A ref, not state: it has to be readable and writable synchronously, in the same tick
     * as the second click. `isSubmitting` is state, so two taps inside one React batch both
     * read `false` and both fire — which is precisely why the old defence had to be the
     * disabled attribute rather than the flag itself.
     *
     * Belt and braces behind this: every payload carries idempotencyKeyRef, and the backend
     * returns the ORIGINAL success for a repeated key (EC1) rather than writing stock twice.
     * The key is regenerated after each successful submit, so the next bill is never
     * mistaken for a replay of this one.
     */
    if (submitInFlightRef.current) {
      console.warn('[StockIn] submit already in flight — ignoring the repeat tap');
      return;
    }
    submitInFlightRef.current = true;
    // Validation already done in handleSubmitClick, proceed with submission
    setIsSubmitting(true); // showModalMessage('Submitting...', 'loading'); // Removed: user doesn't want popup
    // The exact body sent below. A confirmation pop-up resubmits THIS, not a hand-rebuilt
    // copy: the copies in the catch dropped tax, the with-tax total, pack conversions, free
    // pieces, earlier answers and the normalised unit, so a confirmed bill saved different
    // numbers from the ones that were checked.
    let sentPayload = null;
    try {
      // Validate form again as a safety check
      validateForm();


      // **Generate batch numbers if missing (client-side temporary, backend will override)**
      const today = productionDate ? new Date(productionDate) : new Date();
      const datePrefix = today.toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' }).replace(/-/g, ''); // YYYYMMDD
      let batchCounter = 1;

      const itemsWithBatch = items.map(it => {
        if (!it.batch_number || it.batch_number.trim() === '') {
          const batchNum = `${datePrefix}-${String(batchCounter).padStart(5, '0')}`;
          batchCounter++;
          return { ...it, batch_number: batchNum };
        }
        return it;
      });

      // Check if any items are marked as "create new" (user clicked + option)
      const hasNewItems = itemsWithBatch.some(it => it.createAsNew === true);

      // PIECE GUARD: the catch below needs the exact body that was sent, so the piece
      // conversation can resubmit it with the human's answers attached. `payload` is
      // block-scoped to this try, so it is mirrored onto a variable the catch can see.
      // Continuous Closing Stock (spec §8.1, T1): a bill dated the pending count's day or
      // the day after, entered while that count is pending, asks whether its goods were on
      // the shelf when the stock was counted. Yes = they are inside the count, so the
      // register places them on the count date; No keeps the invoice date. Nothing else asks.
      let goodsReceivedDate = null;
      try {
        const st = await http.get('/ccs/status');
        const countDay = st && st.continuous ? st.pending_count_date : null;
        if (countDay && productionDate) {
          const next = new Date(`${countDay}T00:00:00Z`);
          next.setUTCDate(next.getUTCDate() + 1);
          if (productionDate === countDay || productionDate === next.toISOString().slice(0, 10)) {
            if (window.confirm(`A closing count for ${countDay} is still pending.

Were these goods on the shelf when the stock was counted?

OK = yes, they were counted
Cancel = no, they arrived after the count`)) {
              goodsReceivedDate = countDay;
            }
          }
        }
      } catch (_) { /* a convenience question — the bill saves either way */ }

      const payload = {
        vendor_name: vendorName.trim(),
        vendor_phone: vendorPhone.trim(),
        entry_method: ocrData ? 'Scan Bill' : 'Manual Entry', // Track entry method
        scanned_image_id: ocrData?.image_id || null, // Link to scanned image if from OCR
        // Bill's own printed invoice number/date — powers duplicate-bill detection
        invoice_number: ocrData?.invoice_number || null,
        invoice_date: ocrData?.invoice_date || null,
        ocr_grand_total: financialAudit.ocrGrandTotal || null,
        calculated_total: totalCost,
        cgst: displayCgst,
        sgst: displaySgst,
        total_with_tax: taxForStockInEnabled ? totalCost : totalCost + displayCgst + displaySgst,
        skip_nar: hasNewItems, // Skip NAR for items user explicitly marked as new
        stock_in_date: productionDate || null, // Pass custom date if provided (for onboarding)
        goods_received_date: goodsReceivedDate,
        // Edge Case 1: Include idempotency key to prevent duplicate submissions
        idempotency_key: idempotencyKeyRef.current,
        // S4 — answers already given travel with the bill. Without this key the gate began
        // every submit with an empty confirmation list, so cancelling a piece conversation
        // and pressing Submit again meant answering every question a second time.
        // F6 — re-pointed at the array actually being sent. A token whose row no longer
        // exists is DROPPED, never re-pointed at whatever now sits at that index: a
        // deleted row's override must die with it, not migrate to its neighbour.
        item_confirmations: remapConfirmationsToRows(answeredConfirmations, itemsWithBatch),
        items: itemsWithBatch.map(it => {
          const unitNormalized = normalizeUnitForKey(it.unit || '');
          const convKey = `${normalizeItemKey(it)}_${unitNormalized}`;
          const savedConversion = containerConversions[convKey];
          const supplierConversionQuantity = it.supplier_conversion_quantity ?? savedConversion?.factor ?? null;
          const supplierConversionUnit = it.supplier_conversion_unit ?? savedConversion?.toUnitSymbol ?? null;
          const supplierConversionUnitDisplay = it.supplier_conversion_unit_display ?? savedConversion?.toUnitDisplay ?? null;
          return {
            // Send English translation for DB storage, but keep original for display
            // Resolved inventory id (matcher / suggestion chip). Backend verifies
            // ownership and skips NAR for these rows. Null for new/edited names.
            item_id: it.createAsNew ? null : (it.item_id || null),
            item_name: it.is_translated ? (it.db_name || '').trim() : (it.item_name || '').trim(),
            display_name: (it.item_name || '').trim(), // Original text shown to user
            category: it.category,
            // M7 §9.7 — the free goods are STOCK. "Buy 10, get 1 free" puts eleven on the
            // shelf, and recording ten is a shortage the closing count will find and
            // nobody will be able to explain. The MONEY is untouched, which is what makes
            // the per-piece rate come out diluted — and `is_scheme` below is what tells
            // the gate to allow that rather than raise a low-price alarm.
            quantity: Number(parseFloat(it.quantity) || 0) + Number(parseFloat(it.free_quantity) || 0),
            // Always send a canonical Strict-5 symbol (backend enforceBaseUnits accepts only kg/g/L/ml/pcs)
            unit: UnitMappingService.normalizeUnitSymbol(it.unit),
            // ── M7 — the three tags the gate has always read and the form never sent ──
            // stockInGate Layer C reads it.is_scheme / is_free_replacement / free_reason
            // (and the Joi schema has always declared all three). The payload simply never
            // carried them, so a scheme line was questioned as a price anomaly and
            // PieceFreeLinePopup was unreachable code in this door.
            is_scheme: it.is_scheme === true || Number(parseFloat(it.free_quantity) || 0) > 0,
            is_free_replacement: it.is_free_replacement === true,
            free_reason: (it.free_reason || '').trim() || null,
            // T2 — through the one helper: a '0' slab is not tax, and a scanned row whose
            // with-tax figure was never computed must not submit as zero.
            total_price: grossLineTotal(it, taxForStockInEnabled),
            batch_number: (it.batch_number || '').trim(),
            expiry_date: it.expiry_date,
            cgst: Number(parseFloat(it.cgst) || 0),
            sgst: Number(parseFloat(it.sgst) || 0),
            tax_slab: String(parseFloat(it.tax_slab) || 0),
            time: it.time,
            client_lock: false, // Items remain editable - no locking
            original_ocr_name: it.original_ocr_name || '',
            original_language: it.original_language || 'en',
            translated_name: it.translated_name || '',
            is_translated: it.is_translated || false,
            supplier_conversion_quantity: supplierConversionQuantity != null ? Number(supplierConversionQuantity) : null,
            supplier_conversion_unit: supplierConversionUnit || null,
            supplier_conversion_unit_display: supplierConversionUnitDisplay || null,
            // Bill Memory: what was printed or typed and how the row was settled (server ignores while Off)
            ...buildBmItemFields(it)
          };
        }),
        ...buildBmTopFields({ deletedLines: bmDeletedLinesRef.current, addedBack: bmAddedBackRef.current, vendorCertain: bmVendorCertain, vendorId: selectedVendor?.vendor_id, rows: itemsWithBatch })
      };

      // PIECE GUARD — see the note on `payload` above.
      pieceSubmittedPayloadRef.current = payload;
      sentPayload = payload;

      // **NEW: Attempt submission - handle confirmation requirements**
      let r;
      try {
        r = await makeApiCall('', payload);
      } catch (err) {
        // Check if backend is requesting confirmation
        if (err.response && err.response.requiresConfirmation) {
          const confirmData = err.response;

          // Show auto-correction confirmation modal
          setAutoCorrectionData(confirmData);
          setPendingSubmitData(payload);
          setShowAutoCorrectionModal(true);
          setIsSubmitting(false);
          return; // Wait for user response
        }

        // Check for currency conversion requirement
        if (err.response && err.response.requires_manual_rate) {
          setCurrencyConversionData(err.response);
          setShowCurrencyModal(true);
          setIsSubmitting(false);
          return;
        }

        // Regular error
        throw err;
      }

      // P2/P4 — THE ONE SUCCESS PATH. Everything that used to be written out inline here
      // — the idempotency-key regeneration, the tax-toggle revert, haptics, vendor
      // learning, expiry auto-enable, onSuccess(), the draft removal and the notifications
      // refresh — now lives in finalizeSuccessfulSubmit, because there was a SECOND copy of
      // it on the piece path that had quietly lost half of them. Two hand-maintained
      // success paths was the bug; there is one.
      const saved = await finalizeSuccessfulSubmit(r);

      // Check for NAR resolutions and show popup first
      if (r.nar_resolutions && r.nar_resolutions.length > 0) {
        // Store the current form state before showing popup
        setNarOriginalFormData({
          vendor: vendorName,
          vendorPhone: vendorPhone,
          productionDate: productionDate,
          currentShift: currentShift,
          items: [...items], // Deep copy of items
          ocrData: ocrData
        });

        setNarResolutions(r.nar_resolutions);
        setShowNarResolution(true);

        // Wait for user to close the modal before showing success
        await new Promise(resolve => {
          const checkInterval = setInterval(() => {
            if (!showNarResolution) {
              clearInterval(checkInterval);
              resolve();
            }
          }, 100);
        });
      }

      // A replayed key is NOT a save. finalizeSuccessfulSubmit has already told the person
      // that the bill was already recorded, and which record it is; showing a success on
      // top of that is exactly the silence P2 exists to end.
      if (saved) {
        // Check for DSF corrections in response
        let successMessage = `Submitted. ID: ${r.data.stock_in_id}${savedOnLine()}`;
        if (r.dsf_corrections && r.dsf_corrections.length > 0) {
          successMessage += '\n\n✅ Auto-Corrections Applied (DSF):';
          r.dsf_corrections.forEach(corr => {
            successMessage += `\n• ${corr.item_name}: ${corr.original_quantity} → ${corr.corrected_quantity}`;
            successMessage += `\n  ${corr.message}`;
          });
        }

        showModalMessage(successMessage, 'success');
      }

      // Form clearing is now handled by the success modal OK button
    } catch (error) {
      // Close any existing NAR popup first
      setShowNarResolution(false);
      setNarResolutions([]);

      // Bill Memory: the screen was stale for some rows — show the server's answers there and
      // scroll to the first. Nothing was written; no popup (design §12).
      if (error.data?.code === 'bm_rows_need_tap' && Array.isArray(error.data.rows)) {
        bmApplyServerAnswers(error.data.rows);
        setIsSubmitting(false);
        return;
      }

      // Check if backend requires item selection (NAR ambiguity)
      // Note: httpClient uses error.data, not error.response.data
      if (error.data?.requiresItemSelection) {
        const data = error.data;

        // Sort candidates by deviation (lowest = best match)
        const sortedCandidates = [...(data.candidates || [])].sort((a, b) => {
          const devA = Math.abs(a.deviationPercent || 999);
          const devB = Math.abs(b.deviationPercent || 999);
          return devA - devB;
        });

        // Pre-select the best match (lowest deviation)
        // If auto-resolved, use selectedItemId; otherwise use first candidate
        const preSelectedId = data.autoResolved && data.selectedItemId
          ? data.selectedItemId
          : (sortedCandidates.length > 0 ? sortedCandidates[0].item_id : null);

        // Store selection data and show modal
        setItemSelectionData({
          ...data,
          candidates: sortedCandidates,
          selectedItemId: preSelectedId // Pre-select best match
        });
        setShowItemSelectionModal(true);
        setIsSubmitting(false);
        return;
      }

      // The validation gate blocked the bill (contract caps or catastrophic price).
      // Caps/contract blockers must be FIXED (no override exists). A catastrophic
      // price blocker carries `requires` — an explicit per-item acknowledgment
      // token the user can send to force it through (committed as anomaly).
      // ── PIECE GUARD §7 — the piece conversation owns its own blockers ──────────
      // Handled BEFORE the generic gate handling below, which would otherwise render
      // these as a wall of text in an alert. The whole value of this feature is in the
      // wording: "this looks like a packet was entered as 1 piece" is a one-tap fix,
      // while the same fact inside a bulleted error list is a puzzle that gets clicked
      // past. Piece blockers therefore never reach the generic path.
      const route = routeGateBlockers(error.data?.gate_blockers);

      if (route.piece.length > 0) {
        const bulk = error.data.piece_bulk_review || null;
        setPieceGate({
          blockers: route.piece,
          // §7 Popup D: 3+ flags means ONE review screen, never a queue of popups.
          bulkReview: bulk,
          activeBlocker: bulk ? null : route.piece[0],
          // S4 — answers already given are carried in, so a person who cancelled a
          // conversation and resubmitted does not re-answer everything.
          confirmations: (Array.isArray(pieceSubmittedPayloadRef.current?.item_confirmations)
            ? pieceSubmittedPayloadRef.current.item_confirmations
            : []).filter(isValidConfirmationToken),
          resolvedIndexes: [],
          payload: pieceSubmittedPayloadRef.current,
          // H7.2 — the OTHER blockers on this bill are not swallowed. They are shown
          // alongside the piece conversation and re-raised by the gate on the resubmit,
          // where this same router opens the modal each one needs.
          deferredBlockers: route.deferred,
        });
        setIsSubmitting(false);
        return;
      }

      if (route.hasWork) {
        const blockers = error.data.gate_blockers;
        const hardBlockers = route.hard;
        const overridable = route.overridable;

        if (hardBlockers.length > 0 || !overridable) {
          showModalMessage(
            `🚫 Submission blocked:\n\n${blockers.map(b => `• ${b.message}`).join('\n')}\n\nPlease fix the highlighted values and submit again.`,
            'error'
          );
          setIsSubmitting(false);
          return;
        }

        // An overridable blocker → offer the explicit acknowledgment modal. The mapping
        // lives in ONE place (gateBlockerToCorrectionData) and is shared with the resubmit
        // path below, so the two can never again disagree about what a quantity blocker
        // looks like.
        setAutoCorrectionData(gateBlockerToCorrectionData(overridable));

        // Store the original submission payload (no bypass flags — the confirm
        // handler attaches the per-item acknowledgment token).
        // DATE FIX: stock_in_date MUST be included here. Without it, the resubmit
        // after confirming a price/quantity warning defaults to today — this is exactly
        // the bug that shows 'saved under 1 Sep' when the picker showed '30 Aug'.
        // The bill exactly as it was sent (see sentPayload); the copy is only a fallback.
        setPendingSubmitData(sentPayload || {
          vendor_name: vendorName.trim(),
          vendor_phone: vendorPhone.trim(),
          ocr_grand_total: financialAudit.ocrGrandTotal || null,
          calculated_total: totalCost,
          skip_nar: items.some(it => it.createAsNew === true),
          stock_in_date: productionDate || null,
          items: items.map(it => ({
            // Resolved inventory id (matcher / suggestion chip). Backend verifies
            // ownership and skips NAR for these rows. Null for new/edited names.
            item_id: it.createAsNew ? null : (it.item_id || null),
            item_name: it.is_translated ? (it.db_name || '').trim() : (it.item_name || '').trim(),
            display_name: (it.item_name || '').trim(),
            category: it.category,
            quantity: Number(parseFloat(it.quantity) || 0),
            unit: it.unit,
            total_price: Number(parseFloat(it.total_price) || 0),
            batch_number: (it.batch_number || '').trim(),
            expiry_date: it.expiry_date,
            time: it.time,
            original_ocr_name: it.original_ocr_name || '',
            ...buildBmItemFields(it), // Bill Memory (server ignores while Off)
            original_language: it.original_language || 'en',
            translated_name: it.translated_name || '',
            is_translated: it.is_translated || false
          }))
        });

        setShowAutoCorrectionModal(true);
        setIsSubmitting(false);
        return;
      }

      // Check if backend returned a confirmation requirement (DSF corrections or price outliers)
      if (error.data?.requiresConfirmation) {
        const data = error.data;
        const itemDetails = data.itemDetails || {};

        // Show auto-correction modal instead of window.confirm alert
        setAutoCorrectionData({
          requiresConfirmation: true,
          item_name: itemDetails.item_name || 'Unknown',
          original_unit_price: itemDetails.entered_unit_price || itemDetails.entered_price || 0,
          corrected_unit_price: itemDetails.corrected_unit_price || itemDetails.historical_vcp || 0,
          corrected_quantity: itemDetails.corrected_quantity,
          original_total: itemDetails.entered_total || 0,
          corrected_total: itemDetails.corrected_total || itemDetails.expected_total || 0,
          correction_type: data.confirmationType || 'VCP_HIGH_PRICE_CONFIRMATION',
          correction_message: data.confirmationPrompt || data.message || 'Please confirm the entered price.',
          confidence: 0.95,
          vcp: itemDetails.historical_vcp || 0,
          deviation_percentage: itemDetails.deviation_percentage != null
            ? Number(itemDetails.deviation_percentage).toFixed(1)
            : (itemDetails.entered_price && itemDetails.historical_vcp
                ? Math.abs(((itemDetails.entered_price - itemDetails.historical_vcp) / itemDetails.historical_vcp) * 100).toFixed(1)
                : null),
          deviation_direction: itemDetails.deviation_direction || '',
          isConfirmationOnly: true,
          isBackendConfirmation: true, // Flag to indicate this came from backend
          // Stage 1.2 handshake: which item this covers and the exact token to send back
          itemIndex: Number.isInteger(data.itemIndex) ? data.itemIndex : null,
          requiredConfirmation: data.required_confirmation || null
        });

        // Store the original submission payload to resubmit after confirmation.
        // NO bypass flag — the confirm handler applies the corrected values and
        // attaches a per-item item_confirmations token instead.
        // DATE FIX: stock_in_date MUST travel with this payload, same reason as above.
        // The bill exactly as it was sent (see sentPayload); the copy is only a fallback.
        setPendingSubmitData(sentPayload || {
          vendor_name: vendorName.trim(),
          vendor_phone: vendorPhone.trim(),
          ocr_grand_total: financialAudit.ocrGrandTotal || null,
          calculated_total: totalCost,
          skip_nar: items.some(it => it.createAsNew === true),
          stock_in_date: productionDate || null,
          items: items.map(it => ({
            // Resolved inventory id (matcher / suggestion chip). Backend verifies
            // ownership and skips NAR for these rows. Null for new/edited names.
            item_id: it.createAsNew ? null : (it.item_id || null),
            item_name: it.is_translated ? (it.db_name || '').trim() : (it.item_name || '').trim(),
            display_name: (it.item_name || '').trim(),
            category: it.category,
            quantity: Number(parseFloat(it.quantity) || 0),
            unit: it.unit,
            unit_price: Number(parseFloat(it.unit_price) || 0),
            batch_number: (it.batch_number || '').trim(),
            expiry_date: it.expiry_date,
            time: it.time,
            client_lock: false,
            original_ocr_name: it.original_ocr_name || '',
            ...buildBmItemFields(it), // Bill Memory (server ignores while Off)
            original_language: it.original_language || 'en',
            translated_name: it.translated_name || '',
            is_translated: it.is_translated || false
          }))
        });

        setShowAutoCorrectionModal(true);
        setIsSubmitting(false);
        return;
      } else {
        // Pillar 2: Display the clean error message from the backend.
        // Backend now maps raw PG codes to human-readable strings.
        // Priority: error.data.error (Pillar 2 clean msg) > error.data.message > generic
        const backendError   = error.data?.error   || error.data?.message || '';
        const backendDetails = error.data?.details  || '';
        const refId          = error.data?.ref_id   || '';

        // Build user-facing message
        let formattedError = backendError || error.message || 'Submission failed. Please try again.';

        // If it's a unit type mismatch error, strip emoji for cleaner display
        if (formattedError.includes('Unit type mismatch')) {
          formattedError = formattedError.replace(/❌\s*/, '');
        }

        // Append Reference ID for JS crashes (Pillar 5 Black Box)
        if (refId) {
          formattedError += `\n\n📋 Error Reference: ${refId}\n(Please share this with support)`;
        }

        // Show backend details in console for developers
        if (backendDetails && backendDetails !== formattedError) {
          console.error('[StockIn] Backend error details:', backendDetails);
        }

        showModalMessage(formattedError, 'error');
      }
    } finally {
      submitInFlightRef.current = false;
      setIsSubmitting(false);
    }
  };

  // Vendor Modal Component
  const VendorModal = ({ message, onClose, vendorInputRef }) => {
    const handleClose = () => {
      onClose();

      // Focus vendor name input and add red outline for 2 seconds
      if (vendorInputRef && vendorInputRef.current) {
        vendorInputRef.current.focus();
        vendorInputRef.current.style.outline = '2px solid #dc3545';
        vendorInputRef.current.style.outlineOffset = '2px';

        setTimeout(() => {
          if (vendorInputRef.current) {
            vendorInputRef.current.style.outline = '';
            vendorInputRef.current.style.outlineOffset = '';
          }
        }, 2000);
      }
    };

    return (
      <div
        className="stockin-vendor-modal-overlay"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 10000,
          padding: '20px'
        }}
        onClick={handleClose}
      >
        <div
          className="stockin-vendor-modal-content"
          style={{
            backgroundColor: '#ffffff',
            padding: '30px 25px 25px 25px',
            borderRadius: '12px',
            textAlign: 'center',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '20px'
          }}
          onClick={e => e.stopPropagation()}
        >
          <FontAwesomeIcon
            icon={faTimes}
            className="stockin-vendor-modal-icon"
            style={{
              fontSize: '48px',
              color: '#dc3545'
            }}
          />
          <p className="stockin-vendor-modal-message" style={{
            margin: 0,
            padding: 0,
            lineHeight: '1.6',
            fontWeight: 600,
            color: '#000',
            whiteSpace: 'pre-line',
            textAlign: 'center',
            width: '100%'
          }}>
            {message}
          </p>
          <button
            className="stockin-vendor-modal-ok-btn"
            style={{
              padding: '10px 24px',
              border: 'none',
              backgroundColor: '#000000',
              color: '#ffffff',
              cursor: 'pointer',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: 600,
              minWidth: '80px'
            }}
            onClick={handleClose}
          >
            OK
          </button>
        </div>
      </div>
    );
  };

  // Yellow Items Modal Component
  const YellowItemsModal = ({ message, onClose }) => {
    const handleClose = () => {
      onClose();
    };

    return (
      <div
        className="stockin-yellow-items-modal-overlay"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 10000,
          padding: '20px'
        }}
        onClick={handleClose}
      >
        <div
          className="stockin-yellow-items-modal-content"
          style={{
            backgroundColor: '#ffffff',
            padding: '30px 25px 25px 25px',
            borderRadius: '12px',
            textAlign: 'center',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '20px'
          }}
          onClick={e => e.stopPropagation()}
        >
          <FontAwesomeIcon
            icon={faTimes}
            className="stockin-yellow-items-modal-icon"
            style={{
              fontSize: '48px',
              color: '#ff9800'
            }}
          />
          <p className="stockin-yellow-items-modal-message" style={{
            margin: 0,
            padding: 0,
            lineHeight: '1.6',
            fontWeight: 600,
            color: '#000',
            whiteSpace: 'pre-line',
            textAlign: 'left',
            width: '100%'
          }}>
            {message}
          </p>
          <button
            className="stockin-yellow-items-modal-ok-btn"
            style={{
              padding: '10px 24px',
              border: 'none',
              backgroundColor: '#000000',
              color: '#ffffff',
              cursor: 'pointer',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: 600,
              minWidth: '80px'
            }}
            onClick={handleClose}
          >
            OK
          </button>
        </div>
      </div>
    );
  };

  // Unit Mismatch Modal Component
  const UnitMismatchModal = ({ items: mismatchItems, onClose }) => {
    return (
      <div
        className="stockin-unit-mismatch-modal-overlay"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 10000,
          padding: '20px'
        }}
        onClick={onClose}
      >
        <div
          className="stockin-unit-mismatch-modal-content"
          style={{
            backgroundColor: '#ffffff',
            padding: '30px 25px 25px 25px',
            borderRadius: '12px',
            textAlign: 'center',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '80vh',
            overflowY: 'auto',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '16px'
          }}
          onClick={e => e.stopPropagation()}
        >
          <FontAwesomeIcon
            icon={faTimes}
            style={{
              fontSize: '48px',
              color: '#ef4444'
            }}
          />
          <p style={{
            margin: 0,
            fontWeight: 600,
            color: '#000',
            fontSize: '15px'
          }}>
            ⚠ Unit mismatch detected
          </p>
          <p style={{
            margin: 0,
            color: '#666',
            fontSize: '13px',
            lineHeight: 1.5
          }}>
            The following items have a different unit family in the inventory than what the bill shows. Please correct the unit in Inventory Item Mapping.
          </p>
          <div style={{
            width: '100%',
            textAlign: 'left',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px'
          }}>
            {mismatchItems.map((item, i) => (
              <div key={i} style={{
                padding: '10px 14px',
                backgroundColor: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: '8px',
                fontSize: '13px',
                lineHeight: 1.6
              }}>
                <div style={{ fontWeight: 600, color: '#111', marginBottom: 4 }}>
                  {item.itemName}
                </div>
                <div style={{ color: '#666' }}>
                  DB default: <span style={{ fontWeight: 600, color: '#b91c1c' }}>{item.dbUnit}</span> ({item.dbFamily})
                  &nbsp;→&nbsp;
                  Bill shows: <span style={{ fontWeight: 600, color: '#15803d' }}>{item.ocrUnit}</span> ({item.ocrFamily})
                </div>
              </div>
            ))}
          </div>
          <button
            style={{
              padding: '10px 24px',
              border: 'none',
              backgroundColor: '#000000',
              color: '#ffffff',
              cursor: 'pointer',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: 600,
              minWidth: '80px'
            }}
            onClick={onClose}
          >
            OK
          </button>
        </div>
      </div>
    );
  };

  /**
   * A message that reports and disappears. It owns no form state and resets nothing, which
   * is the entire point of it existing separately from SuccessModal.
   */
  const InfoToast = ({ message, onDismiss }) => {
    return (
      <div
        role="status"
        className="stockin-info-toast"
        style={{
          position: 'fixed',
          left: '50%',
          bottom: '28px',
          transform: 'translateX(-50%)',
          zIndex: 9999,
          maxWidth: 'min(420px, calc(100vw - 32px))',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          padding: '12px 16px',
          borderRadius: '10px',
          background: '#111827',
          color: '#ffffff',
          fontSize: '14px',
          fontWeight: 500,
          boxShadow: '0 10px 30px -8px rgba(0,0,0,0.45)'
        }}
      >
        <span style={{ flex: 1 }}>{message}</span>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          style={{
            background: 'transparent',
            border: 'none',
            color: '#ffffff',
            cursor: 'pointer',
            fontSize: '16px',
            lineHeight: 1,
            padding: 0
          }}
        >
          ×
        </button>
      </div>
    );
  };

  /**
   * Post-submit success modal. Its close handler WIPES THE FORM.
   *
   * ⚠ Only route a message through this modal when the form genuinely should be cleared —
   * i.e. the stock-in was saved. An informational message ("Added 7 items from Vendor X")
   * shown here destroyed the very items it was announcing the moment the user clicked OK.
   * Use setInfoToast for anything that merely reports; the rule is: a message that reports
   * one action must never undo a different one.
   *
   * The "Draft saved" exemption below is the historical special case for the same bug.
   */
  const SuccessModal = ({ message, onClose }) => {
    const handleClose = () => {
      onClose();
      if (message && message.includes('Draft saved')) return;
      // Clear all form fields
      setVendorName('');
      setVendorPhone('');
      setVendorRecordPhone('');
      setPhoneTouched(false);
      setCurrentShift('');
      // THE PURCHASE DATE SURVIVES THE SAVE, deliberately.
      //
      // Back-dating is not the exception here, it is the normal shift: yesterday's — or
      // the whole weekend's — bills get keyed in as a stack, one after another. Stamping
      // today back onto the field the instant the success modal closed meant every bill
      // after the first in that stack silently landed on the wrong day unless the person
      // re-picked the date each time, and nothing on screen marked the difference between
      // the bill they had just dated and the next one they had not.
      //
      // The field stays visible in the header, the day is now named in the success message
      // (savedOnLine), and a future date is still clamped on every render — so keeping it
      // cannot strand the form on a day the operator cannot see or has not chosen.
      // M7.4: items, both snapshots, the undo stack, itemsRef and the error state — all of
      // it, from the ONE function every other success path now calls too.
      resetFormAfterSuccess();
      setCgst('');
      setSgst('');
      // T1 — the next bill gets asked its own question. An acknowledgement belongs to the
      // bill it was given for, and that bill has just been saved.
      ackedTotalDifferenceRef.current = null;
      setTotalMismatchPrompt(null);
      setShowBillImage(false);
      setCurrentBillImageIndex(0);
      // Clear vendor selection state
      setSelectedVendor(null);
      setLastOrderBanner(null);
    };

    return (
      <div
        className="stockin-success-modal-overlay"
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 9999,
          padding: '10px'
        }}
      >
        <div
          className="stockin-success-modal-content"
          style={{
            width: 'min(380px, 90vw)',
            background: '#fff',
            borderRadius: 12,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            padding: '24px',
            textAlign: 'center'
          }}
          onClick={e => e.stopPropagation()}
        >
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
            <div style={{
              width: '60px',
              height: '60px',
              borderRadius: '50%',
              border: '3px solid #22c55e',
              backgroundColor: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Check size={36} color="#22c55e" strokeWidth={3} />
            </div>
          </div>
          <div style={{
            fontSize: '18px',
            fontWeight: '600',
            marginBottom: '16px',
            color: '#1f2937'
          }}>
            Stock Recorded Successfully
          </div>
          {bmSaveLine && !(message && message.includes('Draft saved')) && (
            <div className="stockin-unit-cost-display" style={{ justifyContent: 'center', margin: '-8px 0 16px' }}>
              <span>{bmSaveLine}</span>
            </div>
          )}
          <button
            className="stockin-success-modal-ok-btn"
            style={{
              padding: '10px 24px',
              backgroundColor: '#000000',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
            onClick={handleClose}
          >
            OK
          </button>
        </div>
      </div>
    );
  };

  // Error Modal Component
  const ErrorModal = ({ message, onClose }) => {
    const handleClose = () => {
      onClose();
      // Scroll to first error if this was a validation error modal
      if (shouldScrollToError) {
        setTimeout(() => {
          scrollToFirstError();
          setShouldScrollToError(false);
        }, 300);
      }
    };

    // Parse unit mismatch errors for better formatting
    const isUnitMismatchError = message && message.includes('Unit type mismatch');
    let displayTitle = 'Error';
    let displayMessage = message;

    if (isUnitMismatchError) {
      // Extract item name if present
      const itemMatch = message.match(/for "([^"]+)"/);
      const itemName = itemMatch ? itemMatch[1] : null;

      // Extract unit types
      const unitsMatch = message.match(/Cannot use (\w+) unit "([^"]+)" for.*?(\w+) standard unit "([^"]+)"/);

      if (itemName && unitsMatch) {
        const [, receivedType, receivedUnit, standardType, standardUnit] = unitsMatch;
        displayTitle = `Unit Mismatch: ${itemName}`;

        // Suggest compatible units
        if (standardType === 'weight') {
          displayMessage = `Please use: kg, g, or mg`;
        } else if (standardType === 'volume') {
          displayMessage = `Please use: L or ml`;
        } else {
          displayMessage = `Please use a compatible ${standardType} unit.`;
        }
      } else {
        displayTitle = 'Unit Type Mismatch';
      }
    }

    return (
      <div
        className="stockin-error-modal-overlay"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 10000,
          padding: '20px'
        }}
        onClick={handleClose}
      >
        <div
          className="stockin-error-modal-content"
          style={{
            backgroundColor: '#ffffff',
            padding: '30px 25px 25px 25px',
            borderRadius: '12px',
            textAlign: 'center',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '20px'
          }}
          onClick={e => e.stopPropagation()}
        >
          <FontAwesomeIcon
            icon={faTimes}
            className="stockin-error-modal-icon"
            style={{
              fontSize: '48px',
              color: '#dc3545'
            }}
          />
          {isUnitMismatchError && (
            <h3 style={{
              margin: 0,
              padding: 0,
              fontSize: '16px',
              fontWeight: 700,
              color: '#dc3545'
            }}>
              {displayTitle}
            </h3>
          )}
          <p className="stockin-error-modal-message" style={{
            margin: 0,
            padding: 0,
            lineHeight: '1.6',
            fontWeight: isUnitMismatchError ? 500 : 600,
            fontSize: isUnitMismatchError ? '14px' : '16px',
            color: '#000',
            whiteSpace: 'pre-line',
            textAlign: 'center',
            width: '100%'
          }}>
            {displayMessage}
          </p>
          <button
            className="stockin-error-modal-ok-btn"
            style={{
              padding: '10px 24px',
              border: 'none',
              backgroundColor: '#000000',
              color: '#ffffff',
              cursor: 'pointer',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: 600,
              minWidth: '80px'
            }}
            onClick={handleClose}
          >
            OK
          </button>
        </div>
      </div>
    );
  };

  // Loading Modal Component
  const LoadingModal = ({ message }) => {
    return (
      <div
        className="stockin-loading-modal-overlay"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 10000,
          padding: '20px'
        }}
      >
        <div
          className="stockin-loading-modal-content"
          style={{
            backgroundColor: '#ffffff',
            padding: '30px 25px 25px 25px',
            borderRadius: '12px',
            textAlign: 'center',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '20px'
          }}
        >
          <FontAwesomeIcon
            icon={faSpinner}
            className="stockin-loading-modal-icon"
            style={{
              fontSize: '48px',
              color: '#ff9800'
            }}
            spin
          />
          <p className="stockin-loading-modal-message" style={{
            margin: 0,
            padding: 0,
            lineHeight: '1.6',
            fontWeight: 600,
            color: '#000',
            whiteSpace: 'pre-line',
            textAlign: 'center',
            width: '100%'
          }}>
            {message}
          </p>
        </div>
      </div>
    );
  };

  const inventoryItemNames = Array.isArray(inventoryItems) ? inventoryItems.map(i => i?.name).filter(Boolean) : [];
  const isMobileView = typeof window !== 'undefined' && window.innerWidth <= 480;

  const handleSaveKitchenUnit = async () => {
    try {
      const businessId = UnitMappingService.getBusinessId();

      // Validate required fields
      if (!kitchenUnitForm.item || !kitchenUnitForm.kitchenTool || !kitchenUnitForm.quantity || !kitchenUnitForm.unit) {
        showModalMessage('Please fill all fields', 'error');
        return;
      }

      const result = await UnitMappingService.saveKitchenUnit({
        businessId,
        item: kitchenUnitForm.item,
        kitchenTool: kitchenUnitForm.kitchenTool,
        quantity: Number(kitchenUnitForm.quantity),
        unit: kitchenUnitForm.unit,
        type: 'supplier'  // StockInForm uses supplier type
      });

      // Add the new kitchen tool to units list if not already present
      const newTool = kitchenUnitForm.kitchenTool.toLowerCase();
      setUnits(prev => {
        if (!prev.some(u => u.toLowerCase() === newTool)) {
          return [...prev, kitchenUnitForm.kitchenTool];
        }
        return prev;
      });

      setShowKitchenUnitModal(false);
      setKitchenUnitForm({ item: '', kitchenTool: 'Tablespoon', quantity: '15', unit: 'ml' });
      // NOT showModalMessage(..., 'success') — that opens SuccessModal, whose close handler
      // resets the whole form. Saving a unit conversion is not submitting a bill.
      setInfoToast('Kitchen unit saved successfully!');
    } catch (e) {
      console.error('❌ Failed to save kitchen unit:', e);
      const errorMsg = e?.response?.data?.error || e?.message || 'Failed to save kitchen unit';
      showModalMessage(errorMsg, 'error');
    }
  };

  // **NEW: Save container conversion**
  const handleSaveContainerConversion = async () => {


    try {
      const { itemIndex, itemName, containerUnit, quantity, standardUnit } = containerConversionForm;


      // ✅ VALIDATION: Comprehensive input validation with user-friendly messages
      if (!containerUnit || !quantity || !standardUnit) {
        const missingFields = [];
        if (!containerUnit) missingFields.push('Container Unit');
        if (!quantity) missingFields.push('Quantity');
        if (!standardUnit) missingFields.push('Standard Unit');

        const errorMsg = `Please fill all required fields: ${missingFields.join(', ')}`;
        console.error('❌ [Save Container] Validation failed:', errorMsg);
        showModalMessage(errorMsg, 'error');
        return;
      }

      const qtyNum = parseFloat(quantity);

      // Check if valid number
      if (isNaN(qtyNum)) {
        console.error('❌ [Save Container] Invalid quantity:', quantity);
        showModalMessage(`Invalid quantity: "${quantity}" is not a valid number`, 'error');
        return;
      }

      // Check if positive
      if (qtyNum <= 0) {
        console.error('❌ [Save Container] Non-positive quantity:', qtyNum);
        showModalMessage(`Quantity must be greater than zero. You entered: ${qtyNum}`, 'error');
        return;
      }

      // Check if too small (< 0.001)
      if (qtyNum < 0.001) {
        console.error('❌ [Save Container] Quantity too small:', qtyNum);
        showModalMessage(`Quantity too small (${qtyNum}). Minimum allowed: 0.001`, 'error');
        return;
      }

      // Check if too large (> 9999)
      if (qtyNum > 9999) {
        console.error('❌ [Save Container] Quantity too large:', qtyNum);
        showModalMessage(`Quantity too large (${qtyNum}). Maximum allowed: 9999`, 'error');
        return;
      }

      // Warn if suspiciously large (> 1000)
      if (qtyNum > 1000) {
        console.warn('⚠️ [Save Container] Large quantity detected:', qtyNum);
        // Allow but continue
      }

      // Use a placeholder if item name is empty (for global conversions)
      const effectiveItemName = itemName && itemName.trim() ? itemName.trim() : 'Generic Item';
      const standardUnitSymbol = normalizeUnitSymbol(standardUnit);
      const standardUnitDisplay = convertUnitSymbolToName(standardUnitSymbol);


      // Save to backend via supplier conversions API (using plural method with array)
      const businessId = UnitMappingService.getBusinessId();

      // Use saveSupplierConversions (plural) with array of one conversion
      const saveResult = await UnitMappingService.saveSupplierConversions(businessId, [{
        item: effectiveItemName,
        containerType: containerUnit,
        quantity: qtyNum,
        unit: standardUnitSymbol
      }]);

      // ✅ CHECK FOR WARNINGS: Show user if backend had validation issues
      if (saveResult.warnings && saveResult.warnings.length > 0) {
        console.warn('⚠️ [Save Container] Backend returned warnings:', saveResult.warnings);
        const warningMsg = saveResult.warnings.join('\n• ');
        showModalMessage(`Conversion saved with warnings:\n\n• ${warningMsg}`, 'error');
        // Don't proceed with UI updates if there were warnings
        setShowContainerConversionModal(false);
        return;
      }

      // Save to local state for quick lookup (use effective item name)
      const normalizedItemName = normalizeItemKey({ item_name: effectiveItemName });
      const normalizedContainerUnit = normalizeUnitForKey(containerUnit);
      const conversionKey = `${normalizedItemName}_${normalizedContainerUnit}`;

      console.log(`💾 [SAVE CONVERSION] Storing in local map: "${conversionKey}" = ${qtyNum} ${standardUnitSymbol}`);

      setContainerConversions(prev => {
        const newMap = {
          ...prev,
          [conversionKey]: { factor: qtyNum, toUnitSymbol: standardUnitSymbol, toUnitDisplay: standardUnitDisplay }
        };
        console.log(`   Updated conversion map, now has ${Object.keys(newMap).length} entries`);
        return newMap;
      });


      // Store conversion metadata on the current item (keep UI unit as container)
      if (itemIndex !== null) {

        // Through the door: a conversion the user just defined changes what gets submitted.
        applyItems(prev => prev.map((it, idx) => {
          if (idx === itemIndex) {
            const updated = {
              ...it,
              supplier_conversion_quantity: qtyNum,
              supplier_conversion_unit: standardUnitSymbol,
              supplier_conversion_unit_display: standardUnitDisplay
            };
            return updated;
          }
          return it;
        }));
      }

      // ✅ DON'T close modal yet - check if there are more units first
      // Reset form data
      setContainerConversionForm({
        itemIndex: null,
        itemName: '',
        containerUnit: 'box',
        quantity: '',
        standardUnit: 'kg'
      });

      // ✅ Check if we're in QUEUE MODE or SINGLE-SHOT MODE
      const inQueueMode = unknownUnitsQueue && unknownUnitsQueue.length > 0;
      const nextIndex = currentUnknownUnitIndex + 1;

      if (inQueueMode && nextIndex < unknownUnitsQueue.length) {
        // ✅ QUEUE MODE: Keep modal OPEN and show next unknown unit
        const next = unknownUnitsQueue[nextIndex];

        setContainerConversionForm({
          itemIndex: next.itemIndex,
          itemName: next.itemName,
          containerUnit: next.unit,
          quantity: '1',
          standardUnit: 'kg'
        });
        setCurrentUnknownUnitIndex(nextIndex);
        // ✅ Modal stays open with new data
      } else {
        // ✅ SINGLE-SHOT MODE or last unit in queue - close modal
        const mode = inQueueMode ? 'QUEUE MODE - Last unit processed' : 'SINGLE-SHOT MODE - Only 1 unit';
        setShowContainerConversionModal(false);
        setUnknownUnitsQueue([]);
        setCurrentUnknownUnitIndex(0);

        // ✅ FIX: Only add pendingOcrItems if they exist AND items array is empty/placeholder
        // If items already exist in form, don't override them!
        if (pendingOcrItems && pendingOcrItems.length > 0) {
          const hasRealItems = items.length > 0 && items[0].item_name && items[0].item_name.trim();

          if (!hasRealItems) {
            // Form is empty - add pendingOcrItems
            applyItems(pendingOcrItems, { snapshot: true });
            setPendingOcrItems([]);
            setSemanticProcessing(false);
            setHasOcrRunInSession(true);
          } else {
            // Items already in form - just clear pendingOcrItems without overriding
            setPendingOcrItems([]);
            setSemanticProcessing(false);
          }
        }
      }

    } catch (error) {
      console.error('❌ [Save Container] Error:', error);
      console.error('❌ [Save Container] Error details:', {
        message: error.message,
        response: error.response,
        data: error.data,
        status: error.status
      });

      // ✅ IMPROVED ERROR HANDLING: Show specific, user-friendly error messages
      let errorMessage = 'Failed to save container conversion';

      // Extract error from various possible response formats
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.details) {
        errorMessage = error.response.data.details;
      } else if (error.data?.error) {
        errorMessage = error.data.error;
      } else if (error.data?.details) {
        errorMessage = error.data.details;
      } else if (error.message) {
        errorMessage = error.message;
      }

      // Check for validation warnings
      if (error.response?.data?.warnings && Array.isArray(error.response.data.warnings)) {
        const warnings = error.response.data.warnings.join('\n• ');
        errorMessage += `\n\nValidation Issues:\n• ${warnings}`;
      } else if (error.data?.warnings && Array.isArray(error.data.warnings)) {
        const warnings = error.data.warnings.join('\n• ');
        errorMessage += `\n\nValidation Issues:\n• ${warnings}`;
      }

      // Show error modal with details
      showModalMessage(errorMessage, 'error');
    }
  };

  // **NEW: Handle Unit Training (SUF - Filter 2)**
  const handleSaveUnitTraining = async () => {
    try {
      const { itemIndex, itemName, unknownUnit, quantity, standardUnit, conversionFactor } = unitTrainingData;

      if (!unknownUnit || !standardUnit || !conversionFactor || conversionFactor <= 0) {
        showModalMessage('Please fill all fields with valid values', 'error');
        return;
      }

      // Save conversion to backend (Ripple Effect)
      const result = await semanticFilterService.trainUnitConversion(
        unknownUnit,
        standardUnit,
        parseFloat(conversionFactor),
        itemName,
        null // userId
      );

      if (result.success) {
        // Apply conversion to current item
        const convertedQuantity = quantity * parseFloat(conversionFactor);

        applyItems(prev => prev.map((it, idx) =>
          idx === itemIndex
            ? {
              ...it,
              quantity: convertedQuantity,
              unit: standardUnit,
              unit_conversion_applied: true,
              conversion_details: {
                original_quantity: quantity,
                original_unit: unknownUnit,
                converted_quantity: convertedQuantity,
                converted_unit: standardUnit,
                factor: conversionFactor
              }
            }
            : it
        ));

        // Close modal and reset
        setShowUnitTrainingModal(false);
        setUnitTrainingData({
          itemIndex: null,
          itemName: '',
          unknownUnit: '',
          quantity: 0,
          standardUnit: 'kg',
          conversionFactor: 1
        });

        showModalMessage(
          `✅ Conversion saved!\n\n${unknownUnit} → ${standardUnit} (×${conversionFactor})\n\nFuture bills will automatically convert "${unknownUnit}" units.`,
          'success'
        );
      } else {
        showModalMessage('Failed to save unit conversion: ' + (result.error || 'Unknown error'), 'error');
      }
    } catch (error) {
      console.error('Error saving unit training:', error);
      showModalMessage('Failed to save unit conversion', 'error');
    }
  };

  /**
   * The correction pop-up's answer. `keepAsEntered` is its "Keep my numbers" button: the
   * bill is resubmitted exactly as entered with a price_acknowledged token instead of DSF's
   * suggested numbers, so a real price DSF took for a slipped decimal can still be saved.
   */
  const confirmAutoCorrection = async ({ keepAsEntered = false } = {}) => {
    // Visible to the catch block so a SECOND flagged item can
    // chain into a fresh modal without losing earlier tokens
    let lastSubmittedPayload = null;
    try {
      setIsSubmitting(true);

      // ── Numbers changed in the pop-up ─────────────────────────────────
      // Put them on the visible row and on the resubmitted bill, drop this
      // row's confirmation, and send it. The new values face every check as
      // fresh input; if they still look wrong, the catch below reopens the
      // pop-up on them. Nothing is confirmed on the person's behalf.
      if (!keepAsEntered && correctionEdit.available && correctionEdit.edited && pendingSubmitData) {
        const edit = correctionEdit;
        const rowName = String(edit.row.display_name || edit.row.item_name || '').trim().toLowerCase();
        const nameOf = (r) => String(r?.item_name || '').trim().toLowerCase();
        const formRows = itemsRef.current;
        const formIdx = nameOf(formRows[edit.index]) === rowName
          ? edit.index
          : formRows.findIndex(r => nameOf(r) === rowName);
        if (formIdx >= 0) {
          applyItems(prev => prev.map((r, j) => (
            j === formIdx ? applyCorrectionEditToFormRow(r, edit, taxForStockInEnabled) : r
          )));
        }

        lastSubmittedPayload = applyCorrectionEditToPayload(pendingSubmitData, edit);
        const r = await makeApiCall('', lastSubmittedPayload);

        if (r.nar_resolutions && r.nar_resolutions.length > 0) {
          setNarResolutions(r.nar_resolutions);
          setShowNarResolution(true);
        }
        const savedAfterEdit = await finalizeSuccessfulSubmit(r);
        if (savedAfterEdit) {
          const stockInId = r?.data?.stock_in_id;
          const msg = (r?.message || (stockInId ? `Stock recorded successfully!\n\nID: ${stockInId}` : 'Stock recorded successfully!')) + savedOnLine();
          showModalMessage(msg, 'success');
        }
        setShowAutoCorrectionModal(false);
        setAutoCorrectionData(null);
        setPendingSubmitData(null);
        setIsSubmitting(false);
        // F3 — a replayed or failed save never clears the person's work.
        if (savedAfterEdit) setTimeout(() => resetFormAfterSuccess(), 1500);
        return;
      }

      // Critical price confirmed as entered (changes go through the edit
      // box above) → explicit extreme-price acknowledgment: the row commits
      // as an anomaly and never feeds benchmarks. No bypass flags exist.
      if (autoCorrectionData.isCriticalPriceError) {
        if (pendingSubmitData) {
          setIsSubmitting(true);
          const gateIdx = Number.isInteger(autoCorrectionData.itemIndex) ? autoCorrectionData.itemIndex : 0;
          const gateToken = autoCorrectionData.requiredConfirmation
            || { index: gateIdx, type: 'extreme_price_acknowledged', acknowledgment: 'CONFIRM EXTREME PRICE' };
          lastSubmittedPayload = {
            ...pendingSubmitData,
            items: pendingSubmitData.items || [],
            item_confirmations: [
              ...(Array.isArray(pendingSubmitData.item_confirmations)
                ? pendingSubmitData.item_confirmations.filter(t => Number(t.index) !== gateIdx)
                : []),
              gateToken
            ]
          };
          const r = await makeApiCall('', lastSubmittedPayload);

          // Handle success (NAR resolutions, vendor learning, etc.)
          if (r.nar_resolutions && r.nar_resolutions.length > 0) {
            setNarResolutions(r.nar_resolutions);
            setShowNarResolution(true);
          }

          /**
           * F2 — THE THIRD SUCCESS PATH, finally converged.
           *
           * P2/P4 converged handleSubmit and submitPieceAnswers. There were
           * FOUR. This one did none of it: no idempotency-key regeneration,
           * no draft removal, no tax-toggle revert, no onSuccess(), no
           * warnings, no `idempotent` check — and it resubmits
           * `pendingSubmitData`, which still carries the ORIGINAL key. So
           * the bill after a price confirmation was replayed by the backend
           * and could not be saved at all until the component remounted.
           */
          const savedAfterGate = await finalizeSuccessfulSubmit(r);
          if (savedAfterGate) {
            showModalMessage(r.message || 'Stock-in submitted successfully!', 'success');
          }
          setShowAutoCorrectionModal(false);
          setAutoCorrectionData(null);
          setPendingSubmitData(null);
          setIsSubmitting(false);

          // F3 — a replayed or failed save must NEVER clear the person's
          // work. They have just been told to enter it again.
          if (savedAfterGate) setTimeout(() => resetFormAfterSuccess(), 1500);
        }
        return;
      }

      // Check if this is manual entry VCP confirmation (price or quantity)
      if (!pendingSubmitData &&
        (autoCorrectionData.correction_type === 'VCP_HIGH_PRICE_CONFIRMATION' ||
          autoCorrectionData.correction_type === 'VCP_HIGH_QUANTITY_CONFIRMATION')) {

        const isQuantityCheck = autoCorrectionData.isQuantityCheck || autoCorrectionData.correction_type === 'VCP_HIGH_QUANTITY_CONFIRMATION';
        const itemName = (autoCorrectionData.item_name || '').toLowerCase().trim();


        // Create updated confirmations immediately
        const updatedConfirmations = {
          ...confirmedItems,
          [itemName]: {
            ...(confirmedItems[itemName] || {}),
            [isQuantityCheck ? 'quantity' : 'price']: true
          }
        };


        // Update state
        setConfirmedItems(updatedConfirmations);

        // Set global flag to skip ALL frontend VCP checks (use ref for immediate access)
        skipAllVcpChecksRef.current = true;

        setShowAutoCorrectionModal(false);
        setAutoCorrectionData(null);


        // Continue with normal submission - NO TIMEOUT needed, flag is set synchronously
        handleSubmitClick();
        return;
      }

      // Backend confirmation (Stage 1.2 handshake): apply the
      // CORRECTED values into the payload and attach a per-item
      // confirmation token. There is no bypass flag — the
      // resubmitted values walk through every check again, so a
      // confirm can never commit the original bad numbers.
      // Keep my numbers: the same token a price confirmation sends, so DSF and the gate
      // accept the row as entered and nothing below rewrites its values.
      setIsSubmitting(true);
      const confIndex = Number.isInteger(autoCorrectionData.itemIndex) ? autoCorrectionData.itemIndex : 0;
      const confToken = keepAsEntered
        ? { index: autoCorrectionData.requiredConfirmation?.index ?? confIndex, type: 'price_acknowledged' }
        : (autoCorrectionData.requiredConfirmation || { index: confIndex, type: 'price_acknowledged' });
      const priorTokens = Array.isArray(pendingSubmitData.item_confirmations)
        ? pendingSubmitData.item_confirmations.filter(t => Number(t.index) !== Number(confToken.index))
        : [];
      const correctedItems = (pendingSubmitData.items || []).map((x, j) => {
        if (j !== Number(confToken.index) || confToken.type !== 'correction_applied') return x;
        const fixed = { ...x };
        if (autoCorrectionData.corrected_quantity != null && autoCorrectionData.corrected_quantity !== '') {
          fixed.quantity = Number(autoCorrectionData.corrected_quantity);
        }
        if (autoCorrectionData.corrected_unit_price) {
          fixed.unit_price = Number(autoCorrectionData.corrected_unit_price);
        }
        if (autoCorrectionData.corrected_total) {
          fixed.total_price = Number(autoCorrectionData.corrected_total);
        }
        return fixed;
      });
      lastSubmittedPayload = {
        ...pendingSubmitData,
        items: correctedItems,
        item_confirmations: [...priorTokens, confToken]
      };

      const response = await makeApiCall('', lastSubmittedPayload);

      setShowAutoCorrectionModal(false);
      setAutoCorrectionData(null);
      setPendingSubmitData(null);

      // F2 — THE FOURTH SUCCESS PATH. Same story as the one above: it
      // resubmits `pendingSubmitData` with the original idempotency key, so
      // the NEXT bill in the session was replayed and silently discarded.
      // finalizeSuccessfulSubmit regenerates the key, drops the draft,
      // reverts the tax toggle, notifies the parent and surfaces a replay
      // as a warning rather than a success.
      const savedAfterConfirm = await finalizeSuccessfulSubmit(response);
      if (savedAfterConfirm) {
        const stockInId = response?.data?.stock_in_id;
        const msg = (response?.message || (stockInId ? `Stock recorded successfully!\n\nID: ${stockInId}` : 'Stock recorded successfully!')) + savedOnLine();
        showModalMessage(msg, 'success');
      }

      // F3 — never clear the form on a replay. The person has just been
      // told the bill was already recorded and to enter it again.
      if (savedAfterConfirm) setTimeout(() => resetFormAfterSuccess(), 1500);


    } catch (err) {
      console.error('❌ [Confirm Button] Error:', err);
      console.error('❌ [Confirm Button] Error stack:', err.stack);
      console.error('❌ [Confirm Button] Error response:', err.response);
      console.error('❌ [Confirm Button] Error data:', err.data);

      // ANOTHER item needs confirmation — multi-item bills round-trip
      // one item at a time; earlier tokens ride along in the payload
      if (err.data?.requiresConfirmation && lastSubmittedPayload) {
        const data = err.data;
        const itemDetails = data.itemDetails || {};
        setAutoCorrectionData({
          requiresConfirmation: true,
          item_name: itemDetails.item_name || 'Unknown',
          original_unit_price: itemDetails.entered_unit_price || itemDetails.entered_price || 0,
          corrected_unit_price: itemDetails.corrected_unit_price || itemDetails.historical_vcp || 0,
          corrected_quantity: itemDetails.corrected_quantity,
          original_total: itemDetails.entered_total || 0,
          corrected_total: itemDetails.corrected_total || itemDetails.expected_total || 0,
          correction_type: data.confirmationType || 'VCP_HIGH_PRICE_CONFIRMATION',
          correction_message: data.confirmationPrompt || data.message || 'Please confirm the entered price.',
          confidence: 0.95,
          vcp: itemDetails.historical_vcp || 0,
          deviation_percentage: itemDetails.deviation_percentage || '0',
          deviation_direction: itemDetails.deviation_direction || '',
          isConfirmationOnly: true,
          isBackendConfirmation: true,
          itemIndex: Number.isInteger(data.itemIndex) ? data.itemIndex : null,
          requiredConfirmation: data.required_confirmation || null
        });
        setPendingSubmitData(lastSubmittedPayload); // earlier tokens preserved
        setShowAutoCorrectionModal(true);
        setIsSubmitting(false);
        return;
      }

      // Gate catastrophic blocker surfaced on a resubmit → chain into the
      // matching modal for the blocker's own code (or hard-stop if it
      // can't be overridden)
      if (err.data?.gate_blockers?.length && lastSubmittedPayload) {
        const blockers = err.data.gate_blockers;
        // H7 — THE SAME router the first submit uses. This copy used to
        // have no `piece_` filter, so a piece blocker was picked up as
        // "overridable" and opened a modal titled "Extreme price detected"
        // that could never be resolved.
        const rr = routeGateBlockers(blockers);
        if (rr.piece.length > 0) {
          setShowAutoCorrectionModal(false);
          setAutoCorrectionData(null);
          setPendingSubmitData(null);
          setPieceGate({
            blockers: rr.piece,
            bulkReview: err.data.piece_bulk_review || null,
            activeBlocker: err.data.piece_bulk_review ? null : rr.piece[0],
            confirmations: (Array.isArray(lastSubmittedPayload.item_confirmations)
              ? lastSubmittedPayload.item_confirmations
              : []).filter(isValidConfirmationToken),
            resolvedIndexes: [],
            payload: lastSubmittedPayload,
            deferredBlockers: rr.deferred,
          });
          setIsSubmitting(false);
          return;
        }
        const hardBlockers = rr.hard;
        const overridable = rr.overridable;
        if (hardBlockers.length > 0 || !overridable) {
          setShowAutoCorrectionModal(false);
          setAutoCorrectionData(null);
          setPendingSubmitData(null);
          showModalMessage(
            `🚫 Submission blocked:\n\n${blockers.map(b => `• ${b.message}`).join('\n')}\n\nPlease fix the highlighted values and submit again.`,
            'error'
          );
          setIsSubmitting(false);
          return;
        }
        // SAME mapper as the first-submit path (C3). A quantity blocker
        // raised on a resubmit now renders the quantity modal and routes
        // its confirm to extreme_quantity_acknowledged, instead of an
        // editable price box whose correction the gate would reject
        // forever.
        setAutoCorrectionData(gateBlockerToCorrectionData(overridable));
        setPendingSubmitData(lastSubmittedPayload);
        setShowAutoCorrectionModal(true);
        setIsSubmitting(false);
        return;
      }

      // Close the VCP confirmation modal first
      setShowAutoCorrectionModal(false);
      setAutoCorrectionData(null);
      setPendingSubmitData(null);

      // Bill Memory: stale rows get the server's answers, and the first is scrolled to (no popup).
      if (err.data?.code === 'bm_rows_need_tap' && Array.isArray(err.data.rows)) {
        bmApplyServerAnswers(err.data.rows);
        return;
      }

      // Check if backend requires item selection (NAR ambiguity)
      if (err.data?.requiresItemSelection) {
        const data = err.data;

        // Sort candidates by deviation (lowest = best match)
        const sortedCandidates = [...(data.candidates || [])].sort((a, b) => {
          const devA = Math.abs(a.deviationPercent || 999);
          const devB = Math.abs(b.deviationPercent || 999);
          return devA - devB;
        });

        // Pre-select the best match (lowest deviation)
        const preSelectedId = data.autoResolved && data.selectedItemId
          ? data.selectedItemId
          : (sortedCandidates.length > 0 ? sortedCandidates[0].item_id : null);

        // Store selection data and show modal
        setItemSelectionData({
          ...data,
          candidates: sortedCandidates,
          selectedItemId: preSelectedId
        });
        setShowItemSelectionModal(true);
        setIsSubmitting(false);
        return;
      }

      // Fallback: Check if this is an item ambiguity error by message
      if (err.message && err.message.includes('Item ambiguity')) {
        // Extract item name from error message
        const itemNameMatch = err.message.match(/Item ambiguity: "([^"]+)"/);
        const itemName = itemNameMatch ? itemNameMatch[1] : null;

        showModalMessage(
          `⚠️ Item Mapping Required\n\n${err.message}\n\nPlease review "${itemName || 'the highlighted item'}" and select from the dropdown or adjust the price to match existing inventory.`,
          'error'
        );

        // Try to find and scroll to the problematic item
        if (itemName) {
          setTimeout(() => {
            const itemIndex = items.findIndex(item =>
              String(item.item_name || '').toLowerCase() === itemName.toLowerCase()
            );

            if (itemIndex >= 0) {
              const errorElement = document.querySelector(`[data-item-index="${itemIndex}"]`);
              if (errorElement) {
                errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                // Flash the item to draw attention
                errorElement.style.animation = 'pulse 1s ease-in-out 3';
              }
            }
          }, 500);
        }
      } else {
        // Show detailed error message
        const errorMessage = err.data?.error || err.response?.data?.message || err.message || 'Submission failed';
        const errorDetails = err.data?.details || err.response?.data?.details || '';

        // Try to extract item name from error message (e.g., "Quantity too large for "eggs"")
        const quantityErrorMatch = errorDetails.match(/(?:Quantity too large for|unusually large for) "([^"]+)"/);
        const itemName = quantityErrorMatch ? quantityErrorMatch[1] : null;

        showModalMessage(
          `Submission Failed\n\n${errorMessage}`,
          'error'
        );

        // Scroll to the problematic item if found
        if (itemName) {
          setTimeout(() => {
            const itemIndex = items.findIndex(item =>
              String(item.item_name || '').toLowerCase() === itemName.toLowerCase()
            );

            if (itemIndex >= 0) {
              const errorElement = document.querySelector(`[data-item-index="${itemIndex}"]`);
              if (errorElement) {
                errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                // Highlight the problematic item
                errorElement.style.border = '3px solid red';
                errorElement.style.background = '#fff5f5';
                setTimeout(() => {
                  errorElement.style.border = '';
                  errorElement.style.background = '';
                }, 5000);
              }
            }
          }, 500);
        }
      }

      // Reset states so user can fix the issue
      setShowAutoCorrectionModal(false);
      setAutoCorrectionData(null);
      setPendingSubmitData(null);
      setConfirmedItems({}); // Reset confirmations so they can try again
      skipAllVcpChecksRef.current = false; // Reset skip flag
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      {/* Item 1 — closing-stock lock blocks Stock-In */}
      {closingStockLocked && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 100000, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ background: '#fff', borderRadius: 14, maxWidth: 400, width: '100%', padding: 24, textAlign: 'center', boxShadow: '0 10px 40px rgba(0,0,0,0.3)' }}>
            <div style={{ fontSize: 40, marginBottom: 8 }}>🔒</div>
            <h2 style={{ margin: '0 0 8px', fontSize: 18, color: '#1a1a2e' }}>Closing stock in progress</h2>
            <p style={{ margin: '0 0 18px', fontSize: 14, color: '#555', lineHeight: 1.5 }}>
              To record stock-in, turn off the closing-stock toggle first.
            </p>
            <button
              onClick={() => {
                // Dismiss the overlay, then redirect to Settings page with the inventory management section expanded.
                setClosingStockLocked(false);
                if (goTo) {
                  goTo('settings', { expandSection: 'inventory' });
                } else if (onCancel) {
                  onCancel();
                } else if (onBack) {
                  onBack();
                }
              }}
              style={{ background: '#1a1a2e', color: '#fff', border: 'none', borderRadius: 10, padding: '10px 28px', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}
            >
              OK
            </button>
          </div>
        </div>
      )}
      {/* Add spinner and pulse animations */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }
      `}</style>



      {/* OCR Processing Overlay — matches BillScanner's "Processing Your Receipt" design */}
      {ocrFormProcessing && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 15000
        }}>
          <div style={{
            background: 'white',
            padding: '40px 30px',
            borderRadius: '12px',
            textAlign: 'center',
            maxWidth: '400px',
            width: '90%',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)'
          }}>
            <div style={{
              width: '50px',
              height: '50px',
              border: '4px solid #ddd',
              borderTop: '4px solid #000',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 20px'
            }}></div>
            <h3 style={{ color: '#000', marginBottom: '10px', fontSize: '20px' }}>
              Processing Your Receipt
            </h3>
            <p style={{ color: '#666', marginBottom: '20px', fontSize: '14px' }}>
              {inventoryLoading
                ? 'Loading inventory...'
                : 'Applying filters & matching items...'}
            </p>
            <div style={{
              width: '100%',
              height: '8px',
              background: '#ddd',
              borderRadius: '4px',
              overflow: 'hidden',
              margin: '15px 0'
            }}>
              <div style={{
                height: '100%',
                background: 'linear-gradient(90deg, #000, #666)',
                borderRadius: '4px',
                transition: 'width 0.5s ease',
                width: inventoryLoading ? '30%' : semanticProcessing ? '70%' : '95%'
              }}></div>
            </div>
            <p style={{ color: '#666', fontSize: '14px' }}>
              {inventoryLoading ? '30%' : semanticProcessing ? '70%' : '95%'} Complete
            </p>
            <p style={{ marginTop: '-9px', fontSize: '12px', color: '#666', textAlign: 'center' }}>
              Review for accuracy.
            </p>
          </div>
        </div>
      )}

      <div>
        {onBack && (
          <div className="form-header">
            <BackButton goTo={onBack} />
            {ocrData && <div className="ocr-indicator">📄 Data from OCR scan</div>}
          </div>
        )}

        {showBillImage && ocrData && (ocrData.previewImage || (ocrData.previewImages && ocrData.previewImages.length > 0)) && (
          <div className="bill-image-preview">
            <h4 className="bill-image-title">
              Original Bill Image
            </h4>
            <div className="bill-image-container" style={{ position: 'relative' }}>
              {/* Show current image from previewImages array or fallback to single previewImage */}
              {(() => {
                const currentPreview = ocrData.previewImages && ocrData.previewImages.length > 0
                  ? ocrData.previewImages[currentBillImageIndex]
                  : ocrData.previewImage;

                if (currentPreview === 'PDF_PLACEHOLDER') {
                  // Show PDF indicator instead of image
                  return (
                    <div className="pdf-preview-container" style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '40px',
                      background: '#f3f4f6',
                      borderRadius: '8px',
                      minHeight: '300px',
                      border: '2px dashed #d1d5db'
                    }}>
                      <FontAwesomeIcon icon={faFilePdf} style={{ fontSize: '80px', color: '#dc2626', marginBottom: '16px' }} />
                      <p style={{ fontSize: '18px', fontWeight: '600', color: '#374151', margin: '8px 0' }}>PDF Document</p>
                      <p style={{ fontSize: '14px', color: '#6b7280', margin: '4px 0' }}>
                        {ocrData.fileCount > 1 ? `File ${currentBillImageIndex + 1} of ${ocrData.fileCount}` : 'PDF File'}
                      </p>
                    </div>
                  );
                } else {
                  // Show image normally
                  return (
                    <img
                      src={currentPreview}
                      alt={`bill ${currentBillImageIndex + 1}`}
                      className="bill-image"
                    />
                  );
                }
              })()}

              {/* Navigation arrows - only show if multiple images */}
              {ocrData.previewImages && ocrData.previewImages.length > 1 && (
                <>
                  <button
                    className="bill-nav-arrow bill-nav-prev"
                    onClick={() => setCurrentBillImageIndex(prev =>
                      prev > 0 ? prev - 1 : ocrData.previewImages.length - 1
                    )}
                    title="Previous bill"
                    style={{
                      position: 'absolute',
                      left: '10px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'rgba(0, 0, 0, 0.7)',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '50%',
                      width: '40px',
                      height: '40px',
                      fontSize: '20px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 10,
                      transition: 'all 0.2s ease',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(0, 0, 0, 0.9)';
                      e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgba(0, 0, 0, 0.7)';
                      e.currentTarget.style.transform = 'translateY(-50%) scale(1)';
                    }}
                  >
                    <FontAwesomeIcon icon={faChevronLeft} />
                  </button>

                  <button
                    className="bill-nav-arrow bill-nav-next"
                    onClick={() => setCurrentBillImageIndex(prev =>
                      prev < ocrData.previewImages.length - 1 ? prev + 1 : 0
                    )}
                    title="Next bill"
                    style={{
                      position: 'absolute',
                      right: '10px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'rgba(0, 0, 0, 0.7)',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '50%',
                      width: '40px',
                      height: '40px',
                      fontSize: '20px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 10,
                      transition: 'all 0.2s ease',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(0, 0, 0, 0.9)';
                      e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgba(0, 0, 0, 0.7)';
                      e.currentTarget.style.transform = 'translateY(-50%) scale(1)';
                    }}
                  >
                    <FontAwesomeIcon icon={faChevronRight} />
                  </button>

                  {/* Page indicator */}
                  <div
                    className="bill-page-indicator"
                    style={{
                      position: 'absolute',
                      bottom: '-22px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      background: 'rgba(0, 0, 0, 0.7)',
                      color: '#fff',
                      padding: '4px 12px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: '600',
                      zIndex: 10,
                      boxShadow: '0 2px 6px rgba(0,0,0,0.3)'
                    }}
                  >
                    {currentBillImageIndex + 1} / {ocrData.previewImages.length}
                  </div>
                </>
              )}
            </div>
            {ocrData.items && ocrData.items.length === 0 && (
              <div className="ocr-failed-message">
                <p>OCR processing failed, but you can still enter items manually.</p>
                <p>Use the image above as a reference.</p>
              </div>
            )}
          </div>
        )}

        <div className="stock-in-form-box">
          {/* Bill Memory (Fix 12): the one-time introduction, once per user after a source turns On */}
          <IntroLine on={bmModeOn} />
          {/* **NEW: LIFO Revert Controls** */}
          {history.stack.length > 1 && (
            <div className="revert-controls">
              <button
                onClick={handleUndo}
                disabled={!canUndo}
                className="revert-btn undo-btn"
                title="Undo last change"
              >
                Undo
              </button>
              <span className="revert-status">
                {history.index} / {history.stack.length - 1} changes
              </span>
              <button
                onClick={handleRedo}
                disabled={!canRedo}
                className="revert-btn redo-btn"
                title="Redo last change"
              >
                Redo
              </button>
            </div>
          )}

          {/* Vendor Info Section */}
          <div className="vendor-info-row">
            <div className="form-group vendor-field" style={{ position: 'relative' }}>
              <label>
                {t('stockIn.vendorName')}
                {lastOrderFetching && (
                  <span style={{ marginLeft: 8, fontSize: 12, color: '#6b7280', display: 'inline-flex', alignItems: 'center', gap: 4, verticalAlign: 'middle' }}>
                    <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: 11 }} />
                    Loading last order...
                  </span>
                )}
              </label>
              <input
                ref={vendorInputRef}
                type="text"
                value={vendorName}
                onChange={e => {
                  // User is typing manually — reset the selection guard so search works again
                  vendorJustSelectedRef.current = false;
                  setVendorName(e.target.value);
                  // Clear the stored vendor object if user manually edits the name
                  if (selectedVendor && e.target.value !== selectedVendor.name) {
                    setSelectedVendor(null);
                    setLastOrderBanner(null);
                  }
                  setBmVendorCertain(false); // a typed vendor name is not a picked vendor
                  bmReportPick(null);
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    if (phoneInputRef.current) {
                      phoneInputRef.current.focus();
                    }
                  }
                }}
                onBlur={() => {
                  // Delay slightly to allow suggestion click to register first
                  setTimeout(() => {
                    // If a vendor was just selected via click, skip the blur handler entirely
                    if (vendorJustSelectedRef.current) return;
                    if (!selectedVendor && vendorName.trim()) {
                      const exactMatch = vendorSuggestions.find(v => v.name.toLowerCase() === vendorName.trim().toLowerCase());
                      if (exactMatch) {
                        handleVendorSelect(exactMatch);
                      } else {
                        // Fallback: try to fetch with just the name
                        handleVendorSelect({ name: vendorName.trim() });
                      }
                    }
                  }, 200);
                }}
                onFocus={() => {
                  setActiveVendorField('name');
                  if (vendorSuggestions.length > 0) setShowVendorDropdown(true);
                }}
                placeholder={t('stockIn.vendorNamePlaceholder')}
                className="stockin-vendor-name-input"
                maxLength={100}
                autoComplete="off"
              />
              {/* Vendor Autocomplete Dropdown */}
              {activeVendorField === 'name' && showVendorDropdown && (
                <div
                  ref={vendorDropdownRef}
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    maxHeight: '250px',
                    overflowY: 'auto',
                    backgroundColor: '#fff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    boxShadow: 'none',
                    zIndex: 1000,
                    marginTop: '4px'
                  }}
                >
                  {vendorSearchLoading ? (
                    <div style={{ padding: '12px', textAlign: 'center', color: '#666' }}>
                      <FontAwesomeIcon icon={faSpinner} spin /> Searching...
                    </div>
                  ) : vendorSuggestions.length > 0 ? (
                    vendorSuggestions.map((vendor, idx) => {
                      const isSelected = selectedVendor && selectedVendor.vendor_id === vendor.vendor_id;
                      const lastOrderStr = vendor.last_order_date
                        ? new Date(vendor.last_order_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })
                        : vendor.last_ordered_at
                          ? new Date(vendor.last_ordered_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })
                          : null;
                      return (
                        <div
                          key={vendor.vendor_id || idx}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => handleVendorSelect(vendor, null, { bmCertain: true })}
                          style={{
                            padding: '10px 14px',
                            cursor: 'pointer',
                            borderBottom: idx < vendorSuggestions.length - 1 ? '1px solid #f0f0f0' : 'none',
                            transition: 'background 0.15s ease',
                            WebkitTapHighlightColor: 'transparent',
                            background: isSelected ? '#f3f4f6' : 'transparent',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 8
                          }}
                          onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = '#f9fafb'; }}
                          onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent'; }}
                        >
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 600, fontSize: '14px', color: '#111', marginBottom: '2px', display: 'flex', alignItems: 'center', gap: 6 }}>
                              {vendor.name}
                              {isSelected && <span style={{ fontSize: 11, background: '#16a34a', color: '#fff', borderRadius: 4, padding: '1px 6px', fontWeight: 600 }}>✓ Selected</span>}
                            </div>
                            <div style={{ fontSize: '12px', color: '#666', display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: '8px', flexWrap: 'wrap', textAlign: 'left' }}>
                              <span style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'flex-start',
                                padding: '2px 8px',
                                borderRadius: '4px',
                                backgroundColor: vendor.vendor_category === 'meat' ? '#fee' :
                                  vendor.vendor_category === 'seafood' ? '#eff' :
                                    vendor.vendor_category === 'vegetables' ? '#efe' :
                                      vendor.vendor_category === 'fruits' ? '#fef' :
                                        '#f5f5f5',
                                color: '#333',
                                fontSize: '11px',
                                fontWeight: 500,
                                textAlign: 'left'
                              }}>
                                {vendor.vendor_category || 'others'}
                              </span>
                              {vendor.contact_phone && (
                                <span>{vendor.contact_phone}</span>
                              )}
                              {vendor.total_orders > 0 && (
                                <span>{vendor.total_orders} total orders</span>
                              )}
                              {lastOrderStr && (
                                <span style={{ color: '#059669', fontWeight: 500 }}>Last: {lastOrderStr}</span>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div style={{ padding: '12px', textAlign: 'center', color: '#999', fontSize: '13px' }}>
                      No vendors found
                    </div>
                  )}
                </div>
              )}
            </div>
            {/* Bill Memory: "Who is this bill from?" — up to 3 likely vendors when the bill's vendor is not certain */}
            {bmScreenOn && bmScan && bmScan.vendor && (
              <VendorButtons
                suggestions={!bmScan.vendor.certain && !bmVendorCertain ? bmScan.vendor.suggestions : []}
                gstinNote={bmScan.vendor.gstin_note}
                onPick={bmPickVendor}
              />
            )}
            {/* Bill Memory (Fix 12): "<Vendor>: your first bill needed 9 taps; this one needs 1." */}
            <VendorProgressLine vendorId={bmContextVendorId} need={bmCounts.need} on={bmModeOn && bmCounts.total > 0} />
          </div>
          <div className="form-group vendor-field" style={{ position: 'relative' }}>
            <label>{t('stockIn.phoneNumber')}</label>
            <input
              ref={phoneInputRef}
              type="text"
              value={vendorPhone}
              onChange={e => handleVendorPhoneTyped(e.target.value)}
              onFocus={() => {
                setActiveVendorField('phone');
                if (vendorSuggestions.length > 0) setShowVendorDropdown(true);
              }}
              onBlur={() => {
                setPhoneTouched(true);
                setTimeout(() => {
                  if (!selectedVendor && vendorPhone.trim()) {
                    const exactMatch = vendorSuggestions.find(v => v.contact_phone === vendorPhone.trim());
                    if (exactMatch) {
                      handleVendorSelect(exactMatch);
                    }
                  }
                }, 200);
              }}
              onKeyDown={(e) => {
                const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Home', 'End'];
                if (allowedKeys.includes(e.key)) return;
                if ((e.ctrlKey || e.metaKey) && ['a', 'c', 'v', 'x'].includes(e.key.toLowerCase())) return;
                if (!/^[0-9]$/.test(e.key)) {
                  e.preventDefault();
                }
              }}
              onPaste={(e) => {
                const pasteText = (e.clipboardData || window.clipboardData).getData('text');
                e.preventDefault();
                // S2: a PASTE is a finished number, not a half-typed one, so it is marked
                // touched immediately and warns on the spot. Typing stays lenient until the
                // first blur — scolding someone on the third digit teaches them nothing.
                handleVendorPhoneTyped(pasteText, { markTouched: true });
              }}
              placeholder={t('stockIn.phonePlaceholder')}
              className="stockin-vendor-phone-input"
              // 15, not 10. A pasted 15-digit number has to stay VISIBLE so the warning can
              // say "15 entered" — capping the box at 10 would silently swallow the extra
              // digits and leave a plausible-looking wrong number the user never chose.
              maxLength={15}
              inputMode="numeric"
              pattern="[0-9]*"
              style={showPhoneError ? { borderColor: '#dc2626' } : undefined}
              aria-invalid={showPhoneError || undefined}
            />
            {showLegacyPhoneWarning && (
              <div
                className="stockin-vendor-phone-legacy"
                role="status"
                style={{ color: '#b45309', fontSize: '12px', marginTop: '4px', lineHeight: 1.35 }}
              >
                This vendor's saved number isn't a valid 10-digit mobile. Please update it when you can.
              </div>
            )}
            {showPhoneError && (
              <div
                className="stockin-vendor-phone-error"
                role="alert"
                style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px', lineHeight: 1.35 }}
              >
                {phoneValidationError}
              </div>
            )}
            {activeVendorField === 'phone' && showVendorDropdown && (
              <div
                ref={vendorDropdownRef}
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  maxHeight: '250px',
                  overflowY: 'auto',
                  backgroundColor: '#fff',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  boxShadow: 'none',
                  zIndex: 1000,
                  marginTop: '4px'
                }}
              >
                {vendorSearchLoading ? (
                  <div style={{ padding: '12px', textAlign: 'center', color: '#666' }}>
                    <FontAwesomeIcon icon={faSpinner} spin /> Searching...
                  </div>
                ) : vendorSuggestions.length > 0 ? (
                  vendorSuggestions.map((vendor, idx) => {
                    const isSelected = selectedVendor && selectedVendor.vendor_id === vendor.vendor_id;
                    const lastOrderStr = vendor.last_order_date
                      ? new Date(vendor.last_order_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })
                      : vendor.last_ordered_at
                        ? new Date(vendor.last_ordered_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })
                        : null;
                    return (
                      <div
                        key={vendor.vendor_id || idx}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => handleVendorSelect(vendor, null, { bmCertain: true })}
                        style={{
                          padding: '10px 14px',
                          cursor: 'pointer',
                          borderBottom: idx < vendorSuggestions.length - 1 ? '1px solid #f0f0f0' : 'none',
                          transition: 'background 0.15s ease',
                          WebkitTapHighlightColor: 'transparent',
                          background: isSelected ? '#f3f4f6' : 'transparent',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 8
                        }}
                        onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = '#f9fafb'; }}
                        onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent'; }}
                      >
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 600, fontSize: '14px', color: '#111', marginBottom: '2px', display: 'flex', alignItems: 'center', gap: 6 }}>
                            {vendor.name}
                            {isSelected && <span style={{ fontSize: 11, background: '#16a34a', color: '#fff', borderRadius: 4, padding: '1px 6px', fontWeight: 600 }}>✓ Selected</span>}
                          </div>
                          <div style={{ fontSize: '12px', color: '#666', display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: '8px', flexWrap: 'wrap', textAlign: 'left' }}>
                            <span style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'flex-start',
                              padding: '2px 8px',
                              borderRadius: '4px',
                              backgroundColor: vendor.vendor_category === 'meat' ? '#fee' :
                                vendor.vendor_category === 'seafood' ? '#eff' :
                                  vendor.vendor_category === 'vegetables' ? '#efe' :
                                    vendor.vendor_category === 'fruits' ? '#fef' :
                                      '#f5f5f5',
                              color: '#333',
                              fontSize: '11px',
                              fontWeight: 500,
                              textAlign: 'left'
                            }}>
                              {vendor.vendor_category || 'others'}
                            </span>
                            {vendor.contact_phone && (
                              <span>{vendor.contact_phone}</span>
                            )}
                            {vendor.total_orders > 0 && (
                              <span>{vendor.total_orders} total orders</span>
                            )}
                            {lastOrderStr && (
                              <span style={{ color: '#059669', fontWeight: 500 }}>Last: {lastOrderStr}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div style={{ padding: '12px', textAlign: 'center', color: '#999', fontSize: '13px' }}>
                    No vendors found
                  </div>
                )}
              </div>
            )}
          </div>
        </div>



        <div className="items-header">
          <h3 style={{ fontSize: '1.3rem', margin: 0 }}>{t('stockIn.items')}</h3>
          <div className="items-header-actions">
            {/* WhatsApp-style item search */}
            {showItemSearch ? (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  flex: '1 1 0',
                  minWidth: 0,
                  maxWidth: '220px',
                  background: '#ffffff',
                  border: '1px solid #000000',
                  borderRadius: '8px',
                  height: '37px',
                  marginRight: '4px',
                  paddingLeft: '10px'
                }}
              >
                <FontAwesomeIcon icon={faMagnifyingGlass} style={{ color: '#111', fontSize: '13px' }} />
                <input
                  ref={itemSearchInputRef}
                  type="text"
                  autoFocus
                  value={itemSearchQuery}
                  onChange={(e) => setItemSearchQuery(e.target.value)}
                  placeholder={t('stockIn.searchItemsPlaceholder')}
                  style={{
                    flex: 1,
                    minWidth: 0,
                    border: 'none',
                    outline: 'none',
                    background: 'transparent',
                    color: '#111827',
                    fontSize: '13px',
                    fontWeight: '500',
                    padding: '6px 10px',
                    fontFamily: 'inherit'
                  }}
                />
                <button
                  type="button"
                  aria-label="Close search"
                  onClick={() => { setShowItemSearch(false); setItemSearchQuery(''); }}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#111',
                    fontSize: '15px',
                    padding: '0 10px',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <FontAwesomeIcon icon={faXmark} />
                </button>
              </div>
            ) : (
              <button
                type="button"
                aria-label="Search items"
                title="Search items"
                onClick={() => setShowItemSearch(true)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: '#ffffff',
                  border: '1px solid #000000',
                  borderRadius: '8px',
                  height: '37px',
                  width: '37px',
                  marginRight: '4px',
                  cursor: 'pointer',
                  color: '#111'
                }}
              >
                <FontAwesomeIcon icon={faMagnifyingGlass} style={{ fontSize: '14px' }} />
              </button>
            )}
            <div
              className={showItemSearch ? "header-desktop-only" : ""}
              style={{
                display: 'flex',
                alignItems: 'center',
                background: '#ffffff',
                border: '1px solid #000000',
                borderRadius: '8px',
                height: '37px',
                marginRight: '4px',
                cursor: 'pointer',
                paddingRight: '10px'
              }}
              onClick={openProductionPicker}
              title="Select Operational/Procurement Date"
            >
              <input
                type="text"
                readOnly
                value={(() => {
                  if (!productionDate) return '';
                  const s = String(productionDate).split('T')[0];
                  const parts = s.split('-');
                  if (parts.length === 3 && parts[0].length === 4) {
                    return `${parts[2]}-${parts[1]}-${parts[0]}`;
                  }
                  return s;
                })()}
                className="stockin-production-date-input"
                style={{
                  padding: '6px 10px',
                  background: 'transparent',
                  color: '#111827',
                  border: 'none',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  height: '100%',
                  boxSizing: 'border-box',
                  outline: 'none',
                  fontFamily: 'inherit',
                  width: '95px',
                  textAlign: 'center'
                }}
                required
              />
              <FontAwesomeIcon icon={faCalendarRegular} style={{ color: '#111' }} />
            </div>

            {/* Tax for Stock In toggle — same setting as Settings page, controllable inline */}
            <div
              className="tax-toggle-inline header-desktop-only"
              title="Show per-item tax (CGST/SGST) fields for this stock-in"
              style={{ display: 'flex', alignItems: 'center', gap: '6px', height: '37px', flex: '0 0 auto' }}
            >
              <span style={{ fontSize: '12px', fontWeight: 600, color: '#111', whiteSpace: 'nowrap' }}>Tax</span>
              <button
                type="button"
                role="switch"
                aria-checked={taxForStockInEnabled}
                aria-label="Tax for Stock In"
                onClick={() => handleToggleTaxForStockIn(!taxForStockInEnabled)}
                style={{
                  position: 'relative',
                  width: '40px',
                  height: '22px',
                  borderRadius: '11px',
                  border: `1px solid ${taxForStockInEnabled ? '#000000' : '#cbd5e1'}`,
                  background: taxForStockInEnabled ? '#000000' : '#e2e8f0',
                  cursor: 'pointer',
                  padding: 0,
                  flex: '0 0 auto',
                  transition: 'background 0.2s ease, border-color 0.2s ease'
                }}
              >
                <span
                  style={{
                    position: 'absolute',
                    top: '2px',
                    left: taxForStockInEnabled ? '20px' : '2px',
                    width: '16px',
                    height: '16px',
                    borderRadius: '50%',
                    background: '#fff',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.25)',
                    transition: 'left 0.2s ease'
                  }}
                />
              </button>
            </div>

            <button
              type="button"
              className="expiry-threshold-btn header-desktop-only"
              onClick={() => goTo && goTo('expiry-thresholds')}
              title="Configure expiry tracking for items"
              style={{
                padding: '8px 16px',
                background: '#000',
                color: '#fff',
                border: '1px solid #000',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              Expiry
            </button>

            {/* Mobile 3-dot overflow menu */}
            <div ref={overflowMenuRef} className="header-mobile-only" style={{ position: 'relative' }}>
              <button
                type="button"
                className="items-header-overflow-btn"
                onClick={() => setShowOverflowMenu(!showOverflowMenu)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: '#ffffff',
                  border: '1px solid #000000',
                  borderRadius: '8px',
                  height: '37px',
                  width: '37px',
                  cursor: 'pointer',
                  color: '#111'
                }}
                title="More Actions"
              >
                <FontAwesomeIcon icon={faEllipsisVertical} />
              </button>

              {showOverflowMenu && (
                <div
                  className="items-header-overflow-dropdown"
                  style={{
                    position: 'absolute',
                    top: '44px',
                    right: 0,
                    backgroundColor: '#ffffff',
                    border: '1px solid #cbd5e1',
                    borderRadius: '8px',
                    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.18)',
                    padding: '12px 16px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px',
                    zIndex: 10000,
                    minWidth: '160px'
                  }}
                >
                  {/* Tax toggle inside dropdown */}
                  <div
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}
                    title="Show per-item tax (CGST/SGST) fields for this stock-in"
                  >
                    <span style={{ fontSize: '13px', fontWeight: 600, color: '#111' }}>Tax</span>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={taxForStockInEnabled}
                      aria-label="Tax for Stock In"
                      onClick={() => handleToggleTaxForStockIn(!taxForStockInEnabled)}
                      style={{
                        position: 'relative',
                        width: '40px',
                        height: '22px',
                        borderRadius: '11px',
                        border: `1px solid ${taxForStockInEnabled ? '#000000' : '#cbd5e1'}`,
                        background: taxForStockInEnabled ? '#000000' : '#e2e8f0',
                        cursor: 'pointer',
                        padding: 0,
                        flex: '0 0 auto',
                        transition: 'background 0.2s ease, border-color 0.2s ease'
                      }}
                    >
                      <span
                        style={{
                          position: 'absolute',
                          top: '2px',
                          left: taxForStockInEnabled ? '20px' : '2px',
                          width: '16px',
                          height: '16px',
                          borderRadius: '50%',
                          background: '#fff',
                          boxShadow: '0 1px 2px rgba(0,0,0,0.25)',
                          transition: 'left 0.2s ease'
                        }}
                      />
                    </button>
                  </div>

                  <div style={{ height: '1px', backgroundColor: '#cbd5e1', margin: '4px 0' }} />

                  {/* Expiry Button inside dropdown */}
                  <button
                    type="button"
                    onClick={() => {
                      setShowOverflowMenu(false);
                      if (goTo) goTo('expiry-thresholds');
                    }}
                    title="Configure expiry tracking for items"
                    style={{
                      padding: '8px 12px',
                      background: '#000',
                      color: '#fff',
                      border: '1px solid #000',
                      borderRadius: '8px',
                      fontSize: '13px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      width: '100%',
                      textAlign: 'center',
                      boxSizing: 'border-box'
                    }}
                  >
                    Expiry
                  </button>

                  {originalItems.length > 0 && (
                    <>
                      <div style={{ height: '1px', backgroundColor: '#cbd5e1', margin: '4px 0' }} />
                      <button
                        type="button"
                        onClick={() => {
                          setShowOverflowMenu(false);
                          revertAll();
                        }}
                        title="Revert all items to original OCR text"
                        style={{
                          padding: '8px 12px',
                          background: '#000',
                          color: '#fff',
                          border: '1px solid #000',
                          borderRadius: '8px',
                          fontSize: '13px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          width: '100%',
                          textAlign: 'center',
                          boxSizing: 'border-box'
                        }}
                      >
                        Revert All
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
            {originalItems.length > 0 && !showItemSearch && (
              <button
                type="button"
                className="revert-all-btn revert-all-btn--header header-desktop-only"
                onClick={revertAll}
                title="Revert all items to original OCR text"
                style={{
                  padding: '6px 14px',
                  background: '#000',
                  color: '#fff',
                  border: '1px solid #000',
                  borderRadius: '8px',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.35)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.25)';
                }}
              >
                Revert All
              </button>
            )}
          </div>
        </div>
        {/* What the scanned bill's printed date did to the purchase date (see billDateNote) */}
        {billDateNote && (
          <div
            className="stockin-bill-date-note"
            role="status"
            style={{
              margin: '0 0 12px',
              padding: '8px 12px',
              borderRadius: 8,
              fontSize: 13,
              lineHeight: 1.4,
              background: billDateNote.tone === 'warn' ? '#fffbeb' : '#f3f4f6',
              border: `1px solid ${billDateNote.tone === 'warn' ? '#fde68a' : '#e5e7eb'}`,
              color: billDateNote.tone === 'warn' ? '#92400e' : '#374151',
            }}
          >
            {billDateNote.text}
          </div>
        )}
        {/* AI-FIRST: Validation Summary Banner */}
        {validationSummary && (
          <div style={{
            background: validationSummary.passed ? '#d4edda' : '#fff3cd',
            border: `2px solid ${validationSummary.passed ? '#c3e6cb' : '#ffeaa7'}`,
            borderRadius: '12px',
            padding: '16px 20px',
            marginBottom: '20px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: '700', fontSize: '16px', marginBottom: '8px', color: '#212529' }}>
                  {validationSummary.passed ? '✅ Validation Passed' : '⚠️ Review Required'}
                </div>
                <div style={{ fontSize: '14px', color: '#495057', display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                  {validationSummary.summary.green_items > 0 && (
                    <span style={{ padding: '4px 10px', background: '#d4edda', borderRadius: '6px', fontWeight: '600' }}>
                      🟢 {validationSummary.summary.green_items} High Confidence
                    </span>
                  )}
                  {validationSummary.summary.yellow_items > 0 && (
                    <span style={{ padding: '4px 10px', background: '#fff3cd', borderRadius: '6px', fontWeight: '600' }}>
                      🟡 {validationSummary.summary.yellow_items} Medium Confidence
                    </span>
                  )}
                  {validationSummary.summary.red_items > 0 && (
                    <span style={{ padding: '4px 10px', background: '#f8d7da', borderRadius: '6px', fontWeight: '600' }}>
                      🔴 {validationSummary.summary.red_items} Low Confidence
                    </span>
                  )}
                </div>
                {validationSummary.issues && validationSummary.issues.length > 0 && (
                  <div style={{ marginTop: '10px', fontSize: '13px', color: '#721c24' }}>
                    <strong>Issues:</strong>
                    <ul style={{ margin: '6px 0 0 20px', paddingLeft: 0 }}>
                      {validationSummary.issues.slice(0, 3).map((issue, idx) => (
                        <li key={idx}>{issue}</li>
                      ))}
                      {validationSummary.issues.length > 3 && (
                        <li>...and {validationSummary.issues.length - 3} more</li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
              <div>
                {validationSummary.summary.can_auto_save ? (
                  <div style={{ padding: '8px 16px', background: '#28a745', color: '#fff', borderRadius: '8px', fontWeight: '600', fontSize: '14px' }}>
                    ✓ Ready to Save
                  </div>
                ) : (
                  <div style={{ padding: '8px 16px', background: '#ffc107', color: '#000', borderRadius: '8px', fontWeight: '600', fontSize: '14px' }}>
                    ⚠ Review Needed
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        {bmScreenOn && headerText(bmCounts) && (
          <div className="stockin-unit-cost-display" style={{ margin: '4px 0 8px' }}>
            <span>{headerText(bmCounts)}</span>
          </div>
        )}
        {items.map((it, idx) => {
          const errs = itemErrors[idx] || [];
          // WhatsApp-style search: hide cards whose name doesn't match the query
          const searchQ = itemSearchQuery.trim().toLowerCase();
          const searchHidden = searchQ !== '' && !String(it.item_name || '').toLowerCase().includes(searchQ);
          // Split errors by field so each message renders near its input
          const nameErrs = errs.filter(e => /^name /i.test(e) || /duplicate name/i.test(e));
          const categoryErrs = errs.filter(e => /category/i.test(e));
          const qtyErrs = errs.filter(e => /quantity/i.test(e));
          const priceErrs = errs.filter(e => /unit price/i.test(e));
          const batchErrs = errs.filter(e => /batch/i.test(e));
          const expiryErrs = errs.filter(e => /expiry/i.test(e));

          // ✅ FIXED: Proper status determination with fallback to pending
          const matchStatus = it.matchStatus || it.match_type || 'pending';
          const itemNameLength = (it.item_name || '').trim().length;

          // Status classification
          const isPending = matchStatus === 'pending' || itemNameLength <= 2;
          const isExact = matchStatus === 'exact' && itemNameLength > 2;
          const isFuzzy = matchStatus === 'fuzzy' && itemNameLength > 2;
          const isNone = matchStatus === 'none' && itemNameLength > 2;

          // **NEW: Color-coded status classes**
          const isNonConsumable = String(it.category || '').replace(/[_\s-]/g, '').toLowerCase() === 'nonconsumable';

          // Red used to mean two different things at once — "not found, needs your
          // attention" and "classified as non-consumable, nothing to do" — so a bill of
          // cleaning supplies looked like a screen full of problems. A row the user has
          // actually classified gets its own colour, and red goes back to meaning one thing.
          const isSettledNonConsumable = isNonConsumable && (it.redClassified || matchStatus !== 'none');

          let statusClass = '';
          if (isSettledNonConsumable) statusClass = 'status-nonconsumable';
          else if (isNonConsumable) statusClass = 'status-red';
          else if (isExact) statusClass = 'status-green';
          else if (isFuzzy) statusClass = 'status-yellow';
          else if (isNone) statusClass = 'status-red';

          // Validation errors for this item
          const itemValidationErrors = fieldValidationErrors[idx] || {};
          const hasValidationErrors = Object.keys(itemValidationErrors).length > 0;

          // Legacy box class for backward compatibility
          const boxClass = isNonConsumable ? 'none-match' : (isPending ? '' : (isExact ? 'exact-match' : isFuzzy ? 'partial-match' : isNone ? 'none-match' : ''));

          return (
            <div key={idx} data-item-index={idx} className={`item-box ${boxClass} ${statusClass} ${errs.length || hasValidationErrors ? 'has-errors' : ''}`} style={searchHidden ? { display: 'none' } : undefined}>
              <button onClick={() => removeItem(idx)} className="delete-button" type="button">×</button>
              {/* Reset just this row. Only offered when there is a snapshot to reset TO —
                  i.e. after an OCR scan or an autofill, which is exactly when one misread
                  line needs undoing without discarding the user's work on every other. */}
              {loadedRowFor(it) && (
                <button
                  onClick={() => revertItem(idx)}
                  className="row-revert-button"
                  type="button"
                  title="Reset this row to what was loaded"
                  style={{
                    position: 'absolute',
                    top: '8px',
                    right: '38px',
                    background: 'transparent',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    color: '#4b5563',
                    fontSize: '11px',
                    lineHeight: 1,
                    padding: '4px 7px',
                    cursor: 'pointer'
                  }}
                >
                  Reset
                </button>
              )}
              {/* ✅ Status indicators - only show when item name is filled */}
              {!isPending && isExact && <FontAwesomeIcon icon={faCheck} className="match-indicator exact" />}
              {!isPending && isFuzzy && <span className="match-indicator partial">⚠</span>}
              {!isPending && isNone && <span className="match-indicator none">×</span>}

              <div className="form-group">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
                  <label>{t('stockIn.itemName')}</label>
                </div>
                <input
                  type="text"
                  name="item_name"
                  placeholder={t('stockIn.itemNamePlaceholder')}
                  value={it.item_name}
                  onChange={e => handleNameChange(idx, e.target.value)}
                  onKeyDown={e => handleItemNameKeyDown(idx, e)}
                  onBlur={() => { triggerAIValidation(idx); lockIfNeeded(idx); }}
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="none"
                  spellCheck={false}
                  className={`stockin-item-name-input ${matchStatus}`}
                  style={{
                    textTransform: 'none',
                    backgroundColor: it.is_translated ? '#fffbcc' : (isPending ? 'white' : (isExact ? '#f1fbf3' : isFuzzy ? '#fffdf2' : isNone ? '#fff5f5' : 'white')),
                    color: 'black',
                    fontWeight: 'normal'
                  }}
                />
                {/* Bill Memory screen: one grey reason line ("Change" on a green row), then grey notes — today's small text, no icons */}
                {bmScreenOn && (() => {
                  const view = rowView(it);
                  if (!view) return null;
                  const showChange = it.bm_colour === 'green' && !(suggestionsMap[idx] && suggestionsMap[idx].length > 0);
                  return (
                    <>
                      {(view.reasonText || showChange) && (
                        <div className="stockin-unit-cost-display">
                          <span>
                            {view.reasonText}
                            {showChange && (
                              <>
                                {view.reasonText ? ' ' : ''}
                                <button type="button" onClick={() => bmReopenChips(idx)}
                                  style={{ background: 'none', border: 'none', padding: 0, color: 'inherit', textDecoration: 'underline', cursor: 'pointer', font: 'inherit' }}>
                                  Change
                                </button>
                              </>
                            )}
                          </span>
                        </div>
                      )}
                      {view.notes.map((note, k) => (
                        <div key={`bm-note-${k}`} className="stockin-unit-cost-display"><span>{note}</span></div>
                      ))}
                    </>
                  );
                })()}
                {/* Translation banner removed - only show English translations */}
                {/* Only show suggestions if NOT red (pending/none status) */}
                {suggestionsMap[idx] && suggestionsMap[idx].length > 0 && (!isNone || bmScreenOn) && (
                  <div className="suggestions-row">
                    {suggestionsMap[idx]
                      .filter(s => {
                        // Today: the typed-text chip stays hidden. Bill Memory screen: it is shown,
                        // last, as "None of these, add as new" (sorted below).
                        if (s.isTypedOption) {
                          return bmScreenOn;
                        }
                        return true;
                      })
                      .sort((a, b) => (a.isTypedOption === b.isTypedOption ? 0 : a.isTypedOption ? 1 : -1))
                      .map((s, sIdx) => (
                        <button
                          type="button"
                          key={`${s.name}-${sIdx}`}
                          className="suggestion-chip"
                          data-suggestion-index={sIdx}
                          onClick={() => applySuggestion(idx, s)}
                          onFocus={() => setActiveSuggestionIndexMap(prev => ({ ...prev, [idx]: sIdx }))}
                          onKeyDown={(e) => handleSuggestionChipKeyDown(idx, sIdx, s, e)}
                          title={s.isTypedOption ? `Add as new item: ${s.name}` : `Apply from inventory: ${s.name}`}
                          aria-selected={activeSuggestionIndexMap[idx] === sIdx}
                          style={{
                            background: activeSuggestionIndexMap[idx] === sIdx ? '#e7f5ff' : (s.isTypedOption ? '#f1f3f5' : '#f1f3f5'),
                            color: '#212529',
                            fontWeight: s.isTypedOption ? '600' : 'normal',
                            border: activeSuggestionIndexMap[idx] === sIdx ? '1.5px solid #1971c2' : '1px solid #adb5bd',
                            WebkitTapHighlightColor: 'transparent',
                            outline: activeSuggestionIndexMap[idx] === sIdx ? '2px solid rgba(25, 113, 194, 0.18)' : 'none',
                            userSelect: 'none'
                          }}
                        >
                          {s.isTypedOption && bmScreenOn ? 'None of these, add as new' : <>{s.isTypedOption && <span style={{ color: '#000', fontWeight: 'bold' }}>+ </span>}{s.name}</>}
                        </button>
                      ))}
                  </div>
                )}
                {bmScreenOn && bmRedCardDue(it) && (
                  <RedCardActions
                    row={it}
                    inventoryItems={inventoryItems}
                    onAddNew={() => applyItems(prev => prev.map((row, i) => (i === idx
                      ? { ...classifyRedRow(row, 'consumable'), bm_action: 'created_new', bm_settled: true, bmShowRedCard: false }
                      : row)))}
                    onNonConsumable={() => applyItems(prev => prev.map((row, i) => (i === idx
                      ? { ...classifyRedRow(row, 'non_consumable'), bm_action: 'non_consumable', bm_settled: true, bmShowRedCard: false }
                      : row)))}
                    onPick={(inv) => applySuggestion(idx, {
                      name: inv.name, item_id: inv.item_id, category: inv.category_name || inv.category,
                      unit: inv.unit_symbol || inv.unit, unit_symbol: inv.unit_symbol || inv.unit, tax_slab: inv.tax_slab,
                      bmMarks: { bm_action: 'changed', bm_user_changed: true, bm_settled: true, bmShowRedCard: false },
                    })}
                  />
                )}
                {nameErrs.length > 0 && (
                  <ul className="item-error-list">
                    {nameErrs.map((er, i2) => {
                      const isDupName = /duplicate name/i.test(er);
                      if (isDupName) {
                        return (
                          <li
                            key={`name-${i2}`}
                            style={{ cursor: 'pointer', userSelect: 'none' }}
                            onClick={() => handleDuplicateNameClick(idx, it.item_name)}
                            title="Click to find duplicate item card"
                          >
                            {er}
                          </li>
                        );
                      }
                      return <li key={`name-${i2}`}>{er}</li>;
                    })}
                  </ul>
                )}
              </div>

              <div className="form-group"><label>{t('stockIn.category')}</label>
                <UsageStyleDropdown
                  options={categories}
                  value={it.category}
                  onChange={(val) => handleInputChange(idx, 'category', val)}
                  placeholder={t('stockIn.categoryPlaceholder')}
                />
                {/* The default announces itself. Silently rewriting a category the bill
                    supplied would misfile a genuinely new ingredient, and the user would
                    only find out later, in a report. The dropdown stays fully editable —
                    picking anything else clears this. */}
                {it.autoNonConsumable && (
                  <div
                    className="stockin-noncons-badge"
                    style={{
                      marginTop: '4px',
                      fontSize: '11.5px',
                      fontWeight: 600,
                      color: '#6d28d9',
                      lineHeight: 1.35
                    }}
                  >
                    Not in inventory → set as Non-Consumable
                  </div>
                )}
                {categoryErrs.length > 0 && (
                  <ul className="item-error-list">
                    {categoryErrs.map((er, i2) => <li key={`cat-${i2}`}>{er}</li>)}
                  </ul>
                )}
              </div>

              <div className="triple-inline"><div className="form-group"><label>{t('stockIn.quantity')}</label><input type="number" placeholder={t('stockIn.quantityPlaceholder')} value={it.quantity} onChange={e => handleInputChange(idx, 'quantity', e.target.value)} onBlur={e => handleInputBlur(idx, 'quantity', e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); const card = e.target.closest('.item-box'); if (card) { const tp = card.querySelector('.stockin-total-price-input'); if (tp) tp.focus(); } } }} step={isPcsUnit(it.unit) ? "1" : "0.0001"} min="0" className="stockin-quantity-input" />
                {/* Bill Memory (D17): a quantity filled from memory says so, small, under the quantity */}
                {bmScreenOn && it.bm_qty_from_memory && (() => {
                  const view = rowView(it);
                  return view && view.qtyNote ? <div className="stockin-unit-cost-display"><span>{view.qtyNote}</span></div> : null;
                })()}
                {/* M8 — "pieces can't have decimals" existed only on the server, and it
                    arrived in a popup with no way to fix it from inside. Said here, in the
                    SAME WORDS the gate uses, while the number is still being typed. The
                    client is the convenience; the server check stays and is the guarantee. */}
                {(() => {
                  const q = Number(it.quantity);
                  if (!isPcsUnit(it.unit)) return null;
                  if (!Number.isFinite(q) || q <= 0 || Number.isInteger(q)) return null;
                  return (
                    <div className="stockin-qty-hint stockin-qty-hint-red"
                         style={{ marginTop: 4, fontSize: 11.5, lineHeight: 1.35, fontWeight: 600, color: '#b91c1c' }}>
                      ⚠ pieces can't have decimals — you entered {q}. If this item is sold by weight, change the unit to kg or g.
                    </div>
                  );
                })()}
                {qtyErrs.length > 0 && (
                  <ul className="item-error-list">
                    {qtyErrs.map((er, i2) => <li key={`qty-${i2}`}>{er}</li>)}
                  </ul>
                )}
                {/* Said here, while the number is still being typed, rather than after the
                    whole bill is finished and the gate rejects it. Same bands as the gate. */}
                {(() => {
                  const hint = quantityHintFor(it);
                  if (!hint) return null;
                  /**
                   * S2 — up to four hint lines used to stack under one input, and tapping
                   * the guard's own quick-add chip could trigger this one's "more than
                   * usual" nag about the number the guard had just recommended. So at most
                   * one speaks, and when both would, the piece hint wins: it is the more
                   * specific of the two.
                   *
                   * F8 — BUT ONLY WHEN IT IS ACTUALLY SAYING SOMETHING. S2 suppressed this
                   * whenever a pcs row merely HAD a profile, on the premise that the piece
                   * hint is strictly more informative. It is not: `pieceBandVerdict` is a
                   * PRICE-PER-PIECE band. It never reads typicalQuantity or
                   * quantityStdDev, and it returns null outright when the total price is
                   * missing or zero.
                   *
                   * So on a profiled pcs row, 2,000 pcs typed where 100 is usual produced
                   * NO live hint at all while the price was still blank — and a cheerful
                   * green "₹6.88 per piece ✓" once the total scaled with the quantity,
                   * which is exactly what a proportional typo looks like. The 20× server
                   * block still fires at submit, so this was warning late rather than
                   * losing data; but it removed the live warning for precisely the mistake
                   * the quantity band exists to catch.
                   */
                  const pieceVerdict = pieceHintFor(it);
                  const pieceIsSpeaking = !!pieceVerdict && pieceVerdict.level !== 'green';
                  if (pieceIsSpeaking) return null;
                  return (
                    <div
                      className={`stockin-qty-hint stockin-qty-hint-${hint.level}`}
                      style={{
                        marginTop: '4px',
                        fontSize: '11.5px',
                        lineHeight: 1.35,
                        fontWeight: 500,
                        color: hint.level === 'red' ? '#b91c1c' : '#b45309'
                      }}
                    >
                      {hint.message}
                    </div>
                  );
                })()}

                {/* ── PIECE GUARD §5 ────────────────────────────────────────────
                    Everything here exists so the wrong number never gets typed in
                    the first place. The field stays in `pcs` throughout — the chips
                    only change what goes INTO it. */}
                {(() => {
                  const profile = pieceProfileFor(it);
                  const caption = pieceCaptionFor(profile);
                  const chips = quickAddChipsFor(profile);
                  const pHint = pieceHintFor(it);
                  // The free-goods affordance belongs on every pcs row, profile or not —
                  // it is how a scheme becomes expressible at all (M7).
                  if (!caption && chips.length === 0 && !pHint && !isPcsUnit(it.unit)) return null;

                  return (
                    <div style={{ marginTop: 5 }}>
                      {/* §5.3 — sits on the row permanently. A new staff member on
                          their first shift has no idea what convention this business
                          uses; this tells them, without a modal, without training. */}
                      {caption && (
                        <div style={{ fontSize: 11.5, color: '#6b7280', marginBottom: 4 }}>
                          {caption}
                        </div>
                      )}



                      {/* ── M4 §8.4 — "IS THIS RIGHT FOR THIS VENDOR?" ─────────────────
                          The backend has always computed `borrowedFromItemLevel`: this
                          vendor has no profile of their own, so the item-level pack is
                          being lent to them. Nothing read it. Your regular distributor may
                          pack in 4s while the kirana down the road packs in 6s, and
                          silently assuming one vendor's packing is another's is exactly
                          what §8.4 exists to prevent — so it is ASKED, once, in one tap,
                          and then that vendor has its own memory. */}
                      {profile?.borrowedFromItemLevel && currentVendorId && (
                        <div style={{
                          marginTop: 4, padding: '6px 9px', borderRadius: 7,
                          background: '#eff6ff', border: '1px solid #bfdbfe',
                          fontSize: 11.5, color: '#1e40af',
                        }}>
                          Using packets of {profile.piecesPerPack}, same as your usual vendor.
                          {' '}Right for {vendorName || 'this vendor'}?
                          <button
                            type="button"
                            onClick={() => savePieceProfile({
                              item_id: it.item_id,
                              piece_description: profile.pieceDescription,
                              pieces_per_pack: profile.piecesPerPack,
                              arrival_mode: profile.piecesPerPack === 1 ? 'single' : 'packet',
                              readback_confirmed: true,
                              vendor_id: currentVendorId,
                              // No count changes: this confirms WHOSE rule applies, not
                              // what the number on the row means.
                              quantity_meaning: 'pieces',
                            })}
                            style={{
                              marginLeft: 8, padding: '2px 9px', fontSize: 11.5, fontWeight: 600,
                              border: '1px solid #1d4ed8', background: '#1d4ed8', color: '#fff',
                              borderRadius: 999, cursor: 'pointer',
                            }}
                          >
                            Yes, same here
                          </button>
                        </div>
                      )}



                      {/* §5.2 — green when normal, amber the instant it drifts outside
                          the usual band, before any button is pressed. */}
                      {pHint && pHint.level !== 'green' && (
                        <div
                          style={{
                            fontSize: 11.5, lineHeight: 1.35, fontWeight: 500,
                            color: pHint.level === 'red' ? '#b91c1c' : '#b45309',
                          }}
                        >
                          ⚠ {pHint.message}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
                <div className="form-group"><label>{t('stockIn.unit')}</label>
                  <UsageStyleDropdown
                    className="unit-dropdown"
                    options={getFilteredUnitsForItem(it.item_name).map(u => ({ value: u, label: unitLabelFor(u) }))}
                    value={it.unit}
                    onChange={(val) => handleInputChange(idx, 'unit', val)}
                    placeholder={t('stockIn.unitPlaceholder')}
                    onAddClick={() => { setKitchenUnitForm(prev => ({ ...prev, item: items[idx]?.item_name || '' })); setShowKitchenUnitModal(true); }}
                    addLabel="+ Add"
                  />
                </div>
                <div className="form-group">
                  <label title="Enter the total amount you paid for this item (Quantity × Price per unit)">
                    Total Price
                  </label>
                  <input
                    type="number"
                    placeholder="Enter total amount paid"
                    value={it.total_price}
                    onChange={e => handleInputChange(idx, 'total_price', e.target.value)}
                    onBlur={e => handleInputBlur(idx, 'total_price', e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); pendingNewCardFocusRef.current = true; applyItems(prev => [...prev, emptyItem()]); } }}
                    step="0.01"
                    min="0"
                    className="stockin-total-price-input"
                    title="Example: If you bought 3kg at ₹100/kg, enter ₹300"
                  />
                  {priceErrs.length > 0 && (
                    <ul className="item-error-list">
                      {priceErrs.map((er, i2) => <li key={`price-${i2}`}>{er}</li>)}
                    </ul>
                  )}
                  {/* Show auto-calculated unit price on same line */}
                  {it.unit_price && (
                    <div className="stockin-unit-cost-display">
                      <span>Unit Cost: ₹{parseFloat(it.unit_price).toFixed(2)}/{abbreviateUnit(it.unit)}</span>
                    </div>
                  )}
                  {/* cx — "= ₹40 / kg" and "Last time: …" (only with the entry guard on, for a matched item) */}
                  {cxGuardOn && it.item_id && cxContext.byId[it.item_id] && (
                    <CxRowAid aid={cxRowAid({ ...cxPayloadLine(it), ctx: cxContext.byId[it.item_id] })} />
                  )}
                </div></div>

              {taxForStockInEnabled ? (
                <div className="triple-inline stockin-tax-row">
                  <div className="form-group tax-field">
                    <label>Tax Slab</label>
                    <TaxSlabPicker
                      value={it.tax_slab}
                      onChange={(val) => handleInputChange(idx, 'tax_slab', val)}
                    />
                  </div>
                  <div className="form-group expiry-field">
                    <label>{t('stockIn.expiryDate')}</label>
                    <div className="date-picker-wrap">
                      <input type="date" value={it.expiry_date} onChange={e => handleInputChange(idx, 'expiry_date', e.target.value)} min={new Date().toLocaleDateString('en-CA')} className="stockin-expiry-date-input" />
                      <button type="button" className="date-picker-button" aria-label="Open calendar" onClick={() => openExpiryPicker(idx)}>
                        <FontAwesomeIcon icon={faCalendarRegular} />
                      </button>
                    </div>
                    {expiryErrs.length > 0 && (
                      <ul className="item-error-list">
                        {expiryErrs.map((er, i2) => <li key={`exp-${i2}`}>{er}</li>)}
                      </ul>
                    )}
                  </div>
                  <div className="form-group total-with-tax-field">
                    <label title="Total amount after applying the selected tax slab">
                      Total Price With Tax
                    </label>
                    <input
                      type="number"
                      placeholder="Enter total with tax"
                      value={it.total_price_with_tax ?? calculateTotalWithTax(it.total_price, it.tax_slab)}
                      onChange={e => handleInputChange(idx, 'total_price_with_tax', e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); pendingNewCardFocusRef.current = true; applyItems(prev => [...prev, emptyItem()]); } }}
                      step="0.01"
                      min="0"
                      className="stockin-total-price-input stockin-total-price-with-tax-input"
                      title="Total Price + CGST + SGST"
                    />
                  </div>
                </div>
              ) : (
                <div className="inline-fields">
                  <div className="batch-field">
                    <label>{t('stockIn.batchNumber')}</label>
                    <input type="text" value={it.batch_number} onChange={e => handleInputChange(idx, 'batch_number', e.target.value)} placeholder={t('stockIn.batchNumberPlaceholder')} className="stockin-batch-number-input" />
                    {batchErrs.length > 0 && (
                      <ul className="item-error-list">
                        {batchErrs.map((er, i2) => <li key={`batch-${i2}`}>{er}</li>)}
                      </ul>
                    )}
                  </div>
                  <div className="expiry-field">
                    <label>{t('stockIn.expiryDate')}</label>
                    <div className="date-picker-wrap">
                      <input type="date" value={it.expiry_date} onChange={e => handleInputChange(idx, 'expiry_date', e.target.value)} min={new Date().toLocaleDateString('en-CA')} className="stockin-expiry-date-input" />
                      <button type="button" className="date-picker-button" aria-label="Open calendar" onClick={() => openExpiryPicker(idx)}>
                        <FontAwesomeIcon icon={faCalendarRegular} />
                      </button>
                    </div>
                    {expiryErrs.length > 0 && (
                      <ul className="item-error-list">
                        {expiryErrs.map((er, i2) => <li key={`exp-${i2}`}>{er}</li>)}
                      </ul>
                    )}
                  </div>
                </div>
              )}

              {/* **NEW: Duplicate Warning** */}
              {duplicateWarnings[idx] && (
                <div className="duplicate-warning-banner" style={{
                  background: '#fff3cd',
                  border: '2px solid #ffc107',
                  borderRadius: '8px',
                  padding: '12px 16px',
                  marginTop: '12px'
                }}>
                  <div style={{ fontWeight: 700, color: '#856404', marginBottom: '8px' }}>
                    ⚠️ Potential Duplicate Detected
                  </div>
                  <div style={{ fontSize: '14px', color: '#856404', marginBottom: '12px' }}>
                    This item was already added {duplicateWarnings[idx].time_ago || 'recently'}
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={async () => {
                        try {
                          await semanticFilterService.retainDuplicate(duplicateWarnings[idx].line_item_id, null);
                          setDuplicateWarnings(prev => {
                            const next = { ...prev };
                            delete next[idx];
                            return next;
                          });
                          // Informational: the bill is still being worked on. See InfoToast.
                          setInfoToast('Duplicate retained');
                        } catch (error) {
                          showModalMessage('❌ Failed: ' + error.message, 'error');
                        }
                      }}
                      style={{
                        padding: '6px 12px',
                        background: '#28a745',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '13px',
                        cursor: 'pointer'
                      }}
                    >
                      ✅ Keep Both
                    </button>
                    <button
                      onClick={async () => {
                        try {
                          await semanticFilterService.removeDuplicate(duplicateWarnings[idx].line_item_id, null);
                          removeItem(idx);
                          setDuplicateWarnings(prev => {
                            const next = { ...prev };
                            delete next[idx];
                            return next;
                          });
                          // Informational: the bill is still being worked on. See InfoToast.
                          setInfoToast('Duplicate removed');
                        } catch (error) {
                          showModalMessage('❌ Failed: ' + error.message, 'error');
                        }
                      }}
                      style={{
                        padding: '6px 12px',
                        background: '#dc3545',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '13px',
                        cursor: 'pointer'
                      }}
                    >
                      ❌ Remove This
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {/* Bill Memory: lines usually not stock, left out by the scan — one grey line each, "Add back" returns it */}
        {bmScreenOn && bmLeftOut.map((l) => (
          <div key={`bm-left-out-${(l.item && l.item.bm_scan_ref) || ''}-${l.line_no}`} className="stockin-unit-cost-display" style={{ margin: '4px 0' }}>
            <span>
              {bmReasonText('left_out', { name: l.name, amount: l.amount }).replace(/ Add back$/, '')}{' '}
              <button type="button" onClick={() => bmAddBack(l)}
                style={{ background: 'none', border: 'none', padding: 0, color: 'inherit', textDecoration: 'underline', cursor: 'pointer', font: 'inherit' }}>
                Add back
              </button>
            </span>
          </div>
        ))}

        {/* No search results message */}
        {itemSearchQuery.trim() !== '' && !items.some(it => String(it.item_name || '').toLowerCase().includes(itemSearchQuery.trim().toLowerCase())) && (
          <div style={{ textAlign: 'center', padding: '24px 12px', color: '#6b7280', fontSize: '14px' }}>
            No items match "{itemSearchQuery.trim()}"
          </div>
        )}

        {/* Add More Items Button (Inline) */}
        <button
          type="button"
          className="stockin-add-more-items-btn"
          onClick={addItem}
        >
          + Add More Items
        </button>

        {/* Production date calendar overlay */}
        {showProductionDatePicker && (
          <div className="date-picker-overlay" onClick={closeProductionPicker}>
            <div className="date-picker-popup" onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
              <div className="calendar-header">
                <button onClick={() => navigateProductionMonth(-1)}>‹</button>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <CalendarSelect
                    options={monthNames.map((m, i) => ({ label: m, value: i }))}
                    value={productionViewDate.getMonth()}
                    onChange={(val) => { const d = new Date(productionViewDate); d.setMonth(val); setProductionViewDate(d); }}
                    width={130}
                  />
                  <CalendarSelect
                    options={Array.from({ length: 21 }, (_, k) => new Date().getFullYear() - 10 + k).map(y => ({ label: String(y), value: y }))}
                    value={productionViewDate.getFullYear()}
                    onChange={(val) => { const d = new Date(productionViewDate); d.setFullYear(val); setProductionViewDate(d); }}
                    width={90}
                  />
                </div>
                <button onClick={() => navigateProductionMonth(1)}>›</button>
              </div>
              <div className="calendar-weekdays">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
                  <div key={d} className="weekday">{d}</div>
                ))}
              </div>
              <div className="calendar-grid">
                {renderProductionCalendar()}
              </div>
            </div>
          </div>
        )}

        {/* Expiry date calendar overlay */}
        {showExpiryPicker && (
          <div className="date-picker-overlay" onClick={closeExpiryPicker}>
            <div className="date-picker-popup" onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
              <div className="calendar-header">
                <button onClick={() => navigateExpiryMonth(-1)}>‹</button>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <CalendarSelect
                    options={monthNames.map((m, i) => ({ label: m, value: i }))}
                    value={expiryViewDate.getMonth()}
                    onChange={(val) => { const d = new Date(expiryViewDate); d.setMonth(val); setExpiryViewDate(d); }}
                    width={130}
                  />
                  <CalendarSelect
                    options={Array.from({ length: 21 }, (_, k) => new Date().getFullYear() - 10 + k).map(y => ({ label: String(y), value: y }))}
                    value={expiryViewDate.getFullYear()}
                    onChange={(val) => { const d = new Date(expiryViewDate); d.setFullYear(val); setExpiryViewDate(d); }}
                    width={90}
                  />
                </div>
                <button onClick={() => navigateExpiryMonth(1)}>›</button>
              </div>
              <div className="calendar-weekdays">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
                  <div key={d} className="weekday">{d}</div>
                ))}
              </div>
              <div className="calendar-grid">
                {renderExpiryCalendar()}
              </div>
            </div>
          </div>
        )}

        {/* Grand Total Audit UI removed per user request */}

        {/* TAX Section — CGST & SGST */}
        {taxForStockInEnabled && (
          <div style={{ background: '#ffffff', borderRadius: 12, padding: '14px 16px', margin: '0 0 12px 0', border: '1px solid #e9ecef' }}>
            <div style={{ fontSize: 14, fontWeight: '700', color: '#000', marginBottom: 10, fontFamily: 'Roboto, sans-serif' }}>
              TAX (Summary)
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 12, color: '#666', fontWeight: '500', marginBottom: 4, display: 'block', fontFamily: 'Roboto, sans-serif' }}>CGST (%)</label>
                <input
                  type="number"
                  inputMode="decimal"
                  value={displayCgst}
                  readOnly
                  placeholder="0.00"
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1.5px solid #ddd', fontSize: 15, fontWeight: '600', fontFamily: 'Roboto, sans-serif', background: '#f8f9fa', outline: 'none', boxSizing: 'border-box', cursor: 'default' }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 12, color: '#666', fontWeight: '500', marginBottom: 4, display: 'block', fontFamily: 'Roboto, sans-serif' }}>SGST (%)</label>
                <input
                  type="number"
                  inputMode="decimal"
                  value={displaySgst}
                  readOnly
                  placeholder="0.00"
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1.5px solid #ddd', fontSize: 15, fontWeight: '600', fontFamily: 'Roboto, sans-serif', background: '#f8f9fa', outline: 'none', boxSizing: 'border-box', cursor: 'default' }}
                />
              </div>
            </div>
            {(displayCgst > 0 || displaySgst > 0) && (
              <div style={{ marginTop: 8, fontSize: 13, color: '#555', fontFamily: 'Roboto, sans-serif', display: 'flex', justifyContent: 'space-between' }}>
                <span>Tax Total</span>
                <span style={{ fontWeight: '600' }}>{(displayCgst + displaySgst)}%</span>
              </div>
            )}
          </div>
        )}

        <div className="total-cost-card">
          <span className="label">{t('stockIn.totalCost')}</span>
          <span className="amount">
            ₹{(() => {
              // T1 — YOUR total, always. This used to show `ocrGrandTotal` whenever a scan
              // had provided one, so the card displayed the BILL's number while the Submit
              // button refused because the form's own sum differed from it. The one figure
              // that could have explained the refusal was the one being hidden.
              const tax = taxForStockInEnabled ? 0 : (displayCgst + displaySgst);
              return (totalCost + tax).toFixed(2);
            })()}
          </span>
        </div>

        <div className="button-group">
          {onCancel && (
            <button onClick={onCancel} className="stock-in-draft-btn" disabled={isSubmitting} type="button">
              Cancel
            </button>
          )}

          {isLocalCsvDownloadVisible && (
            <button onClick={handleSaveDraft} className="stock-in-draft-btn" disabled={isSubmitting} type="button">
              Save as Draft
            </button>
          )}

          {/* T3 — THIS BUTTON IS NEVER DISABLED. Not by the total audit, not by a submit in
              flight, not by anything.
              It has gone dead on this user twice. First `difference !== 0` — exact to the
              paisa, against a figure computed on a different tax basis, with the panel that
              explained it deleted (see "Grand Total Audit UI removed"), so a taxed bill or
              a deleted line produced a grey button and complete silence. Then `isSubmitting`,
              which four separate modal handlers set and clear across ~700 lines of JSX: every
              one of them currently has a closing `finally`, and the day one does not, the
              module stops and nobody can tell why.
              A disabled button cannot explain itself. Every reason to refuse now lives in
              handleSubmitClick, where it can say what it found and be answered — and the one
              thing a disabled button was genuinely buying, protection from a double tap, is
              bought instead by submitInFlightRef inside handleSubmit, which is where the
              request actually is. The label still reports progress; it just no longer
              confiscates the control. */}
          <button
            onClick={bmScreenOn && bmCounts.need > 0 ? () => bmScrollToRow(bmCounts.firstIndex) : handleSubmitClick}
            className="stock-in-submit-btn"
            type="button"
          >
            {isSubmitting ? 'Submitting...' : (bmScreenOn && bmCounts.need > 0 ? bmReasonText('need_tap', { need: bmCounts.need }) : t('stockIn.submit'))}
          </button>
        </div>
        <p style={{ marginTop: '16px', fontSize: '12px', color: '#666', textAlign: 'center' }}>
          Review for accuracy.
        </p>
      </div>

      {/* Separate Modal Components with Unique Class Names */}
      {showVendorModal && <VendorModal message={vendorModalMessage} onClose={() => setShowVendorModal(false)} vendorInputRef={vendorInputRef} />}
      {showYellowItemsModal && <YellowItemsModal message={yellowItemsModalMessage} onClose={() => setShowYellowItemsModal(false)} />}
      {showUnitMismatchModal && <UnitMismatchModal items={unitMismatchItems} onClose={() => setShowUnitMismatchModal(false)} />}
      {showPreValidationModal && (
        <PreValidationModal 
          warnings={preValidationData.warnings} 
          errors={preValidationData.errors}
          onClose={() => setShowPreValidationModal(false)}
          onProceed={() => {
            setShowPreValidationModal(false);
            setShowConfirmation(true);
          }}
          rows={items}
          onSaveEdits={(edits) => {
            // Exactly what typing into the row does (unit cost and tax follow), then Submit
            // again from the top — handleSubmitClick reads itemsRef, which these writes
            // have already updated, so the re-check sees the corrected numbers.
            for (const e of edits) {
              handleInputChange(e.index, 'quantity', e.quantity);
              handleInputChange(e.index, 'total_price', e.total_price);
            }
            setShowPreValidationModal(false);
            handleSubmitClick();
          }}
        />
      )}
      {/* T1 — the bill-total mismatch. Advisory: it names both numbers and lets the person
          decide, because they are looking at the paper and this figure came from a photo. */}
      {totalMismatchPrompt && (
        <div className="modal-overlay stockin-total-mismatch-modal" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000, padding: 20 }}>
          <div className="modal-content" style={{ width: 'min(94vw, 520px)', background: '#fff', borderRadius: 16, padding: 22, fontFamily: 'Roboto, sans-serif' }}>
            <div style={{ fontSize: 34, textAlign: 'center', marginBottom: 6 }}>⚠️</div>
            <h3 style={{ fontSize: 20, fontWeight: 700, color: '#000', textAlign: 'center', margin: '0 0 12px' }}>
              The totals don't match
            </h3>
            <p style={{ fontSize: 14, color: '#444', lineHeight: 1.5, margin: '0 0 18px' }}>
              {totalMismatchPrompt.message}
            </p>
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                type="button"
                className="stockin-total-mismatch-cancel"
                onClick={() => setTotalMismatchPrompt(null)}
                style={{ flex: 1, padding: 11, borderRadius: 8, cursor: 'pointer', border: '1px solid #d1d5db', background: '#fff', color: '#374151', fontWeight: 600 }}
              >
                Check the lines
              </button>
              <button
                type="button"
                className="stockin-total-mismatch-proceed"
                onClick={() => {
                  ackedTotalDifferenceRef.current = totalMismatchPrompt.difference;
                  setTotalMismatchPrompt(null);
                  // Back in at the top, so steps 4-9 still run. Answering this question
                  // must not buy a pass on the vendor check or the piece guard.
                  setTimeout(() => handleSubmitClick(), 0);
                }}
                style={{ flex: 1, padding: 11, borderRadius: 8, cursor: 'pointer', border: 'none', background: '#000', color: '#fff', fontWeight: 600 }}
              >
                Continue anyway
              </button>
            </div>
          </div>
        </div>
      )}
      {showSuccessModal && <SuccessModal message={successModalMessage} onClose={() => { setShowSuccessModal(false); setBmSaveLine(''); }} />}
      {infoToast && <InfoToast message={infoToast} onDismiss={() => setInfoToast(null)} />}

      {/* Red rows: ONE list, one tap per row, Non-Consumable pre-selected. Never a
          sequence of modals — that is what made this unbearable. */}
      {redRowDecision && (() => {
        // Popup-D standard: no accept-all, no skip. Every row is answered or nothing moves.
        const redRowAnswered = redRowDecision.rows.filter(r => r.choice !== null).length;
        return (
        <div
          className="stockin-redrow-overlay"
          style={{
            position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex', justifyContent: 'center', alignItems: 'center',
            zIndex: 9999, padding: '12px'
          }}
        >
          <div
            style={{
              background: '#fff', borderRadius: '12px', padding: '20px',
              width: 'min(560px, 100%)', maxHeight: '82vh', display: 'flex', flexDirection: 'column',
              fontFamily: 'Roboto, sans-serif'
            }}
          >
            <h3 style={{ margin: '0 0 6px', fontSize: '18px', fontWeight: 700, color: '#111' }}>
              {redRowDecision.rows.length === 1
                ? '1 item is not in your inventory.'
                : `${redRowDecision.rows.length} items are not in your inventory.`}
            </h3>
            <p style={{ margin: '0 0 14px', fontSize: '13px', color: '#4b5563', lineHeight: 1.45 }}>
              Tell us what each one is. Non-consumables are stored and counted, but never
              costed into a dish.
            </p>

            <div style={{ overflowY: 'auto', flex: 1, margin: '0 -4px', padding: '0 4px' }}>
              {redRowDecision.rows.map((row) => (
                <div
                  key={row.index}
                  style={{
                    padding: '10px 0',
                    borderBottom: '1px solid #f0f0f0',
                    display: 'flex', flexDirection: 'column', gap: '8px'
                  }}
                >
                  <div style={{ fontSize: '14px', fontWeight: 600, color: '#111', wordBreak: 'break-word' }}>
                    {row.item_name}
                  </div>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {[
                      // `hinted` is a nudge, not an answer: the likely choice is outlined so
                      // the eye lands on it, but nothing is selected until the user says so.
                      { value: 'non_consumable', label: 'Non-consumable — keep', hinted: true },
                      { value: 'consumable', label: 'New ingredient — add as consumable', hinted: false }
                    ].map(opt => {
                      const selected = row.choice === opt.value;
                      const hint = opt.hinted && row.choice === null;
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          aria-pressed={selected}
                          onClick={() => setRedRowDecision(prev => ({
                            ...prev,
                            rows: prev.rows.map(r => (r.index === row.index ? { ...r, choice: opt.value } : r))
                          }))}
                          style={{
                            flex: '1 1 auto', padding: '8px 10px', borderRadius: '8px', cursor: 'pointer',
                            fontSize: '12.5px', fontWeight: 600,
                            border: selected ? '2px solid #111' : (hint ? '2px dashed #9ca3af' : '1px solid #d1d5db'),
                            background: selected ? '#111' : '#fff',
                            color: selected ? '#fff' : '#374151'
                          }}
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', gap: '10px', marginTop: '16px' }}>
              <button
                type="button"
                onClick={() => setRedRowDecision(null)}
                style={{
                  flex: 1, padding: '11px', borderRadius: '8px', cursor: 'pointer',
                  border: '1px solid #d1d5db', background: '#fff', color: '#374151', fontWeight: 600
                }}
              >
                Back to the bill
              </button>
              <button
                type="button"
                disabled={redRowAnswered < redRowDecision.rows.length}
                onClick={() => {
                  const decisions = redRowDecision.rows;
                  applyItems(prev => prev.map((item, idx) => {
                    const decision = decisions.find(d => d.index === idx);
                    if (!decision) return item;
                    return classifyRedRow(item, decision.choice);
                  }));
                  setRedRowDecision(null);
                  // Straight back into the same ladder: the classified rows are now ordinary
                  // rows, and every check re-runs on the values as they now are. Safe now
                  // that handleSubmitClick reads itemsRef.current (H6) — applyItems above
                  // wrote the ref synchronously, so STEP 7.5 sees the answers rather than
                  // re-detecting the same red rows and reopening this modal.
                  setTimeout(() => handleSubmitClick(), 0);
                }}
                style={{
                  flex: 1, padding: '11px', borderRadius: '8px', fontWeight: 700,
                  border: 'none', background: '#000', color: '#fff',
                  cursor: redRowAnswered < redRowDecision.rows.length ? 'not-allowed' : 'pointer',
                  opacity: redRowAnswered < redRowDecision.rows.length ? 0.45 : 1
                }}
              >
                {redRowAnswered < redRowDecision.rows.length
                  ? `Continue (${redRowAnswered} of ${redRowDecision.rows.length} answered)`
                  : 'Continue'}
              </button>
            </div>
          </div>
        </div>
        );
      })()}
      {showErrorModal && <ErrorModal message={errorModalMessage} onClose={() => setShowErrorModal(false)} />}
      {showLoadingModal && <LoadingModal message={loadingModalMessage} />}



      {/* REMOVED: Voice Input Modal - Now using top banner for instant feedback */}

      {/* Vendor Autofill Confirmation Modal */}
      {vendorAutofillPrompt && (
        <div className="modal-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: '10px' }}>
          <div className="confirmation-modal" style={{ width: 'min(94vw, 500px)', background: '#fff', borderRadius: 16, padding: 24, boxShadow: '0 16px 40px rgba(0,0,0,0.3)', position: 'relative', fontFamily: 'Roboto, sans-serif' }}>
            <h3 style={{ fontSize: 20, fontWeight: '700', color: '#000', textAlign: 'center', margin: '0 0 16px 0' }}>
              Auto-fill Items?
            </h3>
            <p style={{ fontSize: 16, color: '#333', textAlign: 'center', marginBottom: 16, lineHeight: 1.5 }}>
              Do you want to automatically fill the form with the latest items from <strong>{vendorAutofillPrompt.vendorName}</strong>?
            </p>

            <div style={{ marginBottom: 16, border: '1px solid #e9ecef', borderRadius: 8, padding: 12, maxHeight: '300px', overflowY: 'auto' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, paddingBottom: 8, borderBottom: '1px solid #e9ecef' }}>
                <span style={{ fontWeight: '600' }}>Items to auto-fill</span>
                <button
                  type="button"
                  onClick={() => {
                    if (autofillSelectedIndices.size === vendorAutofillPrompt.prefilledItems.length) {
                      setAutofillSelectedIndices(new Set());
                    } else {
                      setAutofillSelectedIndices(new Set(vendorAutofillPrompt.prefilledItems.map((_, i) => i)));
                    }
                  }}
                  style={{ background: 'none', border: 'none', color: '#000', fontWeight: '600', cursor: 'pointer', fontSize: 14 }}
                >
                  {autofillSelectedIndices.size === vendorAutofillPrompt.prefilledItems.length ? 'Deselect All' : 'Select All'}
                </button>
              </div>
              {vendorAutofillPrompt.prefilledItems.map((item, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                  <input
                    type="checkbox"
                    id={`autofill-item-${i}`}
                    checked={autofillSelectedIndices.has(i)}
                    onChange={(e) => {
                      const newSet = new Set(autofillSelectedIndices);
                      if (e.target.checked) newSet.add(i);
                      else newSet.delete(i);
                      setAutofillSelectedIndices(newSet);
                    }}
                    style={{ marginRight: 10, width: 16, height: 16, cursor: 'pointer', accentColor: '#000' }}
                  />
                  <label htmlFor={`autofill-item-${i}`} style={{ cursor: 'pointer', fontSize: 14, color: '#333', flex: 1, display: 'flex', justifyContent: 'space-between' }}>
                    <span>{item.item_name || 'Unnamed Item'}</span>
                    <span style={{ color: '#666', marginLeft: 8 }}>({item.quantity} {item.unit})</span>
                  </label>
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', gap: 12 }}>
              <button
                type="button"
                onClick={() => setVendorAutofillPrompt(null)}
                style={{ flex: 1, padding: '12px', background: '#f1f3f5', color: '#495057', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  const prefilled = vendorAutofillPrompt.prefilledItems.filter((_, i) => autofillSelectedIndices.has(i));
                  if (prefilled.length === 0) {
                    setVendorAutofillPrompt(null);
                    return;
                  }
                  applyItems(prev => {
                    const existingNonEmpty = prev.filter(it => (it.item_name || '').trim());
                    return existingNonEmpty.length > 0
                      ? [...existingNonEmpty, ...prefilled]
                      : prefilled;
                  }, { snapshot: true });
                  setLastOrderBanner({
                    vendorName: vendorAutofillPrompt.vendorName,
                    orderDate: vendorAutofillPrompt.dateLabel,
                    count: prefilled.length,
                    source: vendorAutofillPrompt.source
                  });
                  setVendorAutofillPrompt(null);
                }}
                style={{ flex: 1, padding: '12px', background: '#000', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', opacity: autofillSelectedIndices.size === 0 ? 0.5 : 1 }}
                disabled={autofillSelectedIndices.size === 0}
              >
                Yes, Auto-fill
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmation && (
        <div className="modal-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: '10px' }}>
          <div className="confirmation-modal" style={{ width: 'min(94vw, 500px)', maxHeight: '85vh', overflowY: 'auto', background: '#fff', borderRadius: 16, padding: 24, boxShadow: '0 16px 40px rgba(0,0,0,0.3)', position: 'relative', fontFamily: 'Roboto, sans-serif' }}>
            <button
              onClick={closeStockConfirmation}
              style={{ position: 'absolute', right: 16, top: 16, border: 'none', background: 'transparent', fontSize: 24, cursor: 'pointer', color: '#666', fontWeight: 'bold', fontFamily: 'Roboto, sans-serif' }}
            >×</button>

            <h3 className="stockin-confirm-title" style={{ fontSize: 20, fontWeight: '700', color: '#000', textAlign: 'center', fontFamily: 'Roboto, sans-serif' }}>
              Confirm Stock Entry
            </h3>

            {pendingPieceSubmit && (
              <div role="status" style={{ background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1e40af', padding: '10px 12px', borderRadius: 10, marginBottom: 12, fontSize: 13, textAlign: 'center', fontFamily: 'Roboto, sans-serif' }}>
                Quantities below include your packets / pieces answers. Check them before submitting.
              </div>
            )}

            {/* Vendor Details */}
            <div style={{ background: '#ffffff', padding: 16, borderRadius: 12, marginBottom: 16, fontFamily: 'Roboto, sans-serif' }}>
              <div style={{ fontSize: 18, fontWeight: '600', color: '#000', marginBottom: 8, fontFamily: 'Roboto, sans-serif' }}>Vendor Details</div>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 45%' }}>
                  <span style={{ fontSize: 14, color: '#000', fontFamily: 'Roboto, sans-serif' }}>Name: </span>
                  <span style={{ fontSize: 14, fontWeight: '600', color: '#000', fontFamily: 'Roboto, sans-serif' }}>{vendorName || 'N/A'}</span>
                </div>
                <div style={{ flex: '1 1 45%' }}>
                  <span style={{ fontSize: 14, color: '#000', fontFamily: 'Roboto, sans-serif' }}>Phone: </span>
                  {/* Second net. The submit gate should already have caught this, but the
                      final review is the last moment a human looks at the number, so a bad
                      one is flagged here too rather than trusted to an upstream check. */}
                  <span style={{ fontSize: 14, fontWeight: '600', color: phoneValidationError ? '#b45309' : '#000', fontFamily: 'Roboto, sans-serif' }}>{vendorPhone || 'N/A'}</span>
                  {phoneValidationError && (
                    <span style={{ fontSize: 12, fontWeight: '600', color: '#b45309', marginLeft: 8, fontFamily: 'Roboto, sans-serif' }}>
                      ⚠ not a valid 10-digit number
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Items List */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ border: '1px solid #e9ecef', borderRadius: 10, overflow: 'hidden', width: '100%' }}>
                <Table className="stockin-confirm-standard-table" style={{ width: '100%', tableLayout: 'fixed', fontFamily: 'Roboto, sans-serif' }}>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="stockin-confirm-col-sno" style={{ width: '12%', minWidth: 60, paddingRight: '14px' }}>S.No</TableHead>
                      <TableHead style={{ width: '44%', paddingLeft: '10px' }}>Item Name</TableHead>
                      <TableHead style={{ width: '20%' }}>Qty</TableHead>
                      <TableHead style={{ width: '24%' }}>Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.map((item, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="stockin-confirm-col-sno" style={{ paddingLeft: '18px', paddingRight: '14px' }}>{idx + 1}.</TableCell>
                        <TableCell style={{ whiteSpace: 'normal', wordBreak: 'break-word', paddingLeft: '10px' }}>
                          {item.item_name || 'Unnamed Item'}
                        </TableCell>
                        <TableCell>{item.quantity} {abbreviateUnit(item.unit)}</TableCell>
                        <TableCell>₹{grossLineTotal(item, taxForStockInEnabled).toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>

            {/* Tax Breakdown in Confirmation */}
            {(displayCgst > 0 || displaySgst > 0) && (
              <div style={{ background: '#ffffff', padding: 12, borderRadius: 10, marginBottom: 8, fontFamily: 'Roboto, sans-serif' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#555', marginBottom: 4 }}>
                  <span>Subtotal</span>
                  <span>₹{totalCost.toFixed(2)}</span>
                </div>
                {displayCgst > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#555', marginBottom: 4 }}>
                    <span>CGST</span>
                    <span>{taxForStockInEnabled ? `${displayCgst}%` : `₹${displayCgst.toFixed(2)}`}</span>
                  </div>
                )}
                {displaySgst > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#555' }}>
                    <span>SGST</span>
                    <span>{taxForStockInEnabled ? `${displaySgst}%` : `₹${displaySgst.toFixed(2)}`}</span>
                  </div>
                )}
              </div>
            )}
            {/* Total */}
            <div style={{ background: '#fff', border: '2px solid #e9ecef', padding: 14, borderRadius: 10, marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontFamily: 'Roboto, sans-serif' }}>
              <span style={{ fontSize: 16, fontWeight: '700', color: '#000', fontFamily: 'Roboto, sans-serif' }}>Total Cost</span>
              <span style={{ fontSize: 20, fontWeight: '700', color: '#000', fontFamily: 'Roboto, sans-serif' }}>₹{(taxForStockInEnabled ? totalCost : totalCost + displayCgst + displaySgst).toFixed(2)}</span>
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: 12 }}>
              <button
                onClick={closeStockConfirmation}
                style={{ flex: 1, padding: '11px 16px', background: '#e9ecef', color: '#495057', border: 'none', borderRadius: 10, fontWeight: '700', fontSize: 16, cursor: 'pointer', transition: 'all 0.2s', fontFamily: 'Roboto, sans-serif', minHeight: '48px' }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#dee2e6'}
                onMouseLeave={(e) => e.currentTarget.style.background = '#e9ecef'}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowConfirmation(false);
                  if (pendingPieceSubmit) {
                    // Last step of the piece conversation: post the negotiated bill as-is.
                    const gateState = pendingPieceSubmit;
                    setPendingPieceSubmit(null);
                    submitPieceAnswers(gateState);
                    return;
                  }
                  handleSubmit();
                }}
                disabled={isSubmitting}
                style={{ flex: 1, padding: '11px 16px', background: isSubmitting ? '#666' : '#000', color: '#fff', border: 'none', borderRadius: 10, fontWeight: '700', fontSize: 16, cursor: isSubmitting ? 'not-allowed' : 'pointer', transition: 'all 0.2s', fontFamily: 'Roboto, sans-serif', minHeight: '48px', whiteSpace: 'nowrap', lineHeight: '1' }}
                onMouseEnter={(e) => {
                  if (!isSubmitting) e.currentTarget.style.background = '#333';
                }}
                onMouseLeave={(e) => {
                  if (!isSubmitting) e.currentTarget.style.background = '#000';
                }}
              >
                {isSubmitting ? '..' : 'Confirm Submit'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showKitchenUnitModal && (
        <div className="kitchen-unit-modal-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: 0 }}>
          <div className="kitchen-unit-modal-card" style={{ width: '100%', maxWidth: '500px', overflowY: 'auto', background: '#fff', borderRadius: '0.5rem', padding: "12px", margin: "0 10px", boxShadow: '0 16px 40px rgba(0,0,0,0.25)', position: 'relative' }}>
            <div className="kitchen-unit-modal-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, padding: '0 4px' }}>
              <h3 className="kitchen-unit-modal-title" style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>Item</h3>
              <button className="kitchen-unit-modal-close" onClick={() => setShowKitchenUnitModal(false)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: 4, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <FontAwesomeIcon icon={faXmark} style={{ fontSize: 16, color: '#000' }} />
              </button>
            </div>

            {/* Item row */}
            <div className="kitchen-unit-item-row" style={{ marginBottom: 14 }}>
              <div className="kitchen-unit-item-select-wrapper" style={{ position: 'relative' }}>
                <SearchableSelect
                  value={kitchenUnitForm.item || ''}
                  onChange={(val) => setKitchenUnitForm(prev => ({ ...prev, item: val }))}
                  options={[...inventoryItemNames.map(n => ({ value: n, label: n }))]}
                  placeholder="Select Item"
                />
              </div>
            </div>

            {/* Tool row with +Add Tool */}
            {/* Tool | Quantity | Unit (single row) */}
            <div className="kitchen-unit-form-row" style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6 }}>
              {/* Kitchen Tool */}
              <div className="kitchen-unit-tool-wrapper kitchen-tool-responsive" style={{
                flex: '0 0 200px'
              }}>
                <div style={{ position: 'relative' }}>
                  <SearchableSelect
                    value={kitchenUnitForm.kitchenTool}
                    onChange={(val) => setKitchenUnitForm(prev => ({ ...prev, kitchenTool: val }))}
                    options={[...modalKitchenTools.map(t => ({ value: t, label: t }))]}
                    placeholder="Tablespoon"
                    enableAddPanel={true}
                    addOptionLabel={'+ Add Tool'}
                    addInputPlaceholder={'Enter tool name...'}
                    onAddValue={(name) => {
                      const n = (name || '').trim();
                      if (!n) return;
                      setModalKitchenTools(prev => prev.includes(n) ? prev : [...prev, n]);
                      setKitchenUnitForm(prev => ({ ...prev, kitchenTool: n }));
                    }}
                  />
                </div>
              </div>

              {/* Quantity cluster */}
              <div className="kitchen-unit-quantity-cluster" style={{
                display: 'flex',
                alignItems: 'center',
                gap: window.innerWidth <= 480 ? '4px' : '8px',
              }}>
                <button
                  className="kitchen-unit-quantity-decrease"
                  onClick={() => setKitchenUnitForm(prev => ({ ...prev, quantity: String(Math.max(1, (parseInt(prev.quantity || '1', 10) || 1) - 1)) }))}
                  style={{
                    width: window.innerWidth <= 480 ? 32 : 36,
                    height: 44,
                    border: '1px solid #e5e7eb',
                    borderRadius: 10,
                    background: '#fff',
                    cursor: 'pointer',
                    fontSize: 18
                  }}
                >−</button>
                <input
                  className="kitchen-unit-quantity-input"
                  type="number"
                  value={kitchenUnitForm.quantity}
                  onChange={(e) => setKitchenUnitForm(prev => ({ ...prev, quantity: e.target.value }))}
                  style={{
                    width: window.innerWidth <= 480 ? 50 : 70,
                    height: 44,
                    border: '0px solid #e5e7eb',
                    borderRadius: 10,
                    textAlign: 'center',
                    fontSize: 16
                  }}
                />
                <button
                  className="kitchen-unit-quantity-increase"
                  onClick={() => setKitchenUnitForm(prev => ({ ...prev, quantity: String((parseInt(prev.quantity || '1', 10) || 1) + 1) }))}
                  style={{
                    width: window.innerWidth <= 480 ? 32 : 36,
                    height: 44,
                    border: '1px solid #e5e7eb',
                    borderRadius: 8,
                    background: '#fff',
                    cursor: 'pointer',
                    fontSize: 18
                  }}
                >＋</button>
              </div>

              {/* Unit */}
              <div className="kitchen-unit-unit-wrapper kitchen-unit-responsive" style={{
                flex: '0 0 auto',
                width: '90px'
              }}>
                <SearchableSelect
                  value={kitchenUnitForm.unit}
                  onChange={(val) => setKitchenUnitForm(prev => ({ ...prev, unit: val }))}
                  options={[...baseUnitOptions]}
                  placeholder="ml"
                  menuAlign="right"
                />
              </div>
            </div>

            {/* Footer buttons */}
            <div className="kitchen-unit-modal-footer" style={{ display: 'flex', gap: 14, marginTop: 18 }}>
              <button className="kitchen-unit-remove-btn" onClick={() => setShowKitchenUnitModal(false)} style={{ flex: 1, padding: 14, background: '#ffdddd', color: '#d00', border: 'none', borderRadius: 10, fontWeight: 'bold', cursor: 'pointer' }}>Remove Unit</button>
              <button className="kitchen-unit-save-btn" onClick={handleSaveKitchenUnit} style={{ flex: 1, padding: 14, background: '#000', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 'bold', cursor: 'pointer' }}>Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Container Unit Conversion Modal - Kitchen Unit Style */}
      {showContainerConversionModal && (
        <div className="stockin-container-modal-overlay">
          <div className="stockin-container-modal-card">
            {/* Header with progress indicator */}
            {isMobileView ? (
              <div className="stockin-container-modal-header">
                <h3 className="stockin-container-modal-title">Container Unit Detected</h3>
                <button
                  className="stockin-container-modal-close"
                  onClick={() => {
                    setShowContainerConversionModal(false);
                    setUnknownUnitsQueue([]);
                    setCurrentUnknownUnitIndex(0);
                    // DON'T clear pendingOcrItems or items here - let them stay
                  }}
                >
                  <FontAwesomeIcon icon={faXmark} className="stockin-container-modal-close-icon" />
                </button>
              </div>
            ) : (
              <div className="stockin-container-modal-header stockin-container-modal-header--desktop">
                <h3 className="stockin-container-modal-title stockin-container-modal-title--center">Container Unit Detected</h3>
                <button
                  className="stockin-container-modal-close stockin-container-modal-close--desktop"
                  onClick={() => {
                    setShowContainerConversionModal(false);
                    setUnknownUnitsQueue([]);
                    setCurrentUnknownUnitIndex(0);
                    // DON'T clear pendingOcrItems or items here - let them stay
                  }}
                >
                  <FontAwesomeIcon icon={faXmark} className="stockin-container-modal-close-icon" />
                </button>
              </div>
            )}
            {unknownUnitsQueue.length > 1 && (
              <div className="stockin-container-modal-progress">
                Converting {currentUnknownUnitIndex + 1} of {unknownUnitsQueue.length} unknown units
              </div>
            )}

            {/* Item Name (Read-only dropdown style) */}
            <div className="stockin-container-item-wrap">
              <div className="stockin-container-item-display">
                {containerConversionForm.itemName || 'Unknown Item'}
              </div>
            </div>

            {/* Container Unit | Quantity | Standard Unit (single row) */}
            <div className="stockin-container-row">
              {/* Container Unit (yellow highlight like screenshot) */}
              <div className="kitchen-tool-responsive stockin-container-unit-wrap">
                <div className={`stockin-container-unit-box${isMobileView ? ' stockin-container-unit-box--mobile' : ''}`}>
                  {isMobileView
                    ? String(containerConversionForm.containerUnit || '').replace(/\b\w/g, (c) => c.toUpperCase())
                    : containerConversionForm.containerUnit}
                </div>
              </div>

              {/* Quantity cluster with +/- buttons */}
              <div className="stockin-container-qty">
                <button
                  onClick={() => {
                    const current = parseFloat(containerConversionForm.quantity) || 1;
                    const newVal = Math.max(0.01, current - 1);
                    setContainerConversionForm(prev => ({ ...prev, quantity: String(newVal) }));
                  }}
                  className="stockin-container-qty-btn"
                >−</button>
                <input
                  type="number"
                  value={containerConversionForm.quantity}
                  onChange={(e) => setContainerConversionForm(prev => ({ ...prev, quantity: e.target.value }))}
                  step="0.01"
                  min="0.01"
                  className="stockin-container-qty-input"
                />
                <button
                  onClick={() => {
                    const current = parseFloat(containerConversionForm.quantity) || 1;
                    setContainerConversionForm(prev => ({ ...prev, quantity: String(current + 1) }));
                  }}
                  className="stockin-container-qty-btn"
                >＋</button>
              </div>

              {/* Standard Unit Dropdown - Usage Style */}
              <div className="kitchen-unit-responsive stockin-container-unit-dropdown">
                <UsageStyleDropdown
                  value={containerConversionForm.standardUnit}
                  onChange={(val) => setContainerConversionForm(prev => ({ ...prev, standardUnit: val }))}
                  options={[
                    { value: 'kg', label: 'kg' },
                    { value: 'g', label: 'g' },
                    { value: 'L', label: 'L' },
                    { value: 'ml', label: 'ml' },
                    { value: 'piece', label: 'piece' }
                  ]}
                  width="100%"
                  forceUp={true}
                  contained={true}
                  onAddClick={() => setShowAddUnitModal(true)}
                  addLabel="+ Add"
                />
              </div>
            </div>

            {/* Footer buttons */}
            <div className="stockin-container-footer">
              <button
                onClick={() => {
                  // Skip this conversion and move to next
                  const nextIndex = currentUnknownUnitIndex + 1;
                  if (nextIndex < unknownUnitsQueue.length) {
                    const next = unknownUnitsQueue[nextIndex];
                    setContainerConversionForm({
                      itemIndex: next.itemIndex,
                      itemName: next.itemName,
                      containerUnit: next.unit,
                      quantity: '1',
                      standardUnit: 'kg'
                    });
                    setCurrentUnknownUnitIndex(nextIndex);
                  } else {
                    // No more items, close modal and continue
                    setShowContainerConversionModal(false);
                    setUnknownUnitsQueue([]);
                    setCurrentUnknownUnitIndex(0);
                    if (pendingOcrItems && pendingOcrItems.length > 0) {
                      setTimeout(() => detectAndHandleDuplicates(pendingOcrItems), 300);
                    }
                  }
                }}
                className="stockin-container-skip-btn"
              >
                Skip
              </button>
              <button
                onClick={handleSaveContainerConversion}
                className="stockin-container-save-btn"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Custom Unit Modal */}
      {showAddUnitModal && (
        <div className="cu-add-unit-modal-backdrop-unique" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10001, backdropFilter: 'blur(4px)' }}>
          <div className="cu-add-unit-modal-container-unique" style={{ width: 'min(90%, 500px)', maxHeight: '80vh', overflowY: 'auto', background: '#fff', borderRadius: 12, padding: '20px', boxShadow: '0 20px 60px rgba(0,0,0,0.4)', position: 'relative' }}>
            <div className="cu-add-unit-modal-header-unique" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 className="cu-add-unit-modal-title-unique" style={{ margin: 0, fontSize: '1.25rem', fontWeight: '600', color: '#000' }}>Add Custom Unit</h2>
              <button
                onClick={() => setShowAddUnitModal(false)}
                className="cu-add-unit-modal-close-unique"
                style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#666', padding: '0', width: '30px', height: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                ×
              </button>
            </div>

            <div className="cu-add-unit-modal-body-unique" style={{ marginBottom: '20px' }}>
              <label className="cu-add-unit-modal-label-unique" style={{ display: 'block', marginBottom: '8px', fontSize: '0.875rem', fontWeight: '500', color: '#000' }}>
                Unit Name
              </label>
              <input
                type="text"
                value={newUnitForm}
                onChange={(e) => setNewUnitForm(e.target.value)}
                placeholder="Enter unit name (e.g., packets, boxes)"
                className="cu-add-unit-modal-input-unique"
                style={{ width: '100%', padding: '12px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '0.875rem', boxSizing: 'border-box' }}
              />
            </div>

            <div className="cu-add-unit-modal-footer-unique" style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => setShowAddUnitModal(false)}
                className="cu-add-unit-modal-cancel-unique"
                style={{ flex: 1, padding: '14px', background: '#fff', color: '#000', border: '1px solid #ddd', borderRadius: '10px', fontWeight: '600', cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (newUnitForm.trim()) {
                    // Just close modal for now - frontend only
                    setShowAddUnitModal(false);
                    setNewUnitForm('');
                  }
                }}
                className="cu-add-unit-modal-save-unique"
                style={{ flex: 1, padding: '14px', background: '#000', color: '#fff', border: 'none', borderRadius: '10px', fontWeight: '600', cursor: 'pointer' }}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Unit Training Modal (SUF - Filter 2) */}
      {showUnitTrainingModal && (
        <div className="modal-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2001, padding: '20px', backdropFilter: 'blur(4px)' }}>
          <div className="modal-card" style={{ width: 'min(94vw, 560px)', maxHeight: '90vh', overflowY: 'auto', background: '#fff', borderRadius: 16, padding: 30, boxShadow: '0 20px 60px rgba(0,0,0,0.3)', position: 'relative', animation: 'slideIn 0.3s ease-out' }}>
            {/* Header */}
            <div style={{ textAlign: 'center', marginBottom: 25 }}>
              <div style={{ fontSize: '48px', marginBottom: '10px' }}>🔧</div>
              <h2 style={{ margin: '0 0 10px', fontSize: '24px', color: '#212529', fontWeight: '700' }}>
                New Vendor Unit Detected
              </h2>
              <p style={{ color: '#6c757d', fontSize: '15px', margin: '10px 0 0', lineHeight: '1.5' }}>
                The bill uses the unit <strong>"{unitTrainingData.unknownUnit}"</strong>
                <br />
                Please help us automate future conversions!
              </p>
              <button
                className="modal-close"
                onClick={() => setShowUnitTrainingModal(false)}
                style={{ position: 'absolute', right: 16, top: 16, border: 'none', background: 'transparent', fontSize: 20, cursor: 'pointer', color: '#999', padding: '4px 8px' }}
              >
                ✕
              </button>
            </div>

            {/* Form Content */}
            <div style={{ marginBottom: 20 }}>
              {/* Item Name (Display Only) */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 600, color: '#374151' }}>
                  Item Name
                </label>
                <div style={{
                  padding: '12px 16px',
                  background: '#f3f4f6',
                  borderRadius: 8,
                  fontSize: 15,
                  color: '#111827',
                  fontWeight: 500
                }}>
                  {unitTrainingData.itemName || 'Unknown Item'}
                </div>
              </div>

              {/* Unknown Unit (Display Only) */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 600, color: '#374151' }}>
                  Vendor Unit (from bill)
                </label>
                <div style={{
                  padding: '12px 16px',
                  background: '#fef3c7',
                  border: '2px solid #fbbf24',
                  borderRadius: 8,
                  fontSize: 16,
                  color: '#92400e',
                  fontWeight: 700,
                  letterSpacing: '0.5px'
                }}>
                  {unitTrainingData.unknownUnit}
                </div>
              </div>

              {/* Conversion Mapping */}
              <div style={{
                background: '#f9fafb',
                border: '2px dashed #d1d5db',
                borderRadius: 12,
                padding: 20,
                marginBottom: 20
              }}>
                <div style={{ textAlign: 'center', marginBottom: 16, fontSize: 14, color: '#6b7280', fontWeight: 600 }}>
                  CONVERSION MAPPING
                </div>

                {/* Standard Unit Selection */}
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 600, color: '#374151' }}>
                    Standard Unit <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <select
                    value={unitTrainingData.standardUnit}
                    onChange={(e) => setUnitTrainingData(prev => ({ ...prev, standardUnit: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #d1d5db',
                      borderRadius: 8,
                      fontSize: 15,
                      fontWeight: 500,
                      background: 'white',
                      cursor: 'pointer'
                    }}
                  >
                    <option value="kg">Kilogram (kg)</option>
                    <option value="g">Gram (g)</option>
                    <option value="L">Liter (L)</option>
                    <option value="ml">Milliliter (ml)</option>
                    <option value="piece">Piece</option>
                    <option value="dozen">Dozen</option>
                  </select>
                </div>

                {/* Conversion Formula */}
                <div style={{
                  background: 'white',
                  border: '2px solid #3b82f6',
                  borderRadius: 10,
                  padding: 16,
                  marginBottom: 16
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 18, fontWeight: 700, color: '#1e40af' }}>1</span>
                    <span style={{ fontSize: 16, fontWeight: 600, color: '#fbbf24', padding: '4px 12px', background: '#fef3c7', borderRadius: 6 }}>
                      {unitTrainingData.unknownUnit}
                    </span>
                    <span style={{ fontSize: 20, color: '#6b7280' }}>=</span>
                    <input
                      type="number"
                      value={unitTrainingData.conversionFactor}
                      onChange={(e) => setUnitTrainingData(prev => ({ ...prev, conversionFactor: e.target.value }))}
                      step="0.01"
                      min="0.01"
                      style={{
                        width: 100,
                        padding: '8px 12px',
                        border: '2px solid #3b82f6',
                        borderRadius: 6,
                        fontSize: 16,
                        fontWeight: 700,
                        textAlign: 'center',
                        color: '#1e40af'
                      }}
                    />
                    <span style={{ fontSize: 16, fontWeight: 600, color: '#059669', padding: '4px 12px', background: '#d1fae5', borderRadius: 6 }}>
                      {unitTrainingData.standardUnit}
                    </span>
                  </div>
                </div>

                {/* Example Calculation */}
                <div style={{
                  background: '#eff6ff',
                  border: '1px solid #bfdbfe',
                  borderRadius: 8,
                  padding: 12,
                  fontSize: 13,
                  color: '#1e40af'
                }}>
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>📊 Example:</div>
                  <div>
                    Current bill: <strong>{unitTrainingData.quantity} {unitTrainingData.unknownUnit}</strong> will convert to{' '}
                    <strong style={{ color: '#059669' }}>
                      {(unitTrainingData.quantity * (parseFloat(unitTrainingData.conversionFactor) || 1)).toFixed(2)} {unitTrainingData.standardUnit}
                    </strong>
                  </div>
                </div>
              </div>

              {/* Ripple Effect Notice */}
              <div style={{
                background: '#f0fdf4',
                border: '2px solid #86efac',
                borderRadius: 10,
                padding: 16,
                marginBottom: 20
              }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                  <div style={{ fontSize: 24, lineHeight: 1 }}>🔄</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, color: '#166534', marginBottom: 6, fontSize: 14 }}>
                      ✨ Ripple Effect Activated!
                    </div>
                    <div style={{ fontSize: 13, color: '#15803d', lineHeight: '1.5' }}>
                      Once saved, all future bills containing "<strong>{unitTrainingData.unknownUnit}</strong>" will automatically convert to <strong>{unitTrainingData.standardUnit}</strong>. No manual intervention needed!
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer Buttons */}
            <div style={{ display: 'flex', gap: 12 }}>
              <button
                onClick={() => setShowUnitTrainingModal(false)}
                style={{
                  flex: 1,
                  padding: '14px 20px',
                  background: '#f3f4f6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: 10,
                  fontWeight: 700,
                  fontSize: 15,
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.background = '#e5e7eb'}
                onMouseLeave={(e) => e.target.style.background = '#f3f4f6'}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveUnitTraining}
                style={{
                  flex: 2,
                  padding: '14px 20px',
                  background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 10,
                  fontWeight: 700,
                  fontSize: 15,
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4)',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 6px 16px rgba(59, 130, 246, 0.5)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.4)';
                }}
              >
                Save & Apply Conversion
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Duplicate Detection Modal */}
      {showDuplicateModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.75)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }}>
          <div style={{
            width: 'min(94vw, 600px)',
            maxHeight: '85vh',
            overflowY: 'auto',
            background: '#fff',
            borderRadius: 16,
            padding: 30,
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            position: 'relative'
          }}>

            <h2 style={{ fontSize: 24, fontWeight: 700, marginBottom: 8, color: '#000' }}>
              Duplicate Items Detected
            </h2>
            <p style={{ color: '#6b7280', marginBottom: 24, fontSize: 14 }}>
              The following {duplicateItems.length} item(s) already exist in the form. Select which duplicates to remove:
            </p>

            <div style={{ marginBottom: 20 }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                padding: '12px 16px',
                background: '#fff',
                borderRadius: 10,
                cursor: 'pointer',
                fontWeight: 600,
                fontSize: 14
              }}>
                <input
                  type="checkbox"
                  checked={selectedDuplicates.size === duplicateItems.length}
                  onChange={toggleSelectAllDuplicates}
                  style={{ marginRight: 10, width: 18, height: 18, cursor: 'pointer', accentColor: '#000' }}
                />
                Select All ({duplicateItems.length} items)
              </label>
            </div>

            <div style={{ maxHeight: '300px', overflowY: 'auto', marginBottom: 24 }}>
              {duplicateItems.map((dup, idx) => (
                <label
                  key={idx}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '12px 16px',
                    background: selectedDuplicates.has(idx) ? '#f3f4f6' : '#fff',
                    borderRadius: 10,
                    marginBottom: 8,
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    if (!selectedDuplicates.has(idx)) {
                      e.currentTarget.style.background = '#f9fafb';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!selectedDuplicates.has(idx)) {
                      e.currentTarget.style.background = '#fff';
                    }
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selectedDuplicates.has(idx)}
                    onChange={() => toggleDuplicateSelection(idx)}
                    style={{ marginRight: 12, width: 18, height: 18, cursor: 'pointer', accentColor: '#000' }}
                  />
                  <div style={{ flex: 1, textAlign: 'left' }}>
                    <div style={{ fontWeight: 600, fontSize: 15, color: '#111', marginBottom: 4, textAlign: 'left' }}>
                      {dup.item_name}
                    </div>
                    <div style={{ fontSize: 13, color: '#6b7280', textAlign: 'left' }}>
                      Qty: {dup.quantity} {dup.unit} • Price: ₹{dup.unit_price || '0.00'}
                    </div>
                  </div>
                </label>
              ))}
            </div>

            <div style={{ display: 'flex', gap: 12 }}>
              <button
                onClick={handleDuplicateModalRemove}
                disabled={selectedDuplicates.size === 0}
                style={{
                  flex: 1,
                  padding: '14px 20px',
                  background: selectedDuplicates.size === 0 ? '#e5e7eb' : '#E9ECEF',
                  color: '#000',
                  border: 'none',
                  borderRadius: 10,
                  fontWeight: 700,
                  fontSize: 15,
                  cursor: selectedDuplicates.size === 0 ? 'not-allowed' : 'pointer',
                  opacity: selectedDuplicates.size === 0 ? 0.5 : 1,
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  if (selectedDuplicates.size > 0) {
                    e.target.style.background = '#e8e8e8ff';
                  }
                }}
                onMouseLeave={(e) => {
                  if (selectedDuplicates.size > 0) {
                    e.target.style.background = '#E9ECEF';
                  }
                }}
              >
                Remove ({selectedDuplicates.size})
              </button>
              <button
                onClick={handleDuplicateModalKeep}
                style={{
                  flex: 1,
                  padding: '14px 20px',
                  background: '#000',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 10,
                  fontWeight: 700,
                  fontSize: 15,
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.background = '#000000ff'}
                onMouseLeave={(e) => e.target.style.background = '#000'}
              >
                Keep
              </button>
            </div>
          </div>
        </div>
      )}

      {/* NAR Resolution Modal */}
      {showNarResolution && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          padding: '20px',
          backdropFilter: 'blur(4px)'
        }}>
          <div style={{
            background: '#fff',
            borderRadius: '16px',
            maxWidth: '600px',
            width: '100%',
            padding: '30px',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            animation: 'slideIn 0.3s ease-out'
          }}>
            {/* Header */}
            <div style={{ textAlign: 'center', marginBottom: '25px' }}>
              <div style={{ fontSize: '48px', marginBottom: '10px' }}>🔄</div>
              <h2 style={{ margin: '0 0 10px', fontSize: '24px', color: '#212529', fontWeight: '700' }}>
                Item Names Auto-Resolved
              </h2>
              <p style={{ color: '#6c757d', fontSize: '15px', margin: 0 }}>
                The following items were automatically matched to your inventory
              </p>
            </div>

            {/* Resolutions List */}
            <div style={{
              background: '#ffffff',
              borderRadius: '12px',
              padding: '20px',
              marginBottom: '25px',
              maxHeight: '300px',
              overflowY: 'auto'
            }}>
              {narResolutions.map((res, idx) => (
                <div key={idx} style={{
                  background: '#fff',
                  borderRadius: '10px',
                  padding: '15px',
                  marginBottom: idx < narResolutions.length - 1 ? '12px' : '0',
                  border: '2px solid #28a745'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{
                      fontSize: '24px',
                      color: '#28a745'
                    }}>✓</span>
                    <div style={{ flex: 1 }}>
                      <div style={{
                        fontSize: '14px',
                        color: '#6c757d',
                        marginBottom: '5px'
                      }}>
                        Original Entry:
                      </div>
                      <div style={{
                        fontSize: '16px',
                        color: '#dc3545',
                        fontWeight: '600',
                        textDecoration: 'line-through',
                        marginBottom: '8px'
                      }}>
                        "{res.original_name}"
                      </div>
                      <div style={{
                        fontSize: '14px',
                        color: '#6c757d',
                        marginBottom: '5px'
                      }}>
                        Matched To:
                      </div>
                      <div style={{
                        fontSize: '18px',
                        color: '#28a745',
                        fontWeight: '700'
                      }}>
                        "{res.resolved_name}"
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => {
                  setShowNarResolution(false);
                  setNarResolutions([]);
                  // Accept the matched items - already saved
                }}
                style={{
                  flex: 1,
                  padding: '14px 24px',
                  background: '#28a745',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: '16px',
                  fontWeight: '700',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#218838'}
                onMouseLeave={(e) => e.currentTarget.style.background = '#28a745'}
              >
                ✓ Accept Match
              </button>

              <button
                onClick={async () => {
                  setShowNarResolution(false);

                  if (!narOriginalFormData) {
                    showModalMessage('Original form data not found. Please try again.', 'error');
                    return;
                  }

                  // Restore the original form data
                  setVendorName(narOriginalFormData.vendor);
                  applyVendorPhone(narOriginalFormData.vendorPhone);
                  setProductionDate(narOriginalFormData.productionDate);
                  setCurrentShift(narOriginalFormData.currentShift);

                  // Revert items to original names and clear item_id
                  const revertedItems = narOriginalFormData.items.map(item => {
                    const resolution = narResolutions.find(r => r.resolved_name === item.item_name);
                    if (resolution) {
                      return { ...item, item_name: resolution.original_name, item_id: null };
                    }
                    return item;
                  });

                  // Through the door: this replaces every row, and reverting a revert has
                  // to be possible. Not a snapshot — restoring is not a fill.
                  applyItems(revertedItems);
                  setNarResolutions([]);
                  setNarOriginalFormData(null);

                  // Show message that form is ready for new item creation
                  showModalMessage('Form restored. The items will be created as new inventory items. Please submit again.', 'info');
                }}
                style={{
                  flex: 1,
                  padding: '14px 24px',
                  background: '#6c757d',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: '16px',
                  fontWeight: '700',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#5a6268'}
                onMouseLeave={(e) => e.currentTarget.style.background = '#6c757d'}
              >
                + Create New Item
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Item Selection Modal (NAR Multiple Candidates) */}
      {showItemSelectionModal && itemSelectionData && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          backdropFilter: 'blur(8px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          padding: '20px',
          animation: 'fadeIn 0.3s ease-out'
        }}>
          <div style={{
            background: '#fff',
            borderRadius: '20px',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            maxWidth: '600px',
            width: '100%',
            maxHeight: '80vh',
            overflow: 'auto',
            padding: '30px',
            animation: 'slideUp 0.3s ease-out'
          }}>
            {/* Header */}
            <div style={{ textAlign: 'center', marginBottom: '25px' }}>
              <div style={{ fontSize: '48px', marginBottom: '10px' }}>
                {itemSelectionData.autoResolved ? '✅' : '🔍'}
              </div>
              <h2 style={{ margin: '0 0 10px', fontSize: '24px', color: '#212529', fontWeight: '700' }}>
                {itemSelectionData.autoResolved ? 'Item Auto-Matched' : 'Multiple Items Match'}
              </h2>
              <p style={{ color: '#6c757d', fontSize: '15px', margin: 0 }}>
                "{itemSelectionData.itemName}" at ₹{itemSelectionData.transactionIUP?.toFixed(2)}/unit
                {itemSelectionData.autoResolved
                  ? ' was automatically matched. Please confirm or select different item.'
                  : ` matches ${itemSelectionData.candidates?.length} items`}
              </p>
            </div>

            {/* Candidates List */}
            <div style={{ marginBottom: '25px' }}>
              {itemSelectionData.candidates?.map((candidate, idx) => (
                <div
                  key={candidate.item_id}
                  onClick={() => {
                    setItemSelectionData({
                      ...itemSelectionData,
                      selectedItemId: candidate.item_id
                    });
                  }}
                  style={{
                    background: itemSelectionData.selectedItemId === candidate.item_id ? '#e7f5ff' : '#ffffff',
                    border: itemSelectionData.selectedItemId === candidate.item_id ? '2px solid #1971c2' : '2px solid #dee2e6',
                    borderRadius: '12px',
                    padding: '16px',
                    marginBottom: '12px',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    position: 'relative'
                  }}
                  onMouseEnter={(e) => {
                    if (itemSelectionData.selectedItemId !== candidate.item_id) {
                      e.currentTarget.style.background = '#e9ecef';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (itemSelectionData.selectedItemId !== candidate.item_id) {
                      e.currentTarget.style.background = '#ffffff';
                    }
                  }}
                >
                  {/* Best Match Badge */}
                  {idx === 0 && (
                    <div style={{
                      position: 'absolute',
                      top: '-10px',
                      right: '16px',
                      background: '#ffd43b',
                      color: '#000',
                      padding: '4px 12px',
                      borderRadius: '12px',
                      fontSize: '11px',
                      fontWeight: '700',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                    }}>
                      🏆 BEST MATCH
                    </div>
                  )}

                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                    {/* Radio Button */}
                    <div style={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      border: '2px solid #1971c2',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      marginTop: '2px'
                    }}>
                      {itemSelectionData.selectedItemId === candidate.item_id && (
                        <div style={{
                          width: '14px',
                          height: '14px',
                          borderRadius: '50%',
                          background: '#1971c2'
                        }} />
                      )}
                    </div>

                    {/* Candidate Info */}
                    <div style={{ flex: 1 }}>
                      <div style={{
                        fontSize: '18px',
                        fontWeight: '700',
                        color: '#212529',
                        marginBottom: '8px'
                      }}>
                        {candidate.item_name}
                      </div>

                      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                        <div style={{
                          background: '#fff',
                          padding: '6px 12px',
                          borderRadius: '8px',
                          fontSize: '13px',
                          color: '#495057'
                        }}>
                          <strong>VCP:</strong> ₹{candidate.benchmarks?.vcp?.toFixed(2)}
                        </div>
                        <div style={{
                          background: candidate.deviationPercent < 20 ? '#d3f9d8' : '#fff3bf',
                          color: candidate.deviationPercent < 20 ? '#2b8a3e' : '#e67700',
                          padding: '6px 12px',
                          borderRadius: '8px',
                          fontSize: '13px',
                          fontWeight: '600'
                        }}>
                          {candidate.deviationPercent?.toFixed(1)}% deviation
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {/* Create New Item Option */}
              <div
                onClick={() => {
                  setItemSelectionData({
                    ...itemSelectionData,
                    selectedItemId: 'CREATE_NEW'
                  });
                }}
                style={{
                  background: itemSelectionData.selectedItemId === 'CREATE_NEW' ? '#e7f5ff' : '#ffffff',
                  border: itemSelectionData.selectedItemId === 'CREATE_NEW' ? '2px solid #1971c2' : '2px dashed #adb5bd',
                  borderRadius: '12px',
                  padding: '16px',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  if (itemSelectionData.selectedItemId !== 'CREATE_NEW') {
                    e.currentTarget.style.background = '#e9ecef';
                  }
                }}
                onMouseLeave={(e) => {
                  if (itemSelectionData.selectedItemId !== 'CREATE_NEW') {
                    e.currentTarget.style.background = '#ffffff';
                  }
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  {/* Radio Button */}
                  <div style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    border: '2px solid #1971c2',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    {itemSelectionData.selectedItemId === 'CREATE_NEW' && (
                      <div style={{
                        width: '14px',
                        height: '14px',
                        borderRadius: '50%',
                        background: '#1971c2'
                      }} />
                    )}
                  </div>

                  <div style={{ flex: 1 }}>
                    <div style={{
                      fontSize: '18px',
                      fontWeight: '700',
                      color: '#212529',
                      marginBottom: '4px'
                    }}>
                      ➕ Create as New Item
                    </div>
                    <div style={{ fontSize: '13px', color: '#6c757d' }}>
                      This is a different item not listed above
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => {
                  setShowItemSelectionModal(false);
                  setItemSelectionData(null);
                  setIsSubmitting(false);
                }}
                style={{
                  flex: 1,
                  padding: '14px 24px',
                  background: '#e9ecef',
                  color: '#495057',
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#dee2e6'}
                onMouseLeave={(e) => e.currentTarget.style.background = '#e9ecef'}
              >
                Cancel
              </button>

              <button
                onClick={async () => {
                  if (!itemSelectionData.selectedItemId) {
                    showModalMessage('Please select an item or choose "Create as New Item"', 'error');
                    return;
                  }

                  if (itemSelectionData.selectedItemId === 'CREATE_NEW') {
                    // User wants to create new item - resubmit with skip_nar flag
                    try {
                      setIsSubmitting(true);

                      const payload = {
                        vendor_name: vendorName.trim(),
                        vendor_phone: vendorPhone.trim(),
                        entry_method: ocrData ? 'Scan Bill' : 'Manual Entry',
                        scanned_image_id: ocrData?.image_id || null,
                        ocr_grand_total: financialAudit.ocrGrandTotal || null,
                        // `stock_in_date`, not `production_date`: the route reads exactly
                        // one key, and this rebuilt payload spelled it the other way — so
                        // every bill that came through this modal was filed under today
                        // however far back the purchase-date picker was set. Sent raw
                        // (it is already YYYY-MM-DD) rather than round-tripped through
                        // `new Date()`, which reads it as UTC midnight.
                        stock_in_date: productionDate || null,
                        skip_nar: true, // Skip NAR validation for this submission
                        // (bypass_vcp removed — new items are simply unbenchmarked;
                        // the gate stamps them provisional until history matures)
                        items: items.map(it => ({
                          item_id: null, // Force as new item
                          item_name: (it.item_name || '').trim(),
                          display_name: (it.item_name || '').trim(),
                          category: it.category,
                          quantity: Number(parseFloat(it.quantity) || 0),
                          unit: it.unit,
                          unit_price: Number(parseFloat(it.unit_price) || 0),
                          total_price: grossLineTotal(it, taxForStockInEnabled),   // T2
                          batch_number: (it.batch_number || '').trim(),
                          expiry_date: it.expiry_date,
                          cgst: Number(parseFloat(it.cgst) || 0),
                          sgst: Number(parseFloat(it.sgst) || 0),
                          tax_slab: String(parseFloat(it.tax_slab) || 0),
                          time: it.time,
                          client_lock: false,
                          original_ocr_name: it.original_ocr_name || '',
                          ...buildBmItemFields(it), // Bill Memory (server ignores while Off)
                          original_language: it.original_language || 'en',
                          translated_name: it.translated_name || '',
                          is_translated: it.is_translated || false
                        }))
                      };

                      const r = await makeApiCall('', payload);

                      setShowItemSelectionModal(false);
                      setItemSelectionData(null);

                      // F2 — the NAR resubmit sends no idempotency key, so it cannot be
                      // replayed. But it still saved a bill, and it still owed the rest:
                      // the draft had to go (or the saved bill can be restored from it) and
                      // an Excel-forced tax toggle had to be put back before the next bill.
                      const savedAfterNarNew = await finalizeSuccessfulSubmit(r);
                      if (savedAfterNarNew) {
                        showModalMessage(`✅ Submitted. ID: ${r.data.stock_in_id}${savedOnLine()}`, 'success');
                      }

                      // F3 — a replayed or failed save never clears the person's work.
                      if (savedAfterNarNew) setTimeout(() => resetFormAfterSuccess(), 1500);

                    } catch (err) {
                      console.error('Create new item error:', err);
                      showModalMessage(err.data?.message || err.message || 'Failed to create new item', 'error');
                    } finally {
                      setIsSubmitting(false);
                    }
                    return;
                  }

                  // User selected existing item - resubmit with manual selection
                  try {
                    setIsSubmitting(true);

                    // Update the item with selected item_id
                    const updatedItems = [...items];
                    const itemIndex = itemSelectionData.itemIndex || 0;
                    updatedItems[itemIndex].item_id = itemSelectionData.selectedItemId;

                    const payload = {
                      vendor_name: vendorName.trim(),
                      vendor_phone: vendorPhone.trim(),
                      entry_method: ocrData ? 'Scan Bill' : 'Manual Entry',
                      scanned_image_id: ocrData?.image_id || null,
                      ocr_grand_total: financialAudit.ocrGrandTotal || null,
                      // See the note on the sibling payload above — this key was
                      // `production_date`, which the route does not read.
                      stock_in_date: productionDate || null,
                      items: updatedItems.map(it => ({
                        item_id: it.item_id || null,
                        item_name: (it.item_name || '').trim(),
                        display_name: (it.item_name || '').trim(),
                        category: it.category,
                        quantity: Number(parseFloat(it.quantity) || 0),
                        unit: it.unit,
                        unit_price: Number(parseFloat(it.unit_price) || 0),
                        total_price: grossLineTotal(it, taxForStockInEnabled),   // T2
                        batch_number: (it.batch_number || '').trim(),
                        expiry_date: it.expiry_date,
                        cgst: Number(parseFloat(it.cgst) || 0),
                        sgst: Number(parseFloat(it.sgst) || 0),
                        tax_slab: String(parseFloat(it.tax_slab) || 0),
                        time: it.time,
                        client_lock: false,
                        original_ocr_name: it.original_ocr_name || '',
                        ...buildBmItemFields(it), // Bill Memory (server ignores while Off)
                        original_language: it.original_language || 'en',
                        translated_name: it.translated_name || '',
                        is_translated: it.is_translated || false
                      })),
                      manual_item_selection: {
                        itemIndex: itemIndex,
                        selectedItemId: itemSelectionData.selectedItemId
                      }
                    };

                    const r = await makeApiCall('', payload);

                    setShowItemSelectionModal(false);
                    setItemSelectionData(null);

                    // F2 — same as the create-new-item resubmit above: no key to replay,
                    // but the draft removal and the tax revert were still owed.
                    const savedAfterNarPick = await finalizeSuccessfulSubmit(r);
                    if (savedAfterNarPick) {
                      showModalMessage(`✅ Submitted. ID: ${r.data.stock_in_id}${savedOnLine()}`, 'success');
                    }

                    // F3 — never clear the form unless something was actually saved.
                    if (savedAfterNarPick) setTimeout(() => resetFormAfterSuccess(), 1500);

                  } catch (err) {
                    console.error('Resubmission error:', err);

                    // Check if backend requires price confirmation
                    if (err.data?.requiresConfirmation) {
                      const data = err.data;
                      const itemDetails = data.itemDetails || {};

                      // Close item selection modal first
                      setShowItemSelectionModal(false);

                      // Show VCP confirmation modal
                      setAutoCorrectionData({
                        requiresConfirmation: true,
                        item_name: itemDetails.item_name || 'Unknown',
                        original_unit_price: itemDetails.entered_price || 0,
                        corrected_unit_price: itemDetails.corrected_unit_price || itemDetails.historical_vcp || 0,
                        corrected_quantity: itemDetails.corrected_quantity,
                        original_total: itemDetails.entered_total || 0,
                        corrected_total: itemDetails.corrected_total || itemDetails.expected_total || 0,
                        correction_type: data.confirmationType || 'VCP_HIGH_PRICE_CONFIRMATION',
                        correction_message: data.confirmationPrompt || data.message || 'Please confirm the entered price.',
                        confidence: 0.95,
                        vcp: itemDetails.historical_vcp || 0,
                        deviation_percentage: itemDetails.deviation_percentage != null
                          ? Number(itemDetails.deviation_percentage).toFixed(1)
                          : (itemDetails.entered_price && itemDetails.historical_vcp
                              ? Math.abs(((itemDetails.entered_price - itemDetails.historical_vcp) / itemDetails.historical_vcp) * 100).toFixed(1)
                              : null),
                        isConfirmationOnly: true,
                        isBackendConfirmation: true,
                        // Stage 1.2 handshake token target
                        itemIndex: Number.isInteger(data.itemIndex) ? data.itemIndex : (itemSelectionData.itemIndex || 0),
                        requiredConfirmation: data.required_confirmation || null
                      });

                      // Store the payload for resubmission after confirmation
                      // (no bypass flag — the confirm handler attaches the per-item token)
                      const updatedItems = [...items];
                      const itemIndex = itemSelectionData.itemIndex || 0;
                      updatedItems[itemIndex].item_id = itemSelectionData.selectedItemId;

                      setPendingSubmitData({
                        vendor_name: vendorName.trim(),
                        vendor_phone: vendorPhone.trim(),
                        ocr_grand_total: financialAudit.ocrGrandTotal || null,
                        // `stock_in_date`, not `production_date`: the route reads exactly
                        // one key, and this rebuilt payload spelled it the other way — so
                        // every bill that came through this modal was filed under today
                        // however far back the purchase-date picker was set. Sent raw
                        // (it is already YYYY-MM-DD) rather than round-tripped through
                        // `new Date()`, which reads it as UTC midnight.
                        stock_in_date: productionDate || null,
                        items: updatedItems.map(it => ({
                          item_id: it.item_id || null,
                          item_name: (it.item_name || '').trim(),
                          display_name: (it.item_name || '').trim(),
                          category: it.category,
                          quantity: Number(parseFloat(it.quantity) || 0),
                          unit: it.unit,
                          unit_price: Number(parseFloat(it.unit_price) || 0),
                          batch_number: (it.batch_number || '').trim(),
                          expiry_date: it.expiry_date,
                          time: it.time,
                          client_lock: false,
                          original_ocr_name: it.original_ocr_name || '',
                          ...buildBmItemFields(it), // Bill Memory (server ignores while Off)
                          original_language: it.original_language || 'en',
                          translated_name: it.translated_name || '',
                          is_translated: it.is_translated || false
                        })),
                        manual_item_selection: {
                          itemIndex: itemIndex,
                          selectedItemId: itemSelectionData.selectedItemId
                        }
                      });

                      setShowAutoCorrectionModal(true);
                      setItemSelectionData(null); // Clear selection data
                      setIsSubmitting(false);
                      return;
                    }

                    // Other errors
                    showModalMessage(err.response?.data?.message || err.message || 'Submission failed', 'error');
                  } finally {
                    setIsSubmitting(false);
                  }
                }}
                disabled={!itemSelectionData.selectedItemId}
                style={{
                  flex: 2,
                  padding: '14px 24px',
                  background: itemSelectionData.selectedItemId ? '#1971c2' : '#adb5bd',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: '16px',
                  fontWeight: '700',
                  cursor: itemSelectionData.selectedItemId ? 'pointer' : 'not-allowed',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  if (itemSelectionData.selectedItemId) {
                    e.currentTarget.style.background = '#1864ab';
                  }
                }}
                onMouseLeave={(e) => {
                  if (itemSelectionData.selectedItemId) {
                    e.currentTarget.style.background = '#1971c2';
                  }
                }}
              >
                ✓ Confirm Selection
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Auto-Correction Confirmation Modal */}
      {showAutoCorrectionModal && autoCorrectionData && (
        <div className="auto-correction-modal-overlay">
          <div className="auto-correction-modal-content">
            <div className="auto-correction-modal-top">
              <h2>
                {autoCorrectionData.isConfirmationOnly ? (
                  autoCorrectionData.isQuantityCheck ? 'Quantity looks far higher than usual' : 'Price confirmation required'
                ) : (
                  'Correction confirmation required'
                )}
              </h2>
              <p>
                {autoCorrectionData.isConfirmationOnly
                  ? (autoCorrectionData.isQuantityCheck
                    ? (autoCorrectionData.quantity_summary
                      || 'Quantity entered is higher than expected - please verify')
                    : (autoCorrectionData.isPriceOutlier || autoCorrectionData.deviation_direction)
                      ? `Price entered is ${autoCorrectionData.deviation_direction === 'lower' ? 'much lower' : 'much higher'} than usual - please verify`
                      : 'Price entered is higher than expected - please verify')
                  : autoCorrectionData.correction_type === 'DSF_100X_SHIFT'
                    ? 'Decimal point shift detected (×100 or ÷100)'
                    : 'Price deviation from historical average detected'}
              </p>
            </div>

            <div className="auto-correction-modal-section">
              <div className="correction-item">
                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'flex-start', gap: '8px', marginBottom: '16px', width: '100%' }}>
                  <div style={{ fontSize: '14px', color: '#000000', fontWeight: 700, whiteSpace: 'nowrap' }}>
                    Ingredient Name:
                  </div>
                  <div style={{ fontSize: '16px', color: '#111827', fontWeight: 700, textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {autoCorrectionData.item_name}
                  </div>
                </div>

                {/* Display quantity or price based on check type */}
                {autoCorrectionData.isQuantityCheck ? (
                  // QUANTITY DISPLAY
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <div>
                      <div style={{ fontSize: '12px', color: '#868e96', marginBottom: '4px' }}>
                        {autoCorrectionData.isConfirmationOnly ? 'Entered Quantity' : 'Original Quantity'}
                      </div>
                      <div style={{
                        fontSize: '20px',
                        fontWeight: '700',
                        color: autoCorrectionData.isConfirmationOnly ? '#111827' : '#6b7280',
                        textDecoration: autoCorrectionData.isConfirmationOnly ? 'none' : 'line-through'
                      }}>
                        {autoCorrectionData.original_quantity?.toLocaleString('en-IN')} {autoCorrectionData.original_unit}
                      </div>
                    </div>

                    <div style={{ fontSize: '20px', color: '#9ca3af' }}>→</div>

                    <div>
                      <div style={{ fontSize: '12px', color: '#868e96', marginBottom: '4px' }}>
                        {autoCorrectionData.isConfirmationOnly ? 'Expected Quantity' : 'Corrected Quantity'}
                      </div>
                      <div style={{
                        fontSize: '20px',
                        fontWeight: '700',
                        color: '#111827'
                      }}>
                        {autoCorrectionData.corrected_quantity?.toLocaleString('en-IN')} {autoCorrectionData.original_unit}
                      </div>
                    </div>
                  </div>
                ) : autoCorrectionData.isCriticalPriceError ? (
                  // CRITICAL PRICE — what was entered against what is usual; the numbers
                  // themselves are changed in the edit box below.
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#000000', fontWeight: 400 }}>
                      <div>
                        Entered: ₹{autoCorrectionData.original_unit_price?.toFixed(2)}
                      </div>
                      <div>
                        Usual: ₹{autoCorrectionData.corrected_unit_price?.toFixed(2)}
                      </div>
                    </div>
                  </div>
                ) : (
                  // PRICE DISPLAY
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <div>
                      <div style={{ fontSize: '12px', color: '#868e96', marginBottom: '4px' }}>
                        {autoCorrectionData.isConfirmationOnly ? 'Entered Price' : 'Original Price'}
                      </div>
                      <div style={{
                        fontSize: '20px',
                        fontWeight: '700',
                        color: autoCorrectionData.isConfirmationOnly ? '#111827' : '#6b7280',
                        textDecoration: autoCorrectionData.isConfirmationOnly ? 'none' : 'line-through'
                      }}>
                        ₹{autoCorrectionData.original_unit_price?.toLocaleString('en-IN')}
                      </div>
                    </div>

                    <div style={{ fontSize: '20px', color: '#9ca3af' }}>→</div>

                    <div>
                      <div style={{ fontSize: '12px', color: '#868e96', marginBottom: '4px' }}>
                        {autoCorrectionData.isConfirmationOnly ? 'Expected Price' : 'Corrected Price'}
                      </div>
                      <div style={{
                        fontSize: '20px',
                        fontWeight: '700',
                        color: '#111827'
                      }}>
                        ₹{autoCorrectionData.corrected_unit_price?.toLocaleString('en-IN')}
                      </div>
                    </div>
                  </div>
                )}

                {/* VCP Info Box (for price checks) or Quantity threshold info */}
                {autoCorrectionData.isQuantityCheck && autoCorrectionData.vcp_quantity ? (
                  <div className="auto-correction-info-box">
                    <div className="label">You normally buy about</div>
                    <div className="value">{autoCorrectionData.vcp_quantity?.toLocaleString('en-IN')} {autoCorrectionData.original_unit}</div>
                    {autoCorrectionData.deviation_percentage && (
                      <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>Deviation: +{autoCorrectionData.deviation_percentage}%</div>
                    )}
                  </div>
                ) : autoCorrectionData.vcp ? (
                  <div className="auto-correction-info-box">
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'baseline', gap: '6px', textAlign: 'center' }}>
                      <div className="label" style={{ margin: 0 }}>Usual price (recent purchases)</div>
                      <div className="value" style={{ margin: 0 }}>: ₹{autoCorrectionData.vcp?.toLocaleString('en-IN')}</div>
                    </div>
                    {autoCorrectionData.deviation_label ? (
                      <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>{autoCorrectionData.deviation_label}</div>
                    ) : autoCorrectionData.deviation_percentage != null && autoCorrectionData.deviation_percentage !== '0' && autoCorrectionData.deviation_percentage !== 0 && (
                      <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>Deviation: {autoCorrectionData.deviation_percentage}%</div>
                    )}
                  </div>
                ) : null}
              </div>

              {/* Fix the numbers right here — no trip back to the form. */}
              {correctionEdit.available && (
                <div className="auto-correction-edit" style={{ margin: '4px 0 16px', padding: '14px', border: '1px solid #e5e7eb', borderRadius: '10px', background: '#f9fafb', textAlign: 'left' }}>
                  <div style={{ fontSize: '13px', fontWeight: 700, color: '#111827', marginBottom: '10px' }}>
                    Wrong number? Change it here
                  </div>
                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    <label style={{ flex: '1 1 130px', fontSize: '12px', fontWeight: 600, color: '#374151' }}>
                      Edit Quantity ({correctionEdit.unit})
                      <input
                        type="number"
                        step="any"
                        min="0"
                        inputMode="decimal"
                        aria-label="Edit quantity"
                        className="auto-correction-edit-qty"
                        value={correctionEdit.qtyStr}
                        onChange={(e) => {
                          const v = e.target.value;
                          setAutoCorrectionData(prev => (prev ? { ...prev, editQty: v } : prev));
                        }}
                        disabled={isSubmitting}
                        style={{ display: 'block', width: '100%', marginTop: '6px', padding: '10px', fontSize: '16px', fontWeight: 700, border: '1px solid #d1d5db', borderRadius: '8px', boxSizing: 'border-box' }}
                      />
                    </label>
                    <label style={{ flex: '1 1 130px', fontSize: '12px', fontWeight: 600, color: '#374151' }}>
                      Edit Unit Price (₹ per {correctionEdit.unit})
                      <input
                        type="number"
                        step="any"
                        min="0"
                        inputMode="decimal"
                        aria-label="Edit unit price"
                        className="auto-correction-edit-price"
                        value={correctionEdit.priceStr}
                        onChange={(e) => {
                          const v = e.target.value;
                          setAutoCorrectionData(prev => (prev ? { ...prev, editPrice: v } : prev));
                        }}
                        disabled={isSubmitting}
                        style={{ display: 'block', width: '100%', marginTop: '6px', padding: '10px', fontSize: '16px', fontWeight: 700, border: '1px solid #d1d5db', borderRadius: '8px', boxSizing: 'border-box' }}
                      />
                    </label>
                  </div>
                  <div style={{ marginTop: '10px', fontSize: '13px', color: '#374151' }}>
                    {correctionEdit.valid ? (
                      <>
                        Line total: <strong>₹{correctionEdit.newTotal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                        {correctionEdit.edited && (
                          <span style={{ color: '#6b7280' }}>
                            {' '}(was ₹{correctionEdit.baseTotal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })})
                          </span>
                        )}
                      </>
                    ) : (
                      <span style={{ color: '#b91c1c' }}>Enter a quantity and a unit price above zero.</span>
                    )}
                  </div>
                  {correctionEdit.edited && (
                    <div style={{ marginTop: '6px', fontSize: '12px', color: '#6b7280' }}>
                      Saving updates this line on the bill and checks the new numbers again.
                    </div>
                  )}
                </div>
              )}

              <div className="auto-correction-actions">
                <button
                  onClick={() => {
                    setShowAutoCorrectionModal(false);
                    setAutoCorrectionData(null);
                    setPendingSubmitData(null);
                    setIsSubmitting(false);
                  }}
                  className="auto-correction-btn reject"
                >
                  Cancel
                </button>

                {/* DSF suggested different numbers. Confirm takes the suggestion; this saves
                    the bill as entered. Without it a real price DSF read as a slipped
                    decimal could not be saved at all: Confirm wrote DSF's numbers, and
                    typing the real ones back in raised the same pop-up again. */}
                {canKeepEnteredNumbers && (
                  <button
                    onClick={() => confirmAutoCorrection({ keepAsEntered: true })}
                    className="auto-correction-btn reject"
                    disabled={isSubmitting}
                  >
                    Keep my numbers
                  </button>
                )}

                <button
                  onClick={() => confirmAutoCorrection()}
                  className="auto-correction-btn confirm"
                  disabled={isSubmitting || (correctionEdit.available && !correctionEdit.valid)}
                >
                  {correctionEdit.edited
                    ? (isSubmitting ? 'Saving...' : 'Save changes')
                    : (isSubmitting ? 'Confirming...' : (canKeepEnteredNumbers ? 'Use correction' : 'Confirm'))}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
        * PIECE GUARD (§4, §7)
        *
        * Exactly one of these is ever on screen at a time, and none of them has an
        * "Accept All" or a "Skip All". On a 60-line bill, fifteen popups in a row
        * means that by the fourth the person is clicking whatever makes it go away —
        * so 3+ flags become the review list instead.
        * ══════════════════════════════════════════════════════════════════════ */}

      {/* §4 — Step 1, the piece definition. THE screen the whole feature exists to show. */}
      {pieceGate?.activeBlocker?.code === 'piece_profile_required' && (() => {
        const idx = pieceGate.activeBlocker.index;
        const row = pieceGate.payload?.items?.[idx] || {};
        /**
         * THE SERVER'S ID WINS. It raised this blocker only after checking the id was a
         * positive integer, so it is the authority; `row.item_id` is a re-derivation by
         * array index against whichever payload this code path happened to stash, and when
         * that came back undefined the person completed an unskippable screen and was told
         * "A valid item_id is required." with no way out. Fall back to the row only for a
         * server that predates the blocker carrying its id.
         */
        const resolvedItemId = Number.isInteger(pieceGate.activeBlocker.item_id) && pieceGate.activeBlocker.item_id > 0
          ? pieceGate.activeBlocker.item_id
          : row.item_id;
        return (
          <PieceProfileModal
            key={`piece-profile-gate-${idx}-${resolvedItemId || row.item_name}`}
            isOpen
            itemId={resolvedItemId}
            itemName={row.display_name || row.item_name || 'this item'}
            category={row.category}
            enteredQuantity={row.quantity}
            enteredTotal={row.total_price}
            suggestedPiecesPerPack={pieceGate.activeBlocker.suggested_pieces_per_pack}
            rawItemName={row.display_name || row.item_name}
            originalOcrName={row.original_ocr_name}
            saving={pieceProfileSaving}
            error={pieceProfileError}
            onSave={savePieceProfile}
            onCancel={() => { setPieceGate(null); setPieceProfileError(null); }}
          />
        );
      })()}

      {/* §7 Popup D — one review list. */}
      {pieceGate?.bulkReview && !pieceGate.activeBlocker && (
        <PieceBulkReview
          review={pieceGate.bulkReview}
          items={pieceGate.payload?.items || []}
          resolvedIndexes={pieceGate.resolvedIndexes}
          onPick={(entry) => {
            if (entry === null) {
              requestPieceFinalConfirm(pieceGate);
              return;
            }
            setPieceGate(prev => prev && { ...prev, activeBlocker: entry });
          }}
          onCancel={() => setPieceGate(null)}
        />
      )}

      {/* ── H8.2 — §8.1's SUGGESTIONS, made visible without interrupting ──────────
          The backend returns its unproven verdicts as `gate_warnings` and there were zero
          consumers of that field anywhere in the frontend. So for a provisional profile
          the person got nothing at all: no block (correct — it may not accuse) and no
          suggestion either (wrong — it is supposed to suggest). Half of §8.1 simply did
          not exist on screen.

          A summary line, never a modal. A warning that interrupts is a block wearing a
          different colour, and §8.1 is precisely the rule that says an unproven profile
          may not stop anybody. */}
      {saveWarnings.length > 0 && (
        <div
          role="status"
          style={{
            margin: '10px 0', padding: '9px 12px', borderRadius: 8,
            background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', fontSize: 13,
          }}
        >
          <button
            type="button"
            onClick={() => setSaveWarningsOpen(o => !o)}
            style={{ background: 'none', border: 'none', padding: 0, color: 'inherit', font: 'inherit', cursor: 'pointer', textAlign: 'left' }}
          >
            {saveWarnings.length} item{saveWarnings.length === 1 ? '' : 's'} flagged for review — saved, but kept out of price history
            {' '}<span style={{ textDecoration: 'underline' }}>{saveWarningsOpen ? 'hide' : 'show'}</span>
          </button>
          {saveWarningsOpen && (
            <ul style={{ margin: '8px 0 0', paddingLeft: 18 }}>
              {saveWarnings.map((w, i) => (
                <li key={`${w.code}-${i}`} style={{ marginBottom: 4 }}>{w.message}</li>
              ))}
            </ul>
          )}
          <button
            type="button"
            onClick={() => setSaveWarnings([])}
            style={{ marginLeft: 10, background: 'none', border: 'none', color: '#92400e', textDecoration: 'underline', cursor: 'pointer', fontSize: 12 }}
          >
            dismiss
          </button>
        </div>
      )}

      {/* ── P5 — the new pcs items this bill created, asked about NOW ─────────────
          They saved, and every one of them is quarantined from the price benchmark until
          somebody says what one piece is. Asking here — while the delivery is still on the
          counter — is what makes the quarantine temporary rather than permanent: saving
          the profile retroactively releases that item's earlier rows on the server.
          Dismissing this loses nothing and poisons nothing; the rows simply wait. */}
      {pieceProfileQueue.length > 0 && !pieceGate && (() => {
        const next = pieceProfileQueue[0];
        return (
          <PieceProfileModal
            /**
             * A STABLE IDENTITY, so React reconciles this modal instead of remounting it.
             *
             * It sits among conditional siblings (the warnings summary, the deferred-blocker
             * notice) that flip between `false` and an element as the form re-renders, and
             * without a key that shifts this element's position in the children array — so
             * React unmounts and rebuilds it, and every field the person has typed so far is
             * silently wiped. Found by rendering the thing and typing into it: a description
             * entered before an unrelated re-render simply vanished.
             */
            key={`piece-profile-new-${next.item_id}`}
            isOpen
            itemId={next.item_id}
            itemName={next.item_name || 'this item'}
            category={next.category}
            enteredQuantity={next.quantity}
            enteredTotal={next.total_price}
            rawItemName={next.item_name}
            saving={pieceProfileSaving}
            error={pieceProfileError}
            onSave={async (data) => {
              setPieceProfileSaving(true);
              setPieceProfileError(null);
              try {
                const businessId = UnitMappingService.getBusinessId();
                const res = await http.post(`/stock-in/v2/piece-profiles?businessId=${businessId}`, {
                  ...data,
                  vendor_id: currentVendorId,
                  businessId,
                });
                if (!res?.success) throw new Error(res?.error || 'Could not save the piece definition');
                // The bill is already committed, so there is no row left to correct — the
                // count that was entered IS what is in stock. All this does is define the
                // piece and release the quarantine on the purchases already made.
                invalidatePieceCachesFor([{ item_id: next.item_id }]);
                setPieceProfileQueue(q => q.slice(1));
              } catch (e) {
                setPieceProfileError(e.data?.error || e.message || 'Could not save the piece definition');
              } finally {
                setPieceProfileSaving(false);
              }
            }}
            onCancel={() => { setPieceProfileQueue(q => q.slice(1)); setPieceProfileError(null); }}
          />
        );
      })()}

      {/* H7.2 — the OTHER blockers on this bill are named rather than swallowed. They are
          re-raised by the gate on the resubmit, where the shared router opens the modal
          each one needs; saying so now stops the second round trip being a surprise. */}
      {pieceGate?.deferredBlockers?.length > 0 && (
        <div
          role="status"
          style={{
            margin: '10px 0', padding: '9px 12px', borderRadius: 8,
            background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1e40af', fontSize: 13,
          }}
        >
          {pieceGate.deferredBlockers.length} other item{pieceGate.deferredBlockers.length === 1 ? '' : 's'} on this
          {' '}bill also need{pieceGate.deferredBlockers.length === 1 ? 's' : ''} attention — you will be asked about
          {' '}{pieceGate.deferredBlockers.length === 1 ? 'it' : 'them'} once the piece questions are answered.
          <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
            {pieceGate.deferredBlockers.map((b, i) => (
              <li key={`${b.code}-${i}`}>{b.message}</li>
            ))}
          </ul>
        </div>
      )}

      {/* ── The Stock-In Safety Engine's popup — STEP 8.7, only while the guard is on ──────
          Save turns the answers into the existing tokens and re-enters the ladder; "Back to
          edit" keeps the corrected values on the rows and sends nothing. */}
      {cxPopup && (
        <CxCheckPopup
          key={`cx-${cxPopup.key}`}
          lines={cxPopup.lines}
          scanUnclear={cxPopup.scanUnclear}
          otherTotal={cxPopup.otherTotal}
          onSave={(results) => cxPopupDone(results, true)}
          onCancel={(results) => cxPopupDone(results, false)}
        />
      )}

      {/* ── The repeat-purchase check — packet items only, once per item per bill ──────
          Fires from STEP 8.6 of the submit ladder, before the bill is sent, so "Cancel"
          leaves the person on their own rows with nothing saved and nothing to undo.
          Answering the last one re-enters the ladder, which then walks past this step
          because the ref already holds every id. */}
      {packConfirmQueue.length > 0 && (
        <PiecePackConfirmPopup
          key={`pack-confirm-${packConfirmQueue[0].item_id}`}
          entry={packConfirmQueue[0]}
          remaining={packConfirmQueue.length - 1}
          onContinue={() => {
            const entry = packConfirmQueue[0];
            // Written synchronously — the re-entry below reads this, not state.
            packConfirmedIdsRef.current = [...packConfirmedIdsRef.current, entry.item_id];
            const rest = packConfirmQueue.slice(1);
            setPackConfirmQueue(rest);
            // Last one answered → carry on with the submission that was interrupted.
            if (rest.length === 0) setTimeout(() => { handleSubmitClick(); }, 0);
          }}
          onCancel={() => {
            // Nothing has been sent. The row is theirs to fix, and the question will be
            // asked again on the next attempt — the ref is deliberately NOT written here.
            setPackConfirmQueue([]);
            showModalMessage(
              `Fix the quantity or price for "${packConfirmQueue[0].item_name}" on the row, then submit again.`,
              'error'
            );
          }}
          onFixQuantity={(correctedQty) => {
            const entry = packConfirmQueue[0];
            // The proposal lands in the field and the submission STOPS. Nothing is sent,
            // so the person sees the changed row and presses Submit themselves — §7's
            // "it proposes, a human confirms", with the proposal filled in rather than
            // described.
            setItems((prev) => prev.map((row, i) => (
              i === entry.index ? { ...row, quantity: String(correctedQty) } : row
            )));
            // Marked answered for THIS bill. The person has just been shown both rates and
            // made a deliberate choice about this item; asking the identical question again
            // on the re-submit is the confirmation-nobody-can-fail this popup was rewritten
            // to avoid — and it would re-offer a x{pack} that now compounds on the fixed
            // number. The hard PIECE GUARD ladder still runs on submit either way, so a
            // quantity that is genuinely wrong is still stopped; only this soft read-back
            // is spent.
            packConfirmedIdsRef.current = [...packConfirmedIdsRef.current, entry.item_id];
            setPackConfirmQueue([]);
            // An InfoToast, NEVER showModalMessage's 'success'. That modal's close handler
            // is the global form reset (resetFormAfterSuccess), so announcing the fix
            // through it wiped the whole bill the moment the person clicked OK — which
            // reads exactly like the bill having been submitted. The point of this button
            // is that the row survives to be checked, so the announcement cannot be the
            // one thing that destroys it.

          }}
        />
      )}

      {/* Popups A / B / C — the pack-mistake, price-change and loose-count questions. */}
      {pieceGate?.activeBlocker && pieceGate.activeBlocker.code !== 'piece_profile_required' && (
        <PiecePopupFor
          blocker={pieceGate.activeBlocker}
          items={pieceGate.payload?.items || []}
          onResolve={(token) => resolvePieceAnswer({ ...token, index: pieceGate.activeBlocker.index })}
          onCancel={() => {
            if (pieceGate.bulkReview) setPieceGate(prev => prev && { ...prev, activeBlocker: null });
            else setPieceGate(null);
          }}
        />
      )}

    </div>

  );
};

export default StockInForm;
